import * as THREE from 'three';
import { List } from 'ts-generic-collections-linq';
import nodeRSA from "node-rsa";
import axios from "axios";
import Delaunator from 'delaunator';
import { CSG } from 'three-csg-ts';
import { createApiEndpoint } from '../api/createApiEndpoint.js';
import {
    applyEditorLayoutToSiteLikePreview,
    siteUsesEditorBinLocalCables,
} from '../components/layoutDraftToSite.js';
import {
    attachPreviewPolygonsFromLayoutSides,
    editorDraftPayloadIsValid,
    findLayoutBinForSiteBin,
    findSiteBinForLayoutBin,
    isLayoutPlaceholderBinId,
    catalogBinDataIdForBinRef,
    layoutCablesForLayoutBin,
    saveLayoutProxyForSite3d,
    extractLayoutProxyFromEditorDraft,
    extractLayoutProxyFromSiteFetch,
    parseSiteLayoutEditorDraftFromApi,
    normalizeFocusedBinFootprintForBinView,
    promoteLayoutSidesForDrawSite,
    syncAframeLayoutSensorPositionsFromBts,
    rehydrateLayoutGeometryForDrawSite,
    resolveLayoutProxyForSite3d,
    siteHasCommittedSite3dLayoutProxy,
    siteUsesEditorSite3dLayout,
    siteLayoutLikelyFromEditor,
    siteLayoutLooksLegacyManual,
} from '../components/layoutTableProxy.js';
import {
    attachHumidityLayoutSensorsToSite,
    isHumidityLayoutSensor,
    isHumiditySensor,
    syncHumidityLayoutSensorValues,
    temperatureSensorsFromCable,
} from '../3ds/bin/cable/humidity3d.js';
import {
    resolveBtsCableTopPoint,
    resolveLayoutCableTopPoint,
    flatBinUsesCatalogCableScale,
    isFlatThreeTenRowMajorLayout,
    flatHouseCableGridDimensions,
    flatHouseGridFromCableLayout,
    layoutFootprintSpanFt,
    orientedLayoutFootprintFt,
    binIsRoundSilo,
    parseRoundBinCableLayers,
    roundBinCableWalkIndex,
} from '../components/siteLayoutStice.js';
import {
    isMobile,
    isTablet,
    isDesktop,
    isBrowser,
    isAndroid,
    isIOS,
    deviceType, // 'browser', 'mobile', 'tablet'
} from 'react-device-detect';
import { Goal } from 'lucide-react';

Array.prototype.SelectMany = function (keyGetter) {
    return this.map(x => keyGetter(x)).reduce((a, b) => a.concat(b));
}

Array.prototype.isEmpty = function () {
    return this.length === 0;
}

Array.prototype.any = function (func) {
    return this.some(func || function (x) { return x });
}

const grainLevelMaterial = new THREE.MeshStandardMaterial({
    color: "rgb(235, 211, 124)",
    emissive: "rgb(0, 0, 0)",
    emissiveIntensity: 0.25,
    toneMapped: false,
    envMapIntensity: 1,
    transparent: true,
    opacity: 0.9,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
    roughness: 0.4,
    metalness: 1,
    //fog: true,
    //wireframe: false,
    //normalMap: normalMapTexture,
});

/**
 * Material used by `renderSiteBinLevels` for the BinLevel fill cylinders/boxes inside
 * each bin in Site3d. Tuned to look like corn rather than polished metal:
 *
 *   - `metalness: 0`, `roughness: 0.95`  -- no specular highlight, no mirror reflection.
 *     The previous `grainLevelMaterial` (metalness 1, roughness 0.4) made the top cap
 *     blow out in the top-down camera view because it acted like a polished gold disc
 *     bouncing the directional light straight at the camera.
 *   - `toneMapped: true`, `envMapIntensity: 0.25`, `emissiveIntensity: 0`  -- let the
 *     scene's tone mapper clamp brightness so the fill blends with the rest of the
 *     scene at all angles instead of glowing.
 *   - `opacity: 0.55`, `depthWrite: false`  -- legibility-critical. With opacity 0.9 +
 *     depthWrite true the fill was an opaque wall in front of the cable temperatures.
 *     Disabling depthWrite lets the temperature text and cable meshes draw through the
 *     fill at every camera angle (incl. top-down), and the lower opacity keeps the
 *     corn color from washing the digits out.
 *   - Color is a warm corn-yellow with a touch of brown so it doesn't read as "lemon".
 */
const binLevelFillMaterial = new THREE.MeshStandardMaterial({
    color: "rgb(214, 172, 76)",
    emissive: "rgb(0, 0, 0)",
    emissiveIntensity: 0,
    toneMapped: true,
    envMapIntensity: 0.25,
    transparent: true,
    opacity: 0.55,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    roughness: 0.95,
    metalness: 0,
    flatShading: false,
});

/** Site-card bin tile SVG fill — matches `binLevelFillMaterial` color and opacity in Site3d. */
const BIN_LEVEL_SITE_CARD_FILL = "rgba(214, 172, 76, 0.55)";

// Development: use same-origin (empty) so the webpack dev server proxies to the API (see src/setupProxy.js).
// That avoids CORS and browser TLS issues with https://localhost:44328. Override with REACT_APP_API_URL.
// Production build: use full URL (set REACT_APP_API_URL in CI or build env if needed).
//const ApiUrl = 'https://www.grainwave.app';
const ApiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? '' : 'https://www.grainwave.app');

const Endpoints = {
    GrainWave: '/api/gw',
    Email: '/email',
    Login: '/login',
    Refresh: '/refresh',
    Renew: '/renew',
    UserSites: '/usersites',
    FetchBase64: '/fetchb',
    FetchCompressed: '/fetchc',
    FetchEncrypted: '/fetche',
};

let gidc = 1;
const uuids = [];
const dateFormat = { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };

/** Admin UI: roles come from OAuth login/refresh/renew (`gw.roles`) and match GrainWaveRole in the database (e.g. Administrator). */
const isAdminUser = (email) => {
    if (!email || !gw.roles || !Array.isArray(gw.roles)) return false;
    return gw.roles.includes('Administrator');
};

// Normalize GUID for comparison - API may return different format (with/without dashes, case)
const normalizeGuidForCompare = (g) => (!g ? '' : String(g).toLowerCase().replace(/-/g, ''));

// Helper function to save selected site info
const saveSelectedSite = () => {
    try {
        if (gw.selectedSite && gw.selectedSite.siteGuid) {
            const selectedSiteInfo = {
                siteGuid: gw.selectedSite.siteGuid,
                siteIndex: gw.selectedSite.siteIndex,
                siteName: gw.selectedSite.siteName,
                lastScan: gw.selectedSite.lastScan
            };
            localStorage.setItem('grainwave_selectedSite', JSON.stringify(selectedSiteInfo));
        }
    } catch (error) {
        console.error('Error saving selected site to localStorage:', error);
    }
};

// Helper function to restore selected site info
const restoreSelectedSite = () => {
    try {
        const selectedSiteStr = localStorage.getItem('grainwave_selectedSite');
        if (selectedSiteStr) {
            const selectedSiteInfo = JSON.parse(selectedSiteStr);
            if (selectedSiteInfo && selectedSiteInfo.siteGuid) {
                return selectedSiteInfo;
            }
        }
    } catch (error) {
        console.error('Error restoring selected site from localStorage:', error);
    }
    return null;
};

/** Format site/API lastScan ISO for display (matches CustomerSites / SiteCard). */
const formatLastScanDisplay = (isoDate) => {
    if (isoDate == null || isoDate === '') return null;
    try {
        const raw = typeof isoDate === 'string' ? isoDate.trim() : String(isoDate);
        const normalized = raw.includes('T') ? raw.replace('Z', '-05:00') : raw;
        const d = new Date(normalized);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString('en-us', dateFormat).replaceAll('/', '-').replace(',', '');
    } catch {
        return null;
    }
};

/** Update displayed lastScan and notify listeners; optionally persist for Site3d reload. */
const setSelectedSiteLastScan = (formattedDate, { save = true } = {}) => {
    if (!formattedDate || formattedDate === gw.selectedSite.lastScan) return false;
    gw.selectedSite.lastScan = formattedDate;
    window.dispatchEvent(new CustomEvent('lastScanUpdated', { detail: { lastScan: formattedDate } }));
    if (gw.selectedSite.onLastScanUpdate) {
        gw.selectedSite.onLastScanUpdate();
    }
    if (save) {
        saveSelectedSite();
    }
    return true;
};

/** Sync gw.selectedSite.lastScan from fetchUserSite row (data.lastScan). */
const syncSelectedSiteLastScanFromData = () => {
    const formatted = formatLastScanDisplay(gw.selectedSite?.data?.lastScan);
    if (formatted) {
        return setSelectedSiteLastScan(formatted);
    }
    return false;
};

const GRAINWAVE_IMPERSONATOR_KEY = 'grainwave_impersonator';

// Helper functions to persist authentication state
const saveAuthState = (authObject = null) => {
    try {
        const target = authObject || gw;
        // Persist access token whenever present. Refresh (`auth`) is optional — login may return access-only JWT.
        if (target.token) {
            localStorage.setItem('grainwave_token', target.token);
            if (target.auth != null && target.auth !== '') {
                localStorage.setItem('grainwave_auth', target.auth);
            } else {
                localStorage.removeItem('grainwave_auth');
            }
            localStorage.setItem('grainwave_email', target.email || '');
            localStorage.setItem('grainwave_isAuthenticated', target.isAuthenticated ? 'true' : 'false');
            if (target.roles && Array.isArray(target.roles)) {
                localStorage.setItem('grainwave_roles', JSON.stringify(target.roles));
            }
            
            // Also save user profile if available
            if (target.userProfile) {
                localStorage.setItem('grainwave_userProfile', JSON.stringify(target.userProfile));
            }
            
            // Save bolts if available (needed for encrypted/compressed API calls)
            if (target.bolts) {
                localStorage.setItem('grainwave_bolts', target.bolts);
            }
            if (target.profileUser) {
                localStorage.setItem('grainwave_profileUser', String(target.profileUser));
            }
        }
    } catch (error) {
        console.error('Error saving auth state to localStorage:', error);
    }
};

const clearAuthState = () => {
    try {
        gw.auth401Count = 0;
        localStorage.removeItem('grainwave_token');
        localStorage.removeItem('grainwave_auth');
        localStorage.removeItem('grainwave_email');
        localStorage.removeItem('grainwave_isAuthenticated');
        localStorage.removeItem('grainwave_userProfile');
        localStorage.removeItem('grainwave_bolts');
        localStorage.removeItem('grainwave_roles');
        localStorage.removeItem('grainwave_profileUser');
        localStorage.removeItem('grainwave_selectedSite');
        localStorage.removeItem(GRAINWAVE_IMPERSONATOR_KEY);
        
        // Clear sessionStorage flags (e.g., alpha modal seen flag, browser session)
        sessionStorage.removeItem(GRAINWAVE_BROWSER_SESSION_KEY);
        
        // Clear all intervals and timers
        if (gw.dashboardInterval) {
            clearInterval(gw.dashboardInterval);
            gw.dashboardInterval = null;
        }
        // Clear all dashboard intervals
        for (let di = 0; di < gw.dashboardIntervals.length; di++) {
            clearInterval(gw.dashboardIntervals[di]);
        }
        gw.dashboardIntervals = [];
        // Clear heltec refresh timer
        if (gw.selectedSite && gw.selectedSite.refreshId) {
            clearTimeout(gw.selectedSite.refreshId);
            gw.selectedSite.refreshId = null;
        }
    } catch (error) {
        console.error('Error clearing auth state from localStorage:', error);
    }
};

const restoreAuthState = () => {
    try {
        const token = localStorage.getItem('grainwave_token');
        const auth = localStorage.getItem('grainwave_auth');
        const email = localStorage.getItem('grainwave_email');
        const isAuthenticated = localStorage.getItem('grainwave_isAuthenticated') === 'true';
        const userProfileStr = localStorage.getItem('grainwave_userProfile');
        const bolts = localStorage.getItem('grainwave_bolts');
        const rolesStr = localStorage.getItem('grainwave_roles');
        const profileUserStored = localStorage.getItem('grainwave_profileUser');
        let roles = null;
        if (rolesStr) {
            try {
                roles = JSON.parse(rolesStr);
            } catch (e) {
                console.warn('Error parsing roles from localStorage:', e);
            }
        }
        
        let userProfile = null;
        if (userProfileStr) {
            try {
                userProfile = JSON.parse(userProfileStr);
            } catch (e) {
                console.warn('Error parsing userProfile from localStorage:', e);
            }
        }
        
        if (token) {
            return {
                token,
                auth: auth || null,
                email: email || null,
                isAuthenticated,
                userProfile,
                bolts: bolts || null,
                roles: roles || null,
                profileUser: profileUserStored || null
            };
        }
    } catch (error) {
        console.error('Error restoring auth state from localStorage:', error);
    }
    return null;
};

// Set to 'true' in sessionStorage after successful login or token validation; survives refresh but not window close.
const GRAINWAVE_BROWSER_SESSION_KEY = 'grainwave_browser_session';

export const isGrainWaveBrowserSessionActive = () => {
    try {
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(GRAINWAVE_BROWSER_SESSION_KEY) === 'true';
    } catch {
        return false;
    }
};

const readImpersonator = () => {
    try {
        const raw = localStorage.getItem(GRAINWAVE_IMPERSONATOR_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.token || !parsed?.email) return null;
        return parsed;
    } catch {
        return null;
    }
};

const beginImpersonation = ({ token, email, first, last, roles }) => {
    if (!token) return false;
    const snapshot = {
        token: gw.token,
        auth: gw.auth,
        email: gw.email,
        roles: Array.isArray(gw.roles) ? gw.roles : [],
        profileUser: gw.profileUser || '',
        userProfile: gw.userProfile || null,
        bolts: gw.bolts || null,
    };
    localStorage.setItem(GRAINWAVE_IMPERSONATOR_KEY, JSON.stringify(snapshot));
    const nextEmail = String(email || '').trim().toLowerCase();
    const roleList = Array.isArray(roles) && roles.length ? roles : ['User'];
    const displayName = [first, last].filter((part) => part && String(part).trim()).join(' ').trim();
    gw.siteInfos = [];
    gw.email = nextEmail;
    gw.auth = null;
    gw.roles = roleList;
    gw.profileUser = displayName || nextEmail;
    gw.userProfile = null;
    gw.bolts = null;
    gw.token = token;
    gw.isAuthenticated = true;
    try {
        localStorage.removeItem('grainwave_selectedSite');
    } catch {
        /* ignore */
    }
    saveAuthState();
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    return true;
};

const endImpersonation = () => {
    const snapshot = readImpersonator();
    if (!snapshot?.token) return false;
    gw.token = snapshot.token;
    gw.auth = snapshot.auth || null;
    gw.email = snapshot.email || null;
    gw.roles = Array.isArray(snapshot.roles) ? snapshot.roles : [];
    gw.profileUser = snapshot.profileUser || '';
    gw.userProfile = snapshot.userProfile || null;
    gw.bolts = snapshot.bolts || null;
    gw.siteInfos = [];
    gw.isAuthenticated = true;
    try {
        localStorage.removeItem('grainwave_selectedSite');
    } catch {
        /* ignore */
    }
    saveAuthState();
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + snapshot.token;
    try {
        localStorage.removeItem(GRAINWAVE_IMPERSONATOR_KEY);
    } catch {
        /* ignore */
    }
    return true;
};

const gw = new GrainWave();

const layoutBinMatchesName = (binName, pattern) => {
    const name = String(binName || "");
    if (pattern == null || pattern === "") return true;
    if (pattern instanceof RegExp) return pattern.test(name);
    return name.includes(String(pattern));
};

/** DevTools: __gwDebugRoundCable("19", 1) or __gwDebugRoundCable(1) for any bin */
function gwDebugRoundCable(binNameOrCableNum, cableNumMaybe = 1) {
    const site = gw.selectedSite;
    if (!site) return { error: "no selectedSite — open Site 3D first" };

    let binNameMatch = "";
    let cableNum = 1;
    if (typeof binNameOrCableNum === "number") {
        cableNum = binNameOrCableNum;
    } else {
        binNameMatch = binNameOrCableNum ?? "";
        cableNum = cableNumMaybe ?? 1;
    }

    const layoutBin = (site.layoutBins || []).find((lb) =>
        layoutBinMatchesName(lb.binName, binNameMatch)
    );
    if (!layoutBin) {
        return {
            error: "no matching layoutBin",
            hint: "Try __gwDebugRoundCable() with no args to list bin names",
            bins: (site.layoutBins || []).map((lb) => lb.binName),
        };
    }

    const bin = (site.bins || []).find(
        (b) =>
            b.binDataId === layoutBin.binId || b.binId === layoutBin.binId
    );

    const layoutCable = (site.layoutCables || []).find(
        (lc) =>
            lc.customerSiteLayoutBinId ===
                layoutBin.customerSiteLayoutBinId &&
            (Number(lc.binOrder) === Number(cableNum) ||
                new RegExp(`-${cableNum}$`).test(String(lc.cableName || "")))
    );

    const btsCable =
        (bin?.cableList || []).find(
            (c) =>
                Number(c.number) === Number(cableNum) ||
                new RegExp(`-${cableNum}$`).test(String(c.name || ""))
        ) ?? null;

    const layers = parseRoundBinCableLayers(bin?.cableLayout);
    const layerSum = layers?.reduce((a, b) => a + b, 0) ?? 0;

    return {
        layoutBin: {
            name: layoutBin.binName,
            isRound: layoutBin.isRound,
            width: layoutBin.width,
            length: layoutBin.length,
        },
        bin: bin
            ? {
                  name: bin.binName,
                  cableLayout: bin.cableLayout,
                  cableCount: bin.cableList?.length ?? 0,
                  layers,
                  layerSum,
                  layerSumMatchesCount:
                      layerSum === (bin.cableList?.length ?? 0),
              }
            : { error: "no linked BTS bin row" },
        flags: {
            binIsRoundSilo: binIsRoundSilo(bin, layoutBin),
            editorLayout: siteUsesEditorBinLocalCables(site),
        },
        btsCable: btsCable
            ? {
                  name: btsCable.name,
                  number: btsCable.number,
                  anglePosition: btsCable.anglePosition,
                  walkIndex: roundBinCableWalkIndex(btsCable, bin),
                  topPointX: btsCable.topPointX,
                  topPointZ: btsCable.topPointZ,
              }
            : null,
        layoutCable: layoutCable
            ? {
                  topPointX: layoutCable.topPointX,
                  topPointZ: layoutCable.topPointZ,
              }
            : null,
        resolvedFromBts: btsCable
            ? resolveBtsCableTopPoint(btsCable, bin, layoutBin, site)
            : null,
        resolvedFromLayout: layoutCable
            ? resolveLayoutCableTopPoint(layoutCable, site)
            : null,
    };
}

if (typeof window !== "undefined") {
    window.__gw = gw;
    window.__gwDebugRoundCable = gwDebugRoundCable;
}

if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    /** DevTools: __gwDebugFlatCable(1, /small/i) — footprint + resolved XZ */
    window.__gwDebugFlatCable = (cableNum = 1, binNameMatch = /flat/i) => {
        const site = gw.selectedSite;
        if (!site) return { error: "no selectedSite" };

        const layoutBin = (site.layoutBins || []).find((lb) =>
            layoutBinMatchesName(lb.binName, binNameMatch)
        );
        if (!layoutBin) return { error: "no matching layoutBin" };

        const layoutCable = (site.layoutCables || []).find(
            (lc) =>
                lc.customerSiteLayoutBinId ===
                    layoutBin.customerSiteLayoutBinId &&
                (Number(lc.binOrder) === Number(cableNum) ||
                    new RegExp(`-${cableNum}$`).test(String(lc.cableName || "")))
        );

        const bin = (site.bins || []).find(
            (b) =>
                b.binDataId === layoutBin.binId || b.binId === layoutBin.binId
        );

        const resolved = layoutCable
            ? resolveLayoutCableTopPoint(layoutCable, site, layoutBin)
            : null;

        const sideCount = (site.layoutSides || []).filter(
            (s) =>
                s.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId
        ).length;

        const oriented = orientedLayoutFootprintFt(layoutBin, site);
        const gridDims = flatHouseCableGridDimensions(bin, layoutBin, site);
        const cableCount =
            (site.layoutCables || []).filter(
                (lc) =>
                    lc.customerSiteLayoutBinId ===
                    layoutBin.customerSiteLayoutBinId
            ).length || bin?.cableList?.length || 0;
        const grid = flatHouseGridFromCableLayout(
            bin?.cableLayout,
            gridDims?.width,
            gridDims?.length,
            cableCount
        );

        return {
            layoutBin: {
                name: layoutBin.binName,
                id: layoutBin.customerSiteLayoutBinId,
                width: layoutBin.width,
                length: layoutBin.length,
                hasPreviewPoly: !!(layoutBin.previewPolygonFt?.length >= 3),
                sideCount,
            },
            bin: bin
                ? {
                      name: bin.binName,
                      width: bin.width,
                      length: bin.length,
                      cableLayout: bin.cableLayout,
                      degreesToNorth: bin.degreesToNorth,
                      cableListLen: bin.cableList?.length,
                  }
                : null,
            oriented: oriented
                ? {
                      width: oriented.width,
                      length: oriented.length,
                      angleDeg: (oriented.angleRad * 180) / Math.PI,
                      cx: oriented.cx,
                      cz: oriented.cz,
                  }
                : null,
            gridDims,
            grid,
            cableCount,
            flags: {
                isFlatThreeTen: isFlatThreeTenRowMajorLayout(
                    bin,
                    layoutBin,
                    site
                ),
                usesCatalogCableScale: flatBinUsesCatalogCableScale(
                    layoutBin,
                    site
                ),
                editorLayout: !!(
                    site?.layoutUsesEditorLayoutAxes ||
                    site?.layoutSavedFromSiteLayoutEditor
                ),
            },
            layoutCable: layoutCable
                ? {
                      name: layoutCable.cableName,
                      binOrder: layoutCable.binOrder,
                      topPointX: layoutCable.topPointX,
                      topPointZ: layoutCable.topPointZ,
                  }
                : null,
            resolved,
        };
    };

    window.__gwDebugFlatHouses = () => {
        const site = gw.selectedSite;
        if (!site) return { error: "no selectedSite" };
        return (site.layoutBins || [])
            .filter((lb) => !lb.isRound)
            .map((lb) => window.__gwDebugFlatCable(1, lb.binName));
    };
}

export function resetAuth401Count() {
    gw.auth401Count = 0;
}

/**
 * Call once per auth failure (HTTP 401). Returns true when gw.timeoutTries consecutive failures
 * have been reached and the app should log out.
 */
export function registerAuth401Failure() {
    gw.auth401Count = (gw.auth401Count || 0) + 1;
    const max =
        typeof gw.timeoutTries === 'number' && gw.timeoutTries >= 1 ? gw.timeoutTries : 3;
    return gw.auth401Count >= max;
}

/** Clears axios default Authorization; API calls should go through api/createApiEndpoint. */
export function clearAxiosAuthorizationHeader() {
    delete axios.defaults.headers.common['Authorization'];
}

function GrainWave() {
    this.siteFilter = 0;
    this.intervalIds = [];
    this.dashboardInterval = null;
    this.dashboardIntervals = [];
    this.currentWeatherReadings = [];
    this.weatherStationAbortController = null; // For cancelling weather station API calls
    this.isWeatherStationCallInProgress = false; // Flag to prevent concurrent calls
    this.menuPopupState = null;
    this.isAlphaModalOpen = true;
    this.email = null;
    this.profileUser = "";
    this.profilteRole = "";
    this.profileTime = "";
    this.profileDate = "";
    this.auth = null;
    this.token = null;
    this.test = true;
    //this.bypass = true;
    this.isAuthenticated = false;
    this.bolts = null; // Initialize to null, will be restored from localStorage if available
    /** Consecutive HTTP 401 responses required before forcing logout (axios + manual auth checks). */
    this.timeoutTries = 3;
    /** Internal: incremented on each 401; reset on success or login (see registerAuth401Failure). */
    this.auth401Count = 0;
    /** @type {string[]} Role names from OAuth (`roles`); e.g. Administrator, User, ReadOnly */
    this.roles = [];

    // Restore auth from localStorage when an access token exists. New tabs start with empty
    // sessionStorage but share localStorage — sync the browser-session flag so we hydrate the
    // same credentials as the original tab (otherwise /sites never calls /api/gw and stays blank).
    const savedAuth = restoreAuthState();
    if (savedAuth?.token) {
        try {
            if (!isGrainWaveBrowserSessionActive()) {
                sessionStorage.setItem(GRAINWAVE_BROWSER_SESSION_KEY, 'true');
            }
        } catch {
            /* private mode / storage blocked */
        }
        this.token = savedAuth.token;
        this.auth = savedAuth.auth;
        this.email = savedAuth.email;
        // Token alone is not a valid session; authProvider validateToken sets this after fetchProfile.
        this.isAuthenticated = false;
        this.authReady = false;

        if (savedAuth.userProfile) {
            this.userProfile = savedAuth.userProfile;
        }
        if (savedAuth.bolts) {
            this.bolts = savedAuth.bolts;
        }
        if (savedAuth.roles && Array.isArray(savedAuth.roles)) {
            this.roles = savedAuth.roles;
        }
        if (savedAuth.profileUser) {
            this.profileUser = savedAuth.profileUser;
        }
        axios.defaults.headers.common["Authorization"] = "Bearer " + this.token;
    } else {
        this.authReady = true;
    }
    this.nuts = null;
    this.signs = null;
    this.encs = null;
    this.selectedSiteIndex = null;
    this.selectedSite = new Site();
    this.siteInfos = [];
    this.windowSize = null;
    this.geometries = {
        An0: null,
        An1: null,
        An2: null,
        An3: null,
        An4: null,
        An5: null,
        An6: null,
        An7: null,
        An8: null,
        An9: null,
        AnSpace: null,
        AnDash: null,
        AnE: null,
        AnO: null,
        AnDot: null,
    };
}

function Site() {
    this.selectedBinId = 0;
    this.selectedBinName = "";
    this.isSiteInitialized = false;
    this.isSiteHeltecTc = false;
    this.data = null;
    this.layout = null;
    this.layoutGroups = null;
    this.layoutBins = null;//.filter(x=> x.binName=== "51");
    this.layoutSides = null;//.filter(x=> x.binName=== "51");
    this.layoutCables = null;
    this.layoutSensors = null;

    this.showBinThermo = false;
    this.showGrainLevels = false;
    /** Site 3D: BinLevel fill meshes + product labels. Bin 3D always draws fills. */
    this.showSiteBinLevels = true;
    this.grainLevels = [];
    this.grainLevelsDateFrom = null;

    // Latest BinLevel snapshot rows (one per bin from the BinLevel table).
    // Site3dWindow populates the full set; BinWindow merges in the row for the focused bin.
    this.binLevels = [];
    // BinLevelHistory rows over the active history window (same dateFrom/dateTo as initializeHistory).
    // Used by the history slider so scrubbing a mark can resolve the matching BinLevel for each bin.
    this.binLevelHistory = [];

    this.siteIndex = 0;
    this.siteName = null;
    this.siteGuid = null;
    this.siteGuidZip = null;
    this.siteUuid = null;
    this.opacity = 0.005;

    this.finishedCables = [];
    this.activeScanCableIds = null;
    this.appliedScanSensorCables = [];
    this.pendingScanSensorFetches = {};
    this.addScanInFlight = false;
    this.directScanStatusRows = null;
    this.directScanId = null;
    this.aframes = [];
    this.bins = [];
    this.cables = [];
    this.fans = [];
    this.devices = [];
    this.heltecSensorMappings = [];

    this.scanCables = [];

    this.temperatureComparison = {
        daysToCompare: 1,
        timeToCompare: null,
        plus: 3,
        minus: 3,
        dates: [],
        isAm: true,
        historyDayDates: [],
    }

    this.opacity = 0.005;
    this.color = "#cccccc";
    this.lastScan = '';
    this.layout = null;
    this.layoutGroups = null;
    this.layoutBins = null;
    this.layoutSides = null;
    this.layoutCables = null;
    this.layoutSensors = null;
    this.marks = [];

    this.sides = [];
    this.siteRef = [];
    this.groupsRef = [];
    this.binsRef = [];
    this.cablesRef = [];
    this.sensorsRef = [];
    this.sensorsGroupRef = [];
    this.cableLevelRef = [];

    this.binMeshes = [];
    this.binBoundingBoxes = [];

    this.initialCameraAngle = null;
    this.initialCameraPosition = null;
    this.margin = null;
    this.selectedColorHex = 0xabcdef;
    this.defaultEmissiveHex = 0x000000;
    this.email = null;
    this.opacity = 0.005;
    this.color = "#cccccc";
    this.gl = null;
    this.mouseClickDownTime = null;
    this.mouseClickStartTime = null;
    this.mousePosition = new THREE.Vector3();
    this.mouseStartPosition = new THREE.Vector3();
    this.mouseStartScreenX = 0;
    this.mouseStartScreenY = 0;
    this.defaultEmissiveHex = 0x000000;
    this.canvasRef = null;
    this.canDrag = false;
    this.v1 = new THREE.Vector3();
    this.v2 = new THREE.Vector3();
    this.lastCameraPosition = new THREE.Vector3();
    this.lastCameraLookAt = new THREE.Vector3();
    this.lastCameraRotation = new THREE.Euler();
    this.lastPosition = new THREE.Vector3();
    this.isOffsetChanged = false;
    this.isObjectSelected = false;
    this.isBinToBin = false;
    this.isSiteToBin = false;
    this.lastQuaternion = new THREE.Quaternion();
    this.isCameraSaved = false;
    this.spherical = new THREE.Spherical();
    this.cameraDirection = new THREE.Vector3();
    this.compass = null;
    this.azimuth = null;
    this.polar = null;
    this.isObjectSelected = false;
    this.isCablesOff = false;
    this.isInvalidCablesOn = true;
    this.isGradesOn = false;
    this.lastIsCablesOff = false;
    this.isHistorySliderOn = false;
    this.isComparisonOn = false;
    this.lastAzimuth = null;
    this.lastPolar = null;
    this.lastCanDrag = true;
    this.lastBinCameraPosition = new THREE.Vector3();
    this.lastBinCameraLookAt = new THREE.Vector3();
    this.lastBinCameraRotation = new THREE.Vector3();
    this.lastBinAzimuth = 0;
    this.lastBinPolar = 0;
    this.lastBinCanDrag = false;
    this.lastBinPosition = new THREE.Vector3();
    this.lastBinOffset = new THREE.Vector3();
    this.binTextScale = 0.35;
    this.scaleRatio = 0.0005;
    this.refreshRate = 5000;
    this.refreshId = 0;
    this.binCameraDistance = null;
    this.selectedMark = null;

    // Original bin camera position for cable focus restoration
    this.originalBinCameraPosition = null;
    this.originalBinCameraLookAt = null;
    this.originalBinCameraSaved = false;
    this.cameFromSiteView = false;

    this.main = {
        siteName: null,
        bins: 0,
        plus: null,
        minus: null,
        daysToCompare: null,
    };
    this.globalScale = {
        globalScale: 35.0,
        globalPositionScale: 100.0 / 36.3,
        globalCableScale: 100 / 40,
    };
    this.size = {
        x: 600.0,
        y: 600.0
    };
    this.grid3D = {
        scale: 36.3,
        size: 36,
        divisions: 36,
        color: 0x555555,
        opacity: 0.15,
        x: 0,
        y: -0.25,
        z: 0,
    };
    this.defaultCamera = {
        //  Site Top View
        //position: [0, 160, 0],
        ////  Default View
        //position: [0, 160, 0],
        //  Default View (Site+)
        position: [0, 300, 0],
        //  Angled View
        //position: [0, 72, 0],
        near: 1,
        far: 5000,
        fov: 40,
    };
    this.orbitControls = dummyOrbitControls();
    this.binScale = { x: 0.3, y: 0.3, z: 0.3 };
    this.binRotation = { x: 0, y: 0, z: 0, order: 'XYZ' };
    this.offset = { x: 0, y: 0, z: 0 };
}

const data = {
    sites: [{
        siteGuid: '',
        opacity: 0.005,
        main: {
            siteName: "Default Site",
            bins: 1
        },
        lastScan: '',
        bins: [
            {
                name: 'Bin 1',
                binId: 1,
                peakHeight: 1,
                opacity: 0.005,
            },
            {
                name: 'Bin 2',
                binId: 2,
                peakHeight: 1,
                opacity: 0.005,
            },
        ],
    }],
    newSite: {}
};
const layoutBinMatchesSiteBin = (layoutBin, siteBin) => {
    if (!layoutBin || !siteBin) return false;
    const layoutId = layoutBin.binId;
    const dataId = siteBin.binDataId;
    if (layoutId != null && dataId != null) {
        const a = parseInt(String(layoutId), 10);
        const b = parseInt(String(dataId), 10);
        if (!Number.isNaN(a) && !Number.isNaN(b) && a === b) return true;
    }
    return layoutBin.binName != null && layoutBin.binName === siteBin.binName;
};

/** Site Setup / BTS `Bin.Name` wins over a stale CustomerSiteLayoutBin / LayoutProxyJson label. */
function catalogBinDisplayName(siteBin) {
    return String(siteBin?.binName ?? "").trim();
}

function syncLayoutBinNamesFromCatalog(site) {
    if (!site?.layoutBins?.length || !site?.bins?.length) return;
    site.layoutBins.forEach((layoutBin) => {
        if (layoutBin.binId != null && isLayoutPlaceholderBinId(layoutBin.binId)) {
            return;
        }
        const matched =
            findSiteBinForLayoutBin(site, layoutBin)
            || site.bins.find((x) => layoutBinMatchesSiteBin(layoutBin, x))
            || layoutBin.bin
            || null;
        if (!matched) return;
        const catalogName = catalogBinDisplayName(matched);
        if (!catalogName) return;
        layoutBin.bin = matched;
        const prevName = layoutBin.binName;
        if (prevName === catalogName) return;
        layoutBin.binName = catalogName;
        const layoutBinId = layoutBin.customerSiteLayoutBinId;
        (site.layoutCables || []).forEach((lc) => {
            if (
                layoutBinId != null
                && lc.customerSiteLayoutBinId === layoutBinId
            ) {
                lc.binName = catalogName;
            } else if (prevName && lc.binName === prevName) {
                lc.binName = catalogName;
            }
        });
        (site.layoutSensors || []).forEach((ls) => {
            if (
                layoutBinId != null
                && ls.customerSiteLayoutBinId === layoutBinId
            ) {
                if (ls.binName != null) ls.binName = catalogName;
            } else if (prevName && ls.binName === prevName) {
                ls.binName = catalogName;
            }
        });
    });
}

function initializeSiteLayout() {
    if (gw.selectedSite.layoutBins && gw.selectedSite.layoutBins.length > 0) {
        gw.selectedSite.layoutBins.forEach(layoutBin => {
            if (gw.selectedSite.bins && gw.selectedSite.bins.length > 0) {
                const bins = gw.selectedSite.bins;
                const matched =
                    (layoutBin.bin && bins.find((x) => layoutBinMatchesSiteBin(layoutBin, x) || x === layoutBin.bin)) ||
                    bins.find((x) => layoutBinMatchesSiteBin(layoutBin, x)) ||
                    bins.find((x) => x.binName === layoutBin.binName);
                if (matched) {
                    layoutBin.bin = matched;
                    layoutBin.height = layoutBin.bin.height;
                }
            }

            if (!layoutBin.height || layoutBin.height === 0) {
                layoutBin.height = 50;
            }
        });

        syncLayoutBinNamesFromCatalog(gw.selectedSite);

        if (gw.selectedSite.skipDrawSiteForEditorLayout) {
            ensureLayoutSideRenderGeometry(gw.selectedSite);
        } else {
            drawSite();
        }
    }
    else {
        //  Default Layout
        gw.selectedSite.layout = {
            customerSiteLayoutId: 1,
            customerSiteGuid: gw.selectedSite.siteGuid,
            width: 0,
            length: 0,
            height: 0,
            rotation: 0,
            x: 0,
            y: 0,
            z: 0,
            cx: null,
            cy: null,
            cz: null,
            lx: null,
            ly: null,
            lz: null,
        };
        gw.selectedSite.layoutGroups = [];
        let layoutGroup = {
            customerSiteLayoutGroupId: 1,
            customerSiteLayoutId: 1,
            groupName: "Group 1",
            width: 0,
            length: 0,
            height: 0,
            rotation: 0,
            x: 0,
            y: 0,
            z: 0,
            cx: null,
            cy: null,
            cz: null,
        };
        gw.selectedSite.layoutGroups.push(layoutGroup);

        gw.selectedSite.layoutBins = [];
        gw.selectedSite.bins.map((bin, b) => {
            bin.customerSiteLayoutBinId = b + 1;

            let isRound = !bin.type.toLowerCase().includes("flat");
            let layoutBin = {
                customerSiteLayoutBinId: b + 1,
                customerSiteLayoutId: 1,
                customerSiteLayoutGroupId: 1,
                groupOrder: b + 1,
                binId: bin.binDataId,
                binName: bin.binName,
                width: !isRound ? bin.width / 2.0 : (bin.diameter > 0 ? bin.diameter : bin.width),
                length:!isRound ? bin.length / 2.0 : (bin.diameter > 0 ? bin.diameter : bin.length),
                height: bin.height,
                rotation: bin.degreesToNorth,
                isRound: isRound,
                x: 0,
                y: 0,
                z: 0,
                cx: null,
                cy: null,
                cz: null,
                bin: bin,
            };

            //  Store square bounding box width and length after rotation on layoutBins that are not round
            if (!isRound) {
                const angleRadians = -bin.degreesToNorth * Math.PI / 180;
                const cos = Math.abs(Math.cos(angleRadians));
                const sin = Math.abs(Math.sin(angleRadians));
                
                // Calculate the bounding box dimensions after rotation
                layoutBin.rotatedLength = (bin.width * cos + bin.length * sin);
                layoutBin.rotatedWidth = (bin.width * sin + bin.length * cos);
            }

            gw.selectedSite.layoutBins.push(layoutBin);
        });
        let maxWidth = 0;
        let maxLength = 0;
        let maxHeight = 0;

        const isAllRound = gw.selectedSite.layoutBins.every(x => x.isRound);
        if (isAllRound) {
            gw.selectedSite.layoutBins.forEach(bin => {
                maxWidth = Math.max(maxWidth, bin.width);
                maxLength = Math.max(maxLength, bin.length);
                maxHeight = Math.max(maxHeight, bin.height);
            });

            let cols = Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
            let rows = Math.ceil(gw.selectedSite.layoutBins.length / cols);

            let squarePadding = 1.0;  //  Todo: needs to be automatic depending on size difference
            let totalWidth = (maxWidth * cols) + (squarePadding * (cols - 1));
            let totalLength = (maxLength * rows) + (squarePadding * (rows - 1));

            let squareWidth = totalWidth / cols;
            let squareLength = totalLength / rows;

            let startX = -totalWidth / 2.0;
            let startZ = -totalLength / 2.0; // squareLength / rows - squareLength / 2.0;

            let minX = 0, maxX = 0, minZ = 0, maxZ = 0;

            gw.selectedSite.layoutBins.map((layoutBin, b) => {
                let mod = b % cols;

                layoutBin.x = startX + squareWidth / 2.0;
                layoutBin.y = 0;
                layoutBin.z = -(startZ + squareLength / 2.0);

                minX = Math.min(minX, layoutBin.x - layoutBin.width / 2.0);
                maxX = Math.max(maxX, layoutBin.x + layoutBin.width / 2.0);
                minZ = Math.min(minZ, layoutBin.z - layoutBin.length / 2.0);
                maxZ = Math.max(maxZ, layoutBin.z + layoutBin.length / 2.0);

                if (mod === cols - 1) {
                    startX = -totalWidth / 2.0;
                    startZ += squareLength;
                }
                else {
                    startX += squareWidth;
                }
            });

            gw.selectedSite.layout.width = maxX - minX;
            gw.selectedSite.layout.length = maxZ - minZ;
            gw.selectedSite.layout.height = maxHeight;

            layoutGroup.width = gw.selectedSite.layout.width;
            layoutGroup.length = gw.selectedSite.layout.length;
            layoutGroup.height = gw.selectedSite.layout.height;

            //  Bounding +-x and +-z
            let boundingX = layoutGroup.width / 2.0;
            let boundingY = layoutGroup.height / 2.0;
            let boundingZ = layoutGroup.length / 2.0;

            gw.selectedSite.layout.x = boundingX;
            //gw.selectedSite.layout.y = boundingY;
            gw.selectedSite.layout.z = boundingZ;

            layoutGroup.x = gw.selectedSite.layout.x;
            //layoutGroup.y = gw.selectedSite.layout.y;
            layoutGroup.z = gw.selectedSite.layout.z;

            //  Shift to center
            let shiftX = totalWidth / 2.0 - boundingX;
            let shiftZ = totalLength / 2.0 - boundingZ;

            //  Apply shift to center
            gw.selectedSite.layoutBins.forEach(layoutBin => {
                layoutBin.x += shiftX;
                layoutBin.z += shiftZ;
            });
        }
        else {
            const isAllFlatHouses = gw.selectedSite.layoutBins.every(x => !x.isRound);  

            gw.selectedSite.layoutBins.forEach(bin => {
                if (isAllFlatHouses) {
                    maxWidth = Math.max(maxWidth, bin.width * 2);
                    maxLength = Math.max(maxLength, bin.length * 2);
                    maxHeight = Math.max(maxHeight, bin.height);
                }
                else {
                    maxWidth = Math.max(maxWidth, !bin.isRound ? bin.rotatedLength : bin.width);
                    maxLength = Math.max(maxLength, !bin.isRound ? bin.rotatedWidth : bin.length);
                    maxHeight = Math.max(maxHeight, bin.height);
                }
            });

            // Calculate total width and length needed based on actual bin dimensions
            let totalWidth = 0;
            let totalLength = 0;
            let rowWidths = new Array(Math.ceil(gw.selectedSite.layoutBins.length / Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)))).fill(0);
            let colLengths = new Array(Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length))).fill(0);

            // First pass: calculate dimensions for each row and column
            gw.selectedSite.layoutBins.forEach((bin, index) => {
                const row = Math.floor(index / Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)));
                const col = index % Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
                const binWidth = !bin.isRound ? bin.rotatedWidth : bin.width;
                const binLength = !bin.isRound ? bin.rotatedLength : bin.length;
                
                rowWidths[row] = Math.max(rowWidths[row], binWidth);
                colLengths[col] = Math.max(colLengths[col], binLength);
            });

            // Calculate total dimensions
            totalWidth = rowWidths.reduce((sum, width) => sum + width, 0);
            totalLength = colLengths.reduce((sum, length) => sum + length, 0);

            // Add minimum spacing between bins
            const minSpacing = 1.0;
            totalWidth += minSpacing * (Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)) - 1);
            totalLength += minSpacing * (Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)) - 1);

            // Calculate starting positions
            let startX = -totalWidth / 2.0;
            let startZ = -totalLength / 2.0;

            // Second pass: position bins
            let currentX = startX;
            let currentZ = startZ;
            let rowHeights = new Array(Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length))).fill(0);

            gw.selectedSite.layoutBins.forEach((bin, index) => {
                const row = Math.floor(index / Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)));
                const col = index % Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
                const binWidth = !bin.isRound ? bin.rotatedWidth : bin.width;
                const binLength = !bin.isRound ? bin.rotatedLength : bin.length;

                // Position bin
                bin.x = currentX + binWidth / 2;
                bin.z = currentZ + binLength / 2;

                // Update current position
                currentX += rowWidths[row] + minSpacing;

                // Move to next row if needed
                if (col === Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)) - 1) {
                    currentX = startX;
                    currentZ += colLengths[row] + minSpacing;
                }
            });

            // Update layout dimensions
            gw.selectedSite.layout.width = totalWidth;
            gw.selectedSite.layout.length = totalLength;
            gw.selectedSite.layoutGroups[0].width = totalWidth;
            gw.selectedSite.layoutGroups[0].length = totalLength;

            // Center the layout
            gw.selectedSite.layout.x = 0;
            gw.selectedSite.layout.z = 0;
            gw.selectedSite.layoutGroups[0].x = 0;
            gw.selectedSite.layoutGroups[0].z = 0;

            layoutGroup.width = gw.selectedSite.layout.width;
            layoutGroup.length = gw.selectedSite.layout.length;
            layoutGroup.height = gw.selectedSite.layout.height;

            //  Bounding +-x and +-z
            let boundingX = layoutGroup.width / 2.0;
            let boundingY = layoutGroup.height / 2.0;
            let boundingZ = layoutGroup.length / 2.0;

            gw.selectedSite.layout.x = boundingX;
            //gw.selectedSite.layout.y = boundingY;
            gw.selectedSite.layout.z = boundingZ;

            layoutGroup.x = gw.selectedSite.layout.x;
            //layoutGroup.y = gw.selectedSite.layout.y;
            layoutGroup.z = gw.selectedSite.layout.z;

            //  Shift to center
            let shiftX = totalWidth / 2.0 - boundingX;
            let shiftZ = totalLength / 2.0 - boundingZ;

            //  Apply shift to center
            gw.selectedSite.layoutBins.forEach(layoutBin => {
                layoutBin.x += shiftX;
                layoutBin.z += shiftZ;
            });

            let colRowCenters = [];

            if (gw.selectedSite.layoutBins.any(x => !x.isRound)) {
                //  Move layoutBins closer to each other without overlapping
                let spacing = 60.0;

                // let cols = Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
                // let rows = Math.ceil(gw.selectedSite.layoutBins.length / cols);
                let rows = Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
                let cols = Math.ceil(gw.selectedSite.layoutBins.length / Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)));

                let squarePadding = 1.0;  //  Todo: needs to be automatic depending on size difference
                let totalWidth = (maxWidth * cols) + (squarePadding * (cols - 1)) + spacing;
                let totalLength = (maxLength * rows) + (squarePadding * (rows - 1)) + spacing;
                if (totalLength > totalWidth * 1.5) {
                    cols = Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length));
                    rows = Math.ceil(gw.selectedSite.layoutBins.length / Math.ceil(Math.sqrt(gw.selectedSite.layoutBins.length)));
                    totalWidth = (maxWidth * cols) + (squarePadding * (cols - 1)) + spacing;
                    totalLength = (maxLength * rows) + (squarePadding * (rows - 1)) + spacing;
                }

                let squareWidth = totalWidth / cols;
                let squareLength = totalLength / rows;

                let startX = -totalWidth / 2.0;
                let startZ = -totalLength / 2.0; // squareLength / rows - squareLength / 2.0;
                let minX = 0, maxX = 0, minZ = 0, maxZ = 0;

                gw.selectedSite.layoutBins.map((layoutBin, b) => {
                    let col = b % cols;
                    let row = Math.floor(b / cols);
                    let x = startX + squareWidth / 2.0;
                    let y = 0;
                    let z = -(startZ + squareLength / 2.0);

                    colRowCenters.push({ 
                        col: col, 
                        row: row, 
                        x: x, 
                        y: y,
                        z: z
                    });

                    minX = Math.min(minX, layoutBin.width / 2.0);
                    maxX = Math.max(maxX, layoutBin.width / 2.0);
                    minZ = Math.min(minZ, layoutBin.length / 2.0);
                    maxZ = Math.max(maxZ, layoutBin.length / 2.0);

                    if (col === cols - 1) {
                        startX = -totalWidth / 2.0;
                        startZ += squareLength;
                    }
                    else {
                        startX += squareWidth;
                    }
                });

                startX = -totalWidth / 2.0;
                startZ = -totalLength / 2.0;
                minX = 0, maxX = 0, minZ = 0, maxZ = 0;

                gw.selectedSite.layoutBins.map((layoutBin, b) => {
                    let col = b % cols;
                    let row = Math.floor(b / cols);

                    if (isAllFlatHouses) {
                        layoutBin.x = colRowCenters.find(x => x.col === col).x;
                        layoutBin.z = colRowCenters.find(x => x.row === row).z / 1.5;
                    }
                    else if (!layoutBin.isRound && !isAllFlatHouses) {
                        layoutBin.x = colRowCenters.find(x => x.col === col).x / 2.0;
                        layoutBin.z = colRowCenters.find(x => x.row === row).z / 1.5;
                    } 
                    else if (layoutBin.isRound) {
                        layoutBin.x = colRowCenters.find(x => x.col === col && x.row === row).x;
                        layoutBin.z = colRowCenters.find(x => x.col === col && x.row === row).z;
                    }

                    minX = Math.min(minX, layoutBin.width / 2.0);
                    maxX = Math.max(maxX, layoutBin.width / 2.0);
                    minZ = Math.min(minZ, layoutBin.length / 2.0);
                    maxZ = Math.max(maxZ, layoutBin.length / 2.0);

                    if (col === cols - 1) {
                        startX = -totalWidth / 2.0;
                        startZ += squareLength;
                    }
                    else {
                        startX += squareWidth;
                    }
                });

                gw.selectedSite.layout.width = maxX - minX;
                gw.selectedSite.layout.length = maxZ - minZ;
                gw.selectedSite.layout.height = maxHeight;


                //  Build FlatHouse Sides
                gw.selectedSite.layoutBins.forEach((layoutBin, i) => {
                    if (!layoutBin.isRound) {
                        layoutBin.x *= 2;
                        layoutBin.z += layoutBin.z / 2;
                        for (let s = 0; s < 4; s++) {
                            let layoutSide = {
                                customerSiteLayoutSideId: i * 4 + s + 1,
                                customerSiteLayoutId: 1,
                                customerSiteLayoutGroupId: 1,
                                customerSiteLayoutBinId: layoutBin.customerSiteLayoutBinId,
                                binId: layoutBin.binId,
                                binName: layoutBin.binName,
                                x1: (s === 0 || s === 3 ? -layoutBin.width : layoutBin.width) + layoutBin.x,
                                y1: 0,
                                z1: (s === 0 || s === 1 ? layoutBin.length : -layoutBin.length) + layoutBin.z,
                                x2: (s === 0 || s === 1 ? layoutBin.width : -layoutBin.width) + layoutBin.x,
                                y2: 0,
                                z2: (s === 1 || s === 2 ? -layoutBin.length : layoutBin.length) + layoutBin.z,
                                curveX: 0,
                                curveY: 0,
                                curveZ: 0,
                                curveRadius: 0,
                                isInner: false,
                                isSiteRender: true,
                            };

                            gw.selectedSite.layoutSides.push(layoutSide);
                        }
                    }
                });

                // gw.selectedSite.layout.x = 0;
                // gw.selectedSite.layout.z = 0;
                // gw.selectedSite.layoutGroups[0].x = 0;
                // gw.selectedSite.layoutGroups[0].z = 0;

                drawSite();
            }
        }
    }
}
function drawSite() {
    gw.selectedSite.layoutBins.forEach(layoutBin => {
        try {
        if (!layoutBin.isRound && gw.selectedSite.layoutSides && gw.selectedSite.layoutSides.length) {
            let binSides = gw.selectedSite.layoutSides.filter((x) => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId);

            // drawSite is not idempotent — clear prior computed fields if this runs again.
            binSides.forEach((side) => {
                delete side.lx1;
                delete side.lz1;
                delete side.lx2;
                delete side.lz2;
                delete side.lcurveX;
                delete side.lcurveY;
                delete side.lcurveZ;
                delete side.hlx1;
                delete side.hly1;
                delete side.hlz1;
                delete side.hlx2;
                delete side.hly2;
                delete side.hlz2;
                delete side.newCurveRadius;
                delete side.x3;
                delete side.x4;
                delete side.z3;
                delete side.z4;
            });

            let wallThickness = 0.5;

            binSides.forEach((s, i) => {
                if (s.curveRadius === 0) {
                    let lineDirection = getAngleRadiansBetweenPoints(
                        s.x1,
                        s.z1,
                        s.x2,
                        s.z2
                    );

                    s.x3 = s.x1 + wallThickness * Math.sin(lineDirection);
                    s.x4 = s.x2 + wallThickness * Math.sin(lineDirection);
                    s.z3 = s.z1 + wallThickness * -Math.cos(lineDirection);
                    s.z4 = s.z2 + wallThickness * -Math.cos(lineDirection);
                }
                else {
                    let lineDirection = getAngleRadiansBetweenPoints(
                        s.x1,
                        s.z1,
                        s.x2,
                        s.z2
                    );

                    s.x3 = s.x1 + wallThickness * Math.sin(lineDirection);
                    s.x4 = s.x2 + wallThickness * Math.sin(lineDirection);
                    s.z3 = s.z1 + wallThickness * -Math.cos(lineDirection);
                    s.z4 = s.z2 + wallThickness * -Math.cos(lineDirection);

                    if (s.isInner) {
                        s.newCurveRadius = s.curveRadius + wallThickness;
                    }
                    else {
                        s.newCurveRadius = Math.max(s.curveRadius - wallThickness, 0.1);
                    }
                }
            });

            if (binSides.length < 2) {
                binSides.forEach((s) => {
                    s.hlx1 = s.x1;
                    s.hly1 = s.y1;
                    s.hlz1 = s.z1;
                    s.hlx2 = s.x2;
                    s.hly2 = s.y2;
                    s.hlz2 = s.z2;
                });
            } else {
            //  Get new points from intersects
            binSides.forEach((s, i) => {
                let sideBefore = null;
                let sideAfter = null;
                if (i === 0) {
                    sideBefore = binSides[binSides.length - 1];

                    //  Get side first point
                    if (s.curveRadius === 0 &&
                        sideBefore.curveRadius === 0) {
                        let lineIntersect = checkLineIntersection(
                            s.x3,
                            s.z3,
                            s.x4,
                            s.z4,
                            sideBefore.x3,
                            sideBefore.z3,
                            sideBefore.x4,
                            sideBefore.z4
                        );
                        if (lineIntersect) {
                            s.hlx1 = lineIntersect.x;
                            s.hly1 = s.y1;
                            s.hlz1 = lineIntersect.y;
                        }
                        else {
                            s.hlx1 = s.x1;
                            s.hly1 = s.y1;
                            s.hlz1 = s.z1;
                        }
                    }
                    else if (s.curveRadius !== 0 &&
                        sideBefore.curveRadius === 0) {
                        let circleIntersect = getCircleLineIntersections(
                            sideBefore.x3,
                            sideBefore.z3,
                            sideBefore.x4,
                            sideBefore.z4,
                            s.curveX,
                            s.curveZ,
                            s.newCurveRadius
                        );

                        let closestPoint = getClosestPointToPoints(
                            layoutBin.x,
                            layoutBin.z,
                            intersectionPointsFromResult(circleIntersect)
                        );
                        s.hlx1 = closestPoint.x != null ? closestPoint.x : s.x1;
                        s.hly1 = s.y1;
                        s.hlz1 = closestPoint.y != null ? closestPoint.y : s.z1;
                    }
                    else if (s.curveRadius !== 0 &&
                        sideBefore.curveRadius !== 0) {

                        let circleToCircleIntersect = getIntersectionOfTwoCircles(
                            sideBefore.curveX,
                            sideBefore.curveZ,
                            sideBefore.newCurveRadius,
                            s.curveX,
                            s.curveZ,
                            s.newCurveRadius
                        );

                        let closestPoint = getClosestPointToPoints(
                            layoutBin.x,
                            layoutBin.z,
                            intersectionPointsFromResult(circleToCircleIntersect)
                        );
                        s.hlx1 = closestPoint.x != null ? closestPoint.x : s.x1;
                        s.hly1 = s.y1;
                        s.hlz1 = closestPoint.y != null ? closestPoint.y : s.z1;
                    }
                    else {
                        let circleIntersect = getCircleLineIntersections(
                            s.x3,
                            s.z3,
                            s.x4,
                            s.z4,
                            sideBefore.curveX,
                            sideBefore.curveZ,
                            sideBefore.newCurveRadius
                        );

                        let closestPoint = getClosestPointToPoints(
                            layoutBin.x,
                            layoutBin.z,
                            intersectionPointsFromResult(circleIntersect)
                        );
                        s.hlx1 = closestPoint.x != null ? closestPoint.x : s.x1;
                        s.hly1 = s.y1;
                        s.hlz1 = closestPoint.y != null ? closestPoint.y : s.z1;
                    }
                    sideAfter = binSides[i + 1];
                }
                else if (i === binSides.length - 1) {
                    sideBefore = binSides[i - 1];
                    s.hlx1 = sideBefore.hlx2;
                    s.hly1 = sideBefore.hly2;
                    s.hlz1 = sideBefore.hlz2;
                    sideAfter = binSides[0];
                }
                else {
                    sideBefore = binSides[i - 1];
                    s.hlx1 = sideBefore.hlx2;
                    s.hly1 = sideBefore.hly2;
                    s.hlz1 = sideBefore.hlz2;
                    sideAfter = binSides[i + 1];
                }

                //  Get side second point
                if (!sideAfter) {
                    s.hlx2 = s.x2;
                    s.hly2 = s.y2;
                    s.hlz2 = s.z2;
                    return;
                }
                if (s.curveRadius === 0 &&
                    sideAfter.curveRadius === 0) {
                    let lineIntersect = checkLineIntersection(
                        sideAfter.x3,
                        sideAfter.z3,
                        sideAfter.x4,
                        sideAfter.z4,
                        s.x3,
                        s.z3,
                        s.x4,
                        s.z4
                    );

                    if (lineIntersect) {
                        s.hlx2 = lineIntersect.x;
                        s.hly2 = s.y2;
                        s.hlz2 = lineIntersect.y;
                    }
                    else {
                        s.hlx2 = s.x2;
                        s.hly2 = s.y2;
                        s.hlz2 = s.z2;
                    }
                }
                else if (s.curveRadius !== 0 &&
                    sideAfter.curveRadius === 0) {
                    let circleIntersect = getCircleLineIntersections(
                        sideAfter.x3,
                        sideAfter.z3,
                        sideAfter.x4,
                        sideAfter.z4,
                        s.curveX,
                        s.curveZ,
                        s.newCurveRadius
                    );

                    let closestPoint = getClosestPointToPoints(
                        layoutBin.x,
                        layoutBin.z,
                        intersectionPointsFromResult(circleIntersect)
                    );
                    s.hlx2 = closestPoint.x != null ? closestPoint.x : s.x2;
                    s.hly2 = s.y2;
                    s.hlz2 = closestPoint.y != null ? closestPoint.y : s.z2;
                }
                else if (s.curveRadius !== 0 &&
                    sideAfter.curveRadius !== 0) {

                    let circleToCircleIntersect = getIntersectionOfTwoCircles(
                        sideAfter.curveX,
                        sideAfter.curveZ,
                        sideAfter.newCurveRadius,
                        s.curveX,
                        s.curveZ,
                        s.newCurveRadius
                    );
                    let closestPoint = getClosestPointToPoints(
                        layoutBin.x,
                        layoutBin.z,
                        intersectionPointsFromResult(circleToCircleIntersect)
                    );
                    s.hlx2 = closestPoint.x != null ? closestPoint.x : s.x2;
                    s.hly2 = s.y2;
                    s.hlz2 = closestPoint.y != null ? closestPoint.y : s.z2;
                }
                else {
                    let circleIntersect = getCircleLineIntersections(
                        s.x3,
                        s.z3,
                        s.x4,
                        s.z4,
                        sideAfter.curveX,
                        sideAfter.curveZ,
                        sideAfter.newCurveRadius
                    );

                    let closestPoint = getClosestPointToPoints(
                        layoutBin.x,
                        layoutBin.z,
                        intersectionPointsFromResult(circleIntersect)
                    );
                    s.hlx2 = closestPoint.x != null ? closestPoint.x : s.x2;
                    s.hly2 = s.y2;
                    s.hlz2 = closestPoint.y != null ? closestPoint.y : s.z2;
                }
            });
            }

            //let tesstisdfherwe = binSides.map(s => {
            //    return {
            //        a: s.hlx1,
            //        b: s.hlz1,
            //        c: s.hlx2,
            //        d: s.hlz2,
            //        e: s.x3,
            //        f: s.z3,
            //        g: s.x4,
            //        h: s.z4
            //    }
            //});
            //let testmapped = 0;
            //if (layoutBin.binName === "51") {
            //    let tsdfiefuu = 0;
            //}

            //  Get bin vector for local coordinates
            gw.selectedSite.v2.set(
                layoutBin.x,
                layoutBin.y,
                layoutBin.z
            )

            //  Localize sides position to bin
            for (var s = 0; s < binSides.length; s++) {
                let side = binSides[s];

                gw.selectedSite.v1.set(side.x1, side.y1, side.z1);
                gw.selectedSite.v1.sub(gw.selectedSite.v2);
                side.lx1 = gw.selectedSite.v1.x;
                side.ly1 = gw.selectedSite.v1.y;
                side.lz1 = gw.selectedSite.v1.z;

                gw.selectedSite.v1.set(side.x2, side.y2, side.z2);
                gw.selectedSite.v1.sub(gw.selectedSite.v2);
                side.lx2 = gw.selectedSite.v1.x;
                side.ly2 = gw.selectedSite.v1.y;
                side.lz2 = gw.selectedSite.v1.z;

                gw.selectedSite.v1.set(side.hlx1, side.hly1, side.hlz1);
                gw.selectedSite.v1.sub(gw.selectedSite.v2);
                side.hlx1 = gw.selectedSite.v1.x;
                side.hly1 = gw.selectedSite.v1.y;
                side.hlz1 = gw.selectedSite.v1.z;

                gw.selectedSite.v1.set(side.hlx2, side.hly2, side.hlz2);
                gw.selectedSite.v1.sub(gw.selectedSite.v2);
                side.hlx2 = gw.selectedSite.v1.x;
                side.hly2 = gw.selectedSite.v1.y;
                side.hlz2 = gw.selectedSite.v1.z;

                gw.selectedSite.v1.set(side.curveX, side.curveY, side.curveZ);
                gw.selectedSite.v1.sub(gw.selectedSite.v2);
                side.lcurveX = gw.selectedSite.v1.x;
                side.lcurveY = gw.selectedSite.v1.y;
                side.lcurveZ = gw.selectedSite.v1.z;
            }
        }
        } catch (drawErr) {
            console.warn('drawSite: wall geometry failed for bin', layoutBin?.binName, drawErr);
        }
    });
}
const layoutSideCoordFinite = (n) => typeof n === 'number' && Number.isFinite(n);
const LAYOUT_WALL_THICKNESS_FT = 0.5;

/** Bin 3D (/bin from site card): same wall footprints as Site 3D for flat / odd-shape bins. */
function ensureLayoutGeometryForBinView() {
    if (!gw.selectedSite?.layoutBins?.length) return;

    const site = gw.selectedSite;
    // Fresh Stice refs for single-bin canvas.
    site.groupsRef = [];
    site.binsRef = [];
    site.cablesRef = [];
    site.siteRef = [];

    try {
        promoteLayoutSidesForDrawSite(site);
    } catch (layoutErr) {
        console.warn('ensureLayoutGeometryForBinView: promote sides', layoutErr);
    }

    const skipEditorApply =
        site.usesLegacyManualSiteLayout ||
        (siteLayoutLooksLegacyManual(site) &&
            !siteHasCommittedSite3dLayoutProxy(site));

    let editorApplied = false;
    if (!skipEditorApply) {
        try {
            editorApplied = applyEditorLayoutToSiteLikePreview(site) === true;
        } catch (layoutErr) {
            console.warn('ensureLayoutGeometryForBinView: editor layout', layoutErr);
        }
    }
    if (!editorApplied) {
        try {
            rehydrateLayoutGeometryForDrawSite(site);
        } catch (layoutErr) {
            console.warn('ensureLayoutGeometryForBinView: rehydrate layout', layoutErr);
        }
    }

    attachPreviewPolygonsFromLayoutSides(site);

    const flatBinMissingPoly = (lb) => {
        if (lb.isRound) return false;
        if (lb.previewPolygonFt?.length >= 3) return false;
        const sides = (site.layoutSides || []).filter(
            (s) => s.customerSiteLayoutBinId === lb.customerSiteLayoutBinId
        );
        return sides.length >= 3;
    };

    if ((site.layoutBins || []).some(flatBinMissingPoly)) {
        try {
            drawSite();
        } catch (drawErr) {
            console.warn('ensureLayoutGeometryForBinView: drawSite', drawErr);
        }
        attachPreviewPolygonsFromLayoutSides(site);
    }

    try {
        syncAframeLayoutSensorPositionsFromBts(site);
    } catch (aframeErr) {
        console.warn('ensureLayoutGeometryForBinView: aframe sensor sync', aframeErr);
    }

    fillMissingLayoutCablesFromSiteBins(site);
    attachHumidityLayoutSensorsToSite(site);
    reconcileLayoutCableBinLinks(site);
    syncLayoutCablePositionsFromBinCableList();

    try {
        normalizeFocusedBinFootprintForBinView(site);
    } catch (focusErr) {
        console.warn('ensureLayoutGeometryForBinView: focus bin footprint', focusErr);
    }
}

/**
 * Bin 3D dropdown: SticeArcWall re-centers whichever footprint it draws at the origin, but only
 * the bin focused during initialization had its cables/sensors moved onto that centered frame.
 * Re-run the focus so the newly selected bin's cables land inside its walls instead of off camera.
 * Must run before React re-renders the canvas — the cable groups read these positions on mount.
 */
function focusBinViewOnBin(binId) {
    const site = gw.selectedSite;
    if (!site?.isBinWindowView || !site.layoutBins?.length) return;

    if (binId !== undefined && binId !== null) {
        site.selectedBinId = binId;
    }

    try {
        normalizeFocusedBinFootprintForBinView(site);
    } catch (focusErr) {
        console.warn('focusBinViewOnBin: focus bin footprint', focusErr);
    }
}

/**
 * Bin 3D shows one bin at a time. Groups from the previously selected bin stay in the ref arrays
 * after they unmount and still carry isSelected, so `binsRef.find(b => b.isSelected)` resolves to
 * a bin that is no longer on screen and every cable visibility helper hides the live cables.
 */
function pruneDetachedBinViewRefs() {
    const site = gw.selectedSite;
    if (!site?.isBinWindowView) return;

    const isInScene = (object) => {
        for (let node = object; node; node = node.parent) {
            if (node.isScene) return true;
        }
        return false;
    };

    // A live root that cannot reach the scene means r3f has not attached yet - pruning against it
    // would drop refs that are still valid.
    const root = site.siteRef?.[0];
    if (!root || !isInScene(root)) return;

    ['binsRef', 'cablesRef', 'cableLevelRef', 'sensorsRef', 'sensorsGroupRef'].forEach((key) => {
        if (Array.isArray(site[key])) {
            site[key] = site[key].filter(isInScene);
        }
    });

    (site.groupsRef || []).forEach((g) => {
        g.binsRef = site.binsRef.filter((b) => b.groupId === g.groupId);
    });
}

/** Layout editor preview only — production site3d uses drawSite() like grainwaveclient. */
export function ensureLayoutSideRenderGeometry(site) {
    if (!site?.layoutBins?.length || !site?.layoutSides?.length) return;

    site.layoutBins.forEach((bin) => {
        if (bin.isRound) return;
        const sides = site.layoutSides.filter(
            (s) => s.customerSiteLayoutBinId === bin.customerSiteLayoutBinId
        );
        if (sides.length < 2) return;

        const bx = bin.x || 0;
        const bz = bin.z || 0;

        sides.forEach((s) => {
            if (!layoutSideCoordFinite(s.lx1)) {
                s.lx1 = (s.x1 ?? 0) - bx;
                s.lz1 = (s.z1 ?? 0) - bz;
                s.lx2 = (s.x2 ?? 0) - bx;
                s.lz2 = (s.z2 ?? 0) - bz;
                s.lcurveX = (s.curveX ?? 0) - bx;
                s.lcurveZ = (s.curveZ ?? 0) - bz;
            }

            const straight = !(s.curveRadius > 0);
            if (straight && (!layoutSideCoordFinite(s.hlx1) || !layoutSideCoordFinite(s.hlx2))) {
                const dx = s.lx2 - s.lx1;
                const dz = s.lz2 - s.lz1;
                const len = Math.hypot(dx, dz) || 1;
                const nx = (-dz / len) * LAYOUT_WALL_THICKNESS_FT;
                const nz = (dx / len) * LAYOUT_WALL_THICKNESS_FT;
                s.hlx1 = s.lx1 + nx;
                s.hlz1 = s.lz1 + nz;
                s.hlx2 = s.lx2 + nx;
                s.hlz2 = s.lz2 + nz;
            }

            if (s.curveRadius > 0) {
                if (!layoutSideCoordFinite(s.lcurveX)) {
                    s.lcurveX = (s.curveX ?? 0) - bx;
                    s.lcurveZ = (s.curveZ ?? 0) - bz;
                }
                if (!layoutSideCoordFinite(s.newCurveRadius)) {
                    s.newCurveRadius = s.isInner
                        ? s.curveRadius + LAYOUT_WALL_THICKNESS_FT
                        : Math.max(s.curveRadius - LAYOUT_WALL_THICKNESS_FT, 0.1);
                }
                if (!layoutSideCoordFinite(s.hlx1) || !layoutSideCoordFinite(s.hlx2)) {
                    const innerR =
                        s.newCurveRadius > 0
                            ? s.newCurveRadius
                            : Math.max(s.curveRadius - LAYOUT_WALL_THICKNESS_FT, 0.1);
                    const startA = Math.atan2(s.lz1 - s.lcurveZ, s.lx1 - s.lcurveX);
                    const endA = Math.atan2(s.lz2 - s.lcurveZ, s.lx2 - s.lcurveX);
                    s.hlx1 = s.lcurveX + innerR * Math.cos(startA);
                    s.hlz1 = s.lcurveZ + innerR * Math.sin(startA);
                    s.hlx2 = s.lcurveX + innerR * Math.cos(endA);
                    s.hlz2 = s.lcurveZ + innerR * Math.sin(endA);
                }
            }
        });
    });
}

function resetCamera() {
    if (gw.selectedSite && gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
            gw.selectedSite.orbitControls.reset();

            if (gw.selectedSite.zoom) {
                gw.selectedSite.orbitControls.object.position.x = gw.selectedSite.zoom.x;
                gw.selectedSite.orbitControls.object.position.y = gw.selectedSite.zoom.y;
                gw.selectedSite.orbitControls.object.position.z = gw.selectedSite.zoom.z;
            }

            gw.selectedSite.orbitControls.object.position.set(
                gw.selectedSite.orbitControls.object.position.x,
                gw.selectedSite.orbitControls.object.position.y,
                gw.selectedSite.orbitControls.object.position.z
            );
  
            gw.selectedSite.orbitControls.object.rotation.set(45 * Math.PI / 180.0, gw.selectedSite.orbitControls.object.rotation.y, gw.selectedSite.orbitControls.object.rotation.z);
            gw.selectedSite.orbitControls.object.position.applyEuler(gw.selectedSite.orbitControls.object.rotation);
        }
    }
}
function isSiteLayoutPreview3dSession() {
    return !!(
        gw?.selectedSite?.isLayoutPreview3d ||
        gw?.siteLayoutPreview3dActive
    );
}

function revertCamera() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object) return;
    if (isSiteLayoutPreview3dSession()) return;
    // In Bin3D never restore site camera - there is no site view
    if (gw.selectedSite.isBinWindowView) return;

    gw.selectedSite.orbitControls.reset();

    gw.selectedSite.orbitControls.object.position.set(
        gw.selectedSite.lastCameraPosition.x,
        gw.selectedSite.lastCameraPosition.y,
        gw.selectedSite.lastCameraPosition.z
    );

    gw.selectedSite.orbitControls.object.rotation.set(gw.selectedSite.lastCameraRotation.x, gw.selectedSite.lastCameraRotation.y, gw.selectedSite.lastCameraRotation.z);
    gw.selectedSite.orbitControls.updateCamera(gw.selectedSite.lastCameraLookAt);

    if (!gw.selectedSite.siteRef || !gw.selectedSite.siteRef.length === 0) return;
    let s = gw.selectedSite.siteRef[0];
    s.position.set(
        gw.selectedSite.lastPosition.x,
        gw.selectedSite.lastPosition.y,
        gw.selectedSite.lastPosition.z
    );
    gw.selectedSite.offset.x = gw.selectedSite.lastPosition.x;
    gw.selectedSite.offset.y = gw.selectedSite.lastPosition.y;
    gw.selectedSite.offset.z = gw.selectedSite.lastPosition.z;
}
function saveSiteCamera() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object) return;
    if (gw.selectedSite.isObjectSelected) return;

    gw.selectedSite.lastCameraPosition.set(
        gw.selectedSite.orbitControls.object.position.x,
        gw.selectedSite.orbitControls.object.position.y,
        gw.selectedSite.orbitControls.object.position.z
    );

    gw.selectedSite.lastCameraLookAt.copy(gw.selectedSite.orbitControls.currentLookAt);
    gw.selectedSite.lastCameraRotation.copy(gw.selectedSite.orbitControls.object.rotation);
    
    // Don't update lastAzimuth and lastPolar here - these should only be updated when saving bin camera
    // This prevents overwriting the saved bin camera angles when moving the camera in site view

    gw.selectedSite.lastCanDrag = gw.selectedSite.canDrag;

    if (!gw.selectedSite.siteRef || !gw.selectedSite.siteRef.length === 0) return;
    let s = gw.selectedSite.siteRef[0];
    gw.selectedSite.lastPosition.set(
        s.position.x,
        s.position.y,
        s.position.z
    );
}
function saveBinCamera() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object) return;
    if (!gw.selectedSite.isObjectSelected) return;
    if (gw.selectedSite.isTransitioningFromSiteView) return;

    // If we're focused on a cable, don't overwrite the original bin camera position
    // This preserves the original bin view for when we go back to "Select Cable"
    if (gw.selectedSite.isCableFocused) {
        return;
    }

    // Save the camera position as the original position for this bin
    gw.selectedSite.originalBinCameraPosition = gw.selectedSite.orbitControls.object.position.clone();
    gw.selectedSite.originalBinCameraLookAt = gw.selectedSite.orbitControls.currentLookAt.clone();
    gw.selectedSite.originalBinCameraSaved = true;
    
    // Save the camera angles and zoom to maintain perspective when switching bins
    gw.selectedSite.lastBinPolar = gw.selectedSite.orbitControls.getPolarAngle();
    gw.selectedSite.lastBinAzimuth = gw.selectedSite.orbitControls.getAzimuthalAngle();
    
    gw.selectedSite.lastBinCameraZoom = gw.selectedSite.orbitControls.object.zoom;
    gw.selectedSite.lastBinCameraDistance = gw.selectedSite.orbitControls.object.position.distanceTo(gw.selectedSite.orbitControls.currentLookAt);

    if (gw.selectedSite.canDrag) {
        gw.selectedSite.lastBinOffset.set(
            gw.selectedSite.offset.x,
            gw.selectedSite.offset.y,
            gw.selectedSite.offset.z
        );
    }

    if (!gw.selectedSite.siteRef || !gw.selectedSite.siteRef.length === 0) return;
    let s = gw.selectedSite.siteRef[0];
    gw.selectedSite.lastBinPosition.set(
        s.position.x,
        s.position.y,
        s.position.z
    );
}
function buildSiteViewPayload() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object) return null;
    if (gw.selectedSite.isObjectSelected) return null;
    saveSiteCamera();
    const cam = gw.selectedSite.orbitControls.object;
    const lookAt = gw.selectedSite.orbitControls.currentLookAt;
    let s = gw.selectedSite.siteRef?.[0];
    const pos = s ? { x: s.position.x, y: s.position.y, z: s.position.z } : gw.selectedSite.lastPosition;
    return {
        cameraPositionX: cam.position.x,
        cameraPositionY: cam.position.y,
        cameraPositionZ: cam.position.z,
        lookAtX: lookAt.x,
        lookAtY: lookAt.y,
        lookAtZ: lookAt.z,
        cameraRotationX: cam.rotation.x,
        cameraRotationY: cam.rotation.y,
        cameraRotationZ: cam.rotation.z,
        siteOffsetX: pos.x,
        siteOffsetY: pos.y,
        siteOffsetZ: pos.z,
        polarAngle: null,
        azimuthAngle: null,
        cameraZoom: null,
        cameraDistance: siteUsesEditorSite3dLayout(gw.selectedSite)
            ? EDITOR_LAYOUT_SAVED_VIEW_MARKER
            : null,
        fov: cam.fov ?? 40,
        near: cam.near ?? 1,
        far: cam.far ?? 5000,
        canDrag: gw.selectedSite.canDrag
    };
}
function buildBinViewPayload(binId) {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object) return null;
    if (!gw.selectedSite.isObjectSelected) return null;
    if (gw.selectedSite.isCableFocused) return null;
    saveBinCamera();
    const cam = gw.selectedSite.orbitControls.object;
    const lookAt = gw.selectedSite.orbitControls.currentLookAt;
    const s = gw.selectedSite.siteRef?.[0];
    const siteOffset = s ? { x: s.position.x, y: s.position.y, z: s.position.z } : (gw.selectedSite.canDrag ? gw.selectedSite.lastBinOffset : gw.selectedSite.lastBinPosition);
    return {
        cameraPositionX: cam.position.x,
        cameraPositionY: cam.position.y,
        cameraPositionZ: cam.position.z,
        lookAtX: lookAt.x,
        lookAtY: lookAt.y,
        lookAtZ: lookAt.z,
        cameraRotationX: cam.rotation.x,
        cameraRotationY: cam.rotation.y,
        cameraRotationZ: cam.rotation.z,
        siteOffsetX: siteOffset.x,
        siteOffsetY: siteOffset.y,
        siteOffsetZ: siteOffset.z,
        polarAngle: gw.selectedSite.lastBinPolar ?? null,
        azimuthAngle: gw.selectedSite.lastBinAzimuth ?? null,
        cameraZoom: gw.selectedSite.lastBinCameraZoom ?? null,
        cameraDistance: gw.selectedSite.lastBinCameraDistance ?? null,
        fov: cam.fov ?? 40,
        near: cam.near ?? 1,
        far: cam.far ?? 5000,
        canDrag: gw.selectedSite.canDrag
    };
}
function _v(view, camel, pascal) {
    return view[camel] ?? view[pascal];
}

/** Stored in cameraDistance on Site saves from editor-applied layout (+Z axes). */
const EDITOR_LAYOUT_SAVED_VIEW_MARKER = 1;

/** Ignore cleared placeholders and other non-camera saved rows. */
function savedSiteViewLooksUsable(view) {
    if (!view) return false;
    const px = _v(view, "cameraPositionX", "CameraPositionX");
    const py = _v(view, "cameraPositionY", "CameraPositionY");
    const pz = _v(view, "cameraPositionZ", "CameraPositionZ");
    if (px == null || py == null || pz == null) return false;
    const dist = Math.hypot(px, py, pz);
    return dist > 1;
}

/** Pre-editor saved views used manual layout offsets; skip them on editor-applied sites. */
function savedSiteViewMatchesEditorLayout(view, site) {
    if (!siteUsesEditorSite3dLayout(site)) return true;
    const cd = _v(view, "cameraDistance", "CameraDistance");
    if (cd === EDITOR_LAYOUT_SAVED_VIEW_MARKER) return true;
    if (cd == null) return true;
    // Admin "save for all users" after editor layout — real camera pose, not legacy distance.
    if (
        siteHasCommittedSite3dLayoutProxy(site) &&
        savedSiteViewLooksUsable(view)
    ) {
        return true;
    }
    return false;
}

function fitSiteOverviewCamera() {
    if (!gw.selectedSite?.orbitControls) return;
    const siteObject = gw.selectedSite.groupsRef?.[0]?.parent;
    if (siteObject) {
        fitCameraToObjectWithAngle(
            gw.selectedSite.orbitControls,
            siteObject,
            100,
            Math.PI / 4,
            Math.PI / 2,
            false
        );
    }
}

function resetSiteRootForEditorLayout() {
    if (!gw.selectedSite?.siteRef?.[0]) return;
    gw.selectedSite.siteRef[0].position.set(0, 0, 0);
    gw.selectedSite.offset.x = 0;
    gw.selectedSite.offset.y = 0;
    gw.selectedSite.offset.z = 0;
    gw.selectedSite.lastPosition.x = 0;
    gw.selectedSite.lastPosition.y = 0;
    gw.selectedSite.lastPosition.z = 0;
}

function applySavedView(view, options) {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !view) return false;
    if (isSiteLayoutPreview3dSession()) return false;
    const cam = gw.selectedSite.orbitControls.object;
    const controls = gw.selectedSite.orbitControls;
    let sx = _v(view, 'siteOffsetX', 'SiteOffsetX'), sy = _v(view, 'siteOffsetY', 'SiteOffsetY'), sz = _v(view, 'siteOffsetZ', 'SiteOffsetZ');
    const normalizeBinView = options?.normalizeBinView === true;
    if (normalizeBinView && gw.selectedSite.siteRef?.[0]) {
        sx = sx ?? 0; sy = sy ?? 0; sz = sz ?? 0;
        gw.selectedSite.siteRef[0].position.set(0, 0, 0);
        gw.selectedSite.offset.x = 0;
        gw.selectedSite.offset.y = 0;
        gw.selectedSite.offset.z = 0;
    } else if (gw.selectedSite.siteRef?.[0] && sx != null && sy != null && sz != null) {
        const s = gw.selectedSite.siteRef[0];
        s.position.set(sx, sy, sz);
        gw.selectedSite.offset.x = sx;
        gw.selectedSite.offset.y = sy;
        gw.selectedSite.offset.z = sz;
    }
    let lx = _v(view, 'lookAtX', 'LookAtX'), ly = _v(view, 'lookAtY', 'LookAtY'), lz = _v(view, 'lookAtZ', 'LookAtZ');
    if (normalizeBinView && sx != null && sy != null && sz != null) {
        lx = (lx ?? 0) - sx; ly = (ly ?? 0) - sy; lz = (lz ?? 0) - sz;
    }
    if (lx != null && ly != null && lz != null) {
        controls.currentLookAt.set(lx, ly, lz);
        controls.target.copy(controls.currentLookAt);
    }
    let px = _v(view, 'cameraPositionX', 'CameraPositionX');
    let py = _v(view, 'cameraPositionY', 'CameraPositionY');
    let pz = _v(view, 'cameraPositionZ', 'CameraPositionZ');
    if (px == null || py == null || pz == null) return false;
    if (normalizeBinView && sx != null && sy != null && sz != null) {
        px = px - sx; py = py - sy; pz = pz - sz;
    }
    cam.position.set(px, py, pz);
    if (lx != null && ly != null && lz != null) {
        cam.lookAt(controls.target);
    }
    if (controls.syncFromPositionAndTarget) {
        controls.syncFromPositionAndTarget();
    }
    gw.selectedSite.canDrag = _v(view, 'canDrag', 'CanDrag') ?? true;
    const zoom = _v(view, 'cameraZoom', 'CameraZoom');
    if (zoom != null) cam.zoom = zoom;
    if (cam.updateProjectionMatrix) cam.updateProjectionMatrix();
    if (controls.position0 && cam.position) controls.position0.copy(cam.position);
    if (controls.target0 && controls.target) controls.target0.copy(controls.target);
    if (cam.zoom != null) controls.zoom0 = cam.zoom;
    return true;
}
function fitCameraToObject(controls, object, offset, angleX, angleY, angleZ) {
    if (!controls || !controls.object || !object) return;

    if (!offset) offset = 0;

    //  Reset
    controls.reset();

    const camera = controls.object;
    const isUpY = camera.up.y === 1;
    const positionUp = isUpY ? camera.position.y : camera.position.z;

    //  Reuse vector
    let v = new THREE.Vector3();

    //  Update camera direction (raycast not correct unless)
    camera.getWorldDirection(v);

    //  Get object bounding box
    const box = new THREE.Box3().setFromObject(object);

    //  Get object size, store, and adjust for y or z is length or height
    box.getSize(v);
    const size = {
        width: v.x,
        height: isUpY ? v.y : v.z,
        length: !isUpY ? v.y : v.z
    };

    //  Get object center, store, and adjust for y or z is up
    box.getCenter(v);
    const center = {
        x: v.x,
        y: isUpY ? v.y : v.z,
        z: !isUpY ? v.y : v.z
    };

    ////  Utility Get Local Center
    //v.sub(object.parent.position);

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Shoot top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Top right point in world that meets top right point of canvas (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return;

    //  Adjust for y or z up
    v.set(v.x, isUpY ? v.y : v.z, !isUpY ? -v.y : -v.z);

    //  If camera angle x then width and length gets longer to fit
    if (angleX) {
        size.width += 2 * size.height;
        size.length += 2 * size.height;
    }

    //  Calculate new camera height using scale
    let cameraHeight = Math.abs(Math.max(size.width / v.x, size.length / v.z) * positionUp / 2 + offset);

    //  Adust camera position for y or z up
    v.set(camera.position.x, isUpY ? cameraHeight : center.y, !isUpY ? cameraHeight : center.z);

    camera.position.set(center.x, v.y, center.z);

    //  Object point to focus on
    v.set(center.x, center.y - (isUpY ? 1 : 0), center.z - (!isUpY ? 1 : 0));

    //  Updates camera to look at point
    controls.updateCamera(v);

    //  Change camera angle
    if (angleX || angleY || angleZ) {
        if (!angleX) {
            angleX = camera.rotation.x;
        }
        else {
            angleX = (angleX + 90) * Math.PI / 180;
            v.set(
                v.x,
                isUpY ? camera.position.y - center.y : 0,
                !isUpY ? camera.position.z - center.z : 0
            );
            v.applyEuler(new THREE.Euler(angleX, 0, 0));
            camera.position.set(v.x, v.y + center.y, v.z + center.z);
            camera.rotation.x = angleX;
        }

        if (!angleZ) {
            angleZ = camera.rotation.z;
        }
        else {
            angleZ = (30 - angleZ) * Math.PI / 180;
            v.set(
                camera.position.x - center.x,
                v.y,
                camera.position.z - center.z
            );
            v.applyEuler(new THREE.Euler(camera.rotation.x, camera.rotation.y, angleZ), "XZY");
            camera.position.set(v.x + center.x, v.y, v.z + center.z);
            camera.rotation.y = angleZ;
        }

        if (!angleY) {
            angleY = camera.rotation.y;
        }
        else {
            angleY = (180 - angleY) * Math.PI / 180;
            v.set(
                camera.position.x - center.x,
                camera.position.y - center.y,
                v.z
            );
            v.applyEuler(new THREE.Euler(camera.rotation.x, angleY, camera.rotation.z), "XZY");
            camera.position.set(v.x + center.x, v.y + center.y, v.z);
            camera.rotation.z = angleY;
        }

        //  Set point from camera look at to camera
        
        //v.applyEuler(new THREE.Euler(angleX, angleY, angleZ));
        //camera.position.set(v.x, v.y + center.y, v.z + center.z);

        let c = camera.position;
    }
}
function fitCameraToObjectUpdated(controls, object, offset, angleX, angleY, angleZ) {
    if (!controls || !controls.object || !object) return;

    if (!offset) offset = 0;
    let camera = controls.object;

    let v = new THREE.Vector3();

    object.localToWorld(v);
    let objectLookAt = {
        x: v.x,
        y: controls.currentLookAt.y,
        z: v.z
    }

    controls.reset();

    camera.getWorldDirection(v);

    //  Get object bounding box
    const box = new THREE.Box3().setFromObject(object);

    //  Get object size, store, and adjust for y or z is length or height
    box.getSize(v);
    const size = {
        width: v.x,
        height: v.y,
        length: v.z
    };

    //  Get object center, store, and adjust for y or z is up
    box.getCenter(v);
    const center = {
        x: v.x,
        y: v.y,
        z: v.z
    };

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Shoot top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Top right point in world that meets top right point of canvas (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return;

    //  Adjust for y or z up
    v.set(v.x, v.y, -v.z);

    ////  If camera angle x then width and length gets longer to fit
    //if (angleX) {
    //    size.width += 2 * size.height;
    //    size.length += 2 * size.height;
    //}

    //  Calculate new camera height using scale
    //let maxWidth = Math.max(size.width, size.length, size.height);
    //let maxLength = 0;
    //if (size.width === maxWidth) {
    //    maxLength = Math.max(size.length, size.height);
    //}
    //else if (size.length === maxWidth) {
    //    maxLength = Math.max(size.width, size.height);
    //}
    //else {
    //    maxLength = Math.max(size.width, size.length);
    //}
    //let cameraHeight = Math.abs(Math.max(maxWidth / Math.min(v.x, v.z), maxLength / Math.min(v.x, v.z)) * maxWidth + maxWidth * 1.5 + offset);

    const cameraHeight = Math.abs(Math.max(size.width / v.x, size.length / v.z) * Math.max(camera.position.x, camera.position.y, camera.position.z) / 2 + (Math.max(size.width, size.length) >= size.height * 1.5 ? offset * 1.5 : size.height + offset / 2.15));

    let h = cameraHeight;
    let rotationX = angleX;
    let rotationY = Math.PI / 2 - angleY;
    let positionX = objectLookAt.x + h * Math.sin(rotationX) * Math.cos(rotationY);
    let positionY = objectLookAt.y + h * Math.cos(rotationX);
    let positionZ = objectLookAt.z + h * Math.sin(rotationX) * Math.sin(rotationY);

    controls.object.position.set(
        positionX,
        positionY,
        positionZ
    );

    v.set(
        objectLookAt.x,
        center.y - center.y * 0.1,
        objectLookAt.z
    );

    controls.updateCamera(v);
}
function fitCameraToObjectWithAngle(controls, object, offset, theta, phi, skipReset) {
    if (!controls || !controls.object || !object) return;
    if (!controls.position0 || !controls.target0) return;

    if (!offset) offset = 0;

    const camera = controls.object;

    //  Reuse vector
    let v = new THREE.Vector3();

    //  Get object bounding box and center first (needed for default reset position)
    let box = new THREE.Box3().setFromObject(object);
    box.getCenter(v);
    const center = { x: v.x, y: v.y, z: v.z };

    //  Reset: when !skipReset, set position0/target0 to default above center so raycaster scale is consistent
    //  (otherwise reset uses previous bin's position → wrong scale → alternating zoom / wrong fit)
    if (!skipReset) {
        const rotX = theta;
        const rotY = Math.PI / 2 - phi;
        const defaultDist = 300;
        controls.position0.set(
            center.x + defaultDist * Math.sin(rotX) * Math.cos(rotY),
            center.y + defaultDist * Math.cos(rotX),
            center.z + defaultDist * Math.sin(rotX) * Math.sin(rotY)
        );
        controls.target0.set(center.x, center.y, center.z);
        controls.zoom0 = 1;
        controls.reset();
    }

    //  Get object size
    box.getSize(v);
    let size = {
        width: v.x,
        height: v.y,
        length: v.z
    };

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Shoot top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Top right point in world that meets top right point of canvas (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return;

    //  Adjust for y or z up
    v.set(v.x, v.y, -v.z);
    let scale = {
        x: v.x,
        y: v.y,
        z: v.z
    };

    //  Iterate object children for sizes
    let sphereDiameter = Math.max(size.width, size.length);
    let outerCableRadius = sphereDiameter / 2;
    let sizes = null;
    let maxChildX = 0;
    let maxChildZ = 0;
    let sensorTextHalf = 1.4; // Average half width/height of sensor temperature texts
    if (object.binId) {
        if (object.children && object.children.length > 3) {
            sizes = object.children.map((g, i) => {
                if (g && !g.width) {
                    box = new THREE.Box3().setFromObject(g);

                    //  Get object local center
                    box.getCenter(v);
                    if (object.parent && object.parent.position) {
                        v.sub(object.parent.position);
                        if (object.parent.parent && object.parent.parent.position) {
                            v.sub(object.parent.parent.position);
                        }
                    }
                    v.sub(object.position);
                    let ocenter = {
                        x: v.x,
                        y: v.y,
                        z: v.z
                    };

                    return {
                        x: ocenter.x,
                        y: ocenter.y,
                        z: ocenter.z,
                    }
                }
            });
            maxChildX = Math.max.apply(Math, sizes.map((x, i) => { return Math.abs(x.x); }));
            maxChildZ = Math.max.apply(Math, sizes.map((x, i) => { return Math.abs(x.z); }));
            outerCableRadius = Math.max(maxChildX, maxChildZ) + sensorTextHalf;
        }
        else if (object.children && object.children.length === 3) {
            outerCableRadius = 0;
            size.height += sensorTextHalf;
        }
    }

    ////  Test show sphere
    //if (gw.selectedSite.sceneRef) {
    //    let sphereRadius = Math.sqrt(Math.pow(size.height / 2, 2) + Math.pow(outerCableRadius, 2));
    //    const geometry = new THREE.SphereGeometry(sphereRadius, 40, 20);
    //    const material = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.01, });
    //    const m = new THREE.Mesh(geometry, material);
    //    m.position.set(center.x, center.y, center.z);
    //    gw.selectedSite.sceneRef.add(m);
    //}

    sphereDiameter = Math.sqrt(Math.pow(size.height / 2, 2) + Math.pow(outerCableRadius, 2)) * 2;

    //  Calculate new camera height using scale
    const refY = skipReset ? 300 : camera.position.y;
    let cameraHeight = Math.abs(Math.max(sphereDiameter / scale.x, sphereDiameter / scale.z) * refY / 2 + offset);

    let rotationX = theta;
    let rotationY = Math.PI / 2 - phi;
    let positionX = center.x + cameraHeight * Math.sin(rotationX) * Math.cos(rotationY);
    let positionY = center.y + cameraHeight * Math.cos(rotationX);
    let positionZ = center.z + cameraHeight * Math.sin(rotationX) * Math.sin(rotationY);

    controls.object.position.set(
        positionX,
        positionY,
        positionZ
    );

    //  Object point to focus on
    v.set(center.x, center.y, center.z);

    //  Updates camera to look at point
    controls.updateCamera(v);
}
function fitCameraToObjectWithAngleAndZoomCount(controls, object, offset, theta, phi) {
    if (!controls || !controls.object || !object) return;

    if (!offset) offset = 0;

    // Store the current zoom and distance if we have saved values to preserve
    let preserveZoom = null;
    let preserveDistance = null;
    if (gw.selectedSite && gw.selectedSite.lastBinCameraZoom !== undefined) {
        preserveZoom = gw.selectedSite.lastBinCameraZoom;
        preserveDistance = gw.selectedSite.lastBinCameraDistance;
    }

    //  Reset
    controls.reset();

    const camera = controls.object;

    //  Reuse vector
    let v = new THREE.Vector3();

    //  Update camera direction (raycast not correct unless)
    camera.getWorldDirection(v);

    //  Get object bounding box
    let box = new THREE.Box3().setFromObject(object);

    //  Get object size, store, and adjust for y or z is length or height
    box.getSize(v);
    let size = {
        width: v.x,
        height: v.y,
        length: v.z
    };

    //  Get object center, store, and adjust for y or z is up
    box.getCenter(v);
    const center = {
        x: v.x,
        y: v.y,
        z: v.z
    };

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Shoot top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Top right point in world that meets top right point of canvas (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return;

    //  Adjust for y or z up
    v.set(v.x, v.y, -v.z);
    let scale = {
        x: v.x,
        y: v.y,
        z: v.z
    }

    //  Iterate object children for sizes
    let sphereDiameter = Math.max(size.width, size.length);
    let outerCableRadius = sphereDiameter / 2;
    let sizes = null;
    let maxChildX = 0;
    let maxChildZ = 0;
    let sensorTextHalf = 1.4; // Average half width/height of sensor temperature texts
    if (object.binId) {
        if (object.children && object.children.length > 3) {
            sizes = object.children.map((g, i) => {
                if (g && !g.width) {
                    box = new THREE.Box3().setFromObject(g);

                    //  Get object local center
                    box.getCenter(v);
                    if (object.parent && object.parent.position) {
                        v.sub(object.parent.position);
                        if (object.parent.parent && object.parent.parent.position) {
                            v.sub(object.parent.parent.position);
                        }
                    }
                    v.sub(object.position);
                    let ocenter = {
                        x: v.x,
                        y: v.y,
                        z: v.z
                    };

                    return {
                        x: ocenter.x,
                        y: ocenter.y,
                        z: ocenter.z,
                    }
                }
            });
            maxChildX = Math.max.apply(Math, sizes.map((x, i) => { return Math.abs(x.x); }));
            maxChildZ = Math.max.apply(Math, sizes.map((x, i) => { return Math.abs(x.z); }));
            outerCableRadius = Math.max(maxChildX, maxChildZ) + sensorTextHalf;
        }
        else if (object.children && object.children.length === 3) {
            outerCableRadius = 0;
            size.height += sensorTextHalf;
        }
    }

    ////  Test show sphere
    //if (gw.selectedSite.sceneRef) {
    //    let sphereRadius = Math.sqrt(Math.pow(size.height / 2, 2) + Math.pow(outerCableRadius, 2));
    //    const geometry = new THREE.SphereGeometry(sphereRadius, 40, 20);
    //    const material = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.01, });
    //    const m = new THREE.Mesh(geometry, material);
    //    m.position.set(center.x, center.y, center.z);
    //    gw.selectedSite.sceneRef.add(m);
    //}

    sphereDiameter = Math.sqrt(Math.pow(size.height / 2, 2) + Math.pow(outerCableRadius, 2)) * 2;

    //  Calculate new camera height using scale
    let cameraHeight = Math.abs(Math.max(sphereDiameter / scale.x, sphereDiameter / scale.z) * camera.position.y / 2 + 5 + offset);
    
    // If we have a preserved distance, use that instead of calculating a new one
    if (preserveDistance !== null) {
        cameraHeight = preserveDistance;
    } else if (!gw.selectedSite.binCameraDistance) {
        gw.selectedSite.binCameraDistance = cameraHeight;
    }
    else {
        cameraHeight = gw.selectedSite.binCameraDistance;
    }

    //  Add zoom count
    //for (var i = 0; i < Math.abs(gw.selectedSite.zoomCount); i++) {
    //    if (gw.selectedSite.zoomCount > 0) {
    //        cameraHeight += 0.05263139 * cameraHeight;
    //    }
    //    else {
    //        cameraHeight -= 0.05263139 * cameraHeight;
    //    }
    //}

    let rotationX = theta;
    let rotationY = Math.PI / 2 - phi;
    let positionX = center.x + cameraHeight * Math.sin(rotationX) * Math.cos(rotationY);
    let positionY = center.y + cameraHeight * Math.cos(rotationX);
    let positionZ = center.z + cameraHeight * Math.sin(rotationX) * Math.sin(rotationY);

    controls.object.position.set(
        positionX,
        positionY,
        positionZ
    );

    //  Object point to focus on
    v.set(center.x, center.y, center.z);

    //  Updates camera to look at point
    controls.updateCamera(v);
    
    // Restore the preserved zoom level if we had one
    if (preserveZoom !== null) {
        controls.object.zoom = preserveZoom;
        controls.update();
    }
}
function fitCameraToObjectNoAngle(controls, object, offset) {
    if (!controls || !controls.object || !object) return;

    if (!offset) offset = 0;

    //  Reset
    controls.reset();

    const camera = controls.object;

    //  Reuse vector
    let v = new THREE.Vector3();

    //  Update camera direction (raycast not correct unless)
    camera.getWorldDirection(v);

    //  Get object bounding box
    const box = new THREE.Box3().setFromObject(object);

    //  Get object size, store, and adjust for y or z is length or height
    box.getSize(v);
    const size = {
        width: v.x,
        height: v.y,
        length: v.z
    };

    //  Get object center, store, and adjust for y or z is up
    box.getCenter(v);
    const center = {
        x: v.x,
        y: v.y,
        z: v.z
    };

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Shoot top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Top right point in world that meets top right point of canvas (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return;

    //  Adjust for y or z up
    v.set(v.x, v.y, -v.z);

    //  Calculate new camera height using scale
    let cameraHeight = Math.abs(Math.max(size.width / v.x, size.length / v.z) * camera.position.y / 2 + offset);

    //  Adust camera position for y or z up
    v.set(camera.position.x, cameraHeight, center.z);

    camera.position.set(center.x, v.y, center.z);

    //  Object point to focus on
    v.set(center.x, center.y, center.z);

    //  Updates camera to look at point
    controls.updateCamera(v);
}
function getAxisAndAngelFromQuaternion(q) {
    const angle = 2 * Math.acos(q.w);
    var s;
    if (1 - q.w * q.w < 0.000001) {
        s = 1;
    } else {
        s = Math.sqrt(1 - q.w * q.w);
    }
    return {
        axis: new THREE.Vector3(
            +(q.x / s).toFixed(4),
            +(q.y / s).toFixed(4),
            +(q.z / s).toFixed(4)), angle
    };
}
function updateCompass() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object || !gw.selectedSite.compass) return;
    
    gw.selectedSite.compass.style.transform = `rotate(${THREE.MathUtils.radToDeg(gw.selectedSite.orbitControls.getAzimuthalAngle())}deg)`;
}
function set3dCameraModeOn() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls) return;
    gw.selectedSite.canDrag = false;
    gw.selectedSite.lastCanDrag = false;
    gw.selectedSite.orbitControls.enableRotate = true;
    const isLight = gw.theme === 'light';
    const brightColor = isLight ? "#333333" : "#f4cc8e";
    const dimmedColor = isLight ? "#33333361" : "#f4cc8e61";
    const tvcs = window.document.getElementsByClassName("tvc");
    if (tvcs && tvcs.length >= 2) {
        tvcs[0].style.color = dimmedColor;
        tvcs[1].style.color = brightColor;
    } else if (tvcs && tvcs.length === 1) {
        tvcs[0].style.color = brightColor;
    }
}
/** Site overview — clear stale bin/cable focus from a prior Site 3D session. */
function resetSite3dToSiteOverview() {
    const site = gw.selectedSite;
    if (!site) return;
    site.isObjectSelected = false;
    site.isCableFocused = false;
    site.focusedCableId = null;
    site.zoom = false;
    site.selectedBinId = null;
    site.isTransitioningFromSiteView = false;
    (site.binsRef || []).forEach((b) => {
        b.isSelected = false;
    });
}

/**
 * Clear Three.js object refs + pan/camera interaction state before (re)mounting Site3d.
 * Stice pushes into siteRef/groupsRef/binsRef; leaving disposed objects from a prior visit
 * makes the initial siteView() fit wrong and 3D Pan Mode move a dead site root.
 */
function dummyOrbitControls() {
    return {
        pointer: {
            position: { x: 0, y: 0 }
        },
        target: { x: 0, y: 0, z: 0 },
        object: {
            up: { x: 0, y: 1, z: 0 },
            position: { x: 0, y: 160, z: 0 },
            rotation: { x: 0, y: 0, z: 0, order: "XYZ" },
            quaternion: { x: 0, y: 0, z: 0, w: 0 },
        },
        theta: 0.0,
        phi: 0.0,
        distance: 1.0,
        rotateSpeed: 0.75,
        enableRotate: true,
    };
}

/** Drop leftover Site 3D refs so Bin 3D camera-fit waits for the live /bin canvas. */
function prepareBinViewSceneMount() {
    const site = gw.selectedSite;
    if (!site) return;
    site.siteRef = [];
    site.groupsRef = [];
    site.binsRef = [];
    site.cablesRef = [];
    site.sensorsRef = [];
    site.sensorsGroupRef = [];
    site.cableLevelRef = [];
    site.canvasRef = null;
    site.orbitControls = dummyOrbitControls();
    site.isObjectSelected = false;
    site.isCableFocused = false;
    site.focusedCableId = null;
    site.pendingBinSavedView = undefined;
    site.pendingBinSavedViewKey = null;
    site.binWindowCameraGen = (site.binWindowCameraGen || 0) + 1;
}

function isLiveBinViewCamera(controls) {
    return !!(
        controls &&
        typeof controls.getPolarAngle === "function" &&
        controls.object?.isCamera
    );
}

/** Catalog, layout, and site-card ids a Bin saved view may have been stored under. */
function collectBinSavedViewIds(dropdownId) {
    const ids = [];
    const add = (v) => {
        const n = typeof v === "number" ? v : parseInt(String(v), 10);
        if (Number.isFinite(n) && n >= 0 && !ids.includes(n)) ids.push(n);
    };
    add(dropdownId);
    const site = gw.selectedSite;
    if (!site) {
        add(0);
        return ids;
    }
    const dropStr = dropdownId != null ? String(dropdownId) : "";
    const row = (site.bins || []).find(
        (b) => String(b.binDataId) === dropStr || String(b.binId) === dropStr
    );
    if (row) {
        add(row.binDataId);
        add(row.binId);
    }
    const layoutBin = findLayoutBinForSiteBin(
        site,
        row || { binDataId: dropdownId, binId: dropdownId }
    );
    if (layoutBin) {
        add(layoutBin.binId);
        add(layoutBin.customerSiteLayoutBinId);
    }
    add(0);
    return ids;
}

async function fetchBinSavedView(siteGuid, dropdownId) {
    if (!siteGuid) return null;
    const ids = collectBinSavedViewIds(dropdownId);
    for (const id of ids) {
        try {
            const view = await createApiEndpoint().fetchUserCustomerSiteSavedView(
                "direct",
                siteGuid,
                "Bin",
                id
            );
            if (view) return view;
        } catch {
            // try the next id
        }
    }
    return null;
}

async function prefetchBinSavedView(dropdownId) {
    const site = gw.selectedSite;
    if (!site?.siteGuid) return;
    const cacheKey = `${site.siteGuid}:${String(dropdownId ?? "")}`;
    const view = await fetchBinSavedView(site.siteGuid, dropdownId);
    if (
        site === gw.selectedSite &&
        site.siteGuid &&
        cacheKey.startsWith(String(site.siteGuid))
    ) {
        site.pendingBinSavedView = view;
        site.pendingBinSavedViewKey = cacheKey;
    }
}

function paintSelectedBinForBinWindow(routeBinId) {
    const binRef = routeBinId ? findBinRefFromDropdownId(routeBinId) : null;
    if (binRef && !binRef.isSelected) {
        highlightStice(binRef.binId, { skipCamera: true });
    } else if (binRef?.isSelected) {
        applySelectedBinWallHighlight(binRef);
    }
    restoreCableVisibility?.();
    return binRef;
}

/**
 * Apply the stored Bin camera (or a default fit) to the live /bin OrbitControls.
 * Saved pose does not need binsRef/groupsRef — only the default fit does.
 */
async function applyBinWindowCamera(binId, { reuseGen = false } = {}) {
    const site = gw.selectedSite;
    if (!site?.isBinWindowView) return false;
    const myGen = reuseGen
        ? site.binWindowCameraGen || 0
        : (site.binWindowCameraGen = (site.binWindowCameraGen || 0) + 1);
    const routeBinId = binId ?? site.selectedBinId;
    const cacheKey = `${site.siteGuid}:${String(routeBinId ?? "")}`;

    let savedView =
        site.pendingBinSavedViewKey === cacheKey
            ? site.pendingBinSavedView
            : undefined;
    if (savedView === undefined) {
        savedView = await fetchBinSavedView(site.siteGuid, routeBinId);
        site.pendingBinSavedView = savedView;
        site.pendingBinSavedViewKey = cacheKey;
        if (site.binWindowCameraGen !== myGen) return false;
    }

    if (site.binWindowCameraGen !== myGen) return false;
    if (!isLiveBinViewCamera(site.orbitControls)) return false;
    if (!site.isBinWindowView || site.isCableFocused) return false;

    const binRef = paintSelectedBinForBinWindow(routeBinId);
    const el = site.orbitControls;
    let applied = false;

    if (savedView) {
        applied = applySavedView(savedView, { normalizeBinView: true }) === true;
        if (applied) {
            site.lastBinPosition.set(0, 0, 0);
            site.lastBinOffset.set(0, 0, 0);
            const polar = _v(savedView, "polarAngle", "PolarAngle");
            if (polar != null) site.lastBinPolar = polar;
            const azimuth = _v(savedView, "azimuthAngle", "AzimuthAngle");
            if (azimuth != null) site.lastBinAzimuth = azimuth;
            const zoom = _v(savedView, "cameraZoom", "CameraZoom");
            if (zoom != null) site.lastBinCameraZoom = zoom;
            const dist = _v(savedView, "cameraDistance", "CameraDistance");
            if (dist != null) site.binCameraDistance = dist;
            el.saveState?.();
            el.update?.();
        } else {
            const polar = _v(savedView, "polarAngle", "PolarAngle");
            const azimuth = _v(savedView, "azimuthAngle", "AzimuthAngle");
            const object = binRef || site.groupsRef?.[0]?.parent;
            if (object && polar != null && azimuth != null) {
                fitCameraToObjectWithAngle(el, object, 100, polar, azimuth, false);
                applied = true;
            }
        }
    }

    if (!applied) {
        const object = binRef || site.groupsRef?.[0]?.parent;
        if (!object) return false;
        fitCameraToObjectWithAngle(el, object, 100, Math.PI / 4, Math.PI / 2, false);
        applied = true;
    }

    scaleBinText();
    set3dCameraModeOn();
    site.isObjectSelected = true;
    saveBinCamera();
    return applied;
}

function prepareSite3dSceneMount() {
    const site = gw.selectedSite;
    if (!site) return;

    site.siteRef = [];
    site.groupsRef = [];
    site.binsRef = [];
    site.cablesRef = [];
    site.sensorsRef = [];
    site.sensorsGroupRef = [];
    site.cableLevelRef = [];
    site.canvasRef = null;
    site.canvasRect = null;
    site.compass = null;

    site.canDrag = false;
    site.lastCanDrag = false;
    site.isOffsetChanged = false;
    site.isCameraSaved = false;
    site.offset = { x: 0, y: 0, z: 0 };
    if (site.lastPosition?.set) {
        site.lastPosition.set(0, 0, 0);
    }
    if (site.lastCameraPosition?.set) {
        site.lastCameraPosition.set(0, 0, 0);
    }
    if (site.lastCameraLookAt?.set) {
        site.lastCameraLookAt.set(0, 0, 0);
    }

    // Drop any disposed OrbitControls instance; Site3d ref will assign the live one.
    site.orbitControls = dummyOrbitControls();

    resetSite3dToSiteOverview();
}

/** Re-apply layout-editor proxy and reset 3D refs when opening Site 3D from Site Layout. */
function refreshSite3dFromLayoutEditor() {
    if (!gw.selectedSite?.siteGuid) return false;

    prepareSite3dSceneMount();

    let applied = false;
    try {
        applied = applyEditorLayoutToSiteLikePreview(gw.selectedSite) === true;
        if (!applied && (gw.selectedSite.layoutBins?.length ?? 0) > 0) {
            rehydrateLayoutGeometryForDrawSite(gw.selectedSite);
            attachPreviewPolygonsFromLayoutSides(gw.selectedSite);
            applied = true;
        }
        if (applied) {
            syncLayoutSensorValuesFromCableSensors();
        }
    } catch (err) {
        console.warn("refreshSite3dFromLayoutEditor", err);
    }

    if (typeof window !== "undefined" && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent("grainwaveSelectedSiteReady"));
    }
    return applied;
}

async function siteView() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.groupsRef || gw.selectedSite.groupsRef.length === 0) return;

    // Layout editor 3D preview owns camera + site root (not production saved view).
    if (isSiteLayoutPreview3dSession()) return;

    // In Bin3D (single-bin view) never apply site view - there's no site view, only bin view
    if (gw.selectedSite.isBinWindowView) return;

    // Capture initial cable states if not already captured
    // This ensures we have the true original state when the site view is loaded
    captureInitialCableStates();

    const usesEditorLayout = siteUsesEditorSite3dLayout(gw.selectedSite);
    const siteGuid = gw.selectedSite?.siteGuid ?? null;
    const savedView = await createApiEndpoint().fetchUserCustomerSiteSavedView("direct", siteGuid, "Site", 0);
    const canApplySaved =
        savedView &&
        savedSiteViewLooksUsable(savedView) &&
        savedSiteViewMatchesEditorLayout(savedView, gw.selectedSite);

    if (canApplySaved) {
        applySavedView(savedView);
        setTimeout(() => scaleBinText(), 100);
    } else if (usesEditorLayout) {
        resetSiteRootForEditorLayout();
        fitSiteOverviewCamera();
        setTimeout(() => scaleBinText(), 100);
    } else {
        // No saved view: restore site position first (from bin view it may be offset), then fit or revert
        if (gw.selectedSite.siteRef?.[0]) {
            const s = gw.selectedSite.siteRef[0];
            s.position.set(gw.selectedSite.lastPosition.x, gw.selectedSite.lastPosition.y, gw.selectedSite.lastPosition.z);
            gw.selectedSite.offset.x = gw.selectedSite.lastPosition.x;
            gw.selectedSite.offset.y = gw.selectedSite.lastPosition.y;
            gw.selectedSite.offset.z = gw.selectedSite.lastPosition.z;
        }
        const hasValidLastCamera = gw.selectedSite.lastCameraPosition.x !== 0 || gw.selectedSite.lastCameraPosition.y !== 0 || gw.selectedSite.lastCameraPosition.z !== 0;
        if (hasValidLastCamera) {
            revertCamera();
        } else {
            fitSiteOverviewCamera();
        }
    }

    set3dCameraModeOn();
}
function topView(binId) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0)
        return;

    const comingFromSiteView = !gw.selectedSite.isObjectSelected;

    for (let g = 0; g < gw.selectedSite.groupsRef.length; g++) {
        const group = gw.selectedSite.groupsRef[g];
        if (group.binsRef && group.binsRef.length > 0) {
            let objectBin = group.binsRef.find(x => x.binId === binId);
                if (objectBin) {
                ensureBinRefMeshes(objectBin);
                // Set site position before fit so bin is in correct place (lastBinOffset is 0,0,0 on first load)
                if (!gw.selectedSite.isSiteToBin && !gw.selectedSite.isBinToBin && gw.selectedSite.siteRef?.[0]) {
                    gw.selectedSite.siteRef[0].position.set(0, 0, 0);
                    gw.selectedSite.siteRef[0].position.add(gw.selectedSite.lastBinOffset);
                    gw.selectedSite.offset.x = gw.selectedSite.lastBinOffset.x;
                    gw.selectedSite.offset.y = gw.selectedSite.lastBinOffset.y;
                    gw.selectedSite.offset.z = gw.selectedSite.lastBinOffset.z;
                }
                // When coming from site view: preserve site camera orientation (compass direction) when fitting to bin
                if (comingFromSiteView) {
                    const controls = gw.selectedSite.orbitControls;
                    let theta = controls?.getPolarAngle?.() ?? Math.PI / 4;
                    let phi = controls?.getAzimuthalAngle?.() ?? Math.PI / 2;
                    if (!Number.isFinite(theta) || theta <= 0) theta = Math.PI / 4;
                    if (!Number.isFinite(phi)) phi = Math.PI / 2;
                    gw.selectedSite.lastBinPolar = theta;
                    gw.selectedSite.lastBinAzimuth = phi;
                    fitCameraToObjectWithAngle(controls, objectBin, 100, theta, phi, false);
                    const ob = objectBin;
                    setTimeout(() => {
                        if (gw.selectedSite?.isBinWindowView) return;
                        if (gw.selectedSite?.orbitControls === controls && ob) {
                            ensureBinRefMeshes(ob);
                            ob.parent?.updateMatrixWorld?.(true);
                            fitCameraToObjectWithAngle(gw.selectedSite.orbitControls, ob, 100, theta, phi, false);
                        }
                    }, 300);
                } else {
                    let theta = gw.selectedSite.lastBinPolar;
                    let phi = gw.selectedSite.lastBinAzimuth;
                    if (theta === 0 && phi === 0) {
                        theta = Math.PI / 4;
                        phi = Math.PI / 2;
                    }
                    fitCameraToObjectWithAngleAndZoomCount(gw.selectedSite.orbitControls, objectBin, 0, theta, phi);
                }
                break; // Found the bin, no need to continue searching
            }
        }
    }

    set3dCameraModeOn();
}
function setCameraLookAtBinOrigin() { /* no-op: reverted feature */ }
function resetBinViewToDefault() {
    if (!gw.selectedSite || !gw.selectedSite.orbitControls || !gw.selectedSite.groupsRef?.length) return;
    if (gw.selectedSite.isCableFocused) return;
    const binId = parseInt(gw.selectedSite.selectedBinId, 10);
    let objectToFit = null;
    for (const group of gw.selectedSite.groupsRef) {
        if (group.binsRef?.length && binId) {
            const objectBin = group.binsRef.find(x => x.binId === binId || x.binDataId === binId);
            if (objectBin) {
                ensureBinRefMeshes(objectBin);
                objectToFit = objectBin;
                break;
            }
        }
    }
    if (!objectToFit) objectToFit = gw.selectedSite.groupsRef[0]?.parent;
    if (!objectToFit) return;
    if (gw.selectedSite.siteRef?.[0]) {
        gw.selectedSite.siteRef[0].position.set(0, 0, 0);
        gw.selectedSite.offset.x = 0;
        gw.selectedSite.offset.y = 0;
        gw.selectedSite.offset.z = 0;
        gw.selectedSite.lastBinOffset.set(0, 0, 0);
        gw.selectedSite.lastBinPosition.set(0, 0, 0);
    }
    fitCameraToObjectWithAngle(gw.selectedSite.orbitControls, objectToFit, 100, Math.PI / 4, Math.PI / 2, false);
    gw.selectedSite.orbitControls.currentLookAt.copy(gw.selectedSite.orbitControls.target);
    scaleBinText();
    saveBinCamera();
}

/**
 * True if a layout cable row belongs to a 3D binsRef group. API rows sometimes use dbo.Bin id,
 * layout-bin sequence id, or mixed types; round bins often match binDataId only while flat/stice
 * rows may still key off customerSiteLayoutBinId.
 *
 * Do not compare layoutCable.binId (bin data id) to binRef.binId (layout sequence id) — numeric
 * overlap between bins causes unrelated cables to appear selected.
 */
function layoutCableBelongsToBinRef(layoutCable, binRef) {
    if (!layoutCable || !binRef) return false;
    const sameId = (a, b) => {
        if (a === undefined || a === null || b === undefined || b === null) return false;
        if (a === b) return true;
        const na = parseInt(String(a), 10);
        const nb = parseInt(String(b), 10);
        if (!Number.isNaN(na) && !Number.isNaN(nb) && na === nb) return true;
        return String(a) === String(b);
    };
    const dataId = binRef.binDataId;
    const layoutSeqId = binRef.binId;
    return (
        sameId(layoutCable.customerSiteLayoutBinId, layoutSeqId) ||
        sameId(layoutCable.binId, dataId) ||
        (layoutCable.binDataId != null && sameId(layoutCable.binDataId, dataId))
    );
}

function cableRefBelongsToBinRef(cableRef, binRef) {
    if (!cableRef || !binRef) return false;
    const layoutCable = gw.selectedSite?.layoutCables?.find(lc => lc.cableId === cableRef.cableId);
    if (layoutCable && layoutCableBelongsToBinRef(layoutCable, binRef)) {
        return true;
    }
    return sameLayoutId(cableRef.binId, binRef.binId);
}

function sameLayoutId(a, b) {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    if (a === b) return true;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && na === nb) return true;
    return String(a) === String(b);
}

function binMatchesBinRef(binId, binRef) {
    if (!binRef || binId == null) return false;
    return (
        sameLayoutId(binId, binRef.binId) ||
        sameLayoutId(binId, binRef.binDataId) ||
        sameLayoutId(binId, binRef.customerSiteLayoutBinId)
    );
}

/** Resolve binsRef from a 3D layout id (customerSiteLayoutBinId). Never cross-match binDataId. */
function findBinRefForHighlight(layoutBinId) {
    if (layoutBinId == null || !gw.selectedSite?.binsRef?.length) return null;
    return gw.selectedSite.binsRef.find(b => sameLayoutId(b.binId, layoutBinId)) ?? null;
}

function collectBinRefMeshes(binRef) {
    const meshes = [];
    if (!binRef || typeof binRef.traverse !== 'function') return meshes;
    binRef.traverse((child) => {
        if (child.isMesh) meshes.push(child);
    });
    return meshes;
}

/** Populate binRef.meshes when Stice ref ran before wall geometry mounted (common on flat houses). */
function ensureBinRefMeshes(binRef) {
    if (!binRef || typeof binRef.traverse !== 'function') return [];
    const hasWall =
        Array.isArray(binRef.meshes) &&
        binRef.meshes.some(m => m?.isMesh && m.isWallMesh);
    if (hasWall) return binRef.meshes;
    binRef.meshes = collectBinRefMeshes(binRef);
    return binRef.meshes;
}

/** Paint wall meshes for a bin that is already selected (walls often mount after first highlight). */
function applySelectedBinWallHighlight(binRef) {
    if (!binRef) return;
    ensureBinRefMeshes(binRef).forEach(y => highlight(y, true));
}

/** Resolve binsRef from a dropdown value (site.bins binDataId). */
function findBinRefFromDropdownId(selectedBinId) {
    if (selectedBinId == null || !gw.selectedSite?.binsRef?.length) return null;
    const dataId = typeof selectedBinId === 'number'
        ? selectedBinId
        : parseInt(String(selectedBinId), 10);
    if (Number.isNaN(dataId)) return null;

    let binRef = gw.selectedSite.binsRef.find(b => sameLayoutId(b.binDataId, dataId));
    if (binRef) return binRef;

    const siteBin = (gw.selectedSite.bins || []).find(b => sameLayoutId(b.binDataId, dataId));
    if (siteBin) {
        const layoutBin = findLayoutBinForSiteBin(gw.selectedSite, siteBin);
        if (layoutBin) {
            binRef = gw.selectedSite.binsRef.find(b =>
                sameLayoutId(b.binId, layoutBin.customerSiteLayoutBinId)
            );
            if (binRef) return binRef;
        }
        if (siteBin.binName) {
            binRef = gw.selectedSite.binsRef.find(b => b.name === siteBin.binName || b.binName === siteBin.binName);
            if (binRef) return binRef;
        }
    }

    const layoutBin = (gw.selectedSite.layoutBins || []).find(lb => sameLayoutId(lb.binId, dataId));
    if (layoutBin) {
        binRef = gw.selectedSite.binsRef.find(b => sameLayoutId(b.binId, layoutBin.customerSiteLayoutBinId));
        if (binRef) return binRef;
    }

    return null;
}

function sameCableId(a, b) {
    if (a == null || b == null) return false;
    const na = typeof a === "number" ? a : parseInt(a, 10);
    const nb = typeof b === "number" ? b : parseInt(b, 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return a === b;
}

/** Hide every cable except the one the user selected. Safe to call after a Heltec refresh. */
function hideUnfocusedCables() {
    const site = gw.selectedSite;
    if (!site?.isCableFocused || site.focusedCableId == null || !site.cablesRef) return;

    site.cablesRef.forEach((cable) => {
        if (sameCableId(cable.cableId, site.focusedCableId)) return;
        cable.visible = false;
        (cable.meshes || []).forEach((mesh) => {
            if (mesh?.material && !mesh.isLevel) {
                mesh.material.visible = false;
            }
        });
        (cable.children || []).forEach((child) => {
            if (child?.material && !child.isLevel) {
                child.material.visible = false;
            }
        });
    });
    syncAllSensorGroupVisibility();
}

/** Whether a sensor temperature mesh should be visible given cable toggle, bin view, and cable focus. */
function shouldShowSensorMesh(binId, cableId) {
    if (!gw.selectedSite) return true;

    if (gw.selectedSite.isCableFocused) {
        const focusedCableId = gw.selectedSite.focusedCableId != null
            ? parseInt(gw.selectedSite.focusedCableId, 10)
            : null;
        const thisCableId = cableId != null ? parseInt(cableId, 10) : null;
        if (focusedCableId != null && thisCableId !== focusedCableId) {
            return false;
        }
    }

    const selectedBin = gw.selectedSite.binsRef.find(bin => bin.isSelected);
    if (selectedBin) {
        const layoutCable = gw.selectedSite.layoutCables?.find(lc => lc.cableId === cableId);
        if (layoutCable && layoutCableBelongsToBinRef(layoutCable, selectedBin)) {
            return true;
        }
        // layoutSensor.binId is the bin data id — only compare to binDataId, not layout seq id
        if (binId != null && selectedBin.binDataId != null && sameLayoutId(binId, selectedBin.binDataId)) {
            return true;
        }
        return false;
    }

    return !gw.selectedSite.isCablesOff;
}

function applySensorMeshVisibility(m, binId, cableId) {
    if (!m || !m.material) return;
    if (m.isLevel) {
        m.material.visible = false;
        return;
    }
    const show = shouldShowSensorMesh(binId, cableId);
    m.material.visible = show;
    if (m.name === "AnSpace") {
        m.visible = false;
    } else {
        m.visible = show;
        if (show) {
            m.rotation.set(Math.PI / 2, 0, 0);
        }
    }
}

function glyphGeometryForChar(ts) {
    if (ts == null) return null;
    if (ts === "%") return gw.geometries.AnPercent || null;
    if (ts === "°") return gw.geometries.AnDegree || null;
    if (ts === "-") return gw.geometries.AnDash || null;
    if (ts === ".") return gw.geometries.AnDot || null;
    if (ts === " " || ts === "") return gw.geometries.AnSpace || null;
    if (ts === "@") return gw.geometries.AnAt || null;
    if (ts === "/") return gw.geometries.AnFSlash || null;
    if (ts === ":") return gw.geometries.AnColon || null;
    const code = ts.charCodeAt(0);
    if (code >= 97 && code <= 122) return gw.geometries[`An${ts}${ts}`] || null;
    return gw.geometries[`An${ts}`] || null;
}

function glyphNameForChar(ts) {
    if (ts === "%") return "AnPercent";
    if (ts === "°") return "AnDegree";
    if (ts === "-") return "AnDash";
    if (ts === ".") return "AnDot";
    if (ts === " " || ts === "") return "AnSpace";
    if (ts === "@") return "AnAt";
    if (ts === "/") return "AnFSlash";
    if (ts === ":") return "AnColon";
    const code = ts.charCodeAt(0);
    if (code >= 97 && code <= 122) return `An${ts}${ts}`;
    return `An${ts}`;
}

function setMeshGlyphGeometry(m, geometry, name) {
    if (!m || !geometry) return false;
    m.geometry = geometry;
    if (name) m.name = name;
    return true;
}

/** Light mode: flat readable color on white. Dark mode: emissive glow for the temperature ramp. */
function isFaintSensorLabel(value) {
    const tempStr = value == null ? "" : String(value).trim();
    return tempStr === "" || tempStr === "E" || tempStr === "-E-" || tempStr === "-O-" || /^x[0-9A-Fa-f]{2}$/.test(tempStr);
}

/** How far faint labels sit from the 3D background toward gray (0 = invisible, 1 = full gray). */
const FAINT_SENSOR_LABEL_MIX = 0.12;
const SENSOR_STALE_MS = 60 * 60 * 1000;
const NUMERIC_TEMPERATURE_LABEL = /^-?\d+(\.\d+)?$/;

/** Stale (no update in an hour), -E-/-O-, fault codes (xNN), blank, or otherwise non-numeric readings. */
function isLayoutSensorFaint(layoutSensor) {
    if (!layoutSensor) return false;
    const value = layoutSensor.sensorValueString ?? layoutSensor.sensorValueNumber;
    const label = value == null ? "" : String(value).trim();
    if (isFaintSensorLabel(label) || !NUMERIC_TEMPERATURE_LABEL.test(label)) return true;
    if (gw.selectedSite?.isHistorySliderOn) return false;
    return !!layoutSensor.lastUpdateTimestamp && Date.now() - layoutSensor.lastUpdateTimestamp > SENSOR_STALE_MS;
}

const FAINT_SENSOR_RECOMPUTE_MS = 250;
let faintSensorIds = new Map();
let faintSensorIdsComputedAt = 0;
let faintSensorIdsSource = null;

const faintSensorLabelColor = new THREE.Color();
const FAINT_SENSOR_INK_DARK = new THREE.Color(0x808080);
const FAINT_SENSOR_INK_LIGHT = new THREE.Color(0x878787);
const BLACK_COLOR = new THREE.Color(0x000000);
const restoredSensorIds = new Set();

/**
 * Final say on faint sensor labels. Many update/highlight/flash paths reset sensor glyph
 * materials at arbitrary times, so this must run every frame before render.
 * Fades by color (blended toward the canvas background) rather than opacity: the dark-mode
 * postprocessing composer drops most of the alpha, so low opacity still renders bright.
 */
export function enforceFaintSensorLabels() {
    const site = gw.selectedSite;
    if (!site?.sensorsRef?.length || !site.layoutSensors?.length) return;

    const isLight = gw.theme === 'light';
    faintSensorLabelColor
        .setHex(isLight ? 0xffffff : 0x2d2d2d)
        .lerp(isLight ? FAINT_SENSOR_INK_LIGHT : FAINT_SENSOR_INK_DARK, FAINT_SENSOR_LABEL_MIX);

    const now = Date.now();
    if (faintSensorIdsSource !== site.layoutSensors || now - faintSensorIdsComputedAt > FAINT_SENSOR_RECOMPUTE_MS) {
        faintSensorIds = new Map();
        for (const s of site.layoutSensors) {
            if (!isHumidityLayoutSensor(s)) faintSensorIds.set(Number(s.sensorId), isLayoutSensorFaint(s));
        }
        faintSensorIdsSource = site.layoutSensors;
        faintSensorIdsComputedAt = now;
    }

    for (const m of site.sensorsRef) {
        const material = m?.material;
        if (!material || m.isLevel || m.name === "AnSpace" || m.sensorId == null) continue;
        const faint = faintSensorIds.get(Number(m.sensorId));
        if (faint === undefined) continue;

        if (faint) {
            if (material.transparent || material.opacity !== 1 || material.depthWrite || material.toneMapped) {
                material.transparent = false;
                material.opacity = 1;
                material.depthWrite = false;
                material.toneMapped = false;
                material.needsUpdate = true;
            }
            // Black base + emissive renders the exact faint color regardless of scene lighting.
            if (!material.color.equals(BLACK_COLOR)) material.color.copy(BLACK_COLOR);
            if (material.emissive && !material.emissive.equals(faintSensorLabelColor)) {
                material.emissive.copy(faintSensorLabelColor);
            }
            material.userData.faintSensorLabel = true;
        } else if (material.userData.faintSensorLabel) {
            material.depthWrite = true;
            material.needsUpdate = true;
            material.userData.faintSensorLabel = false;
            restoredSensorIds.add(Number(m.sensorId));
        }
    }

    // Labels that just became valid: repaint with their real temperature color.
    if (restoredSensorIds.size) {
        for (const sensorId of restoredSensorIds) {
            const s = site.layoutSensors.find(x => Number(x.sensorId) === sensorId);
            const value = s?.sensorValueNumber ?? s?.sensorValueString;
            if (s && value != null && value !== "") {
                updateSensorTemperature(s.binId, s.cableId, s.sensorId, value, null, false);
            }
        }
        restoredSensorIds.clear();
    }
}

function applyTemperatureColorToSensorMaterial(material, color) {
    if (!material || !color) return;

    material.color.set(color);
    if (gw.theme === 'light') {
        material.toneMapped = true;
        if (material.emissive) {
            material.emissive.setHex(0x000000);
        }
    } else {
        material.toneMapped = false;
        if (material.emissive) {
            material.emissive.set(color);
        }
    }
    material.needsUpdate = true;
}

function syncAllSensorGroupVisibility() {
    if (!gw.selectedSite?.sensorsGroupRef) return;

    gw.selectedSite.sensorsGroupRef.forEach(group => {
        if (!group?.meshProps) return;
        const { binId, cableId } = group.meshProps;
        group.traverse(child => {
            if (!child.material) return;
            if (child.isLevel) {
                child.material.visible = false;
                return;
            }
            if (child.name === "AnSpace") {
                child.material.visible = false;
                return;
            }
            child.material.visible = shouldShowSensorMesh(binId, cableId);
        });
    });
}

function clearSensorFlashAnimations(restoreVisibility = true) {
    if (!gw.selectedSite?.sensorsGroupRef) return;

    gw.selectedSite.sensorsGroupRef.forEach(group => {
        if (!group) return;

        if (group.flashTimeoutId) {
            clearTimeout(group.flashTimeoutId);
            group.flashTimeoutId = null;
        }
        if (group.flashOnTimeoutId) {
            clearTimeout(group.flashOnTimeoutId);
            group.flashOnTimeoutId = null;
        }
        group.isAnimating = false;
    });

    if (restoreVisibility) {
        syncAllSensorGroupVisibility();
    }
}

function setUnselectedCablesOff(options = {}) {
    clearSensorFlashAnimations();
    const preserveToggleState = options.preserveToggleState === true;
    const skipToggleUpdate = !preserveToggleState && gw.selectedSite.isCablesOff;
    
    let visibleCount = 0;
    let hiddenCount = 0;
    let totalCount = 0;
    
    if (gw.selectedSite.cablesRef && gw.selectedSite.cablesRef.length > 0) {
        const selectedBin = gw.selectedSite.binsRef.find(bin => bin.isSelected);
        gw.selectedSite.cablesRef.forEach((g) => {
            const cableLayoutCable = gw.selectedSite.layoutCables?.find(lc => 
                lc.cableId === g.cableId
            );
            const isBinSelected = selectedBin && (
                (cableLayoutCable && layoutCableBelongsToBinRef(cableLayoutCable, selectedBin)) ||
                cableRefBelongsToBinRef(g, selectedBin)
            );
                
                // Get sensor IDs for this cable to check for error values
                const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === g.cableId) || [];
                const sensorErrorValues = new Set();
                cableSensors.forEach(sensor => {
                    const tempStr = sensor.sensorValueString?.toString().trim() || "";
                    if (isFaintSensorLabel(tempStr)) {
                        sensorErrorValues.add(sensor.sensorId);
                    }
                });
                
                // Also check mesh names as fallback for error pattern detection
                let hasErrorPattern = false;
                let dashCount = 0;
                let eCount = 0;
                let oCount = 0;
                
                g.meshes.forEach(mesh => {
                    if (mesh.name === "AnDash") dashCount++;
                    if (mesh.name === "AnE") eCount++;
                    if (mesh.name === "AnO") oCount++;
                });
                
                // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
                if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                    hasErrorPattern = true;
                }
                
                // Check if this cable has all invalid temperatures
                const hasAllInvalidTemperatures = cableSensors.length > 0 && cableSensors.every(sensor => {
                    const tempStr = sensor.sensorValueString?.toString().trim() || "";
                    return isFaintSensorLabel(tempStr);
                });
                
                // Check if ANY sensor on this cable is in the heltecSensorMappings
                // If a cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
                let hasSensorsInHeltecMapping = false;
                if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
                    // Check if any sensor on this cable has a mapping in heltecSensorMappings
                    for (const sensor of cableSensors) {
                        const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                            mapping.sensorId === sensor.sensorId
                        );
                        if (hasMapping) {
                            hasSensorsInHeltecMapping = true;
                            break;
                        }
                    }
                }
                
                // If invalid cables toggle is off and this cable has all invalid temperatures, hide it
                // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
                const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (hasAllInvalidTemperatures || hasErrorPattern) && !hasSensorsInHeltecMapping;
                
                // Update cable group visibility based on invalid cable toggle
                if (g.visible !== undefined) {
                    if (shouldHideInvalidCable) {
                        g.visible = false;
                    } else if (isBinSelected) {
                        g.visible = true;
                    } else {
                        g.visible = false;
                    }
                }
                
                g.meshes.forEach((m, meshIndex) => {
                    if (m.name === "AnSpace") m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                    totalCount++;
                    if (m.material) {
                // Skip level indicator boxes - they should always be hidden
                // Don't touch their opacity since they start at 0 opacity
                if (m.isLevel) {
                    m.material.visible = false;
                    return;
                }
                        
                        if (isBinSelected) {
                            // Hide invalid cables if toggle is off, even if bin is selected
                            if (shouldHideInvalidCable) {
                                m.material.visible = false;
                                hiddenCount++;
                                return;
                            }
                            
                            // Check if this mesh belongs to a sensor with an error value
                            // sensorId can be on mesh directly or in meshProps
                            const meshSensorId = m.sensorId || m.meshProps?.sensorId;
                            const isErrorSensor = meshSensorId && sensorErrorValues.has(meshSensorId);
                            
                            // Check if it's a sensor mesh (has sensorId or isSensor flag)
                            const isSensorMesh = meshSensorId || m.meshProps?.isSensor;
                            
                            // AnSpace in cable name/grade - keep hidden (should be edge-on/invisible)
                            if (m.name === "AnSpace" && !isSensorMesh) {
                                m.material.visible = false;
                                m.visible = false;
                                return;
                            }
                            
                            // Show cables in selected bin
                            m.material.visible = true;
                            
                // Apply opacity based on error pattern detection
                // Only affect temperature text (isSensor), not cable names
                if (isSensorMesh && m.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (m.name === "AnDash" || m.name === "AnE" || m.name === "AnO")))) {
                    // In light mode, error values should be visible with same color as bin name labels
                    if (gw.theme === 'light') {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                    } else {
                        // Invalid values should have very low opacity when not in light mode
                        m.material.opacity = 0.0025;
                        m.material.transparent = true;
                    }
                } else if (m.name === "AnSpace" && isSensorMesh) {
                                // Space characters always have low opacity (only for temperature text)
                                m.material.opacity = 0.0025;
                                m.material.transparent = true;
                            } else if (!m.isText) {
                                // Regular cable parts get full opacity
                                m.material.opacity = 1.0;
                                m.material.transparent = false;
                            }
                            // Text meshes keep their current opacity unless they're error values
                            
                            m.material.needsUpdate = true;
                            visibleCount++;
                        } else {
                            // Hide cables in unselected bins
                            m.material.visible = false;
                            hiddenCount++;
                        }
                    }
                });
        });
    }
    
    syncAllSensorGroupVisibility();
    
    if (!preserveToggleState && !skipToggleUpdate) {
        gw.selectedSite.isCablesOff = true;
        setAllBinsTextColor();

        // Set button state directly
        let tvcs = window.document.getElementsByClassName("cbi");
        
        // If not found, try to find by tooltip
        if (!tvcs || tvcs.length === 0) {
            const allButtons = window.document.querySelectorAll('button');
            for (let button of allButtons) {
                const tooltip = button.closest('[title="Toggle Cable Visibility"]');
                if (tooltip) {
                    tvcs = [button];
                    break;
                }
            }
        }
        
        if (tvcs && tvcs.length > 0) {
            const isLight = gw.theme === 'light';
            const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            tvcs[0].style.color = dimmedColor;
            
            // Force a style update
            tvcs[0].style.setProperty('color', dimmedColor, 'important');
        }
    }
    
    // Force a complete re-render to make the cable visibility changes visible immediately
    if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        gw.selectedSite.orbitControls.update();
    }
    
    // Force a complete scene re-render
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
    }
    
    // Force Three.js to update all materials
    if (gw.selectedSite.scene) {
        gw.selectedSite.scene.traverse((object) => {
            if (object.material) {
                object.material.needsUpdate = true;
            }
        });
    }
    
    // Additional forced re-render to ensure visibility changes are immediate
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        requestAnimationFrame(() => {
            gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
        });
    }
    
    // Trigger React component re-render
    if (gw.selectedSite.triggerCableVisibilityRerender) {
        gw.selectedSite.triggerCableVisibilityRerender();
    }
}
function setInvalidCables(onOrOff) {
    // Prevent multiple calls
    if (onOrOff === true && gw.selectedSite.isInvalidCablesOn) {
        return;
    }
    if (onOrOff === false && gw.selectedSite.isInvalidCablesOff) {
        return;
    }
    
    let totalCount = 0;
    
    // Determine if we're in Site View or Bin View
    const isBinView = gw.selectedSite.isObjectSelected;
    const selectedBin = isBinView ? gw.selectedSite.binsRef.find(bin => bin.isSelected) : null;
    
    if (gw.selectedSite.cablesRef && gw.selectedSite.cablesRef.length > 0) {
        gw.selectedSite.cablesRef.forEach((g, index) => {
            // Find which bin this cable belongs to
            const cableLayoutCable = gw.selectedSite.layoutCables?.find(lc => 
                lc.cableId === g.cableId
            );
            
            if (cableLayoutCable) {
                // In Bin View, only process cables that belong to the selected bin
                if (isBinView && selectedBin && !layoutCableBelongsToBinRef(cableLayoutCable, selectedBin)) {
                    return;
                }
                
                // Get sensor IDs for this cable to check for error values
                const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === g.cableId) || [];
                const sensorErrorValues = new Set();
                cableSensors.forEach(sensor => {
                    const tempStr = sensor.sensorValueString?.toString().trim() || "";
                    if (isFaintSensorLabel(tempStr)) {
                        sensorErrorValues.add(sensor.sensorId);
                    }
                });

                // Check if this cable has all invalid temperatures
                const hasAllInvalidTemperatures = cableSensors.length > 0 && cableSensors.every(sensor => sensorErrorValues.has(sensor.sensorId));
                if (!hasAllInvalidTemperatures) {
                    return;
                }
                    
                // Also check mesh names as fallback for error pattern detection
                let hasErrorPattern = false;
                let dashCount = 0;
                let eCount = 0;
                let oCount = 0;
                    
                g.meshes.forEach(mesh => {
                    if (mesh.name === "AnDash") dashCount++;
                    if (mesh.name === "AnE") eCount++;
                    if (mesh.name === "AnO") oCount++;
                });
                    
                // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
                if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                    hasErrorPattern = true;
                }
                
                // Update cable group visibility
                if (g.visible !== undefined) {
                    if (onOrOff === true) {
                        g.visible = true;
                    } else {
                        // Only hide if we're turning off, but respect current view state
                        // If in bin view and cable doesn't belong to bin, keep it hidden
                        if (isBinView && selectedBin && !layoutCableBelongsToBinRef(cableLayoutCable, selectedBin)) {
                            g.visible = false;
                        }
                    }
                }
                    
                g.meshes.forEach((m, meshIndex) => {
                    totalCount++;
                    if (m.material) {
                        // Skip level indicator boxes - they should always be hidden
                        // Don't touch their opacity since they start at 0 opacity
                        if (m.isLevel) {
                            m.material.visible = false;
                            return;
                        }
                        if (onOrOff === true) {
                            m.material.visible = true;
                        } else {
                            m.material.visible = false;
                        }
                    }
                });
                
                // Process cable geometry meshes (children)
                if (g.children && g.children.length > 0) {
                    g.children.forEach((child, childIndex) => {
                        if (child.material) {
                            // Skip level indicator boxes - they should always be hidden
                            if (child.isLevel) {
                                child.material.visible = false;
                                return;
                            }
                            if (onOrOff === true) {
                                child.material.visible = true;
                            } else {
                                child.material.visible = false;
                            }
                        }
                    });
                }
            }
        });
    }
    
    gw.selectedSite.isInvalidCables = onOrOff;
    gw.selectedSite.isInvalidCablesOn = onOrOff === true;
    gw.selectedSite.isInvalidCablesOff = onOrOff === false;
    setAllBinsTextColor();

    // Set button state directly
    let tvcs = window.document.getElementsByClassName("cbi");
    
    // If not found, try to find by tooltip
    if (!tvcs || tvcs.length === 0) {
        const allButtons = window.document.querySelectorAll('button');
        for (let button of allButtons) {
            const tooltip = button.closest('[title="Toggle Cable Visibility"]');
            if (tooltip) {
                tvcs = [button];
                break;
            }
        }
    }
    
    if (tvcs && tvcs.length > 0) {
        const isLight = gw.theme === 'light';
        const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
        tvcs[0].style.color = dimmedColor;
        
        // Force a style update
        tvcs[0].style.setProperty('color', dimmedColor, 'important');
    }
    
    // Force a complete re-render to make the cable visibility changes visible immediately
    if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        gw.selectedSite.orbitControls.update();
    }
    
    // Force a complete scene re-render
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
    }
    
    // Force Three.js to update all materials
    if (gw.selectedSite.scene) {
        gw.selectedSite.scene.traverse((object) => {
            if (object.material) {
                object.material.needsUpdate = true;
            }
        });
    }
    
    // Additional forced re-render to ensure visibility changes are immediate
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        requestAnimationFrame(() => {
            gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
        });
    }
    
    // Trigger React component re-render
    if (gw.selectedSite.triggerCableVisibilityRerender) {
        gw.selectedSite.triggerCableVisibilityRerender();
    }
}

function setGradesVisibility(onOrOff) {
    if (!gw.selectedSite) return;
    gw.selectedSite.isGradesOn = onOrOff === true;
    const trigger = gw.selectedSite.triggerGradesRerender || gw.selectedSite.triggerCableVisibilityRerender;
    if (trigger) trigger();
    // Apply text scale from zoom so grades match bin name size
    setTimeout(() => scaleBinText(), 100);
}

function setAllCablesOn() {
    clearSensorFlashAnimations();
    // Always run to ensure cables are visible, especially when coming from cable focus
    // if (!gw.selectedSite.isCablesOff) {
    //     return;
    // }
    
    let visibleCount = 0;
    let totalCount = 0;
    
    // Make all cables visible
    if (gw.selectedSite.cablesRef && gw.selectedSite.cablesRef.length > 0) {
        gw.selectedSite.cablesRef.forEach((g, index) => {
        // Get sensor IDs for this cable to check for error values
        const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === g.cableId) || [];
        const sensorErrorValues = new Set();
        cableSensors.forEach(sensor => {
            const tempStr = sensor.sensorValueString?.toString().trim() || "";
            if (isFaintSensorLabel(tempStr)) {
                sensorErrorValues.add(sensor.sensorId);
            }
        });
        
        // Also check mesh names as fallback for error pattern detection
        let hasErrorPattern = false;
        let dashCount = 0;
        let eCount = 0;
        let oCount = 0;
        
        g.meshes.forEach(mesh => {
            if (mesh.name === "AnDash") dashCount++;
            if (mesh.name === "AnE") eCount++;
            if (mesh.name === "AnO") oCount++;
        });
        
        // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
        if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
            hasErrorPattern = true;
        }
        
        // Check if this cable has all invalid temperatures
        const hasAllInvalidTemperatures = cableSensors.length > 0 && cableSensors.every(sensor => {
            const tempStr = sensor.sensorValueString?.toString().trim() || "";
            return isFaintSensorLabel(tempStr);
        });
        
        // Check if ANY sensor on this cable is in the heltecSensorMappings
        // If a cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
        let hasSensorsInHeltecMapping = false;
        if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
            // Check if any sensor on this cable has a mapping in heltecSensorMappings
            for (const sensor of cableSensors) {
                const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                    mapping.sensorId === sensor.sensorId
                );
                if (hasMapping) {
                    hasSensorsInHeltecMapping = true;
                    break;
                }
            }
        }
        
        // If invalid cables toggle is off and this cable has all invalid temperatures, hide it
        // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
        const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (hasAllInvalidTemperatures || hasErrorPattern) && !hasSensorsInHeltecMapping;
        
        // Make the cable group visible (this is crucial!) - but hide if invalid cable toggle is off
        if (g.visible !== undefined) {
            g.visible = !shouldHideInvalidCable;
        }
        
        // Process text meshes
        g.meshes.forEach((m, meshIndex) => {
            if (m.name === "AnSpace") m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
            totalCount++;
            if (m.material) {
                // Skip level indicator boxes - they should always be hidden
                // Don't touch their opacity since they start at 0 opacity
                if (m.isLevel) {
                    m.material.visible = false;
                    return;
                }
                
                // Hide invalid cables if toggle is off
                if (shouldHideInvalidCable) {
                    m.material.visible = false;
                    return;
                }
                
                // Check if this mesh belongs to a sensor with an error value
                // sensorId can be on mesh directly or in meshProps
                const meshSensorId = m.sensorId || m.meshProps?.sensorId;
                const isErrorSensor = meshSensorId && sensorErrorValues.has(meshSensorId);
                
                // Check if it's a sensor mesh (has sensorId or isSensor flag)
                const isSensorMesh = meshSensorId || m.meshProps?.isSensor;
                
                // AnSpace in cable name/grade - keep hidden (should be edge-on/invisible)
                if (m.name === "AnSpace" && !isSensorMesh) {
                    m.material.visible = false;
                    m.visible = false;
                    return;
                }
                
                m.material.visible = true;
                
                // Apply opacity based on error pattern detection
                // Only affect temperature text (isSensor), not cable names
                if (isSensorMesh && m.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (m.name === "AnDash" || m.name === "AnE" || m.name === "AnO")))) {
                    // In light mode, error values should be visible with same color as bin name labels
                    if (gw.theme === 'light') {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                    } else {
                        // Invalid values should have very low opacity when not in light mode
                        m.material.opacity = 0.0025;
                        m.material.transparent = true;
                    }
                } else if (m.name === "AnSpace" && isSensorMesh) {
                    // Space characters always have low opacity (only for temperature text)
                    m.material.opacity = 0.0025;
                    m.material.transparent = true;
                } else {
                    // All other meshes (including text) get full opacity
                    // This ensures cables hidden by focusOnCable are restored
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                }
                
                m.material.needsUpdate = true;
                visibleCount++;
            } else {
            }
        });
        
        // Process cable geometry meshes (children)
        if (g.children && g.children.length > 0) {
            g.children.forEach((child, childIndex) => {
                totalCount++;
                if (child.material) {
                    // Skip level indicator boxes - they should always be hidden
                    if (child.isLevel) {
                        child.material.visible = false;
                        return;
                    }
                    // Hide invalid cables if toggle is off
                    if (shouldHideInvalidCable) {
                        child.material.visible = false;
                        return;
                    }
                    child.material.visible = true;
                    child.material.opacity = 1.0;
                    child.material.transparent = false;
                    child.material.needsUpdate = true;
                    visibleCount++;
                } else {
                }
            });
        }
    });
    }
    
    gw.selectedSite.isCablesOff = false;
    setAllBinsTextColor();

    // Set button state directly
    let tvcs = window.document.getElementsByClassName("cbi");
    
    // If not found, try to find by tooltip
    if (!tvcs || tvcs.length === 0) {
        const allButtons = window.document.querySelectorAll('button');
        for (let button of allButtons) {
            const tooltip = button.closest('[title="Toggle Cable Visibility"]');
            if (tooltip) {
                tvcs = [button];
                break;
            }
        }
    }
    
    if (tvcs && tvcs.length > 0) {
        const isLight = gw.theme === 'light';
        const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
        tvcs[0].style.color = brightColor;
        
        // Force a style update
        tvcs[0].style.setProperty('color', brightColor, 'important');
    }
    
    // Force a complete re-render to make the cable visibility changes visible immediately
    if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        gw.selectedSite.orbitControls.update();
    }
    
    // Force a complete scene re-render
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
    }
    
    // Force Three.js to update all materials
    if (gw.selectedSite.scene) {
        gw.selectedSite.scene.traverse((object) => {
            if (object.material) {
                object.material.needsUpdate = true;
            }
        });
    }
    
    // Force another render after material updates
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        setTimeout(() => {
            gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
        }, 10);
    }
    
    // Additional forced re-render to ensure visibility changes are immediate
    if (gw.selectedSite.renderer && gw.selectedSite.scene && gw.selectedSite.camera) {
        requestAnimationFrame(() => {
            gw.selectedSite.renderer.render(gw.selectedSite.scene, gw.selectedSite.camera);
        });
    }
    
    // Trigger React component re-render
    if (gw.selectedSite.triggerCableVisibilityRerender) {
        gw.selectedSite.triggerCableVisibilityRerender();
    }
}

function toggleHistorySlider() {
    let historySlider = window.document.getElementsByClassName("dateSlider")[0];
    let tvcs = window.document.getElementsByClassName("cmi");
    const isLight = gw.theme === 'light';
    const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
    const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
    if (gw.selectedSite.isHistorySliderOn) {
        gw.selectedSite.isHistorySliderOn = false;
        if (tvcs && tvcs[0]) tvcs[0].style.color = dimmedColor;
        if (historySlider) historySlider.style.display = "none";
        let dateChange = window.document.getElementsByClassName("dateChange")[0];
        if (dateChange) {
            dateChange.style.display = "none";
        }
        window.dispatchEvent(new CustomEvent("historySliderToggled"));
    }
    else {
        gw.selectedSite.isHistorySliderOn = true;
        if (tvcs && tvcs[0]) tvcs[0].style.color = brightColor;
        if (historySlider) historySlider.style.display = "flex";
        let dateChange = window.document.getElementsByClassName("dateChange")[0];
        if (dateChange) {
            dateChange.style.display = "inherit";
        }
        let markLabels = window.document.getElementsByClassName("MuiSlider-markLabel");
        if (markLabels && markLabels.length > 0) {
            let selectedMark = null;
            Array.from(markLabels).map((markLabel, i) => {
                if (markLabel.style.display === "flex") {
                    selectedMark = markLabel;
                }
            });
            if (!selectedMark) {
                markLabels[markLabels.length - 1].style.display = "flex";
            }
        }
        window.dispatchEvent(new CustomEvent("historySliderToggled"));
    }
}
function toggleComparison() {
    let comparison = window.document.getElementsByClassName("comparison")[0];
    if (comparison.style.display === "none" || comparison.style.display === "") {
        comparison.style.display = "flex";
    }
    else {
        comparison.style.display = "none";
    }
}
function forEachBinTextCharacter(b, fn) {
    if (!b.children || b.children.length === 0) return;
    let container = b.children[0];
    let isWrapper = container.children && container.children[0] && container.children[0].children && container.children[0].children[0] && container.children[0].children[0].children && container.children[0].children[0].children[0] && container.children[0].children[0].children[0].material;
    let textElements = isWrapper ? container.children : [container];
    textElements.forEach(textEl => {
        if (textEl.children) textEl.children.forEach(g => {
            if (g.children && g.children[0] && g.children[0].material) fn(g);
        });
    });
}
function setAllBinsTextColor() {
    if (!gw || !gw.selectedSite) return;

    gw.selectedSite.binsRef.forEach(b => {
        if (b.children.length === 0) return;
        if (gw.theme === 'light') {
            forEachBinTextCharacter(b, g => {
                g.children[0].material.color.set("#878787");
                g.children[0].material.emissive.set("#878787");
                g.children[0].material.transparent = false;
                g.children[0].material.opacity = 1;
                g.children[0].material.needsUpdate = true;
            });
        } else {
            if (b.isSelected) {
                forEachBinTextCharacter(b, g => { g.children[0].material.color.set("#898989"); g.children[0].material.emissive.set("#898989"); });
            } else if (gw.selectedSite.isObjectSelected || gw.selectedSite.isCablesOff) {
                let temperatureList = gw.selectedSite.layoutSensors.filter(x => x.customerSiteLayoutBinId === b.binId && !isHumidityLayoutSensor(x)).map(s => s.sensorValueNumber);
                let maxTemp = getHighestTemperatureColor(temperatureList);
                forEachBinTextCharacter(b, g => { g.children[0].material.color.set(maxTemp); g.children[0].material.emissive.set(maxTemp); });
            } else {
                forEachBinTextCharacter(b, g => { g.children[0].material.color.set("#898989"); g.children[0].material.emissive.set("#898989"); });
            }
        }
    });
}
function setAllBinsTextColorOn() {
    if (!gw || !gw.selectedSite) return;

    gw.selectedSite.binsRef.forEach(b => {
        if (gw.theme === 'light') {
            forEachBinTextCharacter(b, g => {
                g.children[0].material.color.set("#878787");
                g.children[0].material.emissive.set("#878787");
                g.children[0].material.transparent = false;
                g.children[0].material.opacity = 1;
                g.children[0].material.needsUpdate = true;
            });
        } else {
            if (b.isSelected) {
                forEachBinTextCharacter(b, g => { g.children[0].material.color.set("#898989"); g.children[0].material.emissive.set("#898989"); });
            } else {
                let temperatureList = gw.selectedSite.layoutSensors.filter(x => x.customerSiteLayoutBinId === b.binId && !isHumidityLayoutSensor(x)).map(s => s.sensorValueNumber);
                let maxTemp = getHighestTemperatureColor(temperatureList);
                forEachBinTextCharacter(b, g => { g.children[0].material.color.set(maxTemp); g.children[0].material.emissive.set(maxTemp); });
            }
        }
    });
}
function setAllBinsTextColorOff() {
    if (!gw || !gw.selectedSite) return;

    const binTextColor = gw.theme === 'light' ? "#878787" : "#898989";
    gw.selectedSite.binsRef.forEach(b => {
        forEachBinTextCharacter(b, g => {
            g.children[0].material.color.set(binTextColor);
            g.children[0].material.emissive.set(binTextColor);
            g.children[0].material.transparent = false;
            g.children[0].material.opacity = 1;
            g.children[0].material.needsUpdate = true;
        });
    });
}
function getFitCameraToObjectVector3(camera, object, offset) {
    if (!camera || !object) return;

    if (!offset) offset = 0;

    //  Reuse vector
    let v = new THREE.Vector3();

    //  Create objects bounding box
    const box = new THREE.Box3().setFromObject(object);
    box.getSize(v);

    //  Store size and adjust for y or z is length or height
    const size = {
        width: v.x,
        height: camera.up.y === 1 ? v.y : v.z,
        length: camera.up.z === 1 ? v.y : v.z
    };

    box.getCenter(v);

    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(camera.up, 0);

    //  Top right point of canvas
    v.set(1, 1, 1);
    raycaster.setFromCamera(v, camera);

    //  Shoot to get top right point of canvas in world coordinates (intersectPlane returns null if ray misses plane)
    if (!raycaster.ray.intersectPlane(ground, v)) return null;

    //  Adjust for y or z up
    v.set(v.x, camera.up.y === 1 ? v.y : v.z, camera.up.z === 1 ? -v.y : -v.z);

    const positionUp = camera.up.y === 1 ? camera.position.y : camera.position.z;

    //  Calculate new camera height using scale 
    const cameraHeight = Math.max(size.width / v.x, size.length / v.z) * positionUp / 2 + size.height + offset;

    //  Adust camera position for y or z up
    v.set(camera.position.x, camera.up.y === 1 ? cameraHeight : camera.position.y, camera.up.z === 1 ? cameraHeight : camera.position.z);

    return v;
}
function calculateFOV(y, width, length){
    const b = y;
    //scaling mid base of fov triangle to aspect ratio
    const a = ((width / 2) / (width / length));
    //pythagore
    const c = Math.sqrt((a ** 2) + (b ** 2));
    //cosine of angle
    const cosa = a / c;
    //invert cosine to degrees
    const angle = Math.acos(cosa) * (180 / Math.PI);
    //angle left multiplied by 2
    let resAngle = (90 - angle) * 2
    return (resAngle);
}
function getSiteLayoutDimensions() {
    if (!gw.selectedSite) return;

    let maxX = 0;
    let minX = 0;
    let maxY = 0;
    let minY = 0;
    let maxZ = 0;
    let minZ = 0;

    if (gw.selectedSite.layoutGroups && gw.selectedSite.layoutGroups.length > 0 &&
        gw.selectedSite.layoutBins && gw.selectedSite.layoutBins.length > 0) {
        
        gw.selectedSite.layoutBins.forEach(layoutBin => {
            if (!layoutBin.isRound) return;

            let layoutGroup = gw.selectedSite.layoutGroups.filter(x => x.customerSiteLayoutGroupId === layoutBin.customerSiteLayoutGroupId)[0];

            let w = layoutBin.width / 2 + layoutGroup.x;
            let h = layoutBin.height / 2 + layoutGroup.y;
            let l = layoutBin.length / 2 + layoutGroup.z;

            let minWidthX = layoutBin.x - w;
            if (minWidthX < minX) {
                minX = minWidthX;
            }
            let maxWidthX = layoutBin.x + w;
            if (maxWidthX > maxX) {
                maxX = maxWidthX;
            }
            let minHeightY = layoutBin.y - h;
            if (minHeightY < minY) {
                minY = minHeightY;
            }
            let maxHeightY = layoutBin.y + h;
            if (maxHeightY > maxY) {
                maxY = maxHeightY;
            }
            let minLengthZ = layoutBin.z - l;
            if (minLengthZ < minZ) {
                minZ = minLengthZ;
            }
            let maxLengthZ = layoutBin.z + l;
            if (maxLengthZ > maxZ) {
                maxZ = maxLengthZ;
            }
        });


        return {
            width: maxX - minX,
            height: maxY - minY,
            length: maxZ - minZ
        };
    }
}
function getSiteSceneBox() {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0) return;
    let box = new THREE.Box3().setFromObject(gw.selectedSite.groupsRef[0].parent);
    return box;
}
function getSiteSceneDimensions() {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0) return;
    let objectsBoundingBox = new THREE.Vector3();
    let box = getSiteSceneBox();
    box.getSize(objectsBoundingBox);
    return objectsBoundingBox;
}
function centerSite() {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef) return;

    let binsList = new List(gw.selectedSite.groupsRef).selectMany(x => x.binsRef);
    let minX = binsList.min(x => x.position.x);
    let maxX = binsList.max(x => x.position.x);
    let minZ = binsList.min(x => x.position.z);
    let maxZ = binsList.max(x => x.position.z);
    let cp = new THREE.Vector3((maxX + minX) / 2.0, 0, (maxZ + minZ) / 2.0);

    let siteParent = gw.selectedSite.groupsRef[0].parent;
    let box = getSiteSceneBox();

    let shiftX = 0;
    if (box.max.x >= box.min.x) {

    }

    shiftX = box.min.x + (box.max.x - box.min.x) / 2.0;
    let shiftZ = box.min.z + (box.max.z - box.min.z) / 2.0;

    siteParent.position.x = -shiftX;
    siteParent.position.z = -shiftZ;

    gw.selectedSite.groupsRef.forEach(g => {
        //let v = new THREE.Vector3(g.position.x, g.position.y, g.position.z);
        //v.add(cp);
        //g.position.set(v);
    });
}
function positionSite(p) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef) return;

    //  Get all bin meshes
    let meshes = [];
    gw.selectedSite.groupsRef.forEach(g => {
        g.binsRef.forEach(b => {
            b.meshes.forEach(m => {
                if (m.binId) {
                    meshes.push(m);
                }
            });
        });
    });

    let g1 = gw.selectedSite.groupsRef[0];
    let g2 = gw.selectedSite.groupsRef[1];
    g1.lookAt(g2.position);
    g1.updateMatrixWorld();
    let direction = new THREE.Vector3().subVectors(g2.position, g1.position).normalize();
    let r = new THREE.Raycaster();
    r.set(g1.position, direction);
    //r.far = 50.0;
    let intersects = r.intersectObjects(meshes);
    g1.lookAt(g1.position);
    g1.updateMatrixWorld();

    return intersects;
}
function applyMargin(margin) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0)
        return;

    if (!gw.selectedSite.margin) {
        gw.selectedSite.margin = 0;
    }

    //  Apply margin to site
    margin -= gw.selectedSite.margin;

    if (margin === 0) {
        return;
    }

    let originalTotalMarginX = 0;
    let originalTotalMarginZ = 0;

    gw.selectedSite.groupsRef.forEach(g => {
        if (g.binsRef && g.binsRef.length > 0) {
            //  Sort Objects
            let binObjectX = new List(Object.entries(g.binsRef).sort((a, b) => b[1].position.x - a[1].position.x)).select(x => x[1]).toArray();
            let binObjectZ = new List(Object.entries(g.binsRef).sort((a, b) => b[1].position.z - a[1].position.z)).select(x => x[1]).toArray();

            //  Group bins by x and z
            let binObjectGroupX = [];
            let binObjectXList = [];
            let valueX = 0;
            for (var i = 0; i < binObjectX.length; i++) {
                let bino = binObjectX[i];
                if (bino.position.x === valueX) {
                    binObjectXList.push(bino);
                }
                else {
                    binObjectXList = [];
                    binObjectXList.push(bino);
                    valueX = bino.position.x;
                    binObjectGroupX.push(binObjectXList);
                }
            }

            let binObjectGroupZ = [];
            let binObjectZList = [];
            let valueZ = 0;
            for (var i = 0; i < binObjectZ.length; i++) {
                let bino = binObjectZ[i];
                if (bino.position.z === valueZ) {
                    binObjectZList.push(bino);
                }
                else {
                    binObjectZList = [];
                    binObjectZList.push(bino);
                    valueZ = bino.position.z;
                    binObjectGroupZ.push(binObjectZList);
                }
            }

            //  Set total margin x and z
            let totalMarginX = (binObjectGroupX.length - 1) * margin;
            let totalMarginZ = (binObjectGroupZ.length - 1) * margin;

            originalTotalMarginX = totalMarginX;
            originalTotalMarginZ = totalMarginZ;

            let ncenterX = totalMarginX / 2.0;
            let ncenterZ = totalMarginZ / 2.0;

            //  Apply margins x
            for (var gx = 0; gx < binObjectGroupX.length; gx++) {
                let groupX = binObjectGroupX[gx];
                for (var mx = 0; mx < groupX.length; mx++) {
                    let objectBin = groupX[mx];
                    if (objectBin) {
                        objectBin.position.x += totalMarginX - ncenterX;
                    }
                }
                totalMarginX -= margin;
            }

            //  Apply margins z
            for (var gz = 0; gz < binObjectGroupZ.length; gz++) {
                let groupZ = binObjectGroupZ[gz];
                for (var mz = 0; mz < groupZ.length; mz++) {
                    let objectBin = groupZ[mz];
                    if (objectBin) {
                        objectBin.position.z += totalMarginZ - ncenterZ;
                    }
                }
                totalMarginZ -= margin;
            }
        }

        gw.selectedSite.margin = margin;
    });

    //  Sort Groups
    let groupObjectX = new List(Object.entries(gw.selectedSite.groupsRef).sort((a, b) => b[1].position.x - a[1].position.x)).select(x => x[1]).toArray();
    let groupObjectZ = new List(Object.entries(gw.selectedSite.groupsRef).sort((a, b) => b[1].position.z - a[1].position.z)).select(x => x[1]).toArray();

    //  Group groups by x and z
    let groupObjectGroupX = [];
    let groupObjectXList = [];
    let valueX = 0;
    for (var i = 0; i < groupObjectX.length; i++) {
        let groupo = groupObjectX[i];
        if (groupo.position.x === valueX) {
            groupObjectXList.push(groupo);
        }
        else {
            groupObjectXList = [];
            groupObjectXList.push(groupo);
            valueX = groupo.position.x;
            groupObjectGroupX.push(groupObjectXList);
        }
    }

    let groupObjectGroupZ = [];
    let groupObjectZList = [];
    let valueZ = 0;
    for (var i = 0; i < groupObjectZ.length; i++) {
        let groupo = groupObjectZ[i];
        if (groupo.position.z === valueZ) {
            groupObjectZList.push(groupo);
        }
        else {
            groupObjectZList = [];
            groupObjectZList.push(groupo);
            valueZ = groupo.position.z;
            groupObjectGroupZ.push(groupObjectZList);
        }
    }

    //  Set total margin x and z
    let gtotalMarginX = (groupObjectGroupX.length - 1) * originalTotalMarginX;
    let gtotalMarginZ = (groupObjectGroupZ.length - 1) * originalTotalMarginZ;

    let gncenterX = gtotalMarginX / 2.0;
    let gncenterZ = gtotalMarginZ / 2.0;

    //  Apply margins x
    for (var gx = 0; gx < groupObjectGroupX.length; gx++) {
        let groupX = groupObjectGroupX[gx];
        for (var mx = 0; mx < groupX.length; mx++) {
            let objectBin = groupX[mx];
            if (objectBin) {
                objectBin.position.x += gtotalMarginX - gncenterX;
            }
        }
        gtotalMarginX -= originalTotalMarginX;
    }

    //  Apply margins z
    for (var gz = 0; gz < groupObjectGroupZ.length; gz++) {
        let groupZ = groupObjectGroupZ[gz];
        for (var mz = 0; mz < groupZ.length; mz++) {
            let objectBin = groupZ[mz];
            if (objectBin) {
                objectBin.position.z += gtotalMarginZ - gncenterZ;
            }
        }
        gtotalMarginZ -= originalTotalMarginZ;
    }
}
function getScaledPoints(points, scaleX, scaleY, scaleZ) {
    if (!points || points.length === 0)
        return;

    let results = [];
    let result = {x: 0, y: 0, z: 0};

    let isScaleX = scaleX && scaleX !== 0;
    let isScaleY = scaleY && scaleY !== 0;
    let isScaleZ = scaleZ && scaleZ !== 0;

    let Xpoints = [];
    let Ypoints = [];
    let Zpoints = [];

    //  Work X
    if (isScaleX) {
        scaleX = Math.abs(scaleX);
        Xpoints = points.map(x => { return x.x; });
        if (Xpoints && Xpoints.length > 0) {
            let minX = Math.min.apply(Math, Xpoints);
            let maxX = Math.max.apply(Math, Xpoints);
            let newMinX = minX * scaleX;
            let newMaxX = maxX * scaleX;
            if (minX !== maxX) {
                Xpoints.forEach((x, i) => {
                    Xpoints[i] = scaleFormula(x, minX, maxX, newMinX, newMaxX);
                });
            }
        }
    }

    //  Work Y
    if (isScaleY) {
        scaleY = Math.abs(scaleY);
        Ypoints = points.map(y => { return y.y; });
        if (Ypoints && Ypoints.length > 0) {
            let minY = Math.min.apply(Math, Ypoints);
            let maxY = Math.max.apply(Math, Ypoints);
            let newMinY = minY * scaleY;
            let newMaxY = maxY * scaleY;
            if (minY !== maxY) {
                Ypoints.forEach((y, i) => {
                    Ypoints[i] = scaleFormula(y, minY, maxY, newMinY, newMaxY);
                });
            }
        }
    }
    
    //  Work Y
    if (isScaleZ) {
        scaleZ = Math.abs(scaleZ);
        Zpoints = points.map(z => { return z.z; });
        if (Zpoints && Zpoints.length > 0) {
            let minZ = Math.min.apply(Math, Zpoints);
            let maxZ = Math.max.apply(Math, Zpoints);
            let newMinZ = minZ * scaleZ;
            let newMaxZ = maxZ * scaleZ;
            if (minZ !== maxZ) {
                Zpoints.forEach((z, i) => {
                    Zpoints[i] = scaleFormula(z, minZ, maxZ, newMinZ, newMaxZ);
                });
            }
        }
    }

    //  Build results
    points.forEach((p, i) => {
        if (!p.x && !p.y && !p.z) {
            if (isScaleX)
                return Xpoints;
            if (isScaleY)
                return Ypoints;
            if (isScaleZ)
                return Zpoints;
        }

        result = {};

        if (Xpoints.length > 0) 
            result.x = Xpoints[i];
        else
            result.x = 0;

        if (Ypoints.length > 0) 
            result.y = Ypoints[i];
        else
            result.y = 0;

        if (Zpoints.length > 0)
            result.z = Zpoints[i];
        else
            result.z = 0;

        results.push(result);
    });

    return results;
}
function getCenterOfPoints(points) {
    if (!points || points.length === 0)
        return;

    let xpoints = points.map(x => { return x.x });
    let ypoints = points.map(y => { return y.y });
    let xmin = Math.min.apply(Math, xpoints);
    let xmax = Math.max.apply(Math, xpoints);
    let ymin = Math.min.apply(Math, ypoints);
    let ymax = Math.max.apply(Math, ypoints);
    return {
        x: (xmax - xmin) / 2.0 + xmin,
        y: (ymax - ymin) / 2.0 + ymin
    }
}
function highlightStice(binId, options) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0)
        return;

    clearSensorFlashAnimations(false);

    let binRef = findBinRefForHighlight(binId);
    if (!binRef) return;
    ensureBinRefMeshes(binRef);

    let isSiteView = false;
    const skipCamera = options?.skipCamera === true;

    if (!skipCamera) {
        if (binRef.isSelected) {
            // In Bin3D (single-bin view) never zoom out to site view - there's no site view
            if (!gw.selectedSite.isBinWindowView) {
                siteView();
                isSiteView = true;
            }
        }
        else {
            topView(binRef.binId);
        }
    }
    binRef.meshes.forEach(y => {
        highlight(y, !binRef.isSelected);
    });

    // Make cables in the selected bin visible (only when selecting, not deselecting)
    if (!binRef.isSelected) {
    gw.selectedSite.cablesRef.filter(c => cableRefBelongsToBinRef(c, binRef)).forEach(g => {
        if (g.visible !== undefined) {
            g.visible = true;
        }
        // Get sensor IDs for this cable to check for error values
        const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === g.cableId) || [];
        const sensorErrorValues = new Set();
        cableSensors.forEach(sensor => {
            const tempStr = sensor.sensorValueString?.toString().trim() || "";
            if (isFaintSensorLabel(tempStr)) {
                sensorErrorValues.add(sensor.sensorId);
            }
        });
        
        // Also check mesh names as fallback for error pattern detection
        let hasErrorPattern = false;
        let dashCount = 0;
        let eCount = 0;
        let oCount = 0;
        
        g.meshes.forEach(mesh => {
            if (mesh.name === "AnDash") dashCount++;
            if (mesh.name === "AnE") eCount++;
            if (mesh.name === "AnO") oCount++;
        });
        
        // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
        if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
            hasErrorPattern = true;
        }
        
        g.meshes.forEach(m => {
            if (m.name === "AnSpace") m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
            if (m.material) {
                // Skip level indicator boxes - they should always be hidden
                // Don't touch their opacity since they start at 0 opacity
                if (m.isLevel) {
                    m.material.visible = false;
                    return;
                }
                
                // Check if this mesh belongs to a sensor with an error value
                // sensorId can be on mesh directly or in meshProps
                const meshSensorId = m.sensorId || m.meshProps?.sensorId;
                const isErrorSensor = meshSensorId && sensorErrorValues.has(meshSensorId);
                
                // Check if it's a sensor mesh (has sensorId or isSensor flag)
                const isSensorMesh = meshSensorId || m.meshProps?.isSensor;
                
                // AnSpace in cable name/grade - keep hidden (should be edge-on/invisible)
                if (m.name === "AnSpace" && !isSensorMesh) {
                    m.material.visible = false;
                    m.visible = false;
                    return;
                }
                
                m.material.visible = true;
                
                // Apply opacity based on error pattern detection
                // Only affect temperature text (isSensor), not cable names
                if (isSensorMesh && m.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (m.name === "AnDash" || m.name === "AnE" || m.name === "AnO")))) {
                    // In light mode, error values should be visible with same color as bin name labels
                    if (gw.theme === 'light') {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                    } else {
                        // Invalid values should have very low opacity when not in light mode
                        m.material.opacity = 0.0025;
                        m.material.transparent = true;
                    }
                } else if (m.name === "AnSpace" && isSensorMesh) {
                    // Space characters always have low opacity (only for temperature text)
                    m.material.opacity = 0.0025;
                    m.material.transparent = true;
                } else if (isSensorMesh) {
                    // Other sensor temperature characters (non-error) get full opacity
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                } else if (!m.isText) {
                    // Non-text meshes (cable geometry) get full opacity
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                }
                // Note: Cable name text meshes (isText && !isSensor) are not modified here
                
                m.material.needsUpdate = true;
            }
        });
    });
    }

    let wasBinSelected = binRef.isSelected;
    binRef.isSelected = !binRef.isSelected;

    gw.selectedSite.groupsRef.forEach(g => {
        if (g.binsRef && g.binsRef.length > 0) {
            g.binsRef.forEach(x => {
                if (x.binId !== binRef.binId) {
                    ensureBinRefMeshes(x).forEach(y => {
                        highlight(y, false);
                    });
                    x.isSelected = false;
                }
            });
        }
    });

    // Hide cables in all other bins when a bin is selected (not when returning to site view)
    if (binRef.isSelected) {
        gw.selectedSite.cablesRef.filter(c => !cableRefBelongsToBinRef(c, binRef)).forEach(cable => {
            if (cable.visible !== undefined) {
                cable.visible = false;
            }
            cable.meshes.forEach(m => {
                if (m.name === "AnSpace") m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                if (m.material) {
                    if (m.isLevel) {
                        m.material.visible = false;
                    } else {
                        m.material.visible = false;
                    }
                    m.material.needsUpdate = true;
                }
            });
        });
    }

    const hadObjectSelected = gw.selectedSite.isObjectSelected;

    if (hadObjectSelected) {
        if (wasBinSelected) {
            //  Bin to Site - show all cables in site view
            setAllCablesOn();
            gw.selectedSite.isSiteToBin = false;
            gw.selectedSite.isBinToBin = false;
        }
        else {
            //  Bin to Bin
            setUnselectedCablesOff();
            gw.selectedSite.isBinToBin = true;
            gw.selectedSite.isSiteToBin = false;
        }
    }
    else {
        //  Site to Bin
        gw.selectedSite.lastIsCablesOff = gw.selectedSite.isCablesOff;
        setUnselectedCablesOff();
    }

    setIsObjectSelected();

    // Update bin text colors after selection changes
    setAllBinsTextColor();
    
    syncAllSensorGroupVisibility();
    
    // Update the dropdown if the function exists
    if (gw.selectedSite.updateBinDropdown) {
        gw.selectedSite.updateBinDropdown();
    }
    set3dCameraModeOn();
}
function setIsObjectSelected() {
    gw.selectedSite.isObjectSelected = false;
    gw.selectedSite.groupsRef.forEach(g => {
        if (g.binsRef && g.binsRef.length > 0) {
            g.binsRef.forEach(x => {
                if (x.binId && x.isSelected) {
                    gw.selectedSite.isObjectSelected = true;
                }
            });
        }
    });
}

function focusOnCable(cableId) {
    // Find the cable in cablesRef
    const selectedCable = gw.selectedSite.cablesRef.find(cable => 
        cable.cableId === parseInt(cableId)
    );
    
    if (!selectedCable) {
        return;
    }
    
    // Find the layout cable to get the bin information
    const layoutCable = gw.selectedSite.layoutCables.find(layoutCable => 
        layoutCable.cableId === parseInt(cableId)
    );
    
    if (!layoutCable) {
        return;
    }
    
    // Set cable focus state
    gw.selectedSite.isCableFocused = true;
    gw.selectedSite.focusedCableId = cableId;
    if (gw.selectedSite.triggerGradesRerender) gw.selectedSite.triggerGradesRerender();

    // 1. SELECT AND HIGHLIGHT THE BIN if it's not already selected
    let binRef = gw.selectedSite.binsRef.find(bin => layoutCableBelongsToBinRef(layoutCable, bin));
    
    if (binRef) {
        if (!binRef.isSelected) {
        gw.selectedSite.highlightFromCableSelection = true;
        highlightStice(binRef.binId);
        }
        // Keep bin selector in sync so it doesn't revert on re-render
        gw.selectedSite.selectedBinId =
            catalogBinDataIdForBinRef(gw.selectedSite, binRef) ??
            binRef.binDataId ??
            binRef.binId;
        scaleBinText();
    }
    
    // Focus camera on the cable
    if (!gw.selectedSite.orbitControls) {
        return;
    }
    
    const controls = gw.selectedSite.orbitControls;

    // When coming from site view, ensure scene matrices are updated so bounding box is correct
    if (selectedCable.parent) {
        selectedCable.parent.updateMatrixWorld(true);
    }

    // Calculate the bounding box of the cable - use the cable group to include all descendants
    const box = new THREE.Box3();
    if (selectedCable.visible !== false) {
        box.expandByObject(selectedCable);
    }
    if (box.isEmpty()) {
        selectedCable.meshes.forEach(mesh => {
            if (mesh.visible) {
                box.expandByObject(mesh);
            }
        });
        if (selectedCable.children && selectedCable.children.length > 0) {
            selectedCable.children.forEach(child => {
                if (child.visible !== false) {
                    box.expandByObject(child);
                }
            });
        }
    }

    if (box.isEmpty()) {
        return;
    }

    // Get the center and size of the cable
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Calculate appropriate camera distance based on cable size
    const maxDimension = Math.max(size.x, size.y, size.z);
    const cameraDistance = maxDimension * 1.5;

    // Position camera at an angle to the cable
    const angle = Math.PI / 4; // 45 degrees
    const height = cameraDistance * Math.sin(angle);
    const horizontalDistance = cameraDistance * Math.cos(angle);

    // Calculate camera position relative to cable center
    const cameraPosition = new THREE.Vector3(
        center.x + horizontalDistance,
        center.y + height,
        center.z + horizontalDistance
    );
    
    // Animate camera to new position
    controls.object.position.copy(cameraPosition);
    controls.updateCamera(center);
    
    // Save the camera position to prevent it from being reset
    gw.selectedSite.binCameraDistance = controls.object.position.distanceTo(center);

    if (gw.scaleBinText) {
        gw.scaleBinText();
    }

    const focusTargetBinRef = binRef;

    // Hide other cables in the same bin, show only the selected cable with proper opacity
    gw.selectedSite.cablesRef.forEach(cable => {
        // Find the layout cable for this cable to get its bin ID
        const cableLayoutCable = gw.selectedSite.layoutCables.find(lc => 
            lc.cableId === cable.cableId
        );
        
        if (cableLayoutCable && focusTargetBinRef && layoutCableBelongsToBinRef(cableLayoutCable, focusTargetBinRef)) {
            // This cable is in the same bin
            if (cable === selectedCable) {
                // Ensure the cable Group is visible
                if (cable.visible !== undefined) {
                    cable.visible = true;
                }
                
                // Check if this cable has "-E-" or "-O-" pattern by looking at mesh names
                // The pattern is: AnDash, AnE, AnDash (which represents "-E-") or AnDash, AnO, AnDash (which represents "-O-")
                let hasErrorPattern = false;
                let dashCount = 0;
                let eCount = 0;
                let oCount = 0;
                
                cable.meshes.forEach(mesh => {
                    if (mesh.name === "AnDash") dashCount++;
                    if (mesh.name === "AnE") eCount++;
                    if (mesh.name === "AnO") oCount++;
                });
                
                // If we have multiple dashes and E's or O's, it's likely an error pattern
                if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                    hasErrorPattern = true;
                }

                const sensorErrorValues = new Set();
                (gw.selectedSite.layoutSensors || []).forEach(sensor => {
                    if (sensor.cableId === cable.cableId && isFaintSensorLabel(sensor.sensorValueString)) {
                        sensorErrorValues.add(sensor.sensorId);
                    }
                });
                
                cable.meshes.forEach(mesh => {
                    if (mesh.material) {
                        // Skip level indicator boxes - they should always be hidden
                        // Don't touch their opacity since they start at 0 opacity
                        if (mesh.isLevel) {
                            mesh.material.visible = false;
                            return;
                        }
                        
                        // Make sure the mesh is visible first
                        mesh.material.visible = true;
                        
                        // Apply opacity based on error pattern detection
                        // Only affect temperature text (isSensor), not cable names
                        const focusSensorId = mesh.sensorId || mesh.meshProps?.sensorId;
                        const isErrorSensor = focusSensorId && sensorErrorValues.has(focusSensorId);
                        const isSensorMesh = focusSensorId || mesh.meshProps?.isSensor;
                        if (isSensorMesh && mesh.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (mesh.name === "AnDash" || mesh.name === "AnE" || mesh.name === "AnO")))) {
                            // In light mode, error values should be visible with same color as bin name labels
                            if (gw.theme === 'light') {
                                mesh.material.opacity = 1.0;
                                mesh.material.transparent = false;
                                mesh.material.color.set("#b4b4b4");
                                if (mesh.material.emissive) {
                                    mesh.material.emissive.set("#b4b4b4");
                                }
                            } else {
                                // Invalid values should have very low opacity when not in light mode
                                mesh.material.opacity = 0.0025;
                                mesh.material.transparent = true;
                            }
                        } else if (mesh.name === "AnSpace" && mesh.meshProps?.isSensor) {
                            // Space characters always have low opacity (only for temperature text)
                            mesh.material.opacity = 0.0025;
                            mesh.material.transparent = true;
                        } else if (!mesh.isText) {
                            // Regular cable parts get full opacity
                            mesh.material.opacity = 1.0;
                            mesh.material.transparent = false;
                        }
                        
                        mesh.material.needsUpdate = true;
                    }
                });
                
                // Also process cable children (geometry meshes)
                if (cable.children && cable.children.length > 0) {
                    cable.children.forEach((child, childIndex) => {
                        if (child.material) {
                            // Skip level indicator boxes - they should always be hidden
                            if (child.isLevel) {
                                child.material.visible = false;
                                return;
                            }
                            // Make sure the child is visible and has full opacity
                            child.material.visible = true;
                            child.material.opacity = 1.0;
                            child.material.transparent = false;
                            child.material.needsUpdate = true;
                        }
                    });
                }
            } else {
                // Hide other cables in the same bin
                if (cable.visible !== undefined) {
                    cable.visible = false;
                }
                
                cable.meshes.forEach(mesh => {
                    if (mesh.material && !mesh.isText) {
                        mesh.material.opacity = 0.0; // Make completely transparent
                        mesh.material.transparent = true; // Enable transparency
                        mesh.material.needsUpdate = true;
                    }
                });
            }
        } else {
            // Hide cables from other bins
            if (cable.visible !== undefined) {
                cable.visible = false;
            }
            cable.meshes.forEach(mesh => {
                if (mesh.material && !mesh.isText) {
                    mesh.material.opacity = 0.0;
                    mesh.material.transparent = true;
                    mesh.material.needsUpdate = true;
                }
            });
        }
    });
    
    // // Update button state - when a cable is focused, the cable is on (bright state)
    // let tvcs = window.document.getElementsByClassName("cbi");
    // if (tvcs && tvcs.length > 0) {
    //     const isLight = gw.theme === 'light';
    //     const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
    //     tvcs[0].style.color = brightColor;
    //     tvcs[0].style.setProperty('color', brightColor, 'important');
    // }
    
    // Force a re-render to make the cable visibility changes visible immediately
    if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        // Trigger a re-render by updating the camera
        gw.selectedSite.orbitControls.update();
    }
    set3dCameraModeOn();
}
function highlight(mesh, isHighlight) {
    if (!gw.selectedSite || !mesh.isWallMesh)
        return;

    if (isHighlight) {
        mesh.material.opacity = 1.0;
        mesh.material.emissive.setHex(gw.selectedSite.selectedColorHex);
        mesh.material.envMapIntensity = 10;
        mesh.material.toneMapped = false;
    }
    else {
        // Check if this mesh belongs to a bin that should not be unhighlighted
        let shouldPreventUnhighlight = false;
        if (gw.selectedSite.binsRef) {
            for (let bin of gw.selectedSite.binsRef) {
                if (bin.meshes && bin.meshes.includes(mesh) && bin.preventUnhighlight) {
                    shouldPreventUnhighlight = true;
                    break;
                }
            }
        }
        
        // If we should prevent unhighlighting, don't change the material
        if (shouldPreventUnhighlight) {
            return;
        }
        
        if (gw.selectedSite.isObjectSelected) {
            if (mesh.originalMaterial) {
                mesh.material = mesh.originalMaterial.clone();
            }
            else {
                mesh.material.opacity = 0.08;
                mesh.material.emissive.setHex(gw.selectedSite.defaultEmissiveHex);
                mesh.material.envMapIntensity = 1;
                mesh.material.toneMapped = true;
            }
        }
        else {
            mesh.material.opacity = 0.01;
            mesh.material.emissive.setHex(gw.selectedSite.defaultEmissiveHex);
            mesh.material.envMapIntensity = 1;
            mesh.material.toneMapped = true;
        }
    }
};
function lowlight(mesh, isHighlight) {
    if (isHighlight) {
        // Use the initial material state to determine if this should be visible
        // This ensures materials like AnSpace that were originally invisible stay invisible
        if (gw.selectedSite && gw.selectedSite.initialCableStates) {
            // Try to find the initial state for this mesh
            let foundInitialState = false;
            for (let [key, state] of gw.selectedSite.initialCableStates) {
                if (key.includes(mesh.uuid)) {
                    // Use the original visibility state
                    mesh.material.visible = state.visible;
                    foundInitialState = true;
                    break;
                }
            }
            
            // If no initial state found, preserve current visibility
            if (!foundInitialState) {
                // Don't change visibility - preserve current state
                // This prevents overriding materials that should remain invisible
            }
        } else {
            // Fallback: don't force visibility if it was originally false
            // This prevents overriding materials like AnSpace
            if (mesh.material.visible === false) {
                mesh.material.visible = false;
            } else {
        mesh.material.visible = true;
            }
        }
        //mesh.material.opacity = 0.25;
    }
    else {
        mesh.material.visible = false;
    }
}
function generateGrainwaveId() {
    return ++gidc;
}
function generateBinId(siteIndex, binIndex, binId) {
    return `${siteIndex}_${binIndex}_${binId}`;
}
function setPosition(binCount, scale, index) {
    let increment = 45;
    let angle = (binCount - index) * increment;
    let radius = Math.ceil(index / (360.0 / increment));
    let x = Math.round(Math.sin(angle * Math.PI / 180.0)) * radius;
    let z = Math.round(Math.cos(angle * Math.PI / 180.0)) * radius;
    x *= scale * 27.0;
    z *= scale * 27.0;

    ////let radius = Math.ceil(index / 9.0) * 50;
    //let angle = (index + 1) * (360.0 / binCount);
    //let radius = (angle % binCount) == 0 ? index * r : r;
    //let x = Math.round(Math.sin(angle * Math.PI / 180.0)) * objec3D.radius;
    //let z = Math.round(Math.cos(angle * Math.PI / 180.0)) * objec3D.radius;

    return {
        x: x,
        y: 0,
        z: z
    };
}
function setPosition2(binCount, scale, index) {
    let increment = 45;
    let angle = (binCount - index) * increment;
    let radius = Math.ceil(index / (360.0 / increment));
    let x = Math.round(Math.sin(angle * Math.PI / 180.0)) * radius;
    let z = Math.round(Math.cos(angle * Math.PI / 180.0)) * radius;
    x *= scale * 27.0;
    z *= scale * 27.0;

    ////let radius = Math.ceil(index / 9.0) * 50;
    //let angle = (index + 1) * (360.0 / binCount);
    //let radius = (angle % binCount) == 0 ? index * r : r;
    //let x = Math.round(Math.sin(angle * Math.PI / 180.0)) * objec3D.radius;
    //let z = Math.round(Math.cos(angle * Math.PI / 180.0)) * objec3D.radius;

    return {
        x: x,
        y: 0,
        z: z
    };
}
function scaleFormula(valueToScale, min1, max1, min2, max2, mod) {
    let scaled = (Math.abs(valueToScale - min1) / Math.abs(max1 - min1) * Math.abs(max2 - min2) + min2);
    if (mod && mod > 0) {
        scaled = scaled % mod;
    }
    return scaled;
}
function getScaledAngle(positionFrom, positionTo) {
    return scaleFormula(getActualAngleInRadians(positionFrom, positionTo), Math.PI, -Math.PI, 270, 630, 360);
}
function arcAngleRadians(p0, p1, c) {
    var p0c = Math.sqrt(Math.pow(c.x - p0.x, 2) +
        Math.pow(c.y - p0.y, 2)); // p0->c (b)   
    var p1c = Math.sqrt(Math.pow(c.x - p1.x, 2) +
        Math.pow(c.y - p1.y, 2)); // p1->c (a)
    var p0p1 = Math.sqrt(Math.pow(p1.x - p0.x, 2) +
        Math.pow(p1.y - p0.y, 2)); // p0->p1 (c)
    return Math.acos((p1c * p1c + p0c * p0c - p0p1 * p0p1) / (2 * p1c * p0c));
}
function pointToPointLength(p0, p1) {
    return Math.sqrt(Math.pow(p1.x - p0.x, 2) + Math.pow(p1.y - p0.y, 2));
}
function getPointToPointLength(x1, y1, x2, y2) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}
function getArcStartAndEndRadians(x1, y1, x2, y2, cx, cy) {
    return {
        startRadians: Math.atan2(y1 - cy, x1 - cx),
        endRadians: Math.atan2(y2 - cy, x2 - cx)
    };
}
function getThetaStart(x1, y1, x2, y2, cx, cy, isInner) {
    //  Calculating point to point angle starts west at 0 going counter clockwise with limit of 180
    //  N = -90, E = 180, S = 90, W = 0
    //  Calculating object angle starts east at 0 going clockwise
    //  N = -90, E = 0, S = 90, W = 180
    let oa = getActualAngleInRadians({ x: cx, z: cy }, { x: x1, z: y1 });
    let ob = getActualAngleInRadians({ x: cx, z: cy }, { x: x2, z: y2 });

    if (Math.abs(Math.abs(oa) - Math.abs(ob)) >= Math.PI / 2.0) {
        //  Start from ob
        if (isInner) {
            return ob + Math.PI;
        }
        else {
            return ob - Math.PI / 2.0;
        }
    }
    else {
        //  Start from oa
        if (isInner) {
            return oa - Math.PI / 2.0;
        }
        else {
            if (oa >= 0) {
                return Math.PI - oa + Math.PI / 2.0;
            }
            else {
                return Math.PI - oa + Math.PI / 2.0;
            }
        }
    }
}
function getThetaRadians(x1, y1, x2, y2, cx, cy, isInner) {
    //  Calculating point to point angle starts west at 0 going counter clockwise with limit of 180
    //  N = -90, E = 180, S = 90, W = 0
    //  Calculating object angle starts east at 0 going clockwise
    //  N = -90, E = 0, S = 90, W = 180
    let oa = getActualAngleInRadians({ x: cx, z: cy }, { x: x1, z: y1 });
    let ob = getActualAngleInRadians({ x: cx, z: cy }, { x: x2, z: y2 });

    if (Math.abs(Math.abs(oa) - Math.abs(ob)) >= Math.PI / 2.0) {
        //  Start from ob
        if (isInner) {
            return {
                start: ob + Math.PI,
                end: oa + Math.PI,
            }
        }
        else {
            return {
                start: ob - Math.PI / 2.0,
                end: oa - Math.PI / 2.0,
            }
        }
    }
    else {
        //  Start from oa
        if (isInner) {
            return {
                start: oa - Math.PI / 2.0,
                end: ob - Math.PI / 2.0,
            }
        }
        else {
            if (oa >= 0) {
                return {
                    start: Math.PI - oa + Math.PI / 2.0,
                    end: Math.PI - ob + Math.PI / 2.0,
                }
            }
            else {
                return {
                    start: Math.PI - oa + Math.PI / 2.0,
                    end: Math.PI - ob + Math.PI / 2.0,
                }
            }
        }
    }
}
function getThetaAngles(x1, y1, x2, y2, cx, cy, r, isInner) {
    //  Calculating point to point angle starts west at 0 going counter clockwise with limit of 180
    //  N = -90, E = 180, S = 90, W = 0
    //  Calculating object angle starts east at 0 going clockwise
    //  N = -90, E = 0, S = 90, W = 180
    let oa = getActualAngleInRadians({ x: cx, z: cy }, { x: x1, z: y1 });
    let ob = getActualAngleInRadians({ x: cx, z: cy }, { x: x2, z: y2 });

    let oaa = oa * 180.0 / Math.PI;
    let obb = ob * 180.0 / Math.PI;


    if (Math.abs(Math.abs(oa) - Math.abs(ob)) >= Math.PI / 2.0) {
        //  Start from ob
        if (isInner) {
            let arca = {
                start: (ob + Math.PI) * 180.0 / Math.PI,
                end: (oa + Math.PI) * 180.0 / Math.PI,
            }

            arca.start = arca.start < 0 ? 360.0 + arca.start : arca.start;
            arca.end = arca.end < 0 ? 360.0 + arca.end : arca.end;

            let arcm = (arca.start + arca.end < arca.start ? arca.end + 360.0 : arca.end) / 2.0 - 45.0;
            arca.midPointX = cx + r * Math.cos(arcm);
            arca.midPointY = cy + r * Math.sin(arcm);
            return arca;
        }
        else {
            let arca = {
                start: (ob - Math.PI / 2.0) * 180.0 / Math.PI,
                end: (oa - Math.PI / 2.0) * 180.0 / Math.PI,
            }
            let arcm = (arca.start + arca.end) / 2.0;
            arca.midPointX = x1 - r * Math.cos(arcm);
            arca.midPointY = r * Math.sin(arcm) + y1;
            return arca;
        }
    }
    else {
        //  Start from oa
        if (isInner) {
            let arca = {
                start: (oa - Math.PI / 2.0) * 180.0 / Math.PI,
                end: (ob - Math.PI / 2.0) * 180.0 / Math.PI,
            }

            //arca.start = arca.start < 0 ? 360.0 + arca.start : arca.start;
            //arca.end = arca.end < 0 ? 360.0 + arca.end : arca.end;

            let arcm = (arca.start + arca.end < arca.start ? arca.end + 360.0 : arca.end) / 2.0 - 45.0;
            arca.midPointX = cx + r * Math.cos(arcm);
            arca.midPointY = cy + r * Math.sin(arcm);
            return arca;
        }
        else {
            if (oa >= 0) {
                let arca = {
                    start: (Math.PI - oa + Math.PI / 2.0) * 180.0 / Math.PI,
                    end: (Math.PI - ob + Math.PI / 2.0) * 180.0 / Math.PI,
                }
                let arcm = (arca.start + arca.end) / 2.0;
                arca.midPointX = r * Math.cos(arcm) + x2;
                arca.midPointY = r * Math.sin(arcm) + y2;
                return arca;
            }
            else {
                let arca = {
                    start: (Math.PI - oa + Math.PI / 2.0) * 180.0 / Math.PI,
                    end: (Math.PI - ob + Math.PI / 2.0) * 180.0 / Math.PI,
                }
                let arcm = (arca.start + arca.end) / 2.0;
                arca.midPointX = r * Math.cos(arcm) + x2;
                arca.midPointY = r * Math.sin(arcm) + y2;
                return arca;
            }
        }
    }
}
function getPlaneRotation(p1, p2) {
    let angle = getActualAngleInRadians(p1, p2);
    return [0, angle, 0];
}
function getCircleCenterPoint(p1, p2, r, lessX, lessY) {
    let r2 = Math.pow(r, 2);
    let q = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

    let x3 = (p1.x + p2.x) / 2.0;
    let x4 = x3 + Math.sqrt(r2 - ((q / 2.0) * (q / 2.0))) * ((p1.y - p2.y) / q);
    if (lessX && x4 > lessX) {
        x4 = x3 + Math.sqrt(r2 - ((q / 2.0) * (q / 2.0))) * ((p2.y - p1.y) / q);
    }

    let y3 = (p1.y + p2.y) / 2.0;
    let y4 = y3 + Math.sqrt(r2 - ((q / 2.0) * (q / 2.0))) * ((p2.x - p1.x) / q);
    if (lessY && y4 > lessY) {
        y4 = y3 + Math.sqrt(r2 - ((q / 2.0) * (q / 2.0))) * ((p1.x - p2.x) / q);
    }

    return { x: x4, y: y4 };
}
function getAngleRadiansBetweenPoints(x1, y1, x2, y2) {
    return Math.atan2(y2 - y1, x2 - x1);
}
function getArcPoint(cx, cy, radius, angleRadians) {
    return {
        x: +(cx + Math.cos(angleRadians.toFixed(4)) * radius).toFixed(2),
        y: +(cy + Math.sin(angleRadians.toFixed(4)) * radius).toFixed(2),
    }
}
function getArcPoints(cx, cy, radius, angleRadiansStart, angleRadiansStop) {
    return {
        start: getArcPoint(cx, cy, radius, angleRadiansStart),
        stop: getArcPoint(cx, cy, radius, angleRadiansStop)
    }
}
function getAngleQuadrant(angleRadians) {
    let hpi = Math.PI / 2;

    if (angleRadians >= 0 && angleRadians <= hpi) {
        return { x: 1, y: 1 }
    }

    if (angleRadians >= hpi && angleRadians <= Math.PI) {
        return { x: -1, y: 1 }
    }

    if (angleRadians >= -Math.PI && angleRadians <= -hpi) {
        return { x: -1, y: -1 }
    }

    if (angleRadians >= -hpi && angleRadians <= 0) {
        return { x: 1, y: -1 }
    }
}
function getTotalAngle(angleRadians1, angleRadians2) {
    let result = Math.abs(angleRadians1 - angleRadians2);
    if (result >= Math.PI) {
        result = Math.PI * 2 - result;
    }
    return result;
}
function getMiddleAngle(angleRadians1, angleRadians2, isInnerCurve) {
    let result = getTotalAngle(angleRadians1, angleRadians2) / 2;
    if (isInnerCurve) {
        result = angleRadians1 < 0 ? angleRadians1 + result : angleRadians2 - result;
    }
    else {
        result = angleRadians1 < 0 ? angleRadians1 - result : angleRadians2 + result;
    }
    return result;
}
function getMiddleArcPoint(x1, y1, x2, y2, cx, cy, radius, isInnerCurve) {
    let startAngle = getAngleRadiansBetweenPoints(cx,cy,x1,y1);
    let endAngle = getAngleRadiansBetweenPoints(cx, cy, x2, y2);
    let midAngle = getMiddleAngle(startAngle, endAngle, isInnerCurve);
    return getArcPoint(cx,cy,radius,midAngle);
}
function getSlope(x1, y1, x2, y2) {
    if (x1 == x2) return false;
    return (y1 - y2) / (x1 - x2);
}
function getYIntercept(x1, y1, x2, y2) {
    if (x1 === x2) return y1 === 0 ? 0 : false;
    if (y1 === y2) return y1;
    return y1 - getSlope(x1, y1, x2, y2) * x1;
}
function getOtherLineIntersection (x11, y11, x12, y12, x21, y21, x22, y22) {
    var slope1, slope2, yint1, yint2, intx, inty;
    if (x11 == x21 && y11 == y21) return [x11, y11];
    if (x12 == x22 && y12 == y22) return [x12, y22];

    slope1 = getSlope(x11, y11, x12, y12);
    slope2 = getSlope(x21, y21, x22, y22);
    if (slope1 === slope2) return false;

    yint1 = getYIntercept(x11, y11, x12, y12);
    yint2 = getYIntercept(x21, y21, x22, y22);
    if (yint1 === yint2) return yint1 === false ? false : [0, yint1];

    if (slope1 === false) return [y21, slope2 * y21 + yint2];
    if (slope2 === false) return [y11, slope1 * y11 + yint1];
    intx = (slope1 * x11 + yint1 - yint2) / slope2;
    return [intx, slope1 * intx + yint1];
}
function getLineIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
    let m1, m2, yi1, yi2, intx;
    if (x1 == x3 && y1 == y3) return { x: x1, y: y1 };
    if (x2 == x4 && y2 == y4) return { x: x2, y: y4 };

    m1 = getSlope(x1, y1, x2, y2);
    m2 = getSlope(x3, y3, x4, y4);
    if (m1 === m2) return false;
    
    yi1 = getYIntercept(x1, y1, x2, y2);
    yi2 = getYIntercept(x3, y3, x4, y4);
    if (yi1 === yi2) return yi1 === false ? false : { x: 0, y: yi1 };

    if (m1 === false) return { x: x4, y: m2 * y3 + yi2 };
    if (m2 === false) return { x: y1, y: m1 * y1 + yi1 };
    intx = (m1 * x1 + yi1 - yi2) / m2;
    return { x: intx, y: m1 * intx + yi1 };
}

function getLineCylinderIntersection(line, cylinder) {
    // Project line onto plane perpendicular to cylinder's axis
    const plane = new THREE.Plane();
    plane.setFromNormalAndCoplanarPoint(cylinder.axis, cylinder.center);
    const intersectionPoint = new THREE.Vector3();
    if (!plane.intersectLine(line, intersectionPoint)) {
        return null; // Line is parallel to cylinder's axis
    }

    // Calculate distance from intersection point to cylinder's center
    const distance = intersectionPoint.distanceTo(cylinder.center);

    //// Check if intersection is within the cylinder's radius
    //if (distance > cylinder.radius) {
    //    return null; // No intersection
    //}

    // Check if intersection is within the cylinder's height
    const projectedPoint = cylinder.axis.clone().multiplyScalar(intersectionPoint.dot(cylinder.axis));
    const heightDifference = intersectionPoint.clone().sub(projectedPoint).length();
    if (heightDifference > cylinder.height / 2) {
        return null; // No intersection
    }

    return intersectionPoint;
}
function checkLineIntersection(line1StartX, line1StartY, line1EndX, line1EndY, line2StartX, line2StartY, line2EndX, line2EndY) {
    // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
    var denominator, a, b, numerator1, numerator2, result = {
        x: null,
        y: null,
        onLine1: false,
        onLine2: false
    };
    denominator = ((line2EndY - line2StartY) * (line1EndX - line1StartX)) - ((line2EndX - line2StartX) * (line1EndY - line1StartY));
    if (denominator == 0) {
        return result;
    }
    a = line1StartY - line2StartY;
    b = line1StartX - line2StartX;
    numerator1 = ((line2EndX - line2StartX) * a) - ((line2EndY - line2StartY) * b);
    numerator2 = ((line1EndX - line1StartX) * a) - ((line1EndY - line1StartY) * b);
    a = numerator1 / denominator;
    b = numerator2 / denominator;

    // if we cast these lines infinitely in both directions, they intersect here:
    result.x = line1StartX + (a * (line1EndX - line1StartX));
    result.y = line1StartY + (a * (line1EndY - line1StartY));

    // if line1 is a segment and line2 is infinite, they intersect if:
    if (a > 0 && a < 1) {
        result.onLine1 = true;
    }
    // if line2 is a segment and line1 is infinite, they intersect if:
    if (b > 0 && b < 1) {
        result.onLine2 = true;
    }
    // if line1 and line2 are segments, they intersect if both of the above are true
    return result;
};
function getCircleLineIntersections(x1, y1, x2, y2, cx, cy, r) {
    let result = {
        intersectionOne: { x: null, y: null },
        intersectionTwo: { x: null, y: null }
    };
    let m = getSlope(x1, y1, x2, y2);
    if (x1 === x2 && x1 >= cx - r && x1 <= cx + r) {
        result.intersectionOne.x = x1;
        result.intersectionOne.y = Math.sqrt(Math.pow(r, 2) - Math.pow(cx - x1, 2)) + cy;
        result.intersectionTwo.x = x2;
        result.intersectionTwo.y = cy - Math.sqrt(Math.pow(r, 2) - Math.pow(cx - x1, 2));
    }
    else {
        let n = -m * x1 + y1;
        let a = 1 + Math.pow(m, 2);
        let b = -cx * 2 + (m * (n - cy)) * 2;
        let c = Math.pow(cx, 2) + Math.pow(n - cy, 2) - Math.pow(r, 2);
        let d = Math.pow(b, 2) - 4 * a * c;
        if (d >= 0) {
            let xa = (-b + Math.sqrt(Math.pow(b, 2) - 4 * a * c)) / (2 * a);
            let ya = m * xa + n;
            let xb = (-b - Math.sqrt(Math.pow(b, 2) - 4 * a * c)) / (2 * a);
            let yb = m * xb + n;
            result.intersectionOne.x = xa;
            result.intersectionOne.y = ya;
            result.intersectionTwo.x = xb;
            result.intersectionTwo.y = yb;
        }
    }
    return result;
}
function getIntersectionOfTwoCircles(x0, y0, r0, x1, y1, r1) {
    var a, dx, dy, d, h, rx, ry;
    var x2, y2;

    /* dx and dy are the vertical and horizontal distances between
     * the circle centers.
     */
    dx = x1 - x0;
    dy = y1 - y0;

    /* Determine the straight-line distance between the centers. */
    d = Math.sqrt((dy * dy) + (dx * dx));

    /* Check for solvability. */
    if (d > (r0 + r1)) {
        /* no solution. circles do not intersect. */
        return false;
    }
    if (d < Math.abs(r0 - r1)) {
        /* no solution. one circle is contained in the other */
        return false;
    }

    /* 'point 2' is the point where the line through the circle
     * intersection points crosses the line between the circle
     * centers.  
     */

    /* Determine the distance from point 0 to point 2. */
    a = ((r0 * r0) - (r1 * r1) + (d * d)) / (2.0 * d);

    /* Determine the coordinates of point 2. */
    x2 = x0 + (dx * a / d);
    y2 = y0 + (dy * a / d);

    /* Determine the distance from point 2 to either of the
     * intersection points.
     */
    h = Math.sqrt((r0 * r0) - (a * a));

    /* Now determine the offsets of the intersection points from
     * point 2.
     */
    rx = -dy * (h / d);
    ry = dx * (h / d);

    /* Determine the absolute intersection points. */
    var xi = x2 + rx;
    var xi_prime = x2 - rx;
    var yi = y2 + ry;
    var yi_prime = y2 - ry;

    return {
        intersectionOne: {
            x: xi,
            y: yi
        },
        intersectionTwo: {
            x: xi_prime,
            y: yi_prime
        }
    };
}
function intersectionPointsFromResult(result) {
    if (!result || result === false) return [];
    const pts = [];
    if (result.intersectionOne) pts.push(result.intersectionOne);
    if (result.intersectionTwo) pts.push(result.intersectionTwo);
    return pts;
}

function getClosestPointToPoints(x, y, points) {
    let result = { x: null, y: null };
    let d1 = null;
    let d2 = null;
    const list = Array.isArray(points) ? points : intersectionPointsFromResult(points);
    list.forEach(p => {
        if (!p || p.x == null || p.y == null || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
        if (!d1) {
            d1 = Math.pow(x - p.x, 2) + Math.pow(y - p.y, 2);
            result.x = p.x;
            result.y = p.y;
        }
        else {
            d2 = Math.pow(x - p.x, 2) + Math.pow(y - p.y, 2);
            if (d2 < d1) {
                d1 = d2;
                result.x = p.x;
                result.y = p.y;
            }
        }
    });
    return result;
}
function getWorldPointUnderCursor(e, orbitControls) {
    if (!orbitControls) {
        if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0 ||
            !gw.selectedSite.orbitControls || !gw.selectedSite.orbitControls.object)
            return;

        orbitControls = gw.selectedSite.orbitControls;
    }

    let object = {};
    if (e.target && e.target === "canvas") {
        object = e.target;
    }
    else {
        object = gw.selectedSite.canvasRef;
    }

    //  Get world coordinates under mouse pointer
    if (orbitControls && orbitControls.object) {
        let camera = orbitControls.object;
        let raycaster = new THREE.Raycaster();
        let p = new THREE.Vector3();
        let rect = object.getBoundingClientRect();
        p.set(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            ((rect.top - e.clientY) / rect.height) * 2 + 1,
            1
        );

        raycaster.setFromCamera(p, camera);
        let ground = new THREE.Plane(camera.up, 0);
        const hit = raycaster.ray.intersectPlane(ground, p);
        if (!hit) return null;
        return { x: hit.x, y: hit.y, z: -hit.z };
    }
    return null;
}
function getObjectUnderCursor(e) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef || !gw.selectedSite.groupsRef.length === 0)
        return;

    const wp = getWorldPointUnderCursor(e);
    if (!wp) return null;
    let v1 = new THREE.Vector3(wp.x, wp.y, -wp.z);

    let v2 = new THREE.Vector3(v1.x + 1, v1.y, v1.z);
    let intersects = raycastBin(v1, v2, false, 150);
    let i1 = null;
    if (intersects && intersects.length > 0) {
        i1 = intersects[0].object.binId;
    }

    v2 = new THREE.Vector3(v1.x - 1, v1.y, v1.z);
    intersects = raycastBin(v1, v2, false, 150);
    let i2 = null;
    if (intersects && intersects.length > 0) {
        i2 = intersects[0].object.binId;
    }

    if (i1 && i2 && i1 === i2) {
        highlightStice(i1);
        return intersects[0].object;
    }

    return null;
}
function raycastBin(v1, v2, isConvertToWorldPoint, distance) {
    if (!gw.selectedSite || !gw.selectedSite.groupsRef) return;

    //  Get all bin meshes
    let meshes = [];
    gw.selectedSite.groupsRef.forEach(g => {
        g.binsRef.forEach(b => {
            b.meshes.forEach(m => {
                if (m.binId) {
                    meshes.push(m);
                }
            });
        });
    });

    let geometry = new THREE.BoxGeometry(1, 1, 1);
    let material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    let mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(v1.x, v1.y, v1.z);
    mesh.lookAt(v2);

    let p = mesh.position.clone();
    if (isConvertToWorldPoint) {
        let g1 = gw.selectedSite.groupsRef[0];
        g1.updateMatrixWorld();
        p.add(g1.position);
    }

    let direction = new THREE.Vector3().subVectors(v2, v1).normalize();
    let raycaster = new THREE.Raycaster(p, direction);

    if (distance > 0) {
        raycaster.far = distance;
    }

    let intersects = raycaster.intersectObjects(meshes);

    mesh.geometry.dispose();
    mesh.material.dispose();
    mesh = null;

    return intersects;
}
function extendLine(point1, point2, distance) {
    // Create vectors from the given points
    const v1 = new THREE.Vector3(point1.x, point1.y, point1.z);
    const v2 = new THREE.Vector3(point2.x, point2.y, point2.z);

    // Calculate the direction vector
    const direction = new THREE.Vector3().subVectors(v2, v1).normalize();

    // Extend the line
    const extendedPoint = new THREE.Vector3().addVectors(v2, direction.multiplyScalar(distance));

    return extendedPoint;
}
function rayCylinderIntersection(rayOrigin, rayDirection, cylinderCenter, cylinderRadius, cylinderHeight) {
    // Normalize the ray direction
    rayDirection.normalize();

    // Transform the ray to cylinder space
    let rayOriginLocal = rayOrigin.clone().sub(cylinderCenter);

    // Calculate the coefficients of the quadratic equation
    let a = rayDirection.x * rayDirection.x + rayDirection.z * rayDirection.z;
    let b = 2 * (rayOriginLocal.x * rayDirection.x + rayOriginLocal.z * rayDirection.z);
    let c = rayOriginLocal.x * rayOriginLocal.x + rayOriginLocal.z * rayOriginLocal.z - cylinderRadius * cylinderRadius;

    // Solve the quadratic equation
    let discriminant = b * b - 4 * a * c;
    if (discriminant < 0) {
        return null; // No intersection
    }

    let t1 = (-b - Math.sqrt(discriminant)) / (2 * a);
    let t2 = (-b + Math.sqrt(discriminant)) / (2 * a);

    // Check if the intersection points are within the cylinder's height
    let intersectionPoint1 = rayOriginLocal.clone().add(rayDirection.clone().multiplyScalar(t1));
    let intersectionPoint2 = rayOriginLocal.clone().add(rayDirection.clone().multiplyScalar(t2));

    if (Math.abs(intersectionPoint1.y) > cylinderHeight / 2 && Math.abs(intersectionPoint2.y) > cylinderHeight / 2) {
        return null; // No intersection
    }

    // Return the closest intersection point
    if (t1 < t2) {
        return rayOrigin.clone().add(rayDirection.clone().multiplyScalar(t1));
    } else {
        return rayOrigin.clone().add(rayDirection.clone().multiplyScalar(t2));
    }
}
function isPointInsidePoints(p, points) {
    var x = p.x, y = p.y;
    var inside = false;
    for (var i = 0, j = points.length - 1; i < points.length; j = i++) {
        var xi = points[i].x, yi = points[i].y;
        var xj = points[j].x, yj = points[j].y;

        var intersect = ((yi > y) != (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}
function isPointInsidePolygon(point, polygon) {
    const num_vertices = polygon.length;
    const x = point.x;
    const y = point.y;
    let inside = false;

    let p1 = polygon[0];
    let p2;

    for (let i = 1; i <= num_vertices; i++) {
        p2 = polygon[i % num_vertices];

        if (y > Math.min(p1.y, p2.y)) {
            if (y <= Math.max(p1.y, p2.y)) {
                if (x <= Math.max(p1.x, p2.x)) {
                    const x_intersection = ((y - p1.y) * (p2.x - p1.x)) / (p2.y - p1.y) + p1.x;

                    if (p1.x === p2.x || x <= x_intersection) {
                        inside = !inside;
                    }
                }
            }
        }

        p1 = p2;
    }

    return inside;
}
function getActualAngleInRadians(positionFrom, positionTo) {
    return Math.atan2(positionFrom.z - positionTo.z, positionFrom.x - positionTo.x);
}
function scaleBinText() {
    if (!gw || !gw.selectedSite) return;

    gw.selectedSite.binsRef.forEach(b => {
        if (b.children.length === 0) return;
        let container = b.children[0];
        let d = gw.selectedSite.orbitControls.object.position.distanceTo(gw.selectedSite.orbitControls.currentLookAt);
        let scale = scaleFormula(d * gw.selectedSite.scaleRatio, 0.05, gw.selectedSite.binTextScale, 0.1, gw.selectedSite.binTextScale);
        // Wrapper: container.children[0] is CustomText3D (has .children[0].children[0].material). Else container is CustomText3D.
        let isWrapper = container.children && container.children[0] && container.children[0].children && container.children[0].children[0] && container.children[0].children[0].children && container.children[0].children[0].children[0] && container.children[0].children[0].children[0].material;
        let textElements = isWrapper ? container.children : [container];
        textElements.forEach(textEl => {
            textEl.scale.set(scale, scale, scale);
        });
    });
}
function getBinTemperatureColor(layoutBin) {
    if (layoutBin && layoutBin.bin && layoutBin.bin.cableList) {
        let list = [];
        layoutBin.bin.cableList.map(c => {
            return temperatureSensorsFromCable(c).forEach(s => { list.push(+s.temperature); });
        });

        let maxTemperature = getHighestNumber(list);
        if (maxTemperature) {
            return getTemperatureColorString(maxTemperature);
        }
        return "#55ff55";
    }
    return "#898989";
}
function getHighestNumber(list) {
    if (list && list.length > 0) {
        let filteredNumbers = [];

        list.forEach(s => { if (!isNaN(+s)) filteredNumbers.push(+s); });

        let maxNumber = Math.max.apply(Math, filteredNumbers);
        if (maxNumber) {
            return maxNumber;
        }
        return null;
    }
    return null;
}
function getHighestTemperatureColor(list) {
    return getTemperatureColorString(getHighestNumber(list));
}

/** Light-mode heat map: same cold→hot scale as dark mode, tuned for white backgrounds. */
const LIGHT_TEMPERATURE_COLOR_STOPS = [
    { t: 0, h: 300, s: 0.92, l: 0.34 },
    { t: 20, h: 255, s: 0.95, l: 0.36 },
    { t: 40, h: 195, s: 0.96, l: 0.34 },
    { t: 55, h: 145, s: 0.94, l: 0.32 },
    { t: 70, h: 88, s: 0.98, l: 0.30 },
    { t: 85, h: 35, s: 0.98, l: 0.36 },
    { t: 100, h: 0, s: 0.95, l: 0.34 },
];

function lerpHeatMapHueDegrees(h1, h2, factor) {
    let delta = h2 - h1;
    if (delta > 180) {
        delta -= 360;
    }
    if (delta < -180) {
        delta += 360;
    }
    let hue = h1 + delta * factor;
    if (hue < 0) {
        hue += 360;
    }
    if (hue >= 360) {
        hue -= 360;
    }
    return hue;
}

function getLightModeTemperatureColor(temperatureInt) {
    const temperature = Math.max(0, Math.min(100, temperatureInt));
    let stopIndex = 0;
    while (
        stopIndex < LIGHT_TEMPERATURE_COLOR_STOPS.length - 2 &&
        LIGHT_TEMPERATURE_COLOR_STOPS[stopIndex + 1].t < temperature
    ) {
        stopIndex++;
    }

    const start = LIGHT_TEMPERATURE_COLOR_STOPS[stopIndex];
    const end = LIGHT_TEMPERATURE_COLOR_STOPS[stopIndex + 1];
    const span = end.t - start.t;
    const factor = span > 0 ? (temperature - start.t) / span : 0;

    const hue = lerpHeatMapHueDegrees(start.h, end.h, factor);
    const saturation = start.s + (end.s - start.s) * factor;
    const lightness = start.l + (end.l - start.l) * factor;

    return new THREE.Color().setHSL(hue / 360, saturation, lightness);
}

function adjustTemperatureColorForLightBackground(color, temperatureInt) {
    const resolved = Number.isFinite(+temperatureInt) ? +temperatureInt : 50;
    color.copy(getLightModeTemperatureColor(resolved));
    return color;
}

function getTemperatureColor(temperature) {
    const temperatureInt = +temperature;
    if (isNaN(temperatureInt)) {
        //let cr = new THREE.ColorRepresentation
        //return new THREE.Color(
        //    scaleFormula(89, 0, 255, 0, 1),
        //    scaleFormula(89, 0, 255, 0, 1),
        //    scaleFormula(89, 0, 255, 0, 1)
        //);
        return null;
    }

    if (gw.theme === 'light') {
        return getLightModeTemperatureColor(temperatureInt);
    }

    const colorLevel = Math.round(temperatureInt / 100 * 1210, 0); //int
    const step = 242; //int
    let colorReached = false; //bool
    let r = 252;  //int
    let g = 6; //int
    let b = 252; //int

    if (colorLevel > step) {
        r = 0;
    }
    else {
        r -= colorLevel;
        colorReached = true;
    }

    if (colorLevel > step * 2) {
        g = 255;
    }
    else if (!colorReached) {
        g += colorLevel - step;
        colorReached = true;
    }

    if (colorLevel > step * 3) {
        b = 0;
    }
    else if (!colorReached) {
        b -= colorLevel - step * 2;
        colorReached = true;
    }

    if (colorLevel > step * 4) {
        r = 255;
    }
    else if (!colorReached) {
        r += colorLevel - step * 3;
        colorReached = true;
    }

    if (colorLevel > step * 5) {
        g = 0;
    }
    else if (!colorReached) {
        g -= colorLevel - step * 4;
        colorReached = true;
    }

    if (temperature > 98) {
        let teaet = 0;
    }

    const color = new THREE.Color(
        scaleFormula(r + 20, 0, 255, 0, 1),
        scaleFormula(g, 0, 255, 0, 1),
        scaleFormula(b, 0, 255, 0, 1)
    );

    return color;
}
function getTemperatureColorString(temperature) {
    let threeColor = getTemperatureColor(temperature);
    return !threeColor ? "#898989" : `#${threeColor.getHexString()}`;
}

function getGradeColorString(grade, theme) {
    if (grade == null || isNaN(grade)) return null;
    const g = Math.max(0, Math.min(100, Number(grade)));
    if (g < 60) return theme === 'light' ? '#c0392b' : '#e74c3c';
    if (g < 75) return theme === 'light' ? '#d35400' : '#f39c12';
    if (g < 90) return theme === 'light' ? '#b7950b' : '#f1c40f';
    return theme === 'light' ? '#1e8449' : '#2ecc71';
}
function getHighestCarbonDioxideColor(list) {
    return getCarbonDioxideColorString(getHighestNumber(list));
}
function getCarbonDioxideColor(temperature) {
    const temperatureInt = +temperature;
    if (isNaN(temperatureInt)) {
        //let cr = new THREE.ColorRepresentation
        //return new THREE.Color(
        //    scaleFormula(89, 0, 255, 0, 1),
        //    scaleFormula(89, 0, 255, 0, 1),
        //    scaleFormula(89, 0, 255, 0, 1)
        //);
        return null;
    }

    let carbon = scaleFormula(temperatureInt, 0, 5000, 0, 1);

    const colorLevel = Math.round(carbon / 100 * 1210, 0); //int
    const step = 242; //int
    let colorReached = false; //bool
    let r = 252;  //int
    let g = 6; //int
    let b = 252; //int

    if (colorLevel > step) {
        r = 0;
    }
    else {
        r -= colorLevel;
        colorReached = true;
    }

    if (colorLevel > step * 2) {
        g = 255;
    }
    else if (!colorReached) {
        g += colorLevel - step;
        colorReached = true;
    }

    if (colorLevel > step * 3) {
        b = 0;
    }
    else if (!colorReached) {
        b -= colorLevel - step * 2;
        colorReached = true;
    }

    if (colorLevel > step * 4) {
        r = 255;
    }
    else if (!colorReached) {
        r += colorLevel - step * 3;
        colorReached = true;
    }

    if (colorLevel > step * 5) {
        g = 0;
    }
    else if (!colorReached) {
        g -= colorLevel - step * 4;
        colorReached = true;
    }

    if (temperature > 98) {
        let teaet = 0;
    }

    return new THREE.Color(
        scaleFormula(r + 20, 0, 255, 0, 1),
        scaleFormula(g, 0, 255, 0, 1),
        scaleFormula(b, 0, 255, 0, 1)
    );
}
function getCarbonDioxideColorString(temperature) {
    let threeColor = getCarbonDioxideColor(temperature);
    return !threeColor ? "#898989" : `#${threeColor.getHexString()}`;
}
function setSiteName(siteIndex) {
    if (siteIndex >= data.sites.length) return '';

    let dataSite = data.sites[siteIndex];

    if (!dataSite || !dataSite.main) return '';

    return dataSite.main.siteName;
}
function phaseShiftPassY(angle, radius, y)
{
    angle = angle % 360.0;
    let x = 0;
    let z = 0;

    if (angle == 0) {
        x = radius;
        z = 0;
    }
    else if (angle > 0 && angle < 90) {
        //  Q1
        x = radius * Math.cos(angle * Math.PI / 180.0);
        z = -radius * Math.sin(angle * Math.PI / 180.0);
    }
    else if (angle == 90) {
        x = 0;
        z = -radius;
    }
    else if (angle > 90 && angle < 180) {
        //  Q2
        x = -radius * Math.sin((angle - 90.0) * Math.PI / 180.0);
        z = -radius * Math.cos((angle - 90.0) * Math.PI / 180.0);
    }
    else if (angle == 180) {
        x = -radius;
        z = 0;
    }
    else if (angle > 180 && angle < 270) {
        //  Q3
        x = -radius * Math.cos((angle - 180.0) * Math.PI / 180.0);
        z = radius * Math.sin((angle - 180.0) * Math.PI / 180.0);
    }
    else if (angle == 270) {
        x = 0;
        z = radius;
    }
    else if (angle > 270 && angle < 360) {
        //  Q4
        x = radius * Math.sin((angle - 270.0) * Math.PI / 180.0);
        z = radius * Math.cos((angle - 270.0) * Math.PI / 180.0);
    }

    return { x: x, y: y, z: z }
}
function getDefaultBin(siteIndex, binIndex) {
    if (!gw.selectedSite || !gw.selectedSite.data)
        return {};
    
    const dataMain = gw.selectedSite.data.main;
    const dataBin = gw.selectedSite.data.bins[binIndex];
    return {
        binUuid: '',
        gid: generateGrainwaveId(),
        binIndex: binIndex,
        binId: generateBinId(siteIndex, binIndex, dataBin.binId),
        siteName: setSiteName(siteIndex),
        siteIndex: siteIndex,
        //binName: `~${dataBin.name}~`,
        binName: dataBin.name,
        name: (`${dataMain.siteName}${dataBin.name}`).replaceAll(' ', ''),
        position: { x: dataBin.positionX / gw.selectedSite.globalPositionScale, y: 0, z: dataBin.positionZ / gw.selectedSite.globalPositionScale }, //  setPosition(dataMain.bins, 1.25, binIndex + 1) // [binIndex*0.5*Math.PI, 0, 0],
        scale: gw.selectedSite.binScale,
        rotation: gw.selectedSite.binRotation,
        opacity: 0.005,
        //intensity: 1.0,
        isAtomic: false,
        color: "#cccccc",
        isSelected: false,

        binDataId: dataBin.binId,
        number: dataBin.number,
        diameter: dataBin.diameter,
        width: dataBin.width,
        length: dataBin.length,
        bottleCap: dataBin.bottleCap,
        cableLayout: dataBin.cableLayout,
        peakHeight: dataBin.peakHeight,
        height: dataBin.height,
        grainWallHeight: dataBin.grainWallHeight,
        grainHeight: dataBin.grainHeight,
        positionY: dataBin.positionY,
        fontSize: dataBin.fontSize,
        angle: dataBin.angle,
        type: dataBin.type,
        transparency3D: dataBin.transparency3D,
        cableList: dataBin.cableList,
        fanList: dataBin.fanList || [],
        degreesToNorth: dataBin.degreesToNorth,
        emc: dataBin.emc,
    };
}
function isValidUuid(uuid) {
    if (uuids.filter(x => x === uuid)[0]) {
        return false;
    }
    else {
        uuids.push(uuid);
    }

    //  Usage
    //if (!isValidUuid(binMesh.material.uuid)) {
    //    var testhereasdf = 0;
    //}
};
function getFreshDataModel() {
    return {
        siteName: '',
        main: { siteName: "", bins: 1 },
        bins: [{ name: '', binId: 0 }]
    }
}

function toggleCableLevelRef(binId, cableId, sensorId, position) {
    for (let c = 0; c < gw.selectedSite.cableLevelRef.length; c++) {
        let cr = gw.selectedSite.cableLevelRef[c];
        if (cr.cableId === cableId) {
            // Don't make level boxes visible - they should always stay hidden
            // cr.material.visible = true;
            // cr.material.opacity = 1;
            //  Get position of level sensor
            let sensorPosition = null;
            for (let s = 0; s < gw.selectedSite.sensorsRef.length; s++) {
                let sr = gw.selectedSite.sensorsRef[s];
                if (sr.sensorId === sensorId) {
                    if (position === 0) {
                        sensorPosition = { x: sr.parent.parent.parent.position.x, y: 0.5, z: sr.parent.parent.parent.position.z };
                    }
                    else {
                        sensorPosition = sr.parent.parent.parent.position;  
                    }
                    break;
                }
            }

            cr.position.set(sensorPosition.x, sensorPosition.y, sensorPosition.z);
            return { binId: binId, cableId: cableId, sensorId: sensorId, position: cr.position };
            break;
        }
        else if (!cableId) {
            cr.material.visible = false;
            cr.material.opacity = 0;
        }
    }
}

// Clear all flash animations
const clearAllFlashAnimations = () => {
    clearSensorFlashAnimations();
    if (gw.selectedSite && gw.selectedSite.groupsRef) {
        gw.selectedSite.groupsRef.forEach(group => {
            if (group && group.flashTimeoutId) {
                clearTimeout(group.flashTimeoutId);
                group.flashTimeoutId = null;
            }
            if (group && group.flashOnTimeoutId) {
                clearTimeout(group.flashOnTimeoutId);
                group.flashOnTimeoutId = null;
            }
            if (group) {
                group.isAnimating = false;
            }
        });
    }
    // Reset flash animation count
    if (gw.selectedSite) {
        gw.selectedSite.flashAnimationCount = 0;
    }
};

const refreshAllSensorTemperatureColors = () => {
    if (!gw.selectedSite?.layoutSensors?.length) {
        return;
    }

    gw.selectedSite.layoutSensors.forEach((layoutSensor) => {
        if (isHumidityLayoutSensor(layoutSensor)) return;
        const temperature = layoutSensor.sensorValueNumber ?? layoutSensor.sensorValueString;
        if (temperature === undefined || temperature === null || temperature === "") {
            return;
        }

        updateSensorTemperature(
            layoutSensor.binId,
            layoutSensor.cableId,
            layoutSensor.sensorId,
            temperature,
            null,
            false
        );
    });

    setAllBinsTextColor();
};

const updateSensorTemperature = (binId, cableId, sensorId, temperature, lastTemperature, enableFlash = false) => {
    try {
        const humidityRow = gw.selectedSite?.layoutSensors?.find(
            (x) => Number(x.sensorId) === Number(sensorId)
        );
        if (isHumidityLayoutSensor(humidityRow)) return;

        // Store the temperature for delayed update
        let delayedTemperature = temperature;
        let delayedLastTemperature = lastTemperature;
        
        let temperatureString = temperature.toString();

        if (temperatureString === "" || temperatureString === "E") {
            temperatureString = "-E-";
        }

        if (temperatureString.length === 2) {
            temperatureString = ` ${temperatureString}`;
        }
        else if (temperatureString.length === 1) {
            temperatureString = `  ${temperatureString}`;
        }
        else if (temperatureString.length === 0) {
            temperatureString = `   `;
        }

        const isHistoryScrub = gw.selectedSite?.isHistorySliderOn;

        // Helper function to check if sensor is stale
        const checkIfStale = () => {
            if (isHistoryScrub) return false;
            let layoutSensorCheck = gw.selectedSite.layoutSensors.filter(x => Number(x.sensorId) === Number(sensorId));
            if (layoutSensorCheck && layoutSensorCheck.length > 0) {
                const sensor = layoutSensorCheck[0];
                if (sensor.lastUpdateTimestamp) {
                    const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
                    const timeSinceUpdate = Date.now() - sensor.lastUpdateTimestamp;
                    return timeSinceUpdate > oneHourInMs;
                }
            }
            return false;
        };

        // Check if sensor is stale
        const isStale = checkIfStale();

        // Don't update temperature immediately - wait for flash to complete
        let color = new THREE.Color(
            scaleFormula(89, 0, 255, 0, 1),
            scaleFormula(89, 0, 255, 0, 1),
            scaleFormula(89, 0, 255, 0, 1)
        );

        // If sensor is stale (not updated in over an hour), ALWAYS use gray color
        if (isStale) {
            // Gray color: RGB(128, 128, 128)
            color = new THREE.Color(
                scaleFormula(128, 0, 255, 0, 1),
                scaleFormula(128, 0, 255, 0, 1),
                scaleFormula(128, 0, 255, 0, 1)
            );
        }
        else if (lastTemperature && gw.selectedSite.isComparisonOn) {
            if (lastTemperature - temperature >= gw.selectedSite.main.minus) {
                color = new THREE.Color(
                    scaleFormula(0, 0, 255, 0, 1),
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(0, 0, 255, 0, 1)
                );
            }
            else if (temperature - lastTemperature >= gw.selectedSite.main.plus) {
                color = new THREE.Color(
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(0, 0, 255, 0, 1)
                );
            }
        }
        else {
            color = getTemperatureColor(temperature);
        }
        
        if (!color) {
            color = new THREE.Color(
                scaleFormula(89, 0, 255, 0, 1),
                scaleFormula(89, 0, 255, 0, 1),
                scaleFormula(89, 0, 255, 0, 1)
            );
        }
        
        // If sensor is stale, override color to gray regardless of what was calculated above
        if (isStale) {
            // In light mode, use same color as cable names (0xb4b4b4)
            // In dark mode, use gray RGB(128, 128, 128)
            if (gw.theme === 'light') {
                color = new THREE.Color(0xb4b4b4);
            } else {
                // Gray color: RGB(128, 128, 128)
                color = new THREE.Color(
                    scaleFormula(128, 0, 255, 0, 1),
                    scaleFormula(128, 0, 255, 0, 1),
                    scaleFormula(128, 0, 255, 0, 1)
                );
            }
        }

        let sensorsRef = [];
        for (let s = 0; s < gw.selectedSite.sensorsRef.length; s++) {
            let sr = gw.selectedSite.sensorsRef[s];
            if (Number(sr.sensorId) === Number(sensorId)) {
                sensorsRef.push(sr);
            }
        }
        if (sensorsRef.length === 0) {
            return;
        }

        // Check if temperature actually changed by comparing with current display
        let currentDisplay = "";
        for (let j = 0; j < 3; j++) {
            let sensor = sensorsRef[j];
            if (sensor && sensor.name && sensor.name.startsWith("An")) {
                if (sensor.name === "AnDash") {
                    currentDisplay += "-";
                } else if (sensor.name === "AnSpace") {
                    currentDisplay += " ";
                } else {
                    currentDisplay += sensor.name.substring(2); // Remove "An" prefix
                }
            }
        }
        
        // Only proceed if the temperature display will actually change (compare full 3-char layout, not trimmed numeric value)
        if (currentDisplay !== temperatureString) {
            // Check if this cable has all invalid temperatures before flashing
            const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === cableId) || [];
            const sensorErrorValues = new Set();
            cableSensors.forEach(sensor => {
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                if (isFaintSensorLabel(tempStr)) {
                    sensorErrorValues.add(sensor.sensorId);
                }
            });
            
            // Check if this cable has all invalid temperatures (including the new temperature)
            // IMPORTANT: During flash updates, multiple sensors may update simultaneously.
            // We need to be conservative - only hide if we're CERTAIN all sensors are invalid.
            // If ANY sensor has a valid temperature (or is being updated to valid), don't hide.
            const newTempStr = delayedTemperature.toString().trim();
            const isNewTempInvalid = isFaintSensorLabel(newTempStr);
            
            // Check if there are any valid sensors on this cable
            // If even one sensor is valid, the cable should not be hidden
            let hasValidSensor = false;
            if (!isNewTempInvalid) {
                // The sensor being updated will be valid, so cable should not be hidden
                hasValidSensor = true;
            } else {
                // Check other sensors - if any are valid, don't hide
                for (const sensor of cableSensors) {
                    if (sensor.sensorId !== sensorId) {
                        const tempStr = sensor.sensorValueString?.toString().trim() || "";
                        if (!isFaintSensorLabel(tempStr)) {
                            hasValidSensor = true;
                            break;
                        }
                    }
                }
            }
            
            // Only consider cable invalid if ALL sensors are invalid AND we have sensors
            const allSensorsWillBeInvalid = cableSensors.length > 0 && !hasValidSensor && isNewTempInvalid && cableSensors.every(sensor => {
                // If this is the sensor being updated, check the new temperature
                if (sensor.sensorId === sensorId) {
                    return isNewTempInvalid;
                }
                // Otherwise check the current sensor value
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                return isFaintSensorLabel(tempStr);
            });
            
            // Also check mesh names as fallback for error pattern detection
            let hasErrorPattern = false;
            let dashCount = 0;
            let eCount = 0;
            let oCount = 0;
            
            // Get the cable reference to check mesh names
            const cableRef = gw.selectedSite.cablesRef.find(c => c.cableId === cableId);
            if (cableRef) {
                cableRef.meshes.forEach(mesh => {
                    if (mesh.name === "AnDash") dashCount++;
                    if (mesh.name === "AnE") eCount++;
                    if (mesh.name === "AnO") oCount++;
                });
                
                // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
                if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                    hasErrorPattern = true;
                }
            }
            
            // If invalid cables toggle is OFF and this cable has all invalid temperatures, don't flash
            // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            // Check if ANY sensor on this cable is in the heltecSensorMappings
            let hasSensorsInHeltecMapping = false;
            if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
                // Check if any sensor on this cable has a mapping in heltecSensorMappings
                for (const sensor of cableSensors) {
                    const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                        mapping.sensorId === sensor.sensorId
                    );
                    if (hasMapping) {
                        hasSensorsInHeltecMapping = true;
                        break;
                    }
                }
            }
            
            const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (allSensorsWillBeInvalid || hasErrorPattern) && !hasSensorsInHeltecMapping;
            
            // Start flash animation only if enabled
            if (enableFlash) {
                // If this is an invalid cable and toggle is OFF, skip the flash
                // UNLESS the cable has sensors in heltecSensorMappings (actively receiving data)
                if (shouldHideInvalidCable) {
                    // Don't flash, just update the temperature silently (it will be hidden)
                    updateTemperatureAfterFlash(binId, cableId, sensorId, delayedTemperature, delayedLastTemperature);
                    return;
                }
                
                // Check if a cable is currently focused - if so, only allow flash for the focused cable
                const isCableFocused = gw.selectedSite.isCableFocused;
                const isThisCableFocused = sameCableId(cableId, gw.selectedSite.focusedCableId);
                
                // If a cable is focused and this is not the focused cable, don't flash or make visible
                if (isCableFocused && !isThisCableFocused) {
                    // Don't flash, just update the temperature silently (it will remain hidden)
                    updateTemperatureAfterFlash(binId, cableId, sensorId, delayedTemperature, delayedLastTemperature);
                    return;
                }
                
                let m = sensorsRef[0]; // Get the first sensor for the group
                let group = m.parent.parent;
            
                if (!group.isAnimating) {
                    // Cancel any stale timeouts from a prior animation on this group
                    if (group.flashTimeoutId) {
                        clearTimeout(group.flashTimeoutId);
                        group.flashTimeoutId = null;
                    }
                    if (group.flashOnTimeoutId) {
                        clearTimeout(group.flashOnTimeoutId);
                        group.flashOnTimeoutId = null;
                    }

                    group.isAnimating = true;
                    group.flashAnimId = (group.flashAnimId || 0) + 1;
                    const animId = group.flashAnimId;
                    
                    let flashCount = 0;
                    const maxFlashes = 9; // Flash 9 times over 3 seconds
                    const flashInterval = 3000 / maxFlashes; // 333ms per flash cycle

                    const restoreFlashGroupVisibility = () => {
                        if (!shouldShowSensorMesh(binId, cableId)) {
                            hideUnfocusedCables();
                            return;
                        }

                        group.traverse((child) => {
                            if (!child.material) return;
                            if (child.isLevel) {
                                child.material.visible = false;
                            } else if (child.name === "AnSpace") {
                                child.material.visible = false;
                            } else {
                                child.material.visible = true;
                            }
                        });
                    };
                    
                    const flash = () => {
                        if (group.flashAnimId !== animId) {
                            return;
                        }

                        if (flashCount >= maxFlashes) {
                            if (group.flashOnTimeoutId) {
                                clearTimeout(group.flashOnTimeoutId);
                                group.flashOnTimeoutId = null;
                            }
                            
                            group.isAnimating = false;
                            group.flashTimeoutId = null;

                            // Always end visible — the last blink cycle turns off before
                            // flashCount increments, so restore before applying the new value.
                            restoreFlashGroupVisibility();

                            setTimeout(() => {
                                if (group.flashAnimId === animId) {
                                    updateTemperatureAfterFlash(binId, cableId, sensorId, delayedTemperature, delayedLastTemperature);
                                    hideUnfocusedCables();
                                }
                            }, 10);
                            return;
                        }

                        // Flash off (make invisible)
                        group.traverse((child) => {
                            if (child.material) {
                                child.material.visible = false;
                            }
                        });

                        // Flash on after half the interval
                        group.flashOnTimeoutId = setTimeout(() => {
                            if (group.isAnimating && group.flashAnimId === animId) {
                                restoreFlashGroupVisibility();
                            }
                            group.flashOnTimeoutId = null;
                        }, flashInterval / 2);

                        flashCount++;
                        group.flashTimeoutId = setTimeout(flash, flashInterval);
                    };

                    // Start the flash sequence immediately
                    flash();
                }

                // Return early when flash is enabled - don't process meshes immediately
                return;
            }
        }

        // Only process meshes immediately if flash is disabled
        if (!enableFlash) {
            // Check if this cable has all invalid temperatures before processing
            // IMPORTANT: Be conservative - only hide if we're CERTAIN all sensors are invalid.
            const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === cableId) || [];
            const newTempStr = temperature.toString().trim();
            const isNewTempInvalid = isFaintSensorLabel(newTempStr);
            
            // Check if there are any valid sensors on this cable
            // If even one sensor is valid, the cable should not be hidden
            let hasValidSensor = false;
            if (!isNewTempInvalid) {
                // The sensor being updated will be valid, so cable should not be hidden
                hasValidSensor = true;
            } else {
                // Check other sensors - if any are valid, don't hide
                for (const sensor of cableSensors) {
                    if (sensor.sensorId !== sensorId) {
                        const tempStr = sensor.sensorValueString?.toString().trim() || "";
                        if (!isFaintSensorLabel(tempStr)) {
                            hasValidSensor = true;
                            break;
                        }
                    }
                }
            }
            
            // Only consider cable invalid if ALL sensors are invalid AND we have sensors
            const allSensorsWillBeInvalid = cableSensors.length > 0 && !hasValidSensor && isNewTempInvalid && cableSensors.every(sensor => {
                // If this is the sensor being updated, check the new temperature
                if (sensor.sensorId === sensorId) {
                    return isNewTempInvalid;
                }
                // Otherwise check the current sensor value
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                return isFaintSensorLabel(tempStr);
            });
            
            // Also check mesh names as fallback for error pattern detection
            const cableRef = gw.selectedSite.cablesRef.find(c => c.cableId === cableId);
            let hasErrorPattern = false;
            if (cableRef) {
                let dashCount = 0;
                let eCount = 0;
                let oCount = 0;
                
                cableRef.meshes.forEach(mesh => {
                    if (mesh.name === "AnDash") dashCount++;
                    if (mesh.name === "AnE") eCount++;
                    if (mesh.name === "AnO") oCount++;
                });
                
                // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
                if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                    hasErrorPattern = true;
                }
            }
            
            // If invalid cables toggle is OFF and this cable has all invalid temperatures, hide it and return
            // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            // Check if ANY sensor on this cable is in the heltecSensorMappings
            let hasSensorsInHeltecMapping = false;
            if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
                // Check if any sensor on this cable has a mapping in heltecSensorMappings
                for (const sensor of cableSensors) {
                    const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                        mapping.sensorId === sensor.sensorId
                    );
                    if (hasMapping) {
                        hasSensorsInHeltecMapping = true;
                        break;
                    }
                }
            }
            
            // Check if a cable is currently focused - if so, only allow updates for the focused cable
            const isCableFocused = gw.selectedSite.isCableFocused;
            const isThisCableFocused = sameCableId(cableId, gw.selectedSite.focusedCableId);
            
            // If a cable is focused and this is not the focused cable, don't make it visible
            const shouldHideDueToFocus = isCableFocused && !isThisCableFocused;
            
            const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (allSensorsWillBeInvalid || hasErrorPattern) && !hasSensorsInHeltecMapping;
            
            if ((shouldHideInvalidCable || shouldHideDueToFocus) && cableRef) {
                // Update the sensor value even though we're hiding it
                let layoutSensor = gw.selectedSite.layoutSensors.filter(x => Number(x.sensorId) === Number(sensorId));
                if (layoutSensor && layoutSensor.length > 0) {
                    layoutSensor = layoutSensor[0];
                    layoutSensor.sensorValueString = temperature.toString();
                    layoutSensor.sensorValueNumber = temperature;
                }
                
                // Hide the cable group
                if (cableRef.visible !== undefined) {
                    cableRef.visible = false;
                }
                
                // Hide all meshes
                cableRef.meshes.forEach(mesh => {
                    if (mesh.material && !mesh.isLevel) {
                        mesh.material.visible = false;
                    }
                });
                
                // Hide all children (geometry meshes)
                if (cableRef.children && cableRef.children.length > 0) {
                    cableRef.children.forEach(child => {
                        if (child.material && !child.isLevel) {
                            child.material.visible = false;
                        }
                    });
                }
                
                // Return early - don't process meshes
                return;
            }
            
            // Flash is disabled, so process meshes immediately (original functionality)
            // Get the group reference for return value
            let group = sensorsRef[0].parent.parent;

            for (let i = 0; i < 3; i++) {
                let m = sensorsRef[i];
                let ts = temperatureString[i];
                if (!m || ts == null) continue;

                if (m.name === "AnSpace") {
                    m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                }

                            if (ts === "-") {
                m.geometry = gw.geometries.AnDash;
                m.name = "AnDash";
                // Check if this is part of a "-E-" error value
                let isErrorValue = false;
                let checkString = temperatureString.trim();
                if (checkString === "-E-" || checkString === "-O-") {
                    isErrorValue = true;
                }
                // Skip level indicator boxes - they should always be hidden
                if (m.isLevel) {
                    m.material.visible = false;
                    return;
                }
                
                    // If sensor is stale, always use gray (check again right before applying)
                    // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even if stale
                    const currentIsStaleO = checkIfStale();
                    const isHeltecTcError = gw.selectedSite.isSiteHeltecTc && checkString === "-E-";
                    if (currentIsStaleO && !isHeltecTcError) {
                        m.material.opacity = 1;
                        m.material.transparent = false;
                        // Always use gray for stale sensors, regardless of other conditions
                        // In light mode, use same color as cable names (#b4b4b4) - don't set emissive to match cable names
                        // In dark mode, use gray RGB(128, 128, 128)
                        if (gw.theme === 'light') {
                            m.material.color.set("#b4b4b4");
                            // Don't set emissive in light mode to match cable names exactly
                            if (m.material.emissive) {
                                m.material.emissive.set("#000000");
                            }
                        } else {
                            const grayColor = new THREE.Color(
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1)
                            );
                            m.material.color.set(grayColor);
                            if (m.material.emissive) {
                                m.material.emissive.set(grayColor);
                            }
                        }
                        m.material.needsUpdate = true;
                    }
                    // In light mode, error values should be visible with same color as bin name labels
                    // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even in light mode
                    else if (isErrorValue && gw.theme === 'light' && !isHeltecTcError) {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                        m.material.needsUpdate = true;
                    } else {
                        m.material.opacity = isErrorValue ? 0.0025 : 1;
                        m.material.transparent = isErrorValue ? true : false;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    }
                    applySensorMeshVisibility(m, binId, cableId);
                }
                else if (ts === "O") {
                    m.geometry = gw.geometries.AnO;
                    m.name = "AnO";
                    // Check if this is part of a "-O-" error value
                    let isErrorValue = false;
                    if (temperatureString === "-O-") {
                        isErrorValue = true;
                    }
                    // Skip level indicator boxes - they should always be hidden
                    if (m.isLevel) {
                        m.material.visible = false;
                        return;
                    }
                    
                    // If sensor is stale, always use gray (check again right before applying)
                    const currentIsStaleO2 = checkIfStale();
                    if (currentIsStaleO2) {
                        m.material.opacity = 1;
                        m.material.transparent = false;
                        // Always use gray for stale sensors, regardless of other conditions
                        // In light mode, use same color as cable names (#b4b4b4)
                        // In dark mode, use gray RGB(128, 128, 128)
                        const grayColor = gw.theme === 'light' 
                            ? new THREE.Color("#b4b4b4")
                            : new THREE.Color(
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1)
                            );
                        m.material.color.set(grayColor);
                        if (m.material.emissive) {
                            m.material.emissive.set(grayColor);
                        }
                        m.material.needsUpdate = true;
                    }
                    // In light mode, error values should be visible with same color as bin name labels
                    else if (isErrorValue && gw.theme === 'light') {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                        m.material.needsUpdate = true;
                    } else {
                        m.material.opacity = isErrorValue ? 0.0025 : 1;
                        m.material.transparent = isErrorValue ? true : false;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    }
                    applySensorMeshVisibility(m, binId, cableId);
                }
                else if (ts === "E") {
                    m.geometry = gw.geometries.AnE;
                    m.name = "AnE";
                    // Check if this is part of a "-E-" error value
                    let isErrorValue = false;
                    if (temperatureString === "-E-") {
                        isErrorValue = true;
                    }
                    // Skip level indicator boxes - they should always be hidden
                    if (m.isLevel) {
                        m.material.visible = false;
                        return;
                    }
                    
                    // If sensor is stale, always use gray (check again right before applying)
                    // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even if stale
                    const currentIsStaleE = checkIfStale();
                    const isHeltecTcError = gw.selectedSite.isSiteHeltecTc && temperatureString === "-E-";
                    if (currentIsStaleE && !isHeltecTcError) {
                        m.material.opacity = 1;
                        m.material.transparent = false;
                        // Always use gray for stale sensors, regardless of other conditions
                        // In light mode, use same color as cable names (#b4b4b4)
                        // In dark mode, use gray RGB(128, 128, 128)
                        const grayColor = gw.theme === 'light' 
                            ? new THREE.Color("#b4b4b4")
                            : new THREE.Color(
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1)
                            );
                        m.material.color.set(grayColor);
                        if (m.material.emissive) {
                            m.material.emissive.set(grayColor);
                        }
                        m.material.needsUpdate = true;
                    }
                    // In light mode, error values should be visible with same color as bin name labels
                    // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even in light mode
                    else if (isErrorValue && gw.theme === 'light' && !isHeltecTcError) {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                        m.material.needsUpdate = true;
                    } else {
                        m.material.opacity = isErrorValue ? 0.0025 : 1;
                        m.material.transparent = isErrorValue ? true : false;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    }
                    applySensorMeshVisibility(m, binId, cableId);
                }
                else if (ts === " ") {
                    m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                    m.geometry = gw.geometries.AnSpace;
                    m.name = "AnSpace";
                    m.material = new THREE.MeshStandardMaterial({
                        color: "#cccccc",
                        emissive: "#cccccc",
                        side: THREE.FrontSide,
                        toneMapped: false,
                        envMapIntensity: 1,
                        metalness: 0.0,
                        transparent: true,
                        opacity: 0,
                        visible: false,
                    });
                    m.binId = binId;
                    m.cableId = cableId;
                    m.sensorId = sensorId;
                    m.visible = false;
                }
                else if (ts === ".") {
                    m.geometry = gw.geometries.AnDot;
                    m.name = "AnDot";
                    // If sensor is stale, always use gray (check again right before applying)
                    const currentIsStaleDot = checkIfStale();
                    if (currentIsStaleDot) {
                        // Always use gray for stale sensors, regardless of other conditions
                        // In light mode, use same color as cable names (0xb4b4b4) - don't set emissive to match cable names
                        // In dark mode, use gray RGB(128, 128, 128)
                        if (gw.theme === 'light') {
                            // Use hex number format to match cable names exactly (0xb4b4b4)
                            m.material.color.setHex(0xb4b4b4);
                            // Cable names don't have emissive, so set it to black
                            if (m.material.emissive) {
                                m.material.emissive.setHex(0x000000);
                            }
                        } else {
                            const grayColor = new THREE.Color(
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1)
                            );
                            m.material.color.set(grayColor);
                        }
                        m.material.opacity = 1;
                        m.material.transparent = false;
                    } else {
                        m.material.opacity = 1;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    }
                    applySensorMeshVisibility(m, binId, cableId);
                }
                else {
                    if (!setMeshGlyphGeometry(m, glyphGeometryForChar(ts), glyphNameForChar(ts))) {
                        continue;
                    }
                    
                    // Skip level indicator boxes - they should always be hidden
                    if (m.isLevel) {
                        m.material.visible = false;
                        return;
                    }
                    
                    const label = String(temperatureString || "").trim();
                    const faint = isFaintSensorLabel(label);
                    const heltecDim = !!(gw.selectedSite && gw.selectedSite.isSiteHeltecTc && faint);
                    const currentIsStaleNum = checkIfStale();
                    if (currentIsStaleNum && !heltecDim) {
                        // Always use gray for stale sensors, regardless of other conditions
                        // In light mode, use same color as cable names (0xb4b4b4) - don't set emissive to match cable names
                        // In dark mode, use gray RGB(128, 128, 128)
                        if (gw.theme === 'light') {
                            // Use hex number format to match cable names exactly (0xb4b4b4)
                            m.material.color.setHex(0xb4b4b4);
                            // Cable names don't have emissive, so set it to black
                            if (m.material.emissive) {
                                m.material.emissive.setHex(0x000000);
                            }
                        } else {
                            const grayColor = new THREE.Color(
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1),
                                scaleFormula(128, 0, 255, 0, 1)
                            );
                            m.material.color.set(grayColor);
                            if (m.material.emissive) {
                                m.material.emissive.set(grayColor);
                            }
                        }
                        m.material.opacity = 1;
                        m.material.transparent = false;
                    } else if (faint && gw.theme === "light" && !heltecDim) {
                        m.material.opacity = 1.0;
                        m.material.transparent = false;
                        m.material.color.set("#b4b4b4");
                        if (m.material.emissive) {
                            m.material.emissive.set("#b4b4b4");
                        }
                    } else if (faint) {
                        m.material.opacity = 0.0025;
                        m.material.transparent = true;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    } else {
                        m.material.opacity = 1;
                        m.material.transparent = false;
                        applyTemperatureColorToSensorMaterial(m.material, color);
                    }
                    m.material.needsUpdate = true;
                    applySensorMeshVisibility(m, binId, cableId);
                }

                if (i === 2) {
                    centerText3d(m.parent.parent);
                }
            }
        }
    } catch (e) {
        let teste = 0;
    }
}
function centerText(group) {
    let cableTextCenter = group.parent.children[0].position;
    group.position.set(cableTextCenter.x, group.position.y, cableTextCenter.z);

    //let box = new THREE.Box3().setFromObject(group);
    //box.getCenter(gw.selectedSite.v1);
    //gw.selectedSite.v1.add(cableTextCenter);
    //let teste = 0;


    //cableText.getWorldPosition(gw.selectedSite.v1);
    //let box = new THREE.Box3().setFromObject(group);
    //box.getCenter(gw.selectedSite.v2);
    //gw.selectedSite.v2.sub(gw.selectedSite.v1).add(group.position);
    //group.position.set(gw.selectedSite.v2.x, group.position.y, gw.selectedSite.v2.z);
    //let test = 0;
    ////new THREE.Box3().setFromObject(group).getCenter(group.localToWorld(group.position));
}
function getRandomInt(min, max) {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled); // The maximum is exclusive and the minimum is inclusive
}
function centerText3d(group, offset) {
    const spacing = 4.0;
    let widths = [];
    let meshes = [];
    let totalWidth = 0.0;
    for (let c = 0; c < group.children.length; c++) {
        let child = group.children[c];
        if (child?.userData?.isCableNameHit || child?.name === "cableNameHit") continue;
        if (child && child.children.length > 0) {
            let mesh = child.children[0];
            if (!(mesh && mesh.geometry)) {
                mesh = child.children[0].children[0];
            }

            const isHumiditySpace =
                mesh?.name === "AnSpace" &&
                (mesh.isHumiditySensor || mesh.meshProps?.isHumiditySensor);
            const includeMesh =
                mesh &&
                mesh.geometry &&
                (!mesh.sensorId || mesh.name !== "AnSpace" || isHumiditySpace);
            if (includeMesh) {
                if (!mesh.geometry.boundingBox) {
                    mesh.geometry.computeBoundingBox();
                }
                meshes.push(mesh);
                let width = isHumiditySpace
                    ? 12
                    : mesh.geometry.boundingBox
                      ? mesh.geometry.boundingBox.max.x - mesh.geometry.boundingBox.min.x
                      : 8;
                totalWidth += width;
                if (c + 1 < group.children.length) {
                    totalWidth += spacing;
                }
                widths.push(width);
            }
        }
    }

    let x = -totalWidth / 2.0;
    for (let m = 0; m < meshes.length; m++) {
        let mesh = meshes[m];
        x += widths[m] / 2.0 + (offset ? offset : 0);
        mesh.position.x = x;
        if (m < group.children.length) {
            x += widths[m] / 2.0 + spacing;
        }
    }
}
function bytesToHex(bytes) {
    return Array.from(
        bytes,
        byte => byte.toString(16).padStart(2, "0")
    ).join("");
}
function stringToUTF8Bytes(string) {
    return new TextEncoder().encode(string);
}
function stringToUTF16Bytes(string, littleEndian) {
    const bytes = new Uint8Array(string.length * 2);
    // Using DataView is the only way to get a specific
    // endianness.
    const view = new DataView(bytes.buffer);
    for (let i = 0; i != string.length; i++) {
        view.setUint16(i, string.charCodeAt(i), littleEndian);
    }
    return bytes;
}
function stringToUTF32Bytes(string, littleEndian) {
    const codepoints = Array.from(string, c => c.codePointAt(0));
    const bytes = new Uint8Array(codepoints.length * 4);
    // Using DataView is the only way to get a specific
    // endianness.
    const view = new DataView(bytes.buffer);
    for (let i = 0; i != codepoints.length; i++) {
        view.setUint32(i, codepoints[i], littleEndian);
    }
    return bytes;
}
function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i !== bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;

    //new TextDecoder().decode(hexToBytes("68656c6c6f20e6bca2e5ad9720f09f918d"));
    ///* but you can also use:*/
    //new TextDecoder("UTF-16LE").decode(hexToBytes("680065006c006c006f002000226f575b20003dd84ddc"))
    //new TextDecoder("UTF-16BE").decode(hexToBytes(
}
function bytesToStringUTF32(bytes, littleEndian) {
    const view = new DataView(bytes.buffer);
    const codepoints = new Uint32Array(view.byteLength / 4);
    for (let i = 0; i !== codepoints.length; i++) {
        codepoints[i] = view.getUint32(i * 4, littleEndian);
    }
    return String.fromCodePoint(...codepoints);
}
function base64StringToArrayBuffer(base64String) {
    let db64 = atob(base64String);
    let bytes = new Uint8Array(db64.length);
    for (let i = 0; i < db64.length; i++) {
        bytes[i] = db64.charCodeAt(i);
    }
    return bytes.buffer;
}
function base64ToString(message, enc) {
    if (!enc) {
        enc = "";
    }
    return Buffer.from(message, "base64").toString(enc);
}
function arrayBufferToBase64String(arrayBuffer) {
    var binary = '';
    var bytes = new Uint8Array(arrayBuffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
function jsonDateToFormatDate(jsonDate, includeSeconds = true) {
    let testDate = new Date(jsonDate).toLocaleString();
    let dateTimeSplits = testDate.split(",");
    let datePart = dateTimeSplits[0].split("/");
    let year = datePart[2].substring(2, 4);
    let month = datePart[0];
    if (month <= 9) {
        month = "0" + month;
    }
    let day = datePart[1];
    if (day <= 9) {
        day = "0" + day;
    }
    let timePart = dateTimeSplits[1];
    let ttPart = timePart.split(" ");
    let time = ttPart[1].split(":");
    let hour = time[0];
    if (hour <= 9) {
        hour = "0" + hour;
    }
    let minute = time[1];
    let second = time[2] || "00"; // Include seconds, default to "00" if not present
    if (parseInt(second) <= 9) {
        second = "0" + second;
    }
    let tt = ttPart[2];

    const timeStr = includeSeconds ? `${hour}:${minute}:${second}` : `${hour}:${minute}`;
    return `${month}-${day}-${year} ${timeStr} ${tt}`;
}
function jsonDateToFormatDateOnly(jsonDate) {
    if (jsonDate == null) return '';
    try {
        const d = new Date(typeof jsonDate === 'string' ? jsonDate.replace("Z", "-05:00") : jsonDate);
        if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '';
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${month}-${day}-${year}`;
    } catch {
        return '';
    }
}
async function compress(string, encoding) {
    const byteArray = new TextEncoder().encode(string);
    const cs = new CompressionStream(encoding);
    const writer = cs.writable.getWriter();
    writer.write(byteArray);
    writer.close();
    return await new Response(cs.readable).arrayBuffer();
}
function decompress(byteArray, encoding) {
    //const byteArray = new TextEncoder().encode(string);
    const cs = new DecompressionStream(encoding);
    const writer = cs.writable.getWriter();
    writer.write(byteArray);
    writer.close();
    return new Response(cs.readable).arrayBuffer().then(function (arrayBuffer) {
        return new TextDecoder().decode(arrayBuffer);
    });
}
async function decompressAsync(byteArray, encoding) {
    //const byteArray = new TextEncoder().encode(string);
    const cs = new DecompressionStream(encoding);
    const writer = cs.writable.getWriter();
    writer.write(byteArray);
    writer.close();
    return new Response(cs.readable).arrayBuffer().then(function (arrayBuffer) {
        return new TextDecoder().decode(arrayBuffer);
    });
}
function hexToBase64(hexStr) {
    let base64 = "";
    for (let i = 0; i < hexStr.length; i++) {
        base64 += !(i - 1 & 1) ? String.fromCharCode(parseInt(hexStr.substring(i - 1, i + 1), 16)) : ""
    }
    return btoa(base64);
}
function decompressIn(string) {
    const dtask = async () => {
        const db64 = atob(string);
        try {
            let dbBuffer = base64StringToArrayBuffer(string);
            const dd = await decompress(dbBuffer, "gzip").then((fr) => {
                return fr;
            });
        } catch (e) {
            return e.message;
        }
    };
    dtask().catch(console.error);
}
async function setSensorLabelAsync(sensorId, temperature, lastTemperature) {
    if (gw.selectedSite.selectedMark.sensorHistories && gw.selectedSite.selectedMark.sensorHistories.length > 0) {
        updateSensorTemperature(null, null, sensorId, temperature, lastTemperature);

        if (gw.selectedSite.isObjectSelected) {
            setUnselectedCablesOff();
        }
        else {
            if (gw.selectedSite.isCablesOff) {
                setAllBinsTextColorOn();
            }
            else {
                setAllBinsTextColorOff();
            }
        }
    }
}
async function applyDatesAsync(fromDate, toDate) {
    if (!fromDate) {
        let fd = new Date();
        fd.setDate(fd.getDate() - 5);
        fromDate = fd;
    }
    if (!toDate) {
        toDate = new Date();
    }

    let start = fromDate;
    let end = toDate;

    if (fromDate.length !== 24) {
        start = fromDate.toJSON().split("T")[0] + "T00:00:00.000Z";
        end = toDate.toJSON().split("T")[0] + "T23:59:59.999Z";
    }
    start = start.split("T")[0] + "T00:00:00.000Z";

    if (fromDate.length !== 24) {
        end = toDate.toJSON().split("T")[0] + "T23:59:59.999Z";
    }
    end = start.split("T")[0] + "T23:59:59.999Z";

    //  Get history (via api/createApiEndpoint / fetchDirect)
    try {
        const { resolveDefaultHistoryTimeOfDay } = await import('./historyTimeOfDay.js');
        const timeOfDay = await resolveDefaultHistoryTimeOfDay(gw.selectedSite?.siteGuid);
        const response = await createApiEndpoint().fetchUserSiteHistoryWithDate("direct", start, end, timeOfDay);
        if (response && response.data && response.data.getUserHistoryWithDateAndTime) {
            let dateCableReadHistories = response.data.getUserHistoryWithDateAndTime.dateCableReadHistories;
            gw.selectedSite.marks = dateCableReadHistories.map((dcr, i) => {
                let markDate = {
                    label: jsonDateToFormatDate(dcr.dateCableRead),
                    value: i + 1,
                    sensorHistories: dcr.sensorHistories,
                    dateCableRead: dcr.dateCableRead,
                };
                return markDate;
            });
        }
    } catch (e) {
        // match prior behavior: swallow errors from history load
    }
};
async function updateDateAsync(jsonDate) {
    let formattedDate = new Date(jsonDate.replace("Z", "-05:00")).toLocaleString("en-us", dateFormat).replaceAll("/", "-").replace(",", "");
    gw.selectedSite.lastScan = formattedDate;
    window.dispatchEvent(new CustomEvent("lastScanUpdated", { detail: { lastScan: formattedDate } }));
    //let dateLabel = window.document.getElementsByClassName("dateLabel")[0];
    //if (dateLabel) {
    //    dateLabel.children[0].innerHTML = `${date.substring(5, 10)}-${date.substring(2, 4)} ${date.substring(11, 16)}`;
    //}


}

const encryptMessageOutAsync = async (message, sizeRsa, sizeSignature, sizeRsaOut) => {
    let modulusExponent = gw.bolts.split("|");

    if (!modulusExponent || modulusExponent.length !== 2)
        return null;

    if (!gw.nuts) {
        gw.nuts = await generateRsaAsync(sizeRsa);
    }

    let messageOut = message + "|" + gw.nuts.exportKey("pkcs8-public-pem");
    let signatureBase64 = await signMessageAsync(messageOut, sizeSignature);
    return encryptMessage(signatureBase64, modulusExponent[0], sizeRsaOut);
};

const generateRsaAsync = async (size) => {
    let lkey = await window.crypto.subtle.generateKey(
        {
            name: "RSA-OAEP",
            modulusLength: size,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-256",
        },
        true,
        ["encrypt", "decrypt"]
    );

    let lex = await window.crypto.subtle.exportKey("pkcs8", lkey.privateKey);
    const pim = arrayBufferToBase64String(lex);
    const pem = `-----BEGIN PRIVATE KEY-----\n${pim}\n-----END PRIVATE KEY-----`;
    return new nodeRSA(pem);
};

const signMessageAsync = async (message, size) => {
    let key = await window.crypto.subtle.generateKey(
        {
            name: "RSASSA-PKCS1-v1_5",
            modulusLength: size,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-256",
        },
        true,
        ["sign", "verify"]
    );

    let enc = new TextEncoder().encode(message);
    let signature = await window.crypto.subtle.sign(
        {
            name: "RSASSA-PKCS1-v1_5",
        },
        key.privateKey,
        enc
    );

    let publicKey = await crypto.subtle.exportKey("spki", key.publicKey);
    let pub = arrayBufferToBase64String(publicKey);
    return arrayBufferToBase64String(enc) + "|" + arrayBufferToBase64String(signature) + "|" + pub;
};

const encryptMessage = (messageBase64, modulusBase64, size) => {
    let rsa = new nodeRSA({ b: size });
    rsa.importKey({
        n: Buffer.from(base64StringToArrayBuffer(modulusBase64)),
        e: 65537,
    }, 'components-public');
    return rsa.encrypt(messageBase64, "base64");
};

const decryptMessageIn = (response) => {
    let json = response.data;
    return gw.nuts.decrypt(json.value, "json");
};

const decryptDataIn = (response) => {
    let ein = response.data.value;
    let din = gw.nuts.decrypt(ein, "base64");
    let decodedBase64 = base64ToString(din.toString());
    let json = JSON.parse(decodedBase64);
    decodedBase64 = base64ToString(json.Value.value.toString());
    return JSON.parse(decodedBase64);
}

const serializeDataIn = (response) => {
    let decodedBase64 = base64ToString(response.data.value.toString());
    return JSON.parse(decodedBase64);
}

const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const getJsonDateInfo = (jsonDate, index = 0, row = 0) => {
    const date = new Date(jsonDate);
    const dateInfo = getDateInfo(date);
    dateInfo.jsonDate = jsonDate;
    return dateInfo;
}

const getDateInfo = (date) => {
    const dayOfWeek = date.getDay();
    const dayOfMonth = date.getDate();
    const month = date.getMonth();
    const monthString = date.toLocaleString('default', { month: 'long' });
    const onejan = new Date(date.getFullYear(), 0, 1);
    const dayOfYear = Math.floor((date - onejan) / (24 * 60 * 60 * 1000));
    const weekOfYear = Math.ceil((dayOfYear + onejan.getDay() + 1) / 7);
    const timeAmPm = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    return {
        index: 0,
        row: 0,
        dayOfWeek: dayOfWeek,
        monthString: monthString,
        month: month,
        dayOfMonth: dayOfMonth,
        dayOfYear: dayOfYear,
        weekOfYear: weekOfYear,
        time: timeAmPm,
        jsonDate: date.toJSON(),
        year: date.getFullYear(),
        datePart: date.toDateString(),
        date: date,
    };
}

function isPointOutsideCircle(pointX, pointY, circleX, circleY, radius) {
    let distance = Math.sqrt(
        Math.pow(pointX - circleX, 2) + Math.pow(pointY - circleY, 2)
    );
    return distance > radius;
}

function isPointYInsideMeshY(point, mesh) {
    const raycaster = new THREE.Raycaster();
    raycaster.set(point, new THREE.Vector3(0, 1, 0));
    const intersects = raycaster.intersectObject(mesh);
    return intersects.length % 2 === 1;
}

const renderSensorParticles = async (sensorRef) => {
    try {
        const particleGeometry = new THREE.BufferGeometry();

        if (!sensorRef || 
            !sensorRef.parent || 
            !sensorRef.parent.position ||
            !sensorRef.parent.parent || 
            !sensorRef.parent.parent.parent ||
            !sensorRef.parent.parent.parent.position ||
            !sensorRef.parent.parent.parent.parent ||
            !sensorRef.parent.parent.parent.parent.position) {
            return;
        }
        
        let siteWorld = new THREE.Vector3(sensorRef.parent.parent.parent.parent.position.x, sensorRef.parent.parent.parent.parent.position.y, sensorRef.parent.parent.parent.parent.position.z);
        let binWorld = new THREE.Vector3(sensorRef.parent.parent.parent.position.x, sensorRef.parent.parent.parent.position.y, sensorRef.parent.parent.parent.position.z);
        let sensorWorld = new THREE.Vector3(sensorRef.parent.position.x, sensorRef.parent.position.y, sensorRef.parent.position.z);
        let position = sensorRef.position.clone().add(sensorWorld.clone()).add(binWorld.clone().add(siteWorld.clone()));
        const particlePositions = [];
        //for (let i = 0; i < 1000; i++) {
        //    particlePositions.push(position.x + i, position.y, position.z - i);
        //}
    
        //const sphereCenter = new THREE.Vector3(position.x, position.y, position.z);
        //const sphereGeometry = new THREE.SphereGeometry(1, 1, 20);
    
        //const gposition = sphereGeometry.attributes.position;
        //const vector = new THREE.Vector3();
    
        //for (let i = 0, l = gposition.count; i < l; i++) {
        //    if (i > 0) {
        //        vector.fromBufferAttribute(gposition, i);
        //        vector.applyMatrix4(p.parent.parent.parent.parent.parent.parent.matrixWorld);
        //        vector.add(sphereCenter);
        //        if (vector.y > 0.5) {
        //            particlePositions.push(vector.x, vector.y, vector.z);
        //        }
        //    }
        //}
    
        let boundingBox = null;
        let binBoundingBox = gw.selectedSite.binBoundingBoxes.filter(x => x.binId === sensorRef.meshProps.binId);
        let binMesh = gw.selectedSite.binMeshes.filter(x => x.binId === sensorRef.meshProps.binId);
        if (binBoundingBox.length > 0) {
            boundingBox = binBoundingBox[0];
            if (sensorWorld.y > boundingBox.max.y) {
                return;
            }
        }
        else {
            boundingBox = new THREE.Box3().setFromObject(sensorRef.parent.parent.parent);
        }
        const binCenter = new THREE.Vector3();
        boundingBox.getCenter(binCenter);
        const binHeight = boundingBox.max.y;
        const radius = sensorRef.parent.parent.parent.width / 2;
        let iterations = Math.round(radius * 0.48);
        if (iterations < 18) {
            iterations = 18;
        }
    
        let positions = [];
    
        for (let s = 1; s <= iterations; s++) {
            const sphereCenter = new THREE.Vector3(position.x, position.y, position.z);
            const sphereGeometry = new THREE.SphereGeometry(s, 20 + s * 2, 20 + s * 2);
            const gposition = sphereGeometry.attributes.position;
            const vector = new THREE.Vector3();
    
            for (let i = 0, l = gposition.count; i < l; i++) {
                vector.fromBufferAttribute(gposition, i);
    
                if (vector.x === 0 &&
                    vector.z === 0) {
                    continue;
                }
    
                vector.applyMatrix4(sensorRef.parent.parent.parent.parent.parent.parent.matrixWorld);
                vector.add(sphereCenter.clone());
    
                //vector.x = Math.round(vector.x * 10) / 10;
                //vector.y = Math.round(vector.y * 10) / 10;
                //vector.z = Math.round(vector.z * 10) / 10;
    
                if (vector.y > -0.5 && vector.y < binHeight) {
                    let isOutsideCircle = isPointOutsideCircle(vector.x, vector.z, binCenter.x, binCenter.z, radius);
                    if (!isOutsideCircle) {
                        //if (binMesh.length > 0) {
                        //    //isPointYInsideMeshY(vector, binMesh[0])
                        //    raycaster.set(vector, new THREE.Vector3(0, 1, 0));
                        //    const intersects = raycaster.intersectObject(binMesh[0]);
    
                        //    if (intersects.length > 0) {
                        //        //let binXZ = `${vector.x}${vector.z}`;
                        //        //if (!binXZs.includes(binXZ)) {
                        //        //binXZs.push(binXZ);
                        //        particlePositions.push(vector.x, vector.y, vector.z);
                        //        //positions.push({ x: vector.x, y: vector.y, z: vector.z });
                        //        //}
                        //    }
                        //}
                        //else {
                        particlePositions.push(vector.x, vector.y, vector.z);
                        //}
    
                        //positions.push({ x: vector.x, y: vector.y, z: vector.z });
                    }
                }
            }
        }
    
        particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(particlePositions, 3));
        const particleMaterial = new THREE.PointsMaterial({
            color: sensorRef.meshProps.material.color,
            //emissiveIntensity: 0.25,
            //intensity: 1,
            //emissive: p.meshProps.material.color,
            size: 0.05,
            transparent: true,
            opacity: 0.1,
            //opacity: 1,
            //map: smokeTexture,
            //side: THREE.DoubleSide,
        });
        const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
        particleSystem.isParticle = true;
        //return particleSystem;
        //sensorRef.parent.parent.parent.parent.parent.parent.add(particleSystem);
    
        //setTimeout(() => {
        gw.selectedSite.sceneRef.add(particleSystem);
        //}, 1);
    
    
        //let geometry = new THREE.BufferGeometry().setFromPoints(positions);
        //const delaunay = Delaunator.from(positions.map(p => [p.x, p.z]));
        //const indices = [];
        //for (let i = 0; i < delaunay.triangles.length; i += 3) {
        //    indices.push(delaunay.triangles[i], delaunay.triangles[i + 1], delaunay.triangles[i + 2]);
        //}
        //geometry.setIndex(indices);
        //geometry.computeVertexNormals();
        //let mesh = new THREE.Mesh(geometry, particleMaterial);
        //mesh.isParticle = true;
        //sensorRef.parent.parent.parent.parent.parent.parent.add(mesh);
    
        //let binCsg = binCsgs[0];
        //mesh.updateMatrix();
        //const pointsCSG = CSG.fromMesh(mesh);
        //const resultCSG = pointsCSG.intersect(binCsg);
        //const resultMesh = CSG.toMesh(resultCSG, binCsg.matrix);
        //resultMesh.material = particleMaterial;
        //resultMesh.isParticle = true;
        //sensorRef.parent.parent.parent.parent.parent.parent.add(resultMesh);
    } catch (error) {
        
    }
};

const processPoints = (points, radius, binGrainLevelPositions) => {
    if (!points || points.length < 1) {
        return;
    }

    for (let p = 0; p < points.length; p++) {
        let thisPoint = points[p];
        points[p] = new THREE.Vector3(thisPoint.x, thisPoint.y, thisPoint.z);
    }

    for (let p = 0; p < points.length; p++) {
        let thisPoint = points[p];
        for (let op = 0; op < points.length; op++) {
            if (op === p) {
                continue;
            }

            let otherPoint = points[op];
            let extendedPointAB = extendLine(thisPoint, otherPoint, radius);
            let extendedPointBA = extendLine(otherPoint, thisPoint, radius);

            binGrainLevelPositions.push(new THREE.Vector3(extendedPointAB.x, extendedPointAB.y, extendedPointAB.z));
            binGrainLevelPositions.push(new THREE.Vector3(extendedPointBA.x, extendedPointBA.y, extendedPointBA.z));
        }
    }
};

const renderBinGrainLevel = async (grainLevelPositions, binRef) => {
    const boundingBox = new THREE.Box3().setFromObject(binRef);
    const binCenter = new THREE.Vector3();
    const binHeight = boundingBox.max.y;
    boundingBox.getCenter(binCenter);

    if (!binRef ||
        !binRef.position ||
        !binRef.parent ||
        !binRef.parent.position) {
        return;
    }

    let siteWorld = new THREE.Vector3(binRef.parent.position.x, binRef.parent.position.y, binRef.parent.position.z);
    let binWorld = new THREE.Vector3(binRef.position.x, binRef.position.y, binRef.position.z);

    let lengthFromCenters = [];
    let layerIndex = -1;
    let currentLayer = null;

    let sumY = 0;
    let countY = 0;

    grainLevelPositions.filter(p => p.binId === binRef.layoutBin.binId).map((g, i) => {
        if (g.position.y >= 1) {
            sumY += g.position.y;
            countY++;
        }
    });
    let averageY = 0;
    if (countY > 0) {
        averageY = sumY / countY;
    }

    let binGrainLevelPositions = grainLevelPositions.filter(p => p.binId === binRef.layoutBin.binId).map((g, i) => {
        if (binRef.layoutBin.binName === "3") {
            let testheresssdf = 0;
        }

        if (g.position.y < 1) {
            g.position.y = averageY;
        }

        let lengthFromCenter = Math.round((getPointToPointLength(0, 0, g.position.x, g.position.z) * 10)) / 10;
        let angle = Math.round((getAngleRadiansBetweenPoints(0, 0, g.position.x, g.position.z) * 180 / Math.PI * 10)) / 10;
        angle = ((angle % 360) + 360) % 360;
        if (lengthFromCenter !== currentLayer) {
            currentLayer = lengthFromCenter;
            layerIndex++;
        }

        let cableName = null;
        try {
            cableName = gw.selectedSite.cables.filter(x => x.id === g.cableId)[0].name
        } catch {

        }

        let position = g.position.clone().add(binWorld.clone().add(siteWorld.clone()));

        lengthFromCenters.push({
            cableId: g.cableId,
            cableName: cableName,
            lengthFromCenter: lengthFromCenter,
            angle: angle,
            layerIndex: layerIndex,
            layerPoint: position,
        });

        return position;
        //}
    });

    //  Get outer edge points
    //  Figure out layers in 2d (x, z)
    //  Center point will be (0, 0)
    let outerLayerPoints = lengthFromCenters.filter(x => x.layerIndex === layerIndex);
    let beforeOuterLayerPoints = lengthFromCenters.filter(x => x.layerIndex === layerIndex - 1);
    let withinAngle = 0;
    if (beforeOuterLayerPoints.length === 0) {
        //  One Layer
    }
    else {
        withinAngle = 360 / beforeOuterLayerPoints.length;
    }

    let radius = binRef.width / 2.0;
    let isThreePoint = false;

    for (let i = 0; i < outerLayerPoints.length; i++) {
        let outerLayerPoint = outerLayerPoints[i];
        let nearstLayerPoints = beforeOuterLayerPoints.filter(x =>
            x.angle <= outerLayerPoint.angle + withinAngle &&
            x.angle >= outerLayerPoint.angle - withinAngle);

        if (nearstLayerPoints.length === 0 && outerLayerPoints.length === 1 && outerLayerPoint.layerPoint.y > 5) {

        }
        else if (nearstLayerPoints.length === 0 && outerLayerPoints.length === 3) {
            isThreePoint = true;
            processPoints(outerLayerPoints.map(p => { return p.layerPoint }), radius, binGrainLevelPositions);
        }
        else if (nearstLayerPoints.length + outerLayerPoints.length === 3) {
            isThreePoint = true;
            processPoints(outerLayerPoints.concat(nearstLayerPoints).map(p => { return p.layerPoint }), binRef.width + 5, binGrainLevelPositions);
        }
        //else if (nearstLayerPoints.length === 1 && outerLayerPoints.length === 3) {
        //    isThreePoint = true;
        //    processPoints(outerLayerPoints.concat(nearstLayerPoints).map(p => { return p.layerPoint }), b.width + 5, binGrainLevelPositions);
        //}
        else {
            //let closestXzPoint = getClosestPointToPoints(outerLayerPoint.layerPoint.x, outerLayerPoint.layerPoint.z, beforeOuterLayerPoints.map(p => { return { x: p.layerPoint.x, y: p.layerPoint.z } }));
            //let closestPoints = beforeOuterLayerPoints.filter(x => x.layerPoint.x === closestXzPoint.x && x.layerPoint.z === closestXzPoint.y);
            //if (closestPoints && closestPoints.length > 0) {
            //    let point = closestPoints[0];
            //    let lineStart = new THREE.Vector3(point.layerPoint.x, point.layerPoint.y, point.layerPoint.z);
            //    let lineEnd = new THREE.Vector3(outerLayerPoint.layerPoint.x, outerLayerPoint.layerPoint.y, outerLayerPoint.layerPoint.z);
            //    let lineExtended = extendLine(lineStart, lineEnd, radius);

            //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
            //    let mesh = new THREE.Mesh(pointGeometry, material);
            //    mesh.isGrainLight = true;
            //    mesh.position.set(lineExtended.x, lineExtended.y, lineExtended.z);
            //    b.parent.parent.parent.add(mesh);

            //    const line = new THREE.Line3(lineEnd, lineExtended);
            //    const cylinder = {
            //        axis: new THREE.Vector3(0, 1, 0),
            //        center: new THREE.Vector3(b.position.x, b.position.y, b.position.z),
            //        radius: radius,
            //        height: b.height * 2.0,
            //    };
            //    //new THREE.CylinderGeometry(radius, radius, b.height * 2.0, 32); 
            //    const lineCylinderIntersect = getLineCylinderIntersection(line, cylinder);

            //    let asdfdsafadsfasdf = 0;
            //}

            //  calculate end points
            for (var n = 0; n < nearstLayerPoints.length; n++) {
                let point = nearstLayerPoints[n];


                let lineStart = new THREE.Vector3(point.layerPoint.x, point.layerPoint.y, point.layerPoint.z);
                //if (lineStart) {
                //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                //    let mesh = new THREE.Mesh(pointGeometry, material);
                //    mesh.isGrainLight = true;
                //    mesh.position.set(lineStart.x, lineStart.y, lineStart.z);
                //    b.parent.parent.parent.add(mesh);
                //}

                let lineEnd = new THREE.Vector3(outerLayerPoint.layerPoint.x, outerLayerPoint.layerPoint.y, outerLayerPoint.layerPoint.z);
                //if (lineEnd) {
                //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                //    let mesh = new THREE.Mesh(pointGeometry, material);
                //    mesh.isGrainLight = true;
                //    mesh.position.set(lineEnd.x, lineEnd.y, lineEnd.z);
                //    b.parent.parent.parent.add(mesh);
                //}

                let lineExtended = extendLine(lineStart, lineEnd, binRef.width + 5);
                //if (lineExtended) {
                //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                //    let mesh = new THREE.Mesh(pointGeometry, material);
                //    mesh.isGrainLight = true;
                //    mesh.position.set(lineExtended.x, lineExtended.y, lineExtended.z);
                //    b.parent.parent.parent.add(mesh);
                //}

                //if (lineExtended && lineExtended.y >= -5) {
                //let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                //let mesh = new THREE.Mesh(pointGeometry, material);
                //mesh.isGrainLight = true;
                //mesh.position.set(intersect.x, intersect.y, intersect.z);
                //b.parent.parent.parent.add(mesh);
                if (lineExtended.y >= outerLayerPoints[0].layerPoint.y + 1.5) {
                    //lineExtended.y = outerLayerPoints[0].layerPoint.y + 5;
                }


                binGrainLevelPositions.push(new THREE.Vector3(lineExtended.x, lineExtended.y, lineExtended.z));
                //}

                //let direction = new THREE.Vector3().subVectors(lineEnd, lineExtended).normalize()
                //let intersect = rayCylinderIntersection(lineExtended, direction, binCenter, radius + 10, b.height + 5);
                //if (intersect && intersect.y >= -5) {
                //    //let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                //    //let mesh = new THREE.Mesh(pointGeometry, material);
                //    //mesh.isGrainLight = true;
                //    //mesh.position.set(intersect.x, intersect.y, intersect.z);
                //    //b.parent.parent.parent.add(mesh);
                //    binGrainLevelPositions.push(new THREE.Vector3(intersect.x, intersect.y, intersect.z));
                //}

                //const cylinder = {
                //    axis: new THREE.Vector3(0, 1, 0),
                //    center: binCenter,
                //    radius: radius,
                //    height: b.height / 2.0,
                //};
                //if (cylinder) {
                //    const cylinderGeometry = new THREE.CylinderGeometry(cylinder.radius, cylinder.radius, cylinder.height, 32);
                //    const cylinderMaterial = new THREE.MeshStandardMaterial({
                //        color: "rgb(235, 211, 124)",
                //        emissive: "rgb(0, 0, 0)",
                //        emissiveIntensity: 32,
                //        intensity: 5,
                //        toneMapped: false,
                //        envMapIntensity: 1,
                //        transparent: true,
                //        opacity: 0.15,
                //        depthTest: true,
                //        depthWrite: true,
                //        side: THREE.DoubleSide,
                //        roughness: 1,
                //        metalness: 0.1,
                //        fog: true,
                //        wireframe: true,
                //        //normalMap: normalMapTexture,
                //    });
                //    const cylinderMesh = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
                //    cylinderMesh.isGrainLight = true;
                //    cylinderMesh.position.set(cylinder.center.x, cylinder.center.y, cylinder.center.z);
                //    b.parent.parent.parent.add(cylinderMesh);
                //}

                let teasdfsi = 0;
            }

            let testdsfsds = 0;
        }
        let testdsfs = 0;
    }

    let geometry = null;
    let mesh = null;

    if (outerLayerPoints.length === 0) {
        // do nothing
    }
    else if (outerLayerPoints.length === 1) {
        if (outerLayerPoints[0].layerPoint.y > 2) {
            geometry = new THREE.CylinderGeometry(radius, radius, outerLayerPoints[0].layerPoint.y + 1.5, 40);
            mesh = new THREE.Mesh(geometry, grainLevelMaterial);
            //mesh.setRotationFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
            if (beforeOuterLayerPoints.length === 0) {
                mesh.position.set(binCenter.x, outerLayerPoints[0].layerPoint.y / 2 - 0.75, binCenter.z);
            }
            else {
                mesh.position.set(outerLayerPoints[0].layerPoint.x, outerLayerPoints[0].layerPoint.y / 2 - 0.75, outerLayerPoints[0].layerPoint.z);
            }
            mesh.binId = binRef.layoutBin.binId;
            //b.children.push(mesh);
            mesh.updateMatrix();

            gw.selectedSite.binMeshes.push(mesh);
            let bb = new THREE.Box3().setFromObject(mesh);
            bb.binId = binRef.layoutBin.binId;
            gw.selectedSite.binBoundingBoxes.push(bb);
            //binCsgs.push(CSG.fromMesh(mesh));
        }
    }
    else {
        const cylinderGeometry = new THREE.CylinderGeometry(radius, radius, binHeight, 40, 40);
        let shapeMesh = new THREE.Mesh(cylinderGeometry, grainLevelMaterial);
        shapeMesh.position.set(binCenter.x, binHeight / 2 - 1.5, binCenter.z);
        //b.children.push(mesh);
        //mesh = shapeMesh;
        //b.children.push(mesh);

        geometry = new THREE.BufferGeometry().setFromPoints(binGrainLevelPositions);
        const delaunay = Delaunator.from(binGrainLevelPositions.map(p => [p.x, p.z]));
        const indices = [];
        for (let i = 0; i < delaunay.triangles.length; i += 3) {
            indices.push(delaunay.triangles[i], delaunay.triangles[i + 1], delaunay.triangles[i + 2]);
        }
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        mesh = new THREE.Mesh(geometry, grainLevelMaterial);

        mesh.updateMatrix();
        shapeMesh.updateMatrix();

        let bspB = CSG.fromMesh(mesh)
        let bspA = CSG.fromMesh(shapeMesh)
        const intRes = bspA.intersect(bspB);
        let meshResult = CSG.toMesh(intRes, mesh.matrix, grainLevelMaterial)
        meshResult.binId = binRef.layoutBin.binId;
        //b.children.push(meshResult);
        meshResult.updateMatrix();
        gw.selectedSite.binMeshes.push(meshResult);
        let bb = new THREE.Box3().setFromObject(meshResult);
        bb.binId = binRef.layoutBin.binId;
        gw.selectedSite.binBoundingBoxes.push(bb);
        //binCsgs.push(CSG.fromMesh(meshResult));
        //let subRes = bspA.subtract(bspB);
        //const shapeMaterial = new THREE.MeshStandardMaterial({
        //    color: "rgb(255, 255, 255)",
        //    emissive: "rgb(0, 0, 0)",
        //    emissiveIntensity: 32,
        //    intensity: 0.9,
        //    toneMapped: false,
        //    envMapIntensity: 1,
        //    transparent: true,
        //    opacity: 0.0015,
        //    depthTest: true,
        //    depthWrite: true,
        //    side: THREE.DoubleSide,
        //    roughness: 0.8,
        //    metalness: 0.4,
        //    //fog: true,
        //    //wireframe: false,
        //    //normalMap: normalMapTexture,
        //});
        //meshResult = CSG.toMesh(subRes, mesh.matrix, shapeMaterial);
        //b.children.push(meshResult);
    }
};

const renderGrainLevels = (grainLevelPositions) => {
    if (grainLevelPositions &&
        gw.selectedSite.binsRef.length > 0 &&
        grainLevelPositions.length > 0) {
        gw.selectedSite.binsRef.map(b => {
            if (b.layoutBin.binId) {
                renderBinGrainLevel(grainLevelPositions, b);
            }
        });

        let testsenesoro = 0;
    }
    else {
        gw.selectedSite.binsRef.map(b => {
            for (let i = 0; i < b.children.length; i++) {
                let child = b.children[i];
                if (b.children[i].isMesh) {
                    b.remove(child);
                    i--;
                }
            }

            if (!b.parent ||
                !b.parent.parent ||
                !b.parent.parent.parent
            ) {
                return;
            }

            for (let i = 0; i < b.parent.parent.parent.children.length; i++) {
                let child = b.parent.parent.parent.children[i];
                if (child.isGrainLight) {
                    b.parent.parent.parent.remove(child);
                    i--;
                }
                //else if (child.isParticle) {
                //    b.parent.parent.parent.remove(child);
                //    i--;
                //}
            }
        });
    }
};

const getCarbondioxideColor = (carbondioxide, criticalLimit, highLimit, moderateLimit, warningLimit) => {
    if ((!criticalLimit && carbondioxide >= 5000) || carbondioxide >= criticalLimit) {
        return "#d32f2f";
    }
    else if ((!highLimit && carbondioxide >= 2000) || carbondioxide >= highLimit) {
        return "#ff4500";
    }
    else if ((!moderateLimit && carbondioxide >= 1000) || carbondioxide >= moderateLimit) {
        return "#ffa500";
    }
    else if ((!warningLimit && carbondioxide >= 600) || carbondioxide >= warningLimit) {
        return "#ffff00";
    }
    else {
        return "rgb(112, 255, 0)";
    }
};

const groupByMultiple = (array, ...properties) => {
    let groupedData = array.reduce((accumulator, object) => {
        const key = properties.map(property => object[property]).join('-');
        accumulator[key] = accumulator[key] || [];
        accumulator[key].push(object);
        return accumulator;
    }, {});
    return Object.values(groupedData);
}

const setAllSitesWeatherStationReadings = async () => {
    // Prevent concurrent calls - if one is already in progress, skip this call
    if (gw.isWeatherStationCallInProgress) {
        // console.log('setAllSitesWeatherStationReadings: Call already in progress, skipping duplicate call');
        return;
    }
    
    try {
    // Set flag to prevent concurrent calls
    gw.isWeatherStationCallInProgress = true;
    
    // Cancel any existing call
    if (gw.weatherStationAbortController) {
        gw.weatherStationAbortController.abort();
    }
    // Create new AbortController for this call
    gw.weatherStationAbortController = new AbortController();
    
    let response = await createApiEndpoint().fetchAllSitesCurrentWeatherStationReadings("direct", gw.weatherStationAbortController.signal);
    if (response && response.data && response.data.getAllSitesCurrentWeatherStationReadings) {
        let arrayBuffer = base64StringToArrayBuffer(response.data.getAllSitesCurrentWeatherStationReadings);
        let decompressed = await decompress(arrayBuffer, "gzip");
        let wsr = JSON.parse(decompressed);

        if (wsr && wsr.length > 0) {
            // Normalize property names - API/database may return PascalCase (SiteId, BinId)
            gw.currentWeatherReadings = wsr.map(r => ({
                ...r,
                siteId: r.siteId ?? r.SiteId ?? r.siteGuid ?? r.SiteGuid,
                binId: r.binId ?? r.BinId,
                CarbonDioxide: r.CarbonDioxide ?? r.carbonDioxide
            }));
            // Notify React components (e.g. CustomerSites) so CO2 site cards re-render with data
            if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('grainwave-weather-readings-updated'));
            }

            let siteBinWeatherStationReadings = groupByMultiple(gw.currentWeatherReadings, "siteId", "binId");
            for (let i = 0; i < siteBinWeatherStationReadings.length; i++) {
                let binWeatherStationReadings = siteBinWeatherStationReadings[i];
                let showCarbonDioxide = window.document.querySelectorAll(`[siteId='${binWeatherStationReadings[0].siteId}'].showCarbonDioxide `);
                if (showCarbonDioxide && showCarbonDioxide.length > 0 && showCarbonDioxide[0].style.color === "rgb(244, 204, 142)") {
                    if (binWeatherStationReadings.length > 0) {
                        const carbondioxideBins = window.document.querySelectorAll(`[siteId='${binWeatherStationReadings[0].siteId}'][binId='${binWeatherStationReadings[0].binId}'] .binReading`);
                        if (carbondioxideBins && carbondioxideBins.length > 0) {
                            let mappedNumbers = binWeatherStationReadings.map(x => +x.CarbonDioxide);
                            let maxCarbonDioxide = Math.max.apply(Math, mappedNumbers);
                            let carbondioxideBin = carbondioxideBins[0];
                            carbondioxideBin.textContent = maxCarbonDioxide;
                            carbondioxideBin.style.color = getCarbondioxideColor(maxCarbonDioxide);
                            let maxBinWeatherStationReading = binWeatherStationReadings.filter(x => x.CarbonDioxide === maxCarbonDioxide);
                            if (maxBinWeatherStationReading && maxBinWeatherStationReading.length > 0) {
                                carbondioxideBin.style.color = getCarbondioxideColor(maxCarbonDioxide, maxBinWeatherStationReading[0].CriticalLimit, maxBinWeatherStationReading[0].HighLimit, maxBinWeatherStationReading[0].ModerateLimit, maxBinWeatherStationReading[0].WarningLimit);
                                if (maxBinWeatherStationReading[0].CarbonDioxide - maxBinWeatherStationReading[0].CarbonDioxideAtRiseStart >= maxBinWeatherStationReading[0].CarbonDioxideRise) {
                                    carbondioxideBin.nextElementSibling.style.display = "inline-block";
                                }
                                else {
                                    carbondioxideBin.nextElementSibling.style.display = "none";
                                }
                            }
                        }
                    }
                }
            }

            for (let di = 0; di < gw.dashboardIntervals.length; di++) {
                clearInterval(gw.dashboardIntervals[di]);
            }
            const allCarbonDioxideBoxes = window.document.getElementsByClassName(`dashboardBinCo2`);
            if (allCarbonDioxideBoxes && allCarbonDioxideBoxes.length > 0) {
                for (let i = 0; i < allCarbonDioxideBoxes.length; i++) {
                    allCarbonDioxideBoxes[i].style.visibility = "visible";
                    allCarbonDioxideBoxes[i].parentElement.style.borderColor = "transparent";
                }
            }

            let now = new Date();
            for (let i = 0; i < wsr.length; i++) {
                let ws = wsr[i];
                const cabondioxideBoxes = window.document.querySelectorAll(`[siteId='${ws.siteId}'][binId='${ws.binId}'] .dashboardBinCo2[deviceId='${ws.DeviceId}']`);
                if (cabondioxideBoxes.length > 0) {
                    let carbondioxideBox = cabondioxideBoxes[0];
                    carbondioxideBox.textContent = ws.CarbonDioxide;
                    carbondioxideBox.style.color = getCarbondioxideColor(ws.CarbonDioxide, ws.CriticalLimit, ws.HighLimit, ws.ModerateLimit, ws.WarningLimit);
                    carbondioxideBox.parentElement.title = `${ws.DeviceId}: ${jsonDateToFormatDate(ws.DateRead)}`;

                    let holdTooltipLastReadingBox = carbondioxideBox.parentElement.firstChild.firstChild.childNodes[1];
                    if (holdTooltipLastReadingBox) {
                        holdTooltipLastReadingBox.textContent = `Last Reading: ${jsonDateToFormatDate(ws.DateRead)}`;
                    }

                    let dateRead = new Date(ws.DateRead);

                    if (ws.CarbonDioxide - ws.CarbonDioxideAtRiseStart >= ws.CarbonDioxideRise) {
                        carbondioxideBox.nextElementSibling.style.display = "inline-flex";
                        carbondioxideBox.parentElement.style.marginLeft = "20px";
                    }
                    else {
                        carbondioxideBox.nextElementSibling.style.display = "none";
                        carbondioxideBox.parentElement.style.marginLeft = "0px";
                    }

                    dateRead.setHours(dateRead.getHours() + 2);
                    if (dateRead < now) {
                        gw.dashboardIntervals.push(setInterval(() => {
                            carbondioxideBox.parentElement.style.borderColor = (carbondioxideBox.parentElement.style.borderColor === 'transparent') ? 'red' : 'transparent';
                        }, 1000));
                    }
                    else {
                        let icount = 0;
                        let interval = setInterval(() => {
                            icount++;
                            if (icount === 10) {
                                clearInterval(interval);
                            }
                            carbondioxideBox.style.visibility = (carbondioxideBox.style.visibility === 'hidden') ? 'visible' : 'hidden';
                        }, 100);
                    }
                }
            }
        }
    }
    else {
        //console.warn('setAllSitesWeatherStationReadings: No weather station data available');
        // Set empty array to prevent crashes and infinite loading
        gw.currentWeatherReadings = [];
    }
    
    // Reset flag after successful completion
    gw.isWeatherStationCallInProgress = false;
    } catch (error) {
        // Reset flag in all error cases
        gw.isWeatherStationCallInProgress = false;
        
        // Ignore AbortError - request was intentionally cancelled
        if (error.name === 'AbortError' || error.name === 'CanceledError' || (error.message && error.message.includes('canceled'))) {
            return; // Silently return when cancelled
        }
        console.error('setAllSitesWeatherStationReadings: Error fetching weather station data:', error);
        
        // If it's a 401 error, clear the interval and let the axios interceptor handle redirect
        if (error.response && error.response.status === 401) {
            // console.log('setAllSitesWeatherStationReadings: 401 error detected, clearing interval');
            if (gw.dashboardInterval) {
                clearInterval(gw.dashboardInterval);
                gw.dashboardInterval = null;
            }
            // Clear all dashboard intervals
            for (let di = 0; di < gw.dashboardIntervals.length; di++) {
                clearInterval(gw.dashboardIntervals[di]);
            }
            gw.dashboardIntervals = [];
            // Reset flag before re-throwing
            gw.isWeatherStationCallInProgress = false;
            // Re-throw so the axios interceptor can handle it
            throw error;
        }
        // Also check for error message containing "401"
        if (error.message && (error.message.includes("401") || error.message.includes("Unauthorized"))) {
            // console.log('setAllSitesWeatherStationReadings: Unauthorized error detected, clearing interval');
            if (gw.dashboardInterval) {
                clearInterval(gw.dashboardInterval);
                gw.dashboardInterval = null;
            }
            // Clear all dashboard intervals
            for (let di = 0; di < gw.dashboardIntervals.length; di++) {
                clearInterval(gw.dashboardIntervals[di]);
            }
            gw.dashboardIntervals = [];
            // Reset flag before re-throwing
            gw.isWeatherStationCallInProgress = false;
            // Create a proper error object with 401 status
            const authError = new Error(error.message);
            authError.response = { status: 401 };
            throw authError; // Re-throw so the interceptor can handle it
        }
        
        // Set empty array to prevent crashes
        gw.currentWeatherReadings = [];
        // Reset flag for other errors
        gw.isWeatherStationCallInProgress = false;
    }
};

/** @param {object|null|undefined} row BinLevel snapshot row */
const binLevelFillRatioFromRow = (row) => {
    if (!row) return null;
    const productHeight = parseFloat(row.ProductHeight ?? row.productHeight);
    const headroomHeight = parseFloat(row.HeadroomHeight ?? row.headroomHeight);
    const percentFull = parseFloat(row.PercentFull ?? row.percentFull);
    let fillRatio = NaN;
    if (Number.isFinite(productHeight) && Number.isFinite(headroomHeight) &&
        (productHeight + headroomHeight) > 0) {
        fillRatio = productHeight / (productHeight + headroomHeight);
    } else if (Number.isFinite(percentFull)) {
        fillRatio = Math.max(0, Math.min(100, percentFull)) / 100;
    }
    if (!Number.isFinite(fillRatio) || fillRatio <= 0) return null;
    return Math.min(1, fillRatio);
};

const findBinLevelRowForCardBin = (rows, b) => {
    if (!Array.isArray(rows) || !b) return null;
    const ridOf = (row) => row?.BinId ?? row?.binId;
    const matchesBid = (rid, bid) => {
        if (rid === undefined || rid === null || bid === undefined || bid === null) return false;
        const ridNum = parseInt(rid, 10);
        const bn = typeof bid === "number" ? bid : parseInt(String(bid), 10);
        if (!Number.isNaN(ridNum) && !Number.isNaN(bn) && ridNum === bn) return true;
        return String(rid) === String(bid);
    };
    const findWithId = (id) => {
        if (id === undefined || id === null) return null;
        for (const row of rows) {
            if (matchesBid(ridOf(row), id)) return row;
        }
        return null;
    };
    // Prefer BTS `Bin.Id` from layout: `binDataId`, then `binId`. Avoid `number` when either is set — it is often a display ordinal and can match a different bin's `BinId` (e.g. bin with no level between two that have levels).
    if (b.binDataId != null && b.binDataId !== "") {
        const hit = findWithId(b.binDataId);
        if (hit) return hit;
    }
    if (b.binId != null && b.binId !== "" && b.binId !== b.binDataId) {
        const hit = findWithId(b.binId);
        if (hit) return hit;
    }
    if ((b.binDataId == null || b.binDataId === "") && (b.binId == null || b.binId === "") && b.number != null && b.number !== "") {
        return findWithId(b.number);
    }
    return null;
};

/** Fill ratio (0–1) for a dashboard bin tile, or `null` if unknown / empty. */
const getCardBinLevelFillRatio = (rows, b) => binLevelFillRatioFromRow(findBinLevelRowForCardBin(rows, b));

/**
 * Latest `BinLevel` snapshot rows for a site (no date range). Does not update `gw.selectedSite.binLevels`.
 */
const fetchBinLevelSnapshotRowsForSiteGuid = async (siteGuid) => {
    if (!siteGuid || !gw?.email || !gw?.bolts) return [];
    try {
        const response = await createApiEndpoint().fetchSiteBinLevels("direct", {}, siteGuid);
        const payload = response?.data?.getSiteBinLevels;
        if (payload && typeof payload === "string" && payload.length > 0) {
            const arrayBuffer = base64StringToArrayBuffer(payload);
            const decompressed = await decompress(arrayBuffer, "gzip");
            const parsed = JSON.parse(decompressed);
            if (Array.isArray(parsed)) {
                const looksLikeBinRow = (r) => r && (r.BinId !== undefined || r.binId !== undefined);
                return parsed.filter(looksLikeBinRow);
            }
        }
        return [];
    } catch (e) {
        console.warn("fetchBinLevelSnapshotRowsForSiteGuid: failed", e);
        return [];
    }
};

/**
 * Loads the latest BinLevel snapshot for the selected site into `gw.selectedSite.binLevels`.
 *
 * - Pass `binId` to fetch one bin (BinWindow) — the existing rows for other bins are kept and the
 *   row for `binId` is replaced. Pass nothing to fetch all bins (Site3dWindow), which replaces the
 *   array wholesale.
 * - Always sends an empty date window so the SP reads from the latest-snapshot `BinLevel` table
 *   (not `BinLevelHistory`); use `loadSiteBinLevelHistory` for ranged data driving the slider.
 * - Returns the parsed rows so callers can react synchronously; on failure returns an empty array
 *   and leaves any previously cached snapshot intact.
 */
const setSiteBinLevels = async (binId) => {
    if (!gw?.selectedSite?.siteGuid) return [];

    const json = {};
    if (binId !== undefined && binId !== null && binId !== 0 && binId !== "0") {
        json.binId = String(binId);
    }

    try {
        const response = await createApiEndpoint().fetchSiteBinLevels("direct", json);
        const payload = response?.data?.getSiteBinLevels;
        let rows = [];
        if (payload && typeof payload === "string" && payload.length > 0) {
            const arrayBuffer = base64StringToArrayBuffer(payload);
            const decompressed = await decompress(arrayBuffer, "gzip");
            const parsed = JSON.parse(decompressed);
            if (Array.isArray(parsed)) {
                // SP returns a single { HasData: 0|1 } row when the table/window is empty -- ignore those.
                const looksLikeBinRow = (r) => r && (r.BinId !== undefined || r.binId !== undefined);
                rows = parsed.filter(looksLikeBinRow);
            }
        }

        if (!gw.selectedSite) return rows;

        if (json.binId) {
            // Single-bin merge: keep snapshot rows for other bins (e.g. populated earlier by Site3dWindow).
            const targetBinId = parseInt(json.binId, 10);
            const existing = Array.isArray(gw.selectedSite.binLevels) ? gw.selectedSite.binLevels : [];
            const filtered = existing.filter(r => {
                const rowBinId = parseInt(r.BinId ?? r.binId, 10);
                return !Number.isNaN(rowBinId) && rowBinId !== targetBinId;
            });
            gw.selectedSite.binLevels = filtered.concat(rows);
        } else {
            gw.selectedSite.binLevels = rows;
        }

        if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent("grainwave-bin-levels-updated"));
        }
        return rows;
    } catch (error) {
        // Treat fetch failure as "no data" rather than poisoning the cache; auth errors will be handled
        // by the axios interceptor that wraps fetchSwitch.
        console.warn("setSiteBinLevels: failed to load bin levels", error);
        return [];
    }
};

/** Title case for `CurrentContents` (e.g. `CORN` → `Corn`, `no. 2 yellow` → `No. 2 Yellow`). */
const formatBinLevelContentsDisplay = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    return s.split(/\s+/).map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(" ");
};

const clearBinLevelLabelHover = () => {
    const site = gw?.selectedSite;
    const prev = site?._binLevelHoveredSprite;
    if (prev?.userData?.binLevelLabelBaseScaleX != null) {
        prev.scale.set(prev.userData.binLevelLabelBaseScaleX, prev.userData.binLevelLabelBaseScaleY, 1);
    }
    if (site) site._binLevelHoveredSprite = null;
};

const _binLevelLabelRaycaster = new THREE.Raycaster();
const _binLevelLabelPointer = new THREE.Vector2();
const BIN_LEVEL_LABEL_HOVER_MULT = 2.15;

const updateBinLevelLabelHover = (clientX, clientY) => {
    const site = gw?.selectedSite;
    if (!site?.orbitControls?.object) return;
    const sprites = site.binLevelPercentSprites;
    if (!Array.isArray(sprites) || sprites.length === 0) return;

    const object = site.canvasRef;
    if (!object?.getBoundingClientRect) return;
    const rect = object.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    _binLevelLabelPointer.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        ((rect.top - clientY) / rect.height) * 2 + 1,
    );
    _binLevelLabelRaycaster.setFromCamera(_binLevelLabelPointer, site.orbitControls.object);

    const hits = _binLevelLabelRaycaster.intersectObjects(sprites, false);
    const next = (hits.find(h => h.object?.isBinLevelPercentSprite)?.object) ?? null;

    if (site._binLevelHoveredSprite !== next) {
        clearBinLevelLabelHover();
        site._binLevelHoveredSprite = next;
        if (next?.userData?.binLevelLabelBaseScaleX != null) {
            const m = BIN_LEVEL_LABEL_HOVER_MULT;
            next.scale.set(
                next.userData.binLevelLabelBaseScaleX * m,
                next.userData.binLevelLabelBaseScaleY * m,
                1,
            );
        }
    }
};

const _cableNameRaycaster = new THREE.Raycaster();
const _cableNamePointer = new THREE.Vector2();
const _cableNameBox = new THREE.Box3();
const _cableNameHit = new THREE.Vector3();
let _cableLastReadTip = null;

function sameSensorId(a, b) {
    if (a == null || b == null) return false;
    if (a === b || String(a) === String(b)) return true;
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

function sensorIsHeltec(sensor) {
    if (!sensor) return false;
    if (sensor.heltecLastRead) return true;
    const mappings = gw.selectedSite?.heltecSensorMappings;
    if (!mappings?.length) return false;
    return mappings.some((mapping) =>
        sameSensorId(mapping.sensorId, sensor.sensorId) ||
        sameSensorId(mapping.sensorId, sensor.id) ||
        sameSensorId(mapping.sensorId, sensor.customerSiteLayoutSensorId)
    );
}

function considerCableRead(best, value) {
    if (value == null || value === '') return best;
    let ms;
    if (typeof value === 'number' && Number.isFinite(value)) {
        ms = value;
    } else {
        const raw = String(value).trim();
        if (/^\d{12,}$/.test(raw)) {
            ms = Number(raw);
        } else {
            const normalized = raw.includes('T') ? raw.replace(/Z$/i, '-05:00') : raw;
            ms = Date.parse(normalized);
        }
    }
    if (!Number.isFinite(ms) || ms <= best.ms) return best;
    return { ms, value };
}

function formatCableRead(best) {
    if (!Number.isFinite(best.ms)) return null;
    if (typeof best.value === 'string' && !/^\d{12,}$/.test(String(best.value).trim())) {
        const formatted = formatLastScanDisplay(best.value);
        if (formatted) return formatted;
    }
    try {
        return new Date(best.ms).toLocaleString('en-us', dateFormat).replaceAll('/', '-').replace(',', '');
    } catch {
        return null;
    }
}

function binSensorLastGood(sensorId) {
    if (sensorId == null) return null;
    const bins = gw.selectedSite?.data?.bins;
    if (!Array.isArray(bins)) return null;
    for (let b = 0; b < bins.length; b++) {
        const cables = bins[b]?.cableList;
        if (!cables) continue;
        for (let c = 0; c < cables.length; c++) {
            const sensors = cables[c]?.sensorList;
            if (!sensors) continue;
            for (let s = 0; s < sensors.length; s++) {
                const sen = sensors[s];
                if (!sameSensorId(sen.id, sensorId)) continue;
                return sen.lastGoodTempCheck ?? sen.LastGoodTempCheck ?? null;
            }
        }
    }
    return null;
}

function layoutSensorOnCable(sensor, cableId, layoutCableId) {
    if (sameCableId(sensor.cableId, cableId)) return true;
    if (layoutCableId != null && sameCableId(sensor.customerSiteLayoutCableId, layoutCableId)) return true;
    return false;
}

/** Latest Heltec last-good read on this cable, formatted like the Site 3D last-scan label. */
function cableLastReadLabel(cableId, layoutCableId) {
    if (cableId == null && layoutCableId == null) return null;
    let best = { ms: -Infinity, value: null };
    let heltec = false;

    const layoutSensors = gw.selectedSite?.layoutSensors || [];
    for (let i = 0; i < layoutSensors.length; i++) {
        const sensor = layoutSensors[i];
        if (!layoutSensorOnCable(sensor, cableId, layoutCableId) || !sensorIsHeltec(sensor)) continue;
        heltec = true;
        best = considerCableRead(best, sensor.heltecLastRead);
        best = considerCableRead(best, sensor.lastGoodTempCheck);
        best = considerCableRead(best, binSensorLastGood(sensor.sensorId));
    }

    const binSources = [];
    if (Array.isArray(gw.selectedSite?.data?.bins)) binSources.push(...gw.selectedSite.data.bins);
    if (Array.isArray(gw.selectedSite?.bins)) binSources.push(...gw.selectedSite.bins);
    for (let b = 0; b < binSources.length; b++) {
        const cables = binSources[b]?.cableList;
        if (!cables) continue;
        for (let c = 0; c < cables.length; c++) {
            const cable = cables[c];
            const onCable = sameCableId(cable.id, cableId)
                || sameCableId(cable.cableId, cableId)
                || (layoutCableId != null && (
                    sameCableId(cable.id, layoutCableId)
                    || sameCableId(cable.customerSiteLayoutCableId, layoutCableId)
                ));
            if (!onCable) continue;
            const sensors = cable.sensorList || [];
            for (let s = 0; s < sensors.length; s++) {
                const sen = sensors[s];
                if (!sensorIsHeltec({ sensorId: sen.id, id: sen.id, heltecLastRead: sen.heltecLastRead })) continue;
                heltec = true;
                best = considerCableRead(best, sen.lastGoodTempCheck ?? sen.LastGoodTempCheck);
            }
        }
    }

    if (!heltec) return null;
    const formatted = formatCableRead(best);
    if (!formatted) return null;
    return `Last Read: ${formatted}`;
}

function ensureCableLastReadTip() {
    if (_cableLastReadTip && _cableLastReadTip.isConnected) return _cableLastReadTip;
    const el = document.createElement('div');
    el.setAttribute('data-grainwave-cable-last-read', '');
    el.style.position = 'fixed';
    el.style.zIndex = '4000';
    el.style.pointerEvents = 'none';
    el.style.display = 'none';
    el.style.padding = '4px 8px';
    el.style.borderRadius = '4px';
    el.style.fontSize = '13px';
    el.style.lineHeight = '1.3';
    el.style.whiteSpace = 'nowrap';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
    document.body.appendChild(el);
    _cableLastReadTip = el;
    return el;
}

function paintCableLastReadTip(text, clientX, clientY) {
    const el = ensureCableLastReadTip();
    const light = gw.theme === 'light';
    el.style.background = light ? '#ffffff' : '#2d2d2d';
    el.style.color = light ? '#000000' : '#f4cc8e';
    el.style.border = light ? '1px solid #cccccc' : '1px solid #5a4a32';
    el.textContent = text;
    el.style.display = 'block';
    const margin = 12;
    const left = Math.min(clientX + 14, window.innerWidth - el.offsetWidth - margin);
    const top = Math.min(clientY + 16, window.innerHeight - el.offsetHeight - margin);
    el.style.left = `${Math.max(margin, left)}px`;
    el.style.top = `${Math.max(margin, top)}px`;
}

function objectIsShown(object) {
    let node = object;
    while (node) {
        if (node.visible === false) return false;
        if (node.isScene) return true;
        node = node.parent;
    }
    return false;
}

const clearCableNameHover = () => {
    if (_cableLastReadTip) _cableLastReadTip.style.display = 'none';
    if (gw?.selectedSite) gw.selectedSite._cableNameHover = null;
};

function refreshCableNameHover() {
    const hover = gw.selectedSite?._cableNameHover;
    if (!hover) return;
    const text = cableLastReadLabel(hover.cableId, hover.layoutCableId);
    if (!text) {
        if (_cableLastReadTip) _cableLastReadTip.style.display = 'none';
        return;
    }
    paintCableLastReadTip(text, hover.clientX, hover.clientY);
}

const showCableNameLastRead = (cableId, layoutCableId, clientX, clientY) => {
    const site = gw?.selectedSite;
    if (!site || clientX == null || clientY == null) return;
    if (cableId == null && layoutCableId == null) return;
    site._cableNameHover = { cableId, layoutCableId, clientX, clientY };
    const text = cableLastReadLabel(cableId, layoutCableId);
    if (!text) {
        if (_cableLastReadTip) _cableLastReadTip.style.display = 'none';
        return;
    }
    paintCableLastReadTip(text, clientX, clientY);
};

const updateCableNameHover = (clientX, clientY) => {
    const site = gw?.selectedSite;
    const camera = site?.orbitControls?.object;
    const canvas = site?.canvasRef;
    if (!camera || !canvas?.getBoundingClientRect) {
        clearCableNameHover();
        return;
    }
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
        clearCableNameHover();
        return;
    }

    _cableNamePointer.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        ((rect.top - clientY) / rect.height) * 2 + 1,
    );
    _cableNameRaycaster.setFromCamera(_cableNamePointer, camera);

    let bestCableId = null;
    let bestDist = Infinity;
    const cables = site.cablesRef;
    if (Array.isArray(cables)) {
        for (let i = 0; i < cables.length; i++) {
            const cable = cables[i];
            if (!cable?.children || cable.cableId == null || !objectIsShown(cable)) continue;
            for (let c = 0; c < cable.children.length; c++) {
                const child = cable.children[c];
                if (!child?.isCableName || !objectIsShown(child)) continue;
                _cableNameBox.setFromObject(child);
                if (_cableNameBox.isEmpty()) continue;
                _cableNameBox.expandByScalar(0.35);
                if (!_cableNameRaycaster.ray.intersectBox(_cableNameBox, _cableNameHit)) continue;
                const dist = _cableNameHit.distanceTo(_cableNameRaycaster.ray.origin);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestCableId = cable.cableId;
                }
            }
        }
    }

    if (bestCableId == null) {
        clearCableNameHover();
        return;
    }
    site._cableNameHover = { cableId: bestCableId, clientX, clientY };
    const text = cableLastReadLabel(bestCableId);
    if (!text) {
        if (_cableLastReadTip) _cableLastReadTip.style.display = 'none';
        return;
    }
    paintCableLastReadTip(text, clientX, clientY);
};

/**
 * Removes any `isBinLevelMesh` meshes / `isBinLevelPercentSprite` sprites previously inserted
 * by `renderSiteBinLevels`.
 *
 * Bin-level fills are parented directly to their bin group (binRef) so they inherit every
 * site/group/bin transform applied after the initial render -- centerSite shifting the
 * site root, applyMargin nudging individual bins, the Stice useEffect rotating flat
 * houses, etc. Cleanup mirrors that: walk each bin's children and pull our tagged meshes.
 */
const clearSiteBinLevels = () => {
    clearBinLevelLabelHover();
    if (gw?.selectedSite) {
        gw.selectedSite.binLevelPercentSprites = [];
    }

    const binsRef = gw?.selectedSite?.binsRef;
    if (!Array.isArray(binsRef) || binsRef.length === 0) return;

    binsRef.forEach(binRef => {
        if (!binRef?.children) return;
        for (let i = binRef.children.length - 1; i >= 0; i--) {
            const child = binRef.children[i];
            if (child?.isBinLevelMesh || child?.isBinLevelPercentSprite) {
                binRef.remove(child);
                try {
                    if (child.isSprite) {
                        child.material?.map?.dispose?.();
                        // Each sprite has its own SpriteMaterial; never dispose shared fills.
                        child.material?.dispose?.();
                    } else if (child.isMesh) {
                        child.geometry?.dispose?.();
                        // `binLevelFillMaterial` is shared — do not dispose mesh.material.
                    }
                } catch (e) { /* ignore */ }
            }
        }
    });
};

/**
 * Per-commodity **pounds per U.S. bushel** (for fallback when `ProductVolume` is missing).
 * `CurrentContents` selects the row via `binLevelGrainKeyFromContents`.
 *
 * `ProductVolume` / `HeadroomVolume` from BTS Binventory are in the **same inventory units**;
 * `PercentFull` ≈ 100 × ProductVolume / (ProductVolume + HeadroomVolume) (e.g. 107,517 / (107,517 + 15,980) ≈ 87%),
 * so **ProductVolume is already bushels** (bushel-equivalent capacity), not cubic feet — do not divide by 1.244.
 */
const BIN_LEVEL_GRAIN_BUSHEL_RULES = {
    corn: { lbsPerBushel: 56 },
    maize: { lbsPerBushel: 56 },
    soybeans: { lbsPerBushel: 60 },
    soybean: { lbsPerBushel: 60 },
    wheat: { lbsPerBushel: 60 },
    oats: { lbsPerBushel: 32 },
    barley: { lbsPerBushel: 48 },
    rye: { lbsPerBushel: 56 },
    sorghum: { lbsPerBushel: 56 },
    milo: { lbsPerBushel: 56 },
    default: { lbsPerBushel: 56 },
};

const binLevelGrainKeyFromContents = (currentContents) => {
    if (currentContents == null) return "default";
    const s = String(currentContents).trim().toLowerCase();
    if (!s) return "default";
    const keys = Object.keys(BIN_LEVEL_GRAIN_BUSHEL_RULES).filter(k => k !== "default");
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (s.includes(k)) return k;
    }
    return "default";
};

const estimateBushelsFromBinLevelRow = (row) => {
    if (!row) return null;
    const key = binLevelGrainKeyFromContents(row.CurrentContents ?? row.currentContents);
    const rule = BIN_LEVEL_GRAIN_BUSHEL_RULES[key] || BIN_LEVEL_GRAIN_BUSHEL_RULES.default;

    const pv = parseFloat(row.ProductVolume ?? row.productVolume);
    if (Number.isFinite(pv) && pv > 0) {
        return pv;
    }
    const pw = parseFloat(row.ProductWeight ?? row.productWeight);
    if (Number.isFinite(pw) && pw > 0 && rule.lbsPerBushel > 0) {
        return pw / rule.lbsPerBushel;
    }
    return null;
};

/** Product pounds for a BinLevel row; mirrors tooltip logic (prefer stored weight; else derive from volume × lb/bu). */
const estimateProductPoundsFromBinLevelRow = (row) => {
    if (!row) return null;
    const key = binLevelGrainKeyFromContents(row.CurrentContents ?? row.currentContents);
    const rule = BIN_LEVEL_GRAIN_BUSHEL_RULES[key] || BIN_LEVEL_GRAIN_BUSHEL_RULES.default;
    const pw = parseFloat(row.ProductWeight ?? row.productWeight);
    if (Number.isFinite(pw) && pw > 0) return pw;
    const pv = parseFloat(row.ProductVolume ?? row.productVolume);
    if (Number.isFinite(pv) && pv > 0 && rule.lbsPerBushel > 0) return pv * rule.lbsPerBushel;
    return null;
};

/** Headspace bushels (same unit convention as `ProductVolume`). */
const estimateHeadroomBushelsFromBinLevelRow = (row) => {
    if (!row) return null;
    const hv = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
    if (Number.isFinite(hv) && hv > 0) return hv;
    const hw = parseFloat(row.HeadroomWeight ?? row.headroomWeight);
    if (!Number.isFinite(hw) || hw <= 0) return null;
    const key = binLevelGrainKeyFromContents(row.CurrentContents ?? row.currentContents);
    const rule = BIN_LEVEL_GRAIN_BUSHEL_RULES[key] || BIN_LEVEL_GRAIN_BUSHEL_RULES.default;
    if (rule.lbsPerBushel <= 0) return null;
    return hw / rule.lbsPerBushel;
};

/** Headspace pounds; prefer stored headroom weight, else headroom volume × lb/bu for the row’s commodity. */
const estimateHeadroomPoundsFromBinLevelRow = (row) => {
    if (!row) return null;
    const key = binLevelGrainKeyFromContents(row.CurrentContents ?? row.currentContents);
    const rule = BIN_LEVEL_GRAIN_BUSHEL_RULES[key] || BIN_LEVEL_GRAIN_BUSHEL_RULES.default;
    const hw = parseFloat(row.HeadroomWeight ?? row.headroomWeight);
    if (Number.isFinite(hw) && hw > 0) return hw;
    const hv = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
    if (Number.isFinite(hv) && hv > 0 && rule.lbsPerBushel > 0) return hv * rule.lbsPerBushel;
    return null;
};

/** Grouping key + display label for site-card inventory (every bin maps to exactly one bucket). */
const grainBucketKeyAndLabelForInventory = (row) => {
    if (!row) return { grainKey: "__empty__", label: "Empty" };
    const raw = String(row.CurrentContents ?? row.currentContents ?? "").trim();
    if (!raw || raw.toLowerCase() === "empty") {
        return { grainKey: "__empty__", label: "Empty" };
    }
    const grainKey = raw.toLowerCase();
    return { grainKey, label: formatBinLevelContentsDisplay(raw) || grainKey };
};

const formatBinLevelBushelsLine = (bushels) => {
    if (!Number.isFinite(bushels) || bushels <= 0) return null;
    const rounded = Math.round(bushels);
    const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(rounded);
    return `${num} bu`;
};

/** Matches SiteInventoryModal fallback when BTS `BushelsPerCubicFoot` is absent (1 / 1.2445 cu ft per bu). */
const DEFAULT_BUSHELS_PER_CUBIC_FOOT_INVENTORY = 1 / 1.2445;

/**
 * Approximate max grain volume (cu ft) from dashboard `siteInfos` bin fields. Optional `btsBinRow`
 * (full BTS `Bin` row from `getSiteInventory`) supplies geometry when the dashboard bin is missing
 * dimensions — `fetchUserAttribute` only returns `binId/name/temperatures`, so empty bins on the
 * site card otherwise lack any volume input.
 * @param {object|null|undefined} bin Dashboard bin from `siteInfos.bins`
 * @param {object|null|undefined} btsBinRow Optional BTS `Bin` row (PascalCase) for fallbacks
 * @returns {number|null}
 */
const estimateBinMaxVolumeCuFtFromDashboardBin = (bin, btsBinRow) => {
    if (!bin && !btsBinRow) return null;
    const pickN = (...keys) => {
        for (const k of keys) {
            if (bin) {
                const v = bin[k];
                if (v != null && v !== "") {
                    const n = typeof v === "number" ? v : parseFloat(v);
                    if (Number.isFinite(n)) return n;
                }
            }
            if (btsBinRow) {
                const v = btsBinRow[k];
                if (v != null && v !== "") {
                    const n = typeof v === "number" ? v : parseFloat(v);
                    if (Number.isFinite(n)) return n;
                }
            }
        }
        return NaN;
    };
    const diam = pickN("diameter", "Diameter");
    const ht = pickN("height", "Height");
    const len = pickN("length", "Length");
    const wid = pickN("width", "Width");
    const tpRaw = (() => {
        const t1 = bin?.type ?? bin?.Type;
        if (t1 != null && t1 !== "") return String(t1);
        const t2 = btsBinRow?.Type ?? btsBinRow?.type;
        return t2 != null ? String(t2) : "";
    })();
    const tp = tpRaw.toLowerCase();
    const flatLike = tp.includes("flat") || tp.includes("house") || tp.includes("stice");
    if (!flatLike && Number.isFinite(diam) && diam > 0 && Number.isFinite(ht) && ht > 0) {
        const r = diam / 2;
        return Math.PI * r * r * ht;
    }
    if (Number.isFinite(len) && len > 0 && Number.isFinite(wid) && wid > 0 && Number.isFinite(ht) && ht > 0) {
        return len * wid * ht;
    }
    return null;
};

/**
 * Max bushel-equivalent capacity from bin geometry (Site Card / `siteInfos` bins only).
 * @param {object|null|undefined} bin
 * @returns {number|null}
 */
const estimateMaxBushelsCapacityFromDashboardBin = (bin, btsBinRow) => {
    const vol = estimateBinMaxVolumeCuFtFromDashboardBin(bin, btsBinRow);
    if (!(vol > 0)) return null;
    const readBpc = (src) => {
        const raw = src?.bushelsPerCubicFoot ?? src?.BushelsPerCubicFoot ?? src?.BushelsPerCubicFeet;
        if (raw == null || raw === "") return NaN;
        const n = typeof raw === "number" ? raw : parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? n : NaN;
    };
    let factor = readBpc(bin);
    if (!Number.isFinite(factor) || !(factor > 0)) factor = readBpc(btsBinRow);
    if (!Number.isFinite(factor) || !(factor > 0)) factor = DEFAULT_BUSHELS_PER_CUBIC_FOOT_INVENTORY;
    return vol * factor;
};

/** Parses first finite number from alternating Pascal/camel keys on a row (`BinDimension` vs dashboard bin). */
const readInventoryGeomNumber = (row, ...keys) => {
    if (!row) return null;
    for (const k of keys) {
        if (k == null) continue;
        const v = row[k];
        if (v === "" || v == null) continue;
        const n = typeof v === "number" ? v : parseFloat(v);
        if (Number.isFinite(n)) return n;
    }
    return null;
};

/** Picks numeric from `dimRow`, then fallback `dashboardBin`. */
const inventoryGeomCoordinate = (dimRow, dashboardBin, dimKeysPerCase, dashKeysPerCase) => {
    let n = dimRow ? readInventoryGeomNumber(dimRow, ...dimKeysPerCase) : null;
    if (n != null) return n;
    return dashboardBin ? readInventoryGeomNumber(dashboardBin, ...dashKeysPerCase) : null;
};

const textIndicatesFlatHouse = (text) => {
    const t = String(text ?? "").trim().toLowerCase();
    if (!t) return false;
    if (t.includes("flat")) return true;
    if (t.includes("flat") && t.includes("house")) return true;
    if (t === "flathouse" || t.includes("flat_house") || t.includes("flat house")) return true;
    return false;
};

const inferFlatOrRoundShapeFromDashboardBinForInventory = (dashboardBin) => {
    if (textIndicatesFlatHouse(dashboardBin?.type ?? dashboardBin?.Type)) return "FlatHouse";
    if (textIndicatesFlatHouse(
        dashboardBin?.name ?? dashboardBin?.Name ?? dashboardBin?.binName ?? dashboardBin?.BinName,
    )) {
        return "FlatHouse";
    }
    return "Round";
};

const normalizeStoredBinDimensionShapeKind = (rawShape, dashboardBin) => {
    const s = String(rawShape ?? "").trim();
    const fixLegacy = (s === "Slice" ? "Round" : s);
    const low = fixLegacy.toLowerCase();
    if (low.includes("flat") && low.includes("house")) return "FlatHouse";
    if (low === "flathouse" || low.includes("flat_house")) return "FlatHouse";
    if (low === "round") return "Round";
    if (low.includes("stice")) return null;
    if (!fixLegacy) return inferFlatOrRoundShapeFromDashboardBinForInventory(dashboardBin);
    return inferFlatOrRoundShapeFromDashboardBinForInventory(dashboardBin);
};

/**
 * Cubic feet from dbo.BinDimension + dashboard bin fallback for missing coords (matches Site Inventory modal seeding rules; no Temputech layout bins on site cards).
 * @returns {number|null}
 */
const estimateBinMaxVolumeCuFtFromBinDimensionRow = (dimRow, dashboardBin) => {
    if (!dimRow || !dashboardBin) return null;
    let shapeKind = normalizeStoredBinDimensionShapeKind(
        dimRow.ShapeKind ?? dimRow.shapeKind,
        dashboardBin,
    );
    if (shapeKind == null) shapeKind = inferFlatOrRoundShapeFromDashboardBinForInventory(dashboardBin);

    if (shapeKind === "FlatHouse") {
        const lengthFt = inventoryGeomCoordinate(
            dimRow,
            dashboardBin,
            ["Length", "length"],
            ["Length", "length", "lengthFt"],
        );
        const widthFt = inventoryGeomCoordinate(
            dimRow,
            dashboardBin,
            ["Width", "width"],
            ["Width", "width", "widthFt"],
        );
        const heightFt = inventoryGeomCoordinate(
            dimRow,
            dashboardBin,
            ["Height", "height"],
            ["Height", "height", "heightFt"],
        );
        if (lengthFt > 0 && widthFt > 0 && heightFt > 0) return lengthFt * widthFt * heightFt;
        return null;
    }

    const diameterFt = inventoryGeomCoordinate(
        dimRow,
        dashboardBin,
        ["Diameter", "diameter"],
        ["Diameter", "diameter", "diameterFt"],
    );
    const heightFt = inventoryGeomCoordinate(
        dimRow,
        dashboardBin,
        ["Height", "height"],
        ["Height", "height", "heightFt"],
    );
    if (diameterFt > 0 && heightFt > 0) {
        const r = diameterFt / 2;
        return Math.PI * r * r * heightFt;
    }
    return null;
};

/**
 * Snapshot total capacity / bushel-equivalent from BinLevel (product + headroom in the same inventory units).
 * Mirrors site-inventory inference: volumes first, then height pair, then weight-derived bushels.
 * @returns {number|null}
 */
const estimateTotalBushelCapacityFromBinLevelRow = (row) => {
    if (!row) return null;
    const pv = parseFloat(row.ProductVolume ?? row.productVolume);
    const hv = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
    const pvOk = Number.isFinite(pv) && pv >= 0;
    const hvOk = Number.isFinite(hv) && hv >= 0;
    if (pvOk || hvOk) {
        const sum = (pvOk ? pv : 0) + (hvOk ? hv : 0);
        if (sum > 0) return sum;
    }
    const ph = parseFloat(row.ProductHeight ?? row.productHeight);
    const hh = parseFloat(row.HeadroomHeight ?? row.headroomHeight);
    if (Number.isFinite(ph) && Number.isFinite(hh) && ph >= 0 && hh >= 0 && ph + hh > 0) {
        return ph + hh;
    }
    const bu = estimateBushelsFromBinLevelRow(row) ?? 0;
    const hu = estimateHeadroomBushelsFromBinLevelRow(row) ?? 0;
    if (bu + hu > 0) return bu + hu;
    return null;
};

const bushelFactorFromDashboardBinRow = (bin, btsBinRow) => {
    const read = (src) => {
        const raw = src?.bushelsPerCubicFoot ?? src?.BushelsPerCubicFoot ?? src?.BushelsPerCubicFeet;
        if (raw == null || raw === "") return NaN;
        const n = typeof raw === "number" ? raw : parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? n : NaN;
    };
    let f = read(bin);
    if (!(Number.isFinite(f) && f > 0)) f = read(btsBinRow);
    return Number.isFinite(f) && f > 0 ? f : DEFAULT_BUSHELS_PER_CUBIC_FOOT_INVENTORY;
};

/**
 * Max bushel-equivalent storability per bin for site-card inventory (**BinDimension → BinLevel → Bin** geometry).
 * `btsBinRow` is the optional BTS `Bin` row from `getSiteInventory` (used as a geometry fallback for
 * the dashboard bin and for the BinDimension shape-kind/coords merge).
 */
const estimatePrioritizedInventoryMaxBushels = (dashboardBin, levelRow, dimRowMaybe, btsBinRow) => {
    if (!dashboardBin && !btsBinRow) return null;
    if (dimRowMaybe) {
        const volCf = estimateBinMaxVolumeCuFtFromBinDimensionRow(dimRowMaybe, dashboardBin || btsBinRow);
        if (volCf != null && volCf > 0) {
            return volCf * bushelFactorFromDashboardBinRow(dashboardBin, btsBinRow);
        }
    }
    const buFromLevel = estimateTotalBushelCapacityFromBinLevelRow(levelRow);
    if (buFromLevel != null && buFromLevel > 0) return buFromLevel;
    return estimateMaxBushelsCapacityFromDashboardBin(dashboardBin, btsBinRow);
};

/** BTS `Bin.Id`-style key for tying dashboard bins to dbo.BinDimension / BinLevel. Order matches {@link findBinLevelRowForCardBin}. */
const inventoryBtsBinIdFromDashboardBin = (b) => {
    if (!b) return null;
    const coerce = (v) => {
        if (v == null || v === "") return null;
        const n = typeof v === "number" ? v : parseInt(String(v), 10);
        return Number.isNaN(n) ? null : n;
    };
    let id = coerce(b.binDataId ?? b.BinDataId);
    if (id != null) return id;
    id = coerce(b.binId ?? b.BinId);
    if (id != null) return id;
    if (
        (b.binDataId == null || b.binDataId === "") &&
        (b.binId == null || b.binId === "")
    ) {
        return coerce(b.number ?? b.Number);
    }
    return null;
};

/**
 * True when a dashboard / site-card bin represents a flat house (not a round bin).
 * Dashboard `fetchUserAttribute.bins` omits `Type`; falls back to bin name, BTS inventory `Type`,
 * and dbo.BinDimension `ShapeKind` when provided.
 */
const isDashboardBinFlatHouse = (dashboardBin, binDimensionsMaybe, inventoryBinsMaybe) => {
    const btsId = inventoryBtsBinIdFromDashboardBin(dashboardBin);

    if (Array.isArray(binDimensionsMaybe) && btsId != null) {
        for (let di = 0; di < binDimensionsMaybe.length; di++) {
            const dr = binDimensionsMaybe[di];
            const bidRaw = dr?.BinId ?? dr?.binId;
            if (bidRaw == null || bidRaw === "") continue;
            const bid = typeof bidRaw === "number" ? bidRaw : parseInt(String(bidRaw), 10);
            if (Number.isNaN(bid) || bid !== btsId) continue;
            const sk = normalizeStoredBinDimensionShapeKind(
                dr?.ShapeKind ?? dr?.shapeKind,
                dashboardBin,
            );
            if (sk === "FlatHouse") return true;
            if (sk === "Round") return false;
            break;
        }
    }

    if (Array.isArray(inventoryBinsMaybe) && btsId != null) {
        for (let bi = 0; bi < inventoryBinsMaybe.length; bi++) {
            const ib = inventoryBinsMaybe[bi];
            const bidRaw = ib?.BinId ?? ib?.binId ?? ib?.Id ?? ib?.id;
            if (bidRaw == null || bidRaw === "") continue;
            const bid = typeof bidRaw === "number" ? bidRaw : parseInt(String(bidRaw), 10);
            if (Number.isNaN(bid) || bid !== btsId) continue;
            if (textIndicatesFlatHouse(ib?.Type ?? ib?.type)) return true;
            const diam = parseFloat(ib?.Diameter ?? ib?.diameter);
            const len = parseFloat(ib?.Length ?? ib?.length);
            const wid = parseFloat(ib?.Width ?? ib?.width);
            if ((!Number.isFinite(diam) || diam <= 0) && Number.isFinite(len) && len > 0 && Number.isFinite(wid) && wid > 0) {
                return true;
            }
            if (Number.isFinite(diam) && diam > 0) return false;
            break;
        }
    }

    return inferFlatOrRoundShapeFromDashboardBinForInventory(dashboardBin) === "FlatHouse";
};

/**
 * Latest dbo.BinDimension rows for Site Inventory geometry (gzip JSON envelope from `fetchSiteInventory` / GetSiteInventory).
 * @returns {Promise<Array<object>>}
 */
const fetchSiteInventoryBinDimensionsForSiteGuid = async (siteGuid) => {
    const env = await fetchSiteInventoryEnvelopeForSiteGuid(siteGuid);
    return Array.isArray(env?.binDimensions) ? env.binDimensions : [];
};

/**
 * Geometry source for site-card inventory: full BTS `Bin` rows + `binDimensions` from `getSiteInventory`.
 * The dashboard `fetchUserAttribute.bins` payload omits `Diameter/Height/Length/Width/PeakHeight/Type`,
 * so we read those directly from this envelope to size empty bins on the site card.
 * @returns {Promise<{ bins: Array<object>, binDimensions: Array<object> }>}
 */
const fetchSiteInventoryEnvelopeForSiteGuid = async (siteGuid) => {
    const empty = { bins: [], binDimensions: [] };
    if (!siteGuid || !gw?.email || !gw?.bolts) return empty;
    try {
        const response = await createApiEndpoint().fetchSiteInventory("direct", siteGuid);
        const payload = response?.data?.getSiteInventory;
        if (payload && typeof payload === "string" && payload.length > 0) {
            const arrayBuffer = base64StringToArrayBuffer(payload);
            const decompressed = await decompress(arrayBuffer, "gzip");
            const parsed = JSON.parse(decompressed);
            if (parsed && typeof parsed === "object") {
                return {
                    bins: Array.isArray(parsed.bins) ? parsed.bins : [],
                    binDimensions: Array.isArray(parsed.binDimensions) ? parsed.binDimensions : [],
                };
            }
        }
        return empty;
    } catch (e) {
        console.warn("fetchSiteInventoryEnvelopeForSiteGuid: failed", e);
        return empty;
    }
};

/** BTS weights as **pounds**; whole pounds with grouping (e.g. `1,234,567 lb`). */
const formatBinLevelPoundsLine = (pounds) => {
    if (!Number.isFinite(pounds) || pounds <= 0) return null;
    const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(pounds));
    return `${num} lb`;
};

/** Site-card inventory table only: ≥1e6 → `X.XM`, ≥1e3 → `X.XK`, else grouped integer (`bu` suffix). */
const formatSiteInventoryBushelsLine = (bushels) => {
    if (!Number.isFinite(bushels) || bushels <= 0) return null;
    const n = bushels;
    if (n >= 1_000_000) return `${(Math.round((n / 1_000_000) * 10) / 10).toFixed(1)}M bu`;
    if (n >= 1_000) return `${(Math.round((n / 1_000) * 10) / 10).toFixed(1)}K bu`;
    const rounded = Math.round(n);
    const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(rounded);
    return `${num} bu`;
};

/** Site-card inventory table only: same K/M compaction as {@link formatSiteInventoryBushelsLine} (`lb` suffix). */
const formatSiteInventoryPoundsLine = (pounds) => {
    if (!Number.isFinite(pounds) || pounds <= 0) return null;
    const n = pounds;
    if (n >= 1_000_000) return `${(Math.round((n / 1_000_000) * 10) / 10).toFixed(1)}M lb`;
    if (n >= 1_000) return `${(Math.round((n / 1_000) * 10) / 10).toFixed(1)}K lb`;
    const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(n));
    return `${num} lb`;
};

/** Aggregated bin-level-style % full: 100 × product / (product + headspace), bushels first then pounds. */
const computeAggregateFillPercentRounded = (productBu, headroomBu, productLb, headroomLb) => {
    const pBu = Number.isFinite(productBu) && productBu > 0 ? productBu : 0;
    const hBu = Number.isFinite(headroomBu) && headroomBu > 0 ? headroomBu : 0;
    const tBu = pBu + hBu;
    if (tBu > 0) return Math.round((100 * pBu) / tBu);
    const pLb = Number.isFinite(productLb) && productLb > 0 ? productLb : 0;
    const hLb = Number.isFinite(headroomLb) && headroomLb > 0 ? headroomLb : 0;
    const tLb = pLb + hLb;
    if (tLb > 0) return Math.round((100 * pLb) / tLb);
    return null;
};

/** Aggregated % headspace (space): 100 × headspace / (product + headspace), bushels first then pounds. */
const computeAggregateSpacePercentRounded = (productBu, headroomBu, productLb, headroomLb) => {
    const pBu = Number.isFinite(productBu) && productBu > 0 ? productBu : 0;
    const hBu = Number.isFinite(headroomBu) && headroomBu > 0 ? headroomBu : 0;
    const tBu = pBu + hBu;
    if (tBu > 0) return Math.round((100 * hBu) / tBu);
    const pLb = Number.isFinite(productLb) && productLb > 0 ? productLb : 0;
    const hLb = Number.isFinite(headroomLb) && headroomLb > 0 ? headroomLb : 0;
    const tLb = pLb + hLb;
    if (tLb > 0) return Math.round((100 * hLb) / tLb);
    return null;
};

/** Site-card table cell: `% | bu | lb` (pipes between parts; em dash for missing parts). */
const formatInventoryPctBuLbCell = (pctRounded, bushelsLabel, poundsLabel) => {
    const p = pctRounded != null ? `${pctRounded}%` : "—";
    const b = bushelsLabel != null && bushelsLabel !== "" ? bushelsLabel : "—";
    const l = poundsLabel != null && poundsLabel !== "" ? poundsLabel : "—";
    return `${p} | ${b} | ${l}`;
};

/**
 * When stored product/headroom bushels are missing or zero, infer from PercentFull, raw volumes, or heights.
 * Ensures **Empty** bins still contribute headspace when the snapshot has headroom % or geometry.
 * @param {object|null} row
 * @param {number|null|undefined} pbuIn
 * @param {number|null|undefined} hbuIn
 * @returns {{ pbu: number|null, hbu: number|null }}
 */
const inferInventoryBushelsFromPercentFullAndHeights = (row, pbuIn, hbuIn) => {
    if (!row) {
        return {
            pbu: Number.isFinite(pbuIn) && pbuIn > 0 ? pbuIn : null,
            hbu: Number.isFinite(hbuIn) && hbuIn > 0 ? hbuIn : null,
        };
    }
    let pbu = Number.isFinite(pbuIn) && pbuIn > 0 ? pbuIn : 0;
    let hbu = Number.isFinite(hbuIn) && hbuIn > 0 ? hbuIn : 0;

    const pfRaw = parseFloat(row.PercentFull ?? row.percentFull);
    const pfc = Number.isFinite(pfRaw) ? Math.max(0, Math.min(100, pfRaw)) / 100 : NaN;
    const pvR = parseFloat(row.ProductVolume ?? row.productVolume);
    const hvR = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
    const pvOk = Number.isFinite(pvR) && pvR > 0;
    const hvOk = Number.isFinite(hvR) && hvR > 0;

    if (Number.isFinite(pfc) && pfc > 0 && pfc < 1) {
        if (!(hbu > 0)) {
            const pBase = pbu > 0 ? pbu : (pvOk ? pvR : 0);
            if (pBase > 0) hbu = pBase * (1 - pfc) / pfc;
        }
        if (!(pbu > 0)) {
            const hBase = hbu > 0 ? hbu : (hvOk ? hvR : 0);
            if (hBase > 0) pbu = hBase * pfc / (1 - pfc);
        }
    }

    if (Number.isFinite(pfc) && pfc <= 0) {
        if (!(hbu > 0) && hvOk) hbu = hvR;
        if (!(pbu > 0) && pvOk) pbu = pvR;
    }
    if (Number.isFinite(pfc) && pfc >= 1) {
        if (!(pbu > 0) && pvOk) pbu = pvR;
        if (!(hbu > 0) && hvOk) hbu = hvR;
    }

    if (!(pbu > 0) && !(hbu > 0)) {
        const ph = parseFloat(row.ProductHeight ?? row.productHeight);
        const hh = parseFloat(row.HeadroomHeight ?? row.headroomHeight);
        if (Number.isFinite(ph) && Number.isFinite(hh) && ph + hh > 0) {
            pbu = ph;
            hbu = hh;
        }
    }

    return {
        pbu: pbu > 0 ? pbu : null,
        hbu: hbu > 0 ? hbu : null,
    };
};

/** When mass is missing but inferred bushels exist, fill pounds using commodity lb/bu (same as bin tooltips). */
const backfillInventoryPoundsFromBushels = (row, pbu, hbu, plbIn, hlbIn) => {
    if (!row) {
        return {
            plb: Number.isFinite(plbIn) && plbIn > 0 ? plbIn : null,
            hlb: Number.isFinite(hlbIn) && hlbIn > 0 ? hlbIn : null,
        };
    }
    const key = binLevelGrainKeyFromContents(row.CurrentContents ?? row.currentContents);
    const rule = BIN_LEVEL_GRAIN_BUSHEL_RULES[key] || BIN_LEVEL_GRAIN_BUSHEL_RULES.default;
    const lbPerBu = rule.lbsPerBushel > 0 ? rule.lbsPerBushel : null;
    let plb = Number.isFinite(plbIn) && plbIn > 0 ? plbIn : 0;
    let hlb = Number.isFinite(hlbIn) && hlbIn > 0 ? hlbIn : 0;
    if (lbPerBu) {
        if (!(plb > 0) && Number.isFinite(pbu) && pbu > 0) plb = pbu * lbPerBu;
        if (!(hlb > 0) && Number.isFinite(hbu) && hbu > 0) hlb = hbu * lbPerBu;
    }
    return {
        plb: plb > 0 ? plb : null,
        hlb: hlb > 0 ? hlb : null,
    };
};

/**
 * Site inventory: totals and per-commodity breakdown (product + headspace). Every bin is counted once — no row or empty contents → **Empty** bucket.
 * Display labels in cells use compact **K** / **M** (one decimal) for bushels and pounds at ≥1,000 / ≥1,000,000.
 * Per-bin storability (**site capacity**) uses dbo.BinDimension when present (`binDimensions`), else totals from BinLevel,
 * else dashboard `Bin` geometry, else BTS `Bin` rows from `getSiteInventory` (`inventoryBinsMaybe`) — required for Empty
 * bins because `fetchUserAttribute.bins` only returns id/name/temperatures, no Diameter/Height/Length/Width/Type.
 * @returns {{
 *   totalBushels: number|null, totalPounds: number|null, totalHeadroomBushels: number|null, totalHeadroomPounds: number|null,
 *   binsWithProduct: number, totalBinCount: number,
 *   bushelsLabel: string|null, poundsLabel: string|null, headroomBushelsLabel: string|null, headroomPoundsLabel: string|null,
 *   fillPctRounded: number|null, spacePctRounded: number|null,
 *   productCellLine: string, headspaceCellLine: string,
 *   siteTotalCapacityLabel: string|null,
 *   byGrain: Array<{ grainKey: string, label: string, binCount: number,
 *      productBushelsLabel: string|null, productPoundsLabel: string|null,
 *      headroomBushelsLabel: string|null, headroomPoundsLabel: string|null,
 *      fillPctRounded: number|null, spacePctRounded: number|null,
 *      productCellLine: string, headspaceCellLine: string,
 *      capacityBu: number,
 *      capacityCellLine: string }>,
 *   byBin: Array<{ binId: number|null, binName: string, grainKey: string, grainLabel: string,
 *      productCellLine: string, headspaceCellLine: string, capacityCellLine: string }>
 * }}
 */
const summarizeSiteInventoryTotalsFromBinLevelRows = (rows, bins, binDimensionsMaybe, inventoryBinsMaybe) => {
    const empty = {
        totalBushels: null,
        totalPounds: null,
        totalHeadroomBushels: null,
        totalHeadroomPounds: null,
        binsWithProduct: 0,
        totalBinCount: 0,
        bushelsLabel: null,
        poundsLabel: null,
        headroomBushelsLabel: null,
        headroomPoundsLabel: null,
        fillPctRounded: null,
        spacePctRounded: null,
        productCellLine: "— | — | —",
        headspaceCellLine: "— | — | —",
        siteTotalCapacityLabel: null,
        byGrain: [],
        byBin: [],
    };
    if (!Array.isArray(bins) || bins.length === 0) return empty;

    /** @type Map<number, object> */
    const dimByBinId = new Map();
    if (Array.isArray(binDimensionsMaybe)) {
        for (let di = 0; di < binDimensionsMaybe.length; di++) {
            const dr = binDimensionsMaybe[di];
            const bidRaw = dr?.BinId ?? dr?.binId;
            if (bidRaw == null || bidRaw === "") continue;
            const bid = typeof bidRaw === "number" ? bidRaw : parseInt(String(bidRaw), 10);
            if (!Number.isNaN(bid)) dimByBinId.set(bid, dr);
        }
    }

    /** Geometry fallback by BTS `Bin.Id`: dashboard `siteInfos.bins` lacks Diameter/Height/Length/Width/Type. */
    /** @type Map<number, object> */
    const inventoryBinByBinId = new Map();
    if (Array.isArray(inventoryBinsMaybe)) {
        for (let bi = 0; bi < inventoryBinsMaybe.length; bi++) {
            const ib = inventoryBinsMaybe[bi];
            const bidRaw = ib?.BinId ?? ib?.binId ?? ib?.Id ?? ib?.id;
            if (bidRaw == null || bidRaw === "") continue;
            const bid = typeof bidRaw === "number" ? bidRaw : parseInt(String(bidRaw), 10);
            if (!Number.isNaN(bid)) inventoryBinByBinId.set(bid, ib);
        }
    }

    const map = new Map();
    const ensure = (grainKey, label) => {
        if (!map.has(grainKey)) {
            map.set(grainKey, {
                grainKey,
                label,
                binCount: 0,
                productBu: 0,
                productLb: 0,
                headroomBu: 0,
                headroomLb: 0,
                capacityBu: 0,
            });
        }
        return map.get(grainKey);
    };

    let binsWithProduct = 0;
    let totalBu = 0;
    let totalLb = 0;
    let totalHb = 0;
    let totalHl = 0;
    let hasBu = false;
    let hasLb = false;
    let hasHb = false;
    let hasHl = false;
    let sumSiteCapacityBu = 0;
    let hasSiteCapacity = false;
    const byBin = [];

    for (let i = 0; i < bins.length; i++) {
        const b = bins[i];
        const row = Array.isArray(rows) ? findBinLevelRowForCardBin(rows, b) : null;
        const { grainKey, label } = grainBucketKeyAndLabelForInventory(row);
        const agg = ensure(grainKey, label);
        agg.binCount += 1;

        let pbu = row ? estimateBushelsFromBinLevelRow(row) : null;
        let hbu = row ? estimateHeadroomBushelsFromBinLevelRow(row) : null;
        let plb = row ? estimateProductPoundsFromBinLevelRow(row) : null;
        let hlb = row ? estimateHeadroomPoundsFromBinLevelRow(row) : null;

        if (row) {
            const inf = inferInventoryBushelsFromPercentFullAndHeights(row, pbu, hbu);
            pbu = inf.pbu;
            hbu = inf.hbu;
        }

        const rowForLb = row || { CurrentContents: "" };
        const btsId = inventoryBtsBinIdFromDashboardBin(b);
        const dimRow = btsId != null ? dimByBinId.get(btsId) : null;
        const btsBinRow = btsId != null ? inventoryBinByBinId.get(btsId) : null;
        const maxGeoBu = estimatePrioritizedInventoryMaxBushels(b, row, dimRow, btsBinRow);
        const pfRaw = row ? parseFloat(row.PercentFull ?? row.percentFull) : NaN;
        const hasProd = (pbu != null && pbu > 0) || (plb != null && plb > 0);

        /**
         * Unfilled storability (~headspace): for **Empty** commodity bins treat the whole prioritized
         * storability (`maxGeoBu`) as Space when stored product bushels are missing/zero — ignores
         * bogus `PercentFull` (e.g. 100%) that would collapse headroom to 0 despite no inventory.
         */
        if (maxGeoBu != null && maxGeoBu > 0) {
            const hasBuProduct = pbu != null && pbu > 0;
            if (!(hbu != null && hbu > 0)) {
                if (grainKey === "__empty__" && !hasBuProduct) {
                    hbu = maxGeoBu;
                } else if (hasBuProduct) {
                    const remainder = Math.max(0, maxGeoBu - pbu);
                    if (remainder > 0) hbu = remainder;
                } else if (!hasProd) {
                    if (Number.isFinite(pfRaw) && pfRaw >= 0 && pfRaw <= 100) {
                        hbu = maxGeoBu * Math.max(0, 1 - pfRaw / 100);
                    }
                }
            }
        }

        const lbFill = backfillInventoryPoundsFromBushels(
            rowForLb,
            pbu ?? 0,
            hbu ?? 0,
            plb,
            hlb,
        );
        plb = lbFill.plb;
        hlb = lbFill.hlb;

        let capContribution = maxGeoBu != null && maxGeoBu > 0 ? maxGeoBu : 0;
        if (!(capContribution > 0)) {
            capContribution = Math.max(0, (pbu ?? 0) + (hbu ?? 0));
        }
        if (capContribution > 0) {
            sumSiteCapacityBu += capContribution;
            hasSiteCapacity = true;
            agg.capacityBu += capContribution;
        }

        const binHasProduct =
            (pbu != null && pbu > 0) || (plb != null && plb > 0);
        if (binHasProduct) binsWithProduct += 1;

        if (pbu != null && pbu > 0) {
            agg.productBu += pbu;
            totalBu += pbu;
            hasBu = true;
        }
        if (plb != null && plb > 0) {
            agg.productLb += plb;
            totalLb += plb;
            hasLb = true;
        }
        if (hbu != null && hbu > 0) {
            agg.headroomBu += hbu;
            totalHb += hbu;
            hasHb = true;
        }
        if (hlb != null && hlb > 0) {
            agg.headroomLb += hlb;
            totalHl += hlb;
            hasHl = true;
        }

        const binFillPctRounded = computeAggregateFillPercentRounded(
            pbu ?? 0,
            hbu ?? 0,
            plb ?? 0,
            hlb ?? 0,
        );
        const binSpacePctRounded = computeAggregateSpacePercentRounded(
            pbu ?? 0,
            hbu ?? 0,
            plb ?? 0,
            hlb ?? 0,
        );
        const binProductBushelsLabel = pbu != null && pbu > 0 ? formatSiteInventoryBushelsLine(pbu) : null;
        const binProductPoundsLabel = plb != null && plb > 0 ? formatSiteInventoryPoundsLine(plb) : null;
        const binHeadroomBushelsLabel = hbu != null && hbu > 0 ? formatSiteInventoryBushelsLine(hbu) : null;
        const binHeadroomPoundsLabel = hlb != null && hlb > 0 ? formatSiteInventoryPoundsLine(hlb) : null;
        const binCapacityCellLine =
            capContribution > 0 ? formatSiteInventoryBushelsLine(capContribution) : "—";
        const openInventoryBinId = inventoryBtsBinIdFromDashboardBin(b);
        const binNumberRaw = Number(b?.number ?? b?.Number);
        const binNumber = Number.isFinite(binNumberRaw) ? binNumberRaw : null;
        const binDisplayName = String(b?.name ?? b?.binName ?? b?.Name ?? "").trim()
            || (binNumber != null ? `Bin ${binNumber}` : null)
            || (openInventoryBinId != null ? `Bin ${openInventoryBinId}` : `Bin ${i + 1}`);

        byBin.push({
            binId: openInventoryBinId,
            binNumber,
            binName: binDisplayName,
            grainKey,
            grainLabel: label,
            productCellLine: formatInventoryPctBuLbCell(
                binFillPctRounded,
                binProductBushelsLabel,
                binProductPoundsLabel,
            ),
            headspaceCellLine: formatInventoryPctBuLbCell(
                binSpacePctRounded,
                binHeadroomBushelsLabel,
                binHeadroomPoundsLabel,
            ),
            capacityCellLine: binCapacityCellLine,
        });
    }

    const totalBinCount = bins.length;
    const siteFillPctRounded = computeAggregateFillPercentRounded(totalBu, totalHb, totalLb, totalHl);
    const siteSpacePctRounded = computeAggregateSpacePercentRounded(totalBu, totalHb, totalLb, totalHl);
    const siteProductCellLine = formatInventoryPctBuLbCell(siteFillPctRounded, hasBu ? formatSiteInventoryBushelsLine(totalBu) : null, hasLb ? formatSiteInventoryPoundsLine(totalLb) : null);
    const siteHeadspaceCellLine = formatInventoryPctBuLbCell(siteSpacePctRounded, hasHb ? formatSiteInventoryBushelsLine(totalHb) : null, hasHl ? formatSiteInventoryPoundsLine(totalHl) : null);

    const byGrain = Array.from(map.values())
        .map((g) => {
            const fillPctRounded = computeAggregateFillPercentRounded(
                g.productBu,
                g.headroomBu,
                g.productLb,
                g.headroomLb,
            );
            const spacePctRounded = computeAggregateSpacePercentRounded(
                g.productBu,
                g.headroomBu,
                g.productLb,
                g.headroomLb,
            );
            const productBushelsLabel = g.productBu > 0 ? formatSiteInventoryBushelsLine(g.productBu) : null;
            const productPoundsLabel = g.productLb > 0 ? formatSiteInventoryPoundsLine(g.productLb) : null;
            const headroomBushelsLabel = g.headroomBu > 0 ? formatSiteInventoryBushelsLine(g.headroomBu) : null;
            const headroomPoundsLabel = g.headroomLb > 0 ? formatSiteInventoryPoundsLine(g.headroomLb) : null;
            const capBuNum = typeof g.capacityBu === "number" && Number.isFinite(g.capacityBu)
                ? g.capacityBu
                : 0;
            const capacityCellLine =
                capBuNum > 0 ? formatSiteInventoryBushelsLine(capBuNum) : "—";
            return {
                grainKey: g.grainKey,
                label: g.label,
                binCount: g.binCount,
                productBushelsLabel,
                productPoundsLabel,
                headroomBushelsLabel,
                headroomPoundsLabel,
                fillPctRounded,
                spacePctRounded,
                productCellLine: formatInventoryPctBuLbCell(fillPctRounded, productBushelsLabel, productPoundsLabel),
                headspaceCellLine: formatInventoryPctBuLbCell(spacePctRounded, headroomBushelsLabel, headroomPoundsLabel),
                capacityBu: capBuNum,
                capacityCellLine,
            };
        })
        .sort((a, b) => {
            if (a.grainKey === "__empty__") return 1;
            if (b.grainKey === "__empty__") return -1;
            return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
        });

    byBin.sort((a, b) => {
        const an = a.binNumber;
        const bn = b.binNumber;
        if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
        return String(a.binName || "").localeCompare(String(b.binName || ""), undefined, {
            sensitivity: "base",
            numeric: true,
        });
    });

    return {
        totalBushels: hasBu ? totalBu : null,
        totalPounds: hasLb ? totalLb : null,
        totalHeadroomBushels: hasHb ? totalHb : null,
        totalHeadroomPounds: hasHl ? totalHl : null,
        binsWithProduct,
        totalBinCount,
        bushelsLabel: hasBu ? formatSiteInventoryBushelsLine(totalBu) : null,
        poundsLabel: hasLb ? formatSiteInventoryPoundsLine(totalLb) : null,
        headroomBushelsLabel: hasHb ? formatSiteInventoryBushelsLine(totalHb) : null,
        headroomPoundsLabel: hasHl ? formatSiteInventoryPoundsLine(totalHl) : null,
        fillPctRounded: siteFillPctRounded,
        spacePctRounded: siteSpacePctRounded,
        productCellLine: siteProductCellLine,
        headspaceCellLine: siteHeadspaceCellLine,
        siteTotalCapacityLabel: hasSiteCapacity ? formatSiteInventoryBushelsLine(sumSiteCapacityBu) : null,
        byGrain,
        byBin,
    };
};

/** BinLevel `MeasurementDate` on the label footer: `MM-dd-yy HH:mm am` / `pm` (12-hour, local). */
const formatBinLevelMeasurementDate = (raw) => {
    if (raw == null || raw === "") return null;
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear() % 100).padStart(2, "0");
    const h24 = d.getHours();
    const isPm = h24 >= 12;
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    const hh = String(h12).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}-${dd}-${yy} ${hh}:${min} ${isPm ? "pm" : "am"}`;
};

/** Visual separator between sections (mirrors billboard dividers). */
const BIN_LEVEL_TOOLTIP_DIVIDER = "────────────";

/**
 * Multi-line plain text matching the Site3D bin level billboard, for site-card tooltips.
 * @param {object} row BinLevel snapshot/history row
 * @returns {string|null}
 */
const buildBinLevelTooltipTextFromRow = (row) => {
    if (!row) return null;
    const fr = binLevelFillRatioFromRow(row);
    let pctRounded;
    if (fr != null) {
        pctRounded = Math.round(fr * 100);
    } else {
        const pf = parseFloat(row.PercentFull ?? row.percentFull);
        pctRounded = Number.isFinite(pf) ? Math.round(Math.max(0, Math.min(100, pf))) : 0;
    }

    const hvRaw = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
    const headroomBuLine = (Number.isFinite(hvRaw) && hvRaw > 0)
        ? formatBinLevelBushelsLine(hvRaw)
        : null;
    const buEst = estimateBushelsFromBinLevelRow(row);
    const productBuLine = formatBinLevelBushelsLine(buEst);
    const headroomLbLine = formatBinLevelPoundsLine(
        parseFloat(row.HeadroomWeight ?? row.headroomWeight));
    const productLbLine = formatBinLevelPoundsLine(
        parseFloat(row.ProductWeight ?? row.productWeight));
    const contentsTop = (row.CurrentContents ?? row.currentContents ?? "").trim();

    const lines = [];
    if (contentsTop) lines.push(formatBinLevelContentsDisplay(contentsTop));
    lines.push(`${pctRounded}%`);
    lines.push(BIN_LEVEL_TOOLTIP_DIVIDER);
    lines.push("Headspace:");
    if (headroomBuLine) lines.push(headroomBuLine);
    if (headroomLbLine) lines.push(headroomLbLine);
    lines.push(BIN_LEVEL_TOOLTIP_DIVIDER);
    lines.push("Product:");
    if (productBuLine) lines.push(productBuLine);
    if (productLbLine) lines.push(productLbLine);
    const measurementDateLine = formatBinLevelMeasurementDate(row.MeasurementDate ?? row.measurementDate);
    if (measurementDateLine) {
        lines.push(BIN_LEVEL_TOOLTIP_DIVIDER);
        lines.push(measurementDateLine);
    }
    return lines.join("\n");
};

/** @returns {string|null} */
const formatBinLevelTooltipTextFromRows = (rows, b) => {
    const row = findBinLevelRowForCardBin(rows, b);
    if (!row) return null;
    return buildBinLevelTooltipTextFromRow(row);
};

/**
 * Approximate canvas height of the original **% + 2× bu** label (one main line + two sublines).
 * Taller textures (more lines) get scaled up in `renderSiteBinLevels` so each row stays as legible.
 */
const BIN_LEVEL_LABEL_REF_CANVAS_HEIGHT = 510;

/**
 * Bin-level billboard: **CurrentContents**, **%**, divider, **Headspace:** + volumes/weights, divider,
 * **Product:** + volumes/weights, divider, **MeasurementDate** (`MM-dd-yy HH:mm am`/`pm`). Canvas texture → `Sprite` (always faces camera).
 *
 * @param {object} opts
 * @param {string|null|undefined} opts.contentsLine
 * @param {number} opts.percentRounded
 * @param {string|null|undefined} opts.headroomBuLine
 * @param {string|null|undefined} opts.headroomLbLine
 * @param {string|null|undefined} opts.productBuLine
 * @param {string|null|undefined} opts.productLbLine
 * @param {string|number|Date|null|undefined} opts.measurementDate raw `MeasurementDate` from bin level row
 */
const createBinLevelPercentSprite = (opts) => {
    const {
        contentsLine = null,
        percentRounded,
        headroomBuLine = null,
        headroomLbLine = null,
        productBuLine = null,
        productLbLine = null,
        measurementDate = null,
    } = opts || {};

    const rows = [];
    const c = contentsLine != null ? String(contentsLine).trim() : "";
    if (c) rows.push({ type: "main", text: formatBinLevelContentsDisplay(c) });
    rows.push({ type: "main", text: `${percentRounded}%` });
    rows.push({ type: "divider", tight: true });
    rows.push({ type: "header", text: "Headspace:", tightTop: true });
    if (headroomBuLine) rows.push({ type: "sub", text: headroomBuLine });
    if (headroomLbLine) rows.push({ type: "sub", text: headroomLbLine });
    rows.push({ type: "divider" });
    rows.push({ type: "header", text: "Product:" });
    if (productBuLine) rows.push({ type: "sub", text: productBuLine });
    if (productLbLine) rows.push({ type: "sub", text: productLbLine });
    const measurementDateLine = formatBinLevelMeasurementDate(measurementDate);
    if (measurementDateLine) {
        rows.push({ type: "divider" });
        rows.push({ type: "sub", text: measurementDateLine });
    }

    const pad = 56;
    const fontSizeMain = 184;
    const fontSizeSub = Math.round(fontSizeMain * 0.44);
    const lineGap = 20;
    const mainLineGap = 14;
    /** After the `%` line, advance less than a full main row so the divider sits closer. */
    const mainStepAfterPercent = Math.round(fontSizeMain * 0.66) + 3;
    const fontMain = `bold ${fontSizeMain}px system-ui, "Segoe UI", Arial, sans-serif`;
    const fontSub = `600 ${fontSizeSub}px system-ui, "Segoe UI", Arial, sans-serif`;
    const fontHeader = `700 ${fontSizeSub}px system-ui, "Segoe UI", Arial, sans-serif`;

    const canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
    if (!canvas) return null;

    const ctx = canvas.getContext("2d");
    let maxW = 1;
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (row.type === "main") {
            ctx.font = fontMain;
            maxW = Math.max(maxW, ctx.measureText(row.text).width);
        } else if (row.type === "header") {
            ctx.font = fontHeader;
            maxW = Math.max(maxW, ctx.measureText(row.text).width);
        } else if (row.type === "sub") {
            ctx.font = fontSub;
            maxW = Math.max(maxW, ctx.measureText(row.text).width);
        }
    }
    const contentW = Math.ceil(maxW);

    const measureHeight = () => {
        let y = fontSizeMain + pad - 16;
        let isFirstMain = true;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row.type === "main") {
                if (!isFirstMain) y += mainLineGap;
                isFirstMain = false;
                const next = rows[i + 1];
                const shortTailPct =
                    next?.type === "divider" && next.tight && String(row.text).endsWith("%");
                y += shortTailPct ? mainStepAfterPercent : fontSizeMain + 3;
            } else if (row.type === "divider") {
                const top = row.tight ? 0 : 8;
                const bottom = row.tight ? 5 : 3 + 16;
                y += top + bottom;
            } else if (row.type === "header") {
                const headLead = row.tightTop ? 1 : lineGap * 0.5;
                y += headLead + fontSizeSub + 6;
            } else if (row.type === "sub") {
                y += lineGap * 0.4 + fontSizeSub + lineGap * 0.8;
            }
        }
        return Math.ceil(y + pad);
    };

    canvas.width = contentW + pad * 2;
    canvas.height = measureHeight();

    const isLight = gw.theme === "light";
    ctx.fillStyle = isLight ? "rgba(255, 252, 245, 0.94)" : "rgba(30, 24, 14, 0.9)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const mainFill = isLight ? "rgba(55, 42, 22, 0.98)" : "rgba(235, 215, 160, 0.98)";
    const subFill = isLight ? "rgba(55, 42, 22, 0.92)" : "rgba(235, 215, 160, 0.92)";
    const headerFill = isLight ? "rgba(35, 28, 18, 0.96)" : "rgba(245, 230, 190, 0.96)";
    const strokeCol = isLight ? "rgba(0, 0, 0, 0.35)" : "rgba(255, 255, 255, 0.25)";
    const dividerStroke = isLight ? "rgba(0, 0, 0, 0.22)" : "rgba(255, 255, 255, 0.2)";

    let baseY = fontSizeMain + pad - 16;
    let isFirstMain = true;
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (row.type === "main") {
            if (!isFirstMain) baseY += mainLineGap;
            isFirstMain = false;
            ctx.font = fontMain;
            ctx.fillStyle = mainFill;
            ctx.strokeStyle = strokeCol;
            ctx.lineWidth = 10;
            const wi = Math.ceil(ctx.measureText(row.text).width);
            const xi = pad + (contentW - wi) / 2;
            ctx.strokeText(row.text, xi, baseY);
            ctx.fillText(row.text, xi, baseY);
            const next = rows[i + 1];
            const shortTailPct =
                next?.type === "divider" && next.tight && String(row.text).endsWith("%");
            baseY += shortTailPct ? mainStepAfterPercent : fontSizeMain + 3;
        } else if (row.type === "divider") {
            baseY += row.tight ? 0 : 8;
            ctx.strokeStyle = dividerStroke;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(pad, baseY);
            ctx.lineTo(pad + contentW, baseY);
            ctx.stroke();
            baseY += row.tight ? 5 : 3 + 16;
            ctx.strokeStyle = strokeCol;
        } else if (row.type === "header") {
            baseY += row.tightTop ? 1 : lineGap * 0.5;
            ctx.font = fontHeader;
            ctx.fillStyle = headerFill;
            ctx.strokeStyle = strokeCol;
            ctx.lineWidth = 5;
            const wi = Math.ceil(ctx.measureText(row.text).width);
            const xi = pad + (contentW - wi) / 2;
            ctx.strokeText(row.text, xi, baseY);
            ctx.fillText(row.text, xi, baseY);
            baseY += fontSizeSub + 6;
        } else if (row.type === "sub") {
            baseY += lineGap * 0.4;
            ctx.font = fontSub;
            ctx.fillStyle = subFill;
            ctx.strokeStyle = strokeCol;
            ctx.lineWidth = 5;
            const wi = Math.ceil(ctx.measureText(row.text).width);
            const xi = pad + (contentW - wi) / 2;
            ctx.strokeText(row.text, xi, baseY);
            ctx.fillText(row.text, xi, baseY);
            baseY += fontSizeSub + lineGap * 0.8;
        }
    }

    const texture = new THREE.CanvasTexture(canvas);
    if (THREE.SRGBColorSpace) {
        texture.colorSpace = THREE.SRGBColorSpace;
    }
    texture.needsUpdate = true;

    const mat = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        toneMapped: true,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.isBinLevelPercentSprite = true;
    sprite.binLevelPercent = percentRounded;
    sprite.renderOrder = 12;
    const aspect = canvas.width / canvas.height;
    sprite.userData.binLevelLabelAspect = aspect;
    sprite.userData.labelCanvasHeight = canvas.height;
    return sprite;
};

/**
 * Returns the bbox (in `binRef` local space) of just the bin's wall meshes. Excludes the
 * binName/date/grade text labels in `binRef.children[0]`, which sit at `[0, -2.25, 0]` and
 * extend well beyond the wall radius -- including them shifts the bbox center off-bin and
 * pushes its floor below the actual bin floor (visible as fills floating away/below bins).
 *
 * Strategy:
 *   1) Prefer `binRef.meshes` (Stice/BinStice already filter to wall meshes here).
 *   2) Fall back to any descendant tagged `isWallMesh` if `meshes` wasn't populated yet.
 *   3) As a last resort, the whole-binRef bbox -- centered but possibly skewed by labels.
 *
 * The bbox is built in world space, then each corner is converted to `binRef`-local. Doing
 * the conversion here (instead of returning a world bbox) lets the caller parent the fill
 * to `binRef` without re-deriving any transforms.
 */
const getBinWallLocalBox = (binRef) => {
    if (!binRef) return null;

    binRef.updateMatrixWorld(true);

    const worldBox = new THREE.Box3();
    let hasWall = false;

    if (Array.isArray(binRef.meshes) && binRef.meshes.length > 0) {
        binRef.meshes.forEach(m => {
            if (m?.isMesh && m.isWallMesh) {
                worldBox.expandByObject(m);
                hasWall = true;
            }
        });
    }
    if (!hasWall) {
        binRef.traverse(child => {
            if (child?.isMesh && child.isWallMesh) {
                worldBox.expandByObject(child);
                hasWall = true;
            }
        });
    }
    if (!hasWall) {
        worldBox.setFromObject(binRef);
        if (worldBox.isEmpty()) return null;
    }

    // Project all 8 corners into binRef-local so a rotated wall group (flat-house
    // bin3dModel.rotation, set in Stice's post-render useEffect) still yields the
    // correct AABB in the bin's local frame.
    const minW = worldBox.min;
    const maxW = worldBox.max;
    const corners = [
        new THREE.Vector3(minW.x, minW.y, minW.z),
        new THREE.Vector3(maxW.x, minW.y, minW.z),
        new THREE.Vector3(minW.x, maxW.y, minW.z),
        new THREE.Vector3(maxW.x, maxW.y, minW.z),
        new THREE.Vector3(minW.x, minW.y, maxW.z),
        new THREE.Vector3(maxW.x, minW.y, maxW.z),
        new THREE.Vector3(minW.x, maxW.y, maxW.z),
        new THREE.Vector3(maxW.x, maxW.y, maxW.z),
    ].map(v => binRef.worldToLocal(v));

    const localBox = new THREE.Box3();
    corners.forEach(c => localBox.expandByPoint(c));
    return localBox;
};

/**
 * Rows for Site3D / Bin3D bin fills/labels: if the history slider mark has `dateCableRead` and
 * `binLevelHistory` is loaded, pick per bin on that **calendar day** (ISO date prefix) the latest
 * `MeasurementDate` that is still **≤ `dateCableRead`** (as-of the mark); if none, use the latest
 * row that day so a later same-day snapshot wins over an earlier one. Otherwise use `binLevels`,
 * keeping at most one row per bin (latest `MeasurementDate` when duplicates exist).
 */
const getBinLevelRowsForSite3dRender = () => {
    const site = gw?.selectedSite;
    if (!site) return [];
    const history = site.binLevelHistory;
    const mark = site.selectedMark;
    const markIso = mark?.dateCableRead;

    if (Array.isArray(history) && history.length > 0 && markIso) {
        const markStr = typeof markIso === "string" ? markIso : String(markIso);
        const markDateKey =
            (markStr.includes("T") ? markStr.split("T")[0] : markStr.slice(0, 10)) || "";
        const markMs = new Date(markStr.replace("Z", "-05:00")).getTime();
        if (markDateKey.length >= 8 && Number.isFinite(markMs)) {
            const byBin = new Map();
            for (const r of history) {
                const rid = r?.BinId ?? r?.binId;
                if (rid === undefined || rid === null) continue;
                const ridNum = parseInt(rid, 10);
                if (Number.isNaN(ridNum)) continue;
                const rawMd = r?.MeasurementDate ?? r?.measurementDate;
                if (rawMd == null && rawMd !== 0) continue;
                const mdStr = typeof rawMd === "string" ? rawMd : String(rawMd);
                const rowDateKey =
                    (mdStr.includes("T") ? mdStr.split("T")[0] : mdStr.slice(0, 10)) || "";
                if (rowDateKey !== markDateKey) continue;
                const t = new Date(mdStr.replace("Z", "-05:00")).getTime();
                if (!Number.isFinite(t)) continue;

                let agg = byBin.get(ridNum) || { bestAsOf: null, dayMax: null };
                if (!agg.dayMax || t > agg.dayMax.t) agg.dayMax = { row: r, t };
                if (t <= markMs && (!agg.bestAsOf || t > agg.bestAsOf.t)) {
                    agg.bestAsOf = { row: r, t };
                }
                byBin.set(ridNum, agg);
            }
            if (byBin.size > 0) {
                return Array.from(byBin.values())
                    .map((agg) => (agg.bestAsOf ?? agg.dayMax)?.row)
                    .filter(Boolean);
            }
        }
    }

    const snap = site.binLevels;
    if (!Array.isArray(snap) || snap.length === 0) return [];

    const dedup = new Map();
    for (const r of snap) {
        const rid = r?.BinId ?? r?.binId;
        if (rid === undefined || rid === null) continue;
        const ridNum = parseInt(rid, 10);
        if (Number.isNaN(ridNum)) continue;
        const rawMd = r?.MeasurementDate ?? r?.measurementDate;
        let t = 0;
        if (rawMd != null && rawMd !== "") {
            const ms = new Date(String(rawMd).replace("Z", "-05:00")).getTime();
            if (Number.isFinite(ms)) t = ms;
        }
        const prev = dedup.get(ridNum);
        if (!prev || t >= prev.t) dedup.set(ridNum, { row: r, t });
    }
    return Array.from(dedup.values()).map((x) => x.row);
};

/** Reusable temps for bin-level fill sensor height sampling. */
const _binFillSensorTmp = new THREE.Vector3();
const _binFillSensorInv = new THREE.Matrix4();

/**
 * Where 100% grain reaches between the **topmost sensor** (local Y `topSensorY`) and the wall
 * rim (`localBox.max.y`). Interpretation: `grainTop = topSensorY + fraction × (wallTop - topSensorY)`.
 *
 * Pure midpoint is `fraction = 0.5`. User feedback (~2026‑05): ~0.25 reads closer to perceptual "half".
 * Tune at runtime via `gw.selectedSite.binLevelFillSensorToRimFraction` in [0, 1].
 */
const BIN_LEVEL_FILL_SENSOR_TO_RIM_FRACTION_DEFAULT = 0.15;

/**
 * Set of all numeric ids that may be used to associate `cablesRef[i]` with a given bin
 * (BTS `binDataId`, layout `customerSiteLayoutBinId`, layoutBin's `binId`, etc.).
 */
const collectBinIdCandidates = (binRef, binDbIdNum) => {
    const ids = new Set();
    const add = (v) => {
        if (v == null || v === "") return;
        const n = Number(v);
        if (!Number.isNaN(n)) ids.add(n);
    };
    add(binDbIdNum);
    if (binRef) {
        add(binRef.binDataId);
        add(binRef.binId);
        add(binRef.customerSiteLayoutBinId);
        const lb = binRef.layoutBin;
        if (lb) {
            add(lb.binId);
            add(lb.BinId);
            add(lb.binDataId);
            add(lb.customerSiteLayoutBinId);
            add(lb.CustomerSiteLayoutBinId);
        }
    }
    return ids;
};

/**
 * Bin-local Y of the highest temperature sensor on the tallest cable run inside `binRef`.
 *
 * `sensorsGroupRef` items are the React groups rendered by `CustomText3D` inside `SensorNew`;
 * they carry `meshProps.binId / cableId / sensorId` set in `SensorNew`. The deepest sensor
 * meshes (`sensorsRef`) do NOT carry `meshProps`, so we sample positions here. Group by
 * `cableId`, take each cable's max world Y, convert the top to bin-local Y, return the max
 * across cables (= top sensor of the tallest run).
 *
 * @returns {number|null}
 */
const getTopSensorLocalYForBinFromMeshes = (binRef, binDbIdNum) => {
    const sensorGroups = gw?.selectedSite?.sensorsGroupRef;
    if (!Array.isArray(sensorGroups) || sensorGroups.length === 0 || !binRef) return null;

    const candidateIds = collectBinIdCandidates(binRef, binDbIdNum);
    binRef.updateMatrixWorld(true);
    _binFillSensorInv.copy(binRef.matrixWorld).invert();

    // For each cable, track the world point of its highest sensor.
    const cableTopWorld = new Map(); // cableId -> { x, y, z }
    for (let i = 0; i < sensorGroups.length; i++) {
        const grp = sensorGroups[i];
        const mp = grp?.meshProps;
        if (!mp || mp.binId == null || mp.cableId == null) continue;
        const sensorBinId = Number(mp.binId);
        if (!Number.isFinite(sensorBinId) || !candidateIds.has(sensorBinId)) continue;

        grp.updateMatrixWorld(true);
        grp.getWorldPosition(_binFillSensorTmp);
        const wx = _binFillSensorTmp.x;
        const wy = _binFillSensorTmp.y;
        const wz = _binFillSensorTmp.z;
        if (!Number.isFinite(wy)) continue;

        const cid = Number(mp.cableId);
        if (!Number.isFinite(cid)) continue;
        const prev = cableTopWorld.get(cid);
        if (!prev || wy > prev.y) cableTopWorld.set(cid, { x: wx, y: wy, z: wz });
    }
    if (cableTopWorld.size === 0) return null;

    // Tallest cable run = the one whose top sensor has the greatest world Y.
    let tallest = null;
    cableTopWorld.forEach((p) => { if (!tallest || p.y > tallest.y) tallest = p; });
    if (!tallest) return null;

    _binFillSensorTmp.set(tallest.x, tallest.y, tallest.z);
    _binFillSensorTmp.applyMatrix4(_binFillSensorInv);
    return Number.isFinite(_binFillSensorTmp.y) ? _binFillSensorTmp.y : null;
};

/**
 * Top of the grain column in binRef-local Y. Site 3D wall meshes are a ~1.25 ft rim only;
 * full bin height comes from BTS `binRef.height` and/or temperature sensor stacks (legacy
 * code used the all-mesh bbox max Y for this — not the decorative wall).
 */
const getBinLevelInteriorRimLocalY = (binRef, localWallBox, binDbIdNum) => {
    const floorY = localWallBox.min.y;
    const topSensorY = getTopSensorLocalYForBinFromMeshes(binRef, binDbIdNum);

    const catalogH = Number(binRef?.height);
    const catalogTop =
        Number.isFinite(catalogH) && catalogH > 0 ? floorY + catalogH : null;

    if (topSensorY != null && Number.isFinite(topSensorY)) {
        if (catalogTop != null && catalogTop > topSensorY) return catalogTop;
        const headroom = Math.max(2, (topSensorY - floorY) * 0.08);
        return topSensorY + headroom;
    }

    if (catalogTop != null) return catalogTop;
    return localWallBox.max.y;
};

/**
 * Visual cap for 100% grain: interpolate upward from the top sensor toward the bin interior rim
 * using {@link BIN_LEVEL_FILL_SENSOR_TO_RIM_FRACTION_DEFAULT} unless
 * `gw.selectedSite.binLevelFillSensorToRimFraction` overrides.
 *
 * @returns {number} Grain surface top in binRef-local Y (clamped to rim / floor).
 */
const getBinLevelFillEffectiveGrainTopLocalY = (binRef, localBox, binDbIdNum) => {
    const floorY = localBox.min.y;
    const interiorRimY = getBinLevelInteriorRimLocalY(binRef, localBox, binDbIdNum);
    if (!(interiorRimY > floorY)) return interiorRimY;

    const topSensorY = getTopSensorLocalYForBinFromMeshes(binRef, binDbIdNum);
    if (topSensorY == null || !Number.isFinite(topSensorY)) return interiorRimY;

    let frac = BIN_LEVEL_FILL_SENSOR_TO_RIM_FRACTION_DEFAULT;
    const ov = gw?.selectedSite?.binLevelFillSensorToRimFraction;
    if (ov !== undefined && ov !== null && ov !== "") {
        const parsed = typeof ov === "number" ? ov : parseFloat(ov);
        if (Number.isFinite(parsed)) frac = Math.max(0, Math.min(1, parsed));
    }

    const gap = interiorRimY - topSensorY;
    if (!(gap > 0)) return Math.min(interiorRimY, Math.max(floorY + 1e-5, topSensorY));

    const grainTopLocalY = topSensorY + frac * gap;
    return Math.min(interiorRimY, Math.max(floorY + 1e-5, grainTopLocalY));
};

/**
 * Renders flat-top fills inside each bin in the Site3D scene. Row source is
 * `getBinLevelRowsForSite3dRender()` (history → latest same-day level as-of the mark, else snapshot).
 *
 * Centering / sizing:
 *   - The fill is parented directly to its `binRef`. That handles every adjustment that
 *     happens *after* the initial mount: `centerSite()` shifting the site parent,
 *     `applyMargin()` repositioning individual bins, theme-driven re-renders, and the
 *     post-render flat-house rotation in `Stice` -- the fill follows the bin in all cases.
 *   - X/Z are taken from the wall-only bbox (see `getBinWallLocalBox`) so text labels
 *     don't pull the center off-bin.
 *   - Y span uses BTS bin height and/or sensor stacks (`getBinLevelInteriorRimLocalY`), not
 *     the decorative ~1.25 ft SticeArcWall rim height.
 *   - Vertical range for the fill stops short of the interior rim when sensors exist: 100% grain tops at
 *     `topSensor + fraction × (rim − sensor)` ({@link BIN_LEVEL_FILL_SENSOR_TO_RIM_FRACTION_DEFAULT} by default).
 *     Override `gw.selectedSite.binLevelFillSensorToRimFraction` in `[0,1]` (`0.5` = midpoint, `1` = rim).
 *   - Width/length still come from layout / wall bounds.
 *
 * Fill ratio:
 *   - `ProductHeight / (ProductHeight + HeadroomHeight)` when both are present (correct
 *     for cylindrical and rectangular bins where %-of-height equals %-of-volume).
 *   - Falls back to `PercentFull / 100` when heights are missing.
 *
 * A billboard sprite (`isBinLevelPercentSprite`) shows **CurrentContents** (top), `…%`, then
 * headroom/product **bu** and **lb** lines. Scale grows with line count so text matches the original
 * `% + 2 bu` size (`BIN_LEVEL_LABEL_REF_CANVAS_HEIGHT`). Placed along **-Z** outside `localBox.min.z`.
 */
const renderSiteBinLevels = () => {
    clearSiteBinLevels();

    if (
        gw?.selectedSite?.isLayoutPreview3d ||
        gw?.selectedSite?.isLayoutPreviewSite
    ) {
        return;
    }

    // Site 3D honors the toolbar toggle. Bin 3D always shows fills when data is present.
    if (
        !gw?.selectedSite?.isBinWindowView
        && gw?.selectedSite?.showSiteBinLevels === false
    ) {
        return;
    }

    const binsRef = gw?.selectedSite?.binsRef;
    const binLevels = getBinLevelRowsForSite3dRender();
    if (!Array.isArray(binsRef) || binsRef.length === 0) return;
    if (!Array.isArray(binLevels) || binLevels.length === 0) return;

    const clientDeviceInfo = getClientDeviceInfo();
    const cylinderSegments = clientDeviceInfo.isDesktop ? 60 : 32;

    binsRef.forEach(binRef => {
        if (!binRef) return;

        // BinLevel.BinId comes from the BTS Bin.Id and is mirrored onto binRef.binDataId; fall
        // back to layoutBin.binId / binId for older sites where binDataId may be unset.
        const binDbId = binRef.binDataId ?? binRef.layoutBin?.binId ?? binRef.binId;
        if (binDbId === undefined || binDbId === null) return;
        const binDbIdNum = parseInt(binDbId, 10);
        if (Number.isNaN(binDbIdNum)) return;

        const row = binLevels.find(r => {
            const rid = r?.BinId ?? r?.binId;
            if (rid === undefined || rid === null) return false;
            const ridNum = parseInt(rid, 10);
            return !Number.isNaN(ridNum) && ridNum === binDbIdNum;
        });
        if (!row) return;

        const fillRatio = binLevelFillRatioFromRow(row);
        if (fillRatio == null) return;

        const localBox = getBinWallLocalBox(binRef);
        if (!localBox || localBox.isEmpty()) return;

        const localCenter = new THREE.Vector3();
        localBox.getCenter(localCenter);
        const wallWidth = localBox.max.x - localBox.min.x;
        const wallDepth = localBox.max.z - localBox.min.z;
        const floorY = localBox.min.y;
        const wallTopY = localBox.max.y;
        const wallHeight = wallTopY - floorY;
        if (wallWidth <= 0 || wallDepth <= 0 || wallHeight <= 0) return;

        // Grain stacks only to `grainTopLocalY` (below the wall rim when sensors exist):
        // midpoint between rim and top sensor on the tallest cable run — see
        // `getBinLevelFillEffectiveGrainTopLocalY`. Otherwise same as wall top.
        const grainTopLocalY = getBinLevelFillEffectiveGrainTopLocalY(binRef, localBox, binDbIdNum);
        const grainSpan = Math.max(1e-6, grainTopLocalY - floorY);
        const fillHeight = grainSpan * fillRatio;
        if (fillHeight <= 0) return;

        // Slightly inset from the bin walls so the fill doesn't z-fight with the structure.
        const inset = 0.97;

        // Half the smaller horizontal extent (used for round radius or box label scaling).
        const refHalf = binRef.isRound
            ? (((Number.isFinite(binRef.width) && binRef.width > 0)
                ? binRef.width / 2.0
                : Math.min(wallWidth, wallDepth) / 2.0) * inset)
            : Math.min(
                ((Number.isFinite(binRef.width) && binRef.width > 0) ? binRef.width : wallWidth) * inset,
                ((Number.isFinite(binRef.length) && binRef.length > 0) ? binRef.length : wallDepth) * inset
            ) / 2;

        let geometry;
        if (binRef.isRound) {
            const radius = refHalf;
            geometry = new THREE.CylinderGeometry(radius, radius, fillHeight, cylinderSegments);
        } else {
            const w = ((Number.isFinite(binRef.width) && binRef.width > 0) ? binRef.width : wallWidth) * inset;
            const d = ((Number.isFinite(binRef.length) && binRef.length > 0) ? binRef.length : wallDepth) * inset;
            geometry = new THREE.BoxGeometry(w, fillHeight, d);
        }

        const mesh = new THREE.Mesh(geometry, binLevelFillMaterial);
        // Sit the fill on the wall floor (`floorY`) and center it on the wall x/z so
        // off-axis labels can't pull it off-bin. Cylinder/Box pivots are at center, hence
        // the +fillHeight/2 lift.
        mesh.position.set(localCenter.x, floorY + fillHeight / 2, localCenter.z);

        // Match the wall group's rotation for flat houses so the fill box aligns with the
        // rotated walls instead of the unrotated layoutBin.x/z axes. binRef.children[1] is
        // bin3dModel (the wall group); Stice's useEffect sets its Y rotation from
        // bin.degreesToNorth. For round bins this is a no-op.
        const wallGroup = Array.isArray(binRef.children)
            ? binRef.children.find(c => c?.name === 'sticeArcWall')
                ?? binRef.children[1]
            : null;
        if (wallGroup?.rotation && !binRef.isRound) {
            mesh.rotation.copy(wallGroup.rotation);
        }

        mesh.isBinLevelMesh = true;
        mesh.binDataId = binDbIdNum;
        // Draw after opaque scene content (cables, walls, temperature text) so the
        // depthWrite:false transparent fill blends *over* them instead of being sorted
        // unpredictably. Three sorts transparents back-to-front within the same
        // renderOrder, so a high value guarantees the fill is the last thing painted.
        mesh.renderOrder = 10;
        mesh.updateMatrix();
        binRef.add(mesh);

        // Billboard "%" outside the bin on the -Z side (wall bbox min.z). Standoff from
        // the rim to the label's bin-facing edge is half the previous gap (tighter to bin).
        const pctRounded = Math.round(fillRatio * 100);
        const hvRaw = parseFloat(row.HeadroomVolume ?? row.headroomVolume);
        const headroomBuLine = (Number.isFinite(hvRaw) && hvRaw > 0)
            ? formatBinLevelBushelsLine(hvRaw)
            : null;
        const buEst = estimateBushelsFromBinLevelRow(row);
        const productBuLine = formatBinLevelBushelsLine(buEst);
        const headroomLbLine = formatBinLevelPoundsLine(
            parseFloat(row.HeadroomWeight ?? row.headroomWeight));
        const productLbLine = formatBinLevelPoundsLine(
            parseFloat(row.ProductWeight ?? row.productWeight));
        const contentsTop = (row.CurrentContents ?? row.currentContents ?? "").trim();
        const pctLabel = createBinLevelPercentSprite({
            contentsLine: contentsTop || null,
            percentRounded: pctRounded,
            headroomBuLine,
            headroomLbLine,
            productBuLine,
            productLbLine,
            measurementDate: row.MeasurementDate ?? row.measurementDate,
        });
        if (pctLabel) {
            const aspect = pctLabel.userData.binLevelLabelAspect || 3;
            const ch = pctLabel.userData.labelCanvasHeight || BIN_LEVEL_LABEL_REF_CANVAS_HEIGHT;
            const fy = Math.max(1, ch / BIN_LEVEL_LABEL_REF_CANVAS_HEIGHT);
            const labelH = Math.max(3.6, Math.min(refHalf * 1.92, 14.4));
            pctLabel.scale.set(aspect * labelH * fy, labelH * fy, 1);
            const rimZ = localBox.min.z;
            const spanZ = localBox.max.z - localBox.min.z;
            const outsideGap = Math.max(3.0, Math.min(spanZ * 0.08 + 1.5, 9.0));
            const labelHalfAlongZ = (aspect * labelH * fy) / 2;
            const labelWorldH = labelH * fy;
            const labelY = localBox.min.y + labelWorldH / 2;
            const rimStandoff = outsideGap * 0.5;
            pctLabel.position.set(localCenter.x, labelY, rimZ - rimStandoff - labelHalfAlongZ);
            pctLabel.userData.binLevelLabelBaseScaleX = aspect * labelH * fy;
            pctLabel.userData.binLevelLabelBaseScaleY = labelH * fy;
            if (!Array.isArray(gw.selectedSite.binLevelPercentSprites)) {
                gw.selectedSite.binLevelPercentSprites = [];
            }
            gw.selectedSite.binLevelPercentSprites.push(pctLabel);
            binRef.add(pctLabel);
        }
    });
};

/**
 * Loads BinLevelHistory rows for the active history window and stores them on
 * `gw.selectedSite.binLevelHistory`. Called from `initializeHistory` so the same dateFrom/dateTo
 * driving the temperature slider also drives bin-level scrubbing.
 */
const loadSiteBinLevelHistory = async (dateFromIso, dateToIso) => {
    if (!gw?.selectedSite?.siteGuid || !dateFromIso || !dateToIso) return [];

    try {
        const response = await createApiEndpoint().fetchSiteBinLevels("direct", {
            dateFrom: dateFromIso,
            dateTo: dateToIso,
        });
        const payload = response?.data?.getSiteBinLevels;
        let rows = [];
        if (payload && typeof payload === "string" && payload.length > 0) {
            const arrayBuffer = base64StringToArrayBuffer(payload);
            const decompressed = await decompress(arrayBuffer, "gzip");
            const parsed = JSON.parse(decompressed);
            if (Array.isArray(parsed)) {
                const looksLikeBinRow = (r) => r && (r.BinId !== undefined || r.binId !== undefined);
                rows = parsed.filter(looksLikeBinRow);
            }
        }

        if (gw.selectedSite) {
            gw.selectedSite.binLevelHistory = rows;
            if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent("grainwave-bin-level-history-updated"));
            }
        }
        return rows;
    } catch (error) {
        console.warn("loadSiteBinLevelHistory: failed to load bin level history", error);
        return [];
    }
};

const getDatesByHour = (dateFrom, dateTo) => {
    const hourlyDates = [];
    const currentDate = dateFrom;
    currentDate.setHours(0, 0, 0, 0);
    const endDate = dateTo ? dateTo : new Date();
    while (currentDate <= endDate) {
        hourlyDates.push(new Date(currentDate));
        currentDate.setHours(currentDate.getHours() + 1);
    }
    return hourlyDates;
};

const getClientDeviceInfo = () => {
    const result = {
        isMobile: isMobile,
        isTablet: isTablet,
        isDesktop: isDesktop,
        isBrowser: isBrowser,
        isAndroid: isAndroid,
        isIOS: isIOS,
        deviceType: deviceType,
    }
    return result;
};

const dateToJsonCurrentTimeZone = (date) => {
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
    return adjustedDate.toISOString();
};

/**
 * `fetchUserSiteAttribute` returns all user sites; only the requested site's row has `main` loaded from BTS.
 * Do not use sites[0] (first by LastScan) or you'll read null main and wrong lastScan.
 */
const pickSiteDataFromFetchResponse = (sites, wantGuid) => {
    if (!Array.isArray(sites) || sites.length === 0) return null;
    const norm = (g) => String(g == null ? '' : g).replace(/[{}]/g, '').toLowerCase();
    const want = norm(wantGuid);
    if (want) {
        const found = sites.find((s) => {
            const g = s.siteGuid ?? s.SiteGuid;
            return norm(g) === want;
        });
        if (found) return found;
    }
    return sites[0];
};

/**
 * Normalize `main` from fetchUserSiteAttribute site row. GraphQL/JSON may use PascalCase
 * (Plus, Minus, DaysToCompare) or camelCase; values may be strings.
 */
const normalizeMainFromFetch = (mainRow) => {
    if (mainRow == null || typeof mainRow !== 'object') {
        return {
            siteName: null,
            bins: 0,
            plus: null,
            minus: null,
            daysToCompare: null,
        };
    }
    const pickStr = (camel, pascal) => {
        const a = mainRow[camel];
        const b = mainRow[pascal];
        if (a != null && a !== '') return a;
        if (b != null && b !== '') return b;
        return null;
    };
    const pickInt = (camel, pascal) => {
        const v = pickStr(camel, pascal);
        if (v == null || v === '') return null;
        if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
        const n = parseInt(String(v).trim(), 10);
        return Number.isFinite(n) ? n : null;
    };
    const siteName = pickStr('siteName', 'SiteName');
    const bins = pickInt('bins', 'Bins');
    const emailReportTo = pickStr('emailReportTo', 'EmailReportTo');
    return {
        siteName: siteName == null ? null : String(siteName),
        bins: bins != null ? bins : 0,
        plus: pickInt('plus', 'Plus'),
        minus: pickInt('minus', 'Minus'),
        daysToCompare: pickInt('daysToCompare', 'DaysToCompare'),
        emailReportTo: emailReportTo == null ? null : String(emailReportTo),
    };
};

/** UTC YYYY-MM-DD from an ISO-like timestamp (stable across mixed server serializations). */
const utcYmdFromTimestamp = (ts) => {
    if (ts == null || ts === "") return "";
    const raw = typeof ts === "string" ? ts.trim() : ts;
    const ms = Date.parse(raw);
    if (!Number.isFinite(ms)) {
        const s = String(raw).trim();
        return s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
    }
    return new Date(ms).toISOString().slice(0, 10);
};

/** True when last-good read aligns with site lastScan (same UTC calendar day or within 36h for skewed payloads). */
const sensorReadMatchesSiteLastScan = (siteLastScanIso, lastGoodTempCheckIso) => {
    if (!siteLastScanIso || !lastGoodTempCheckIso) return false;
    const a = utcYmdFromTimestamp(siteLastScanIso);
    const b = utcYmdFromTimestamp(lastGoodTempCheckIso);
    if (a && b && a === b) return true;
    const msA = Date.parse(siteLastScanIso);
    const msB = Date.parse(lastGoodTempCheckIso);
    if (!Number.isFinite(msA) || !Number.isFinite(msB)) return false;
    return Math.abs(msA - msB) < 36 * 60 * 60 * 1000;
};

const isLiveTemperatureReading = (tempRaw) => {
    const t = tempRaw == null ? "" : String(tempRaw).trim();
    return t !== "" && t !== "E" && t !== "-E-" && t !== "-O-";
};

const applyTopPointToLayoutCable = (layoutCable, topPoint) => {
    if (!layoutCable || !topPoint) return;
    layoutCable.topPointX = topPoint.topPointX;
    layoutCable.topPointZ = topPoint.topPointZ;
    layoutCable.bottomPointX = topPoint.topPointX;
    layoutCable.bottomPointZ = topPoint.topPointZ;
    layoutCable.x = topPoint.topPointX;
    layoutCable.z = topPoint.topPointZ;
};

/** Pull cable XZ from live bin.cableList (TopPointX/Z first, then BTS grid slot). */
const syncLayoutCablePositionsFromBinCableList = () => {
    const site = gw.selectedSite;
    if (!site?.bins?.length) return;
    // Editor layout rebuilds bin-local cables from BTS; do not overwrite with stale DB rows.
    if (siteUsesEditorBinLocalCables(site)) return;
    if (!Array.isArray(site.layoutCables)) site.layoutCables = [];

    const normalizeCableName = (name) =>
        String(name || "").toLowerCase().replace(/\s+/g, "");

    const cableRefById = new Map();
    const cableRefByName = new Map();
    for (const bin of site.bins) {
        for (const cable of bin.cableList || []) {
            const ref = { bin, cable };
            if (cable.id != null) cableRefById.set(Number(cable.id), ref);
            if (cable.name) {
                cableRefByName.set(cable.name, ref);
                cableRefByName.set(normalizeCableName(cable.name), ref);
            }
        }
    }

    for (const lc of site.layoutCables) {
        if (lc.aframe) continue;
        const resolved = resolveLayoutCableTopPoint(lc, site);
        if (resolved) {
            applyTopPointToLayoutCable(lc, resolved);
            continue;
        }
        const ref =
            (lc.cableId != null && cableRefById.get(Number(lc.cableId))) ||
            (lc.cableName && cableRefByName.get(lc.cableName)) ||
            (lc.cableName &&
                cableRefByName.get(normalizeCableName(lc.cableName)));
        if (!ref) continue;
        const layoutBinForRef = findLayoutBinForSiteBin(site, ref.bin);
        applyTopPointToLayoutCable(
            lc,
            resolveBtsCableTopPoint(
                ref.cable,
                ref.bin,
                layoutBinForRef,
                site
            )
        );
    }

    site.bins.forEach((bin) => {
        if (!bin.cableList?.length) return;
        const layoutBin = findLayoutBinForSiteBin(site, bin);
        if (!layoutBin) return;
        bin.cableList.forEach((cable) => {
            if (cable.aframeId != null && Number(cable.aframeId) > 0) return;
            let lc =
                site.layoutCables.find(
                    (row) =>
                        Number(row.cableId) === Number(cable.id) ||
                        (row.cableName &&
                            cable.name &&
                            normalizeCableName(row.cableName) ===
                                normalizeCableName(cable.name))
                ) ?? null;
            if (!lc) return;
            const resolved = resolveLayoutCableTopPoint(lc, site);
            applyTopPointToLayoutCable(
                lc,
                resolved ??
                    resolveBtsCableTopPoint(cable, bin, layoutBin, site)
            );
        });
    });
};

/** Prefer live BTS cable.sensorList values over Temputech layout cache (fixes stale SensorValueString / -E- in 3D). */
const syncLayoutSensorValuesFromCableSensors = () => {
    const siteLastScanIso = gw.selectedSite?.data?.lastScan;
    const bins = gw.selectedSite?.bins;
    const layoutSensors = gw.selectedSite?.layoutSensors;
    if (!Array.isArray(bins) || !Array.isArray(layoutSensors)) return;

    if (siteLastScanIso) {
        for (const bin of bins) {
            for (const cable of bin.cableList || []) {
                for (const sensor of cable.sensorList || []) {
                    const layoutSensor = layoutSensors.find((x) => Number(x.sensorId) === Number(sensor.id));
                    if (!layoutSensor) continue;
                    if (sensor.lastGoodTempCheck) {
                        layoutSensor.lastGoodTempCheck = sensor.lastGoodTempCheck;
                    }
                    if (isHumiditySensor(sensor) || isHumidityLayoutSensor(layoutSensor)) {
                        continue;
                    }
                    if (!sensor.lastGoodTempCheck || !isLiveTemperatureReading(sensor.temperature)) continue;
                    if (!sensorReadMatchesSiteLastScan(siteLastScanIso, sensor.lastGoodTempCheck)) continue;
                    layoutSensor.sensorValueString = String(sensor.temperature).trim();
                    const n = parseFloat(sensor.temperature);
                    if (Number.isFinite(n)) layoutSensor.sensorValueNumber = n;
                }
            }
        }
    }
    syncHumidityLayoutSensorValues(gw.selectedSite);
};

const nextLayoutCableId = (site) => {
    const ids = (site.layoutCables || [])
        .map((c) => c.customerSiteLayoutCableId)
        .filter((n) => Number.isFinite(n));
    return (ids.length ? Math.max(...ids) : 0) + 1;
};

const nextLayoutSensorId = (site) => {
    const ids = (site.layoutSensors || [])
        .map((s) => s.customerSiteLayoutSensorId)
        .filter((n) => Number.isFinite(n));
    return (ids.length ? Math.max(...ids) : 0) + 1;
};

const pushCableFromBinList = (
    site,
    bin,
    layoutBin,
    siteLastScanIso,
    idState
) => {
    if (!bin.cableList?.length || !layoutBin) return;
    if (bin.diameter > 0) {
        bin.width = bin.diameter;
        bin.length = bin.diameter;
        bin.radius = bin.diameter / 2.0;
    }

    bin.cableList.forEach((cable) => {
        if (cable.aframeId != null && Number(cable.aframeId) > 0) {
            const topX = cable.topPointX;
            const topZ = cable.topPointZ;
            const layoutCable = {
                customerSiteLayoutCableId: idState.cableId++,
                customerSiteLayoutId: layoutBin.customerSiteLayoutId,
                customerSiteLayoutGroupId: layoutBin.customerSiteLayoutGroupId,
                customerSiteLayoutBinId: layoutBin.customerSiteLayoutBinId,
                binOrder: cable.number,
                binId: bin.binDataId ?? bin.binId,
                binName: bin.binName ?? layoutBin.binName,
                cableId: cable.id,
                cableName: cable.name,
                width: Math.abs(cable.bottomPointX - cable.topPointX),
                length: Math.abs(cable.bottomPointZ - cable.topPointZ),
                height: (cable.sensorCount * (cable.sensorSpacing - 1)) / 2,
                x: topX,
                y: (cable.sensorCount * (cable.sensorSpacing - 1)) / 2.0,
                z: topZ,
                topPointX: topX,
                topPointY:
                    cable.firstSensorHeight +
                    (cable.sensorCount * (cable.sensorSpacing - 1)) / 2.0,
                topPointZ: topZ,
                bottomPointX: topX,
                bottomPointY: cable.firstSensorHeight,
                bottomPointZ: topZ,
                firstBannerSensorHeight: cable.firstBannerSensorHeight,
                bannerSensorSpacing: cable.bannerSensorSpacing,
            };
            site.layoutCables.push(layoutCable);
            const tempSensors = temperatureSensorsFromCable(cable);
            if (tempSensors.length > 0) {
                const sensorSpacing = layoutCable.height / tempSensors.length;
                let sensorY = layoutCable.bottomPointY;
                tempSensors
                    .sort((a, b) => a.position - b.position)
                    .forEach((sensor) => {
                        const showLiveTemp =
                            siteLastScanIso &&
                            sensorReadMatchesSiteLastScan(
                                siteLastScanIso,
                                sensor.lastGoodTempCheck
                            ) &&
                            isLiveTemperatureReading(sensor.temperature);
                        const parsedTemp = parseFloat(sensor.temperature);
                        site.layoutSensors.push({
                            customerSiteLayoutSensorId: idState.sensorId++,
                            customerSiteLayoutCableId:
                                layoutCable.customerSiteLayoutCableId,
                            customerSiteLayoutId: layoutCable.customerSiteLayoutId,
                            customerSiteLayoutGroupId:
                                layoutCable.customerSiteLayoutGroupId,
                            customerSiteLayoutBinId:
                                layoutCable.customerSiteLayoutBinId,
                            cableOrder: sensor.position,
                            binId: bin.binDataId ?? bin.binId,
                            cableId: cable.id,
                            sensorId: sensor.id,
                            sensorName: sensor.name,
                            sensorValueString: showLiveTemp
                                ? String(sensor.temperature).trim()
                                : "-E-",
                            sensorValueNumber:
                                showLiveTemp && Number.isFinite(parsedTemp)
                                    ? parsedTemp
                                    : null,
                            x:
                                Number.isFinite(Number(sensor.positionX))
                                    ? Number(sensor.positionX)
                                    : layoutCable.topPointX,
                            y:
                                Number.isFinite(Number(sensor.positionY))
                                    ? Number(sensor.positionY) + 4
                                    : sensorY,
                            z:
                                Number.isFinite(Number(sensor.positionZ))
                                    ? Number(sensor.positionZ)
                                    : layoutCable.topPointZ,
                        });
                        sensorY += sensorSpacing;
                    });
            }
            return;
        }
        const topPoint = resolveBtsCableTopPoint(
            cable,
            bin,
            layoutBin,
            site
        );
        const topX = topPoint?.topPointX ?? cable.topPointX;
        const topZ = topPoint?.topPointZ ?? cable.topPointZ;
        const layoutCable = {
            customerSiteLayoutCableId: idState.cableId++,
            customerSiteLayoutId: layoutBin.customerSiteLayoutId,
            customerSiteLayoutGroupId: layoutBin.customerSiteLayoutGroupId,
            customerSiteLayoutBinId: layoutBin.customerSiteLayoutBinId,
            binOrder: cable.number,
            binId: bin.binDataId ?? bin.binId,
            binName: bin.binName ?? layoutBin.binName,
            cableId: cable.id,
            cableName: cable.name,
            width: Math.abs(cable.bottomPointX - cable.topPointX),
            length: Math.abs(cable.bottomPointZ - cable.topPointZ),
            height: (cable.sensorCount * (cable.sensorSpacing - 1)) / 2,
            x: topX,
            y: (cable.sensorCount * (cable.sensorSpacing - 1)) / 2.0,
            z: topZ,
            topPointX: topX,
            topPointY:
                cable.firstSensorHeight +
                (cable.sensorCount * (cable.sensorSpacing - 1)) / 2.0,
            topPointZ: topZ,
            bottomPointX: topX,
            bottomPointY: cable.firstSensorHeight,
            bottomPointZ: topZ,
            firstBannerSensorHeight: cable.firstBannerSensorHeight,
            bannerSensorSpacing: cable.bannerSensorSpacing,
        };
        site.layoutCables.push(layoutCable);

        const tempSensors = temperatureSensorsFromCable(cable);
        if (tempSensors.length > 0) {
            const sensorSpacing = layoutCable.height / tempSensors.length;
            let sensorY = layoutCable.bottomPointY;
            tempSensors
                .sort((a, b) => a.position - b.position)
                .forEach((sensor) => {
                    const showLiveTemp =
                        siteLastScanIso &&
                        sensorReadMatchesSiteLastScan(
                            siteLastScanIso,
                            sensor.lastGoodTempCheck
                        ) &&
                        isLiveTemperatureReading(sensor.temperature);
                    const parsedTemp = parseFloat(sensor.temperature);
                    site.layoutSensors.push({
                        customerSiteLayoutSensorId: idState.sensorId++,
                        customerSiteLayoutCableId:
                            layoutCable.customerSiteLayoutCableId,
                        customerSiteLayoutId: layoutCable.customerSiteLayoutId,
                        customerSiteLayoutGroupId:
                            layoutCable.customerSiteLayoutGroupId,
                        customerSiteLayoutBinId:
                            layoutCable.customerSiteLayoutBinId,
                        cableOrder: sensor.position,
                        binId: bin.binDataId ?? bin.binId,
                        cableId: cable.id,
                        sensorId: sensor.id,
                        sensorName: sensor.name,
                        sensorValueString: showLiveTemp
                            ? String(sensor.temperature).trim()
                            : "-E-",
                        sensorValueNumber:
                            showLiveTemp && Number.isFinite(parsedTemp)
                                ? parsedTemp
                                : null,
                        x: layoutCable.topPointX,
                        y: sensorY,
                        z: layoutCable.topPointZ,
                    });
                    sensorY += sensorSpacing;
                });
        }
    });
};

/** Build layoutCables/layoutSensors from bin cableList when the API returned none. */
const populateLayoutCablesFromBins = (site, options = {}) => {
    if (!site) return;
    const force = options.force === true;
    if (!force && site.layoutCables?.length > 0) return;
    if (!site.bins?.length) return;

    if (force) {
        site.layoutCables = [];
        site.layoutSensors = [];
    }
    if (!Array.isArray(site.layoutCables)) site.layoutCables = [];
    if (!Array.isArray(site.layoutSensors)) site.layoutSensors = [];

    const siteLastScanIso = site.data?.lastScan;
    const idState = {
        cableId: nextLayoutCableId(site),
        sensorId: nextLayoutSensorId(site),
    };

    site.bins.forEach((bin) => {
        if (!bin.cableList?.length) return;
        const layoutBin = findLayoutBinForSiteBin(site, bin);
        if (!layoutBin) return;
        pushCableFromBinList(site, bin, layoutBin, siteLastScanIso, idState);
    });
    attachHumidityLayoutSensorsToSite(site);
};

/**
 * Add cables for layout bins that have none (e.g. flat stices 33/34/35 after partial remap).
 */
export const fillMissingLayoutCablesFromSiteBins = (site) => {
    if (!site?.layoutBins?.length) return;
    if (!Array.isArray(site.layoutCables)) site.layoutCables = [];
    if (!Array.isArray(site.layoutSensors)) site.layoutSensors = [];

    const siteLastScanIso = site.data?.lastScan;
    const idState = {
        cableId: nextLayoutCableId(site),
        sensorId: nextLayoutSensorId(site),
    };

    site.layoutBins.forEach((layoutBin) => {
        if (layoutCablesForLayoutBin(site, layoutBin).length > 0) return;
        const siteBin = findSiteBinForLayoutBin(site, layoutBin);
        if (!siteBin?.cableList?.length) return;
        pushCableFromBinList(site, siteBin, layoutBin, siteLastScanIso, idState);
    });
    attachHumidityLayoutSensorsToSite(site);
};

/** Point layout cable rows at the visible layout bin for each catalog bin id. */
export const reconcileLayoutCableBinLinks = (site) => {
    if (!site?.layoutCables?.length || !site?.layoutBins?.length) return;

    site.layoutCables.forEach((lc) => {
        const catalogId = lc.binDataId ?? lc.binId;
        if (catalogId == null) return;
        const layoutBin = findLayoutBinForSiteBin(site, {
            binDataId: catalogId,
            binName: lc.binName,
        });
        if (!layoutBin) return;
        if (lc.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId) return;
        lc.customerSiteLayoutBinId = layoutBin.customerSiteLayoutBinId;
        lc.customerSiteLayoutGroupId = layoutBin.customerSiteLayoutGroupId;
        lc.customerSiteLayoutId =
            layoutBin.customerSiteLayoutId ?? lc.customerSiteLayoutId;
        lc.binId = catalogId;
        if (!lc.binName && layoutBin.binName) lc.binName = layoutBin.binName;
    });

    (site.layoutSensors || []).forEach((ls) => {
        const layoutCable = site.layoutCables.find(
            (lc) =>
                lc.customerSiteLayoutCableId === ls.customerSiteLayoutCableId ||
                (ls.cableId != null && lc.cableId === ls.cableId)
        );
        if (!layoutCable) return;
        ls.customerSiteLayoutBinId = layoutCable.customerSiteLayoutBinId;
        ls.customerSiteLayoutGroupId = layoutCable.customerSiteLayoutGroupId;
        ls.customerSiteLayoutId = layoutCable.customerSiteLayoutId;
        ls.customerSiteLayoutCableId = layoutCable.customerSiteLayoutCableId;
    });
};

/** Avoid importing layoutTableProxy here (circular with global). */
const siteHasCommittedEditorLayout = (site) =>
    !!(
        site?.layoutSavedFromSiteLayoutEditor ||
        site?.hasEditorLayoutDraft ||
        site?.persistedLayoutProxyFromEditor?.layoutBins?.length
    );

const MAX_INIT_SITE_AUTH_RETRIES = 1;

const parseFetchUserSiteAttributeSites = (response) => {
    if (!response || response === false) {
        return { sites: null, graphqlErrors: null };
    }
    const graphqlErrors = response.errors ?? response.data?.errors ?? null;
    const attr =
        response.data?.fetchUserSiteAttribute ??
        response.data?.FetchUserSiteAttribute;
    const sites = attr?.sites ?? attr?.Sites;
    return {
        sites: Array.isArray(sites) ? sites : null,
        graphqlErrors: Array.isArray(graphqlErrors) ? graphqlErrors : null,
    };
};

const redirectToLoginAfterAuthFailure = (navigate) => {
    clearAuthState();
    gw.token = null;
    gw.auth = null;
    gw.isAuthenticated = false;
    delete axios.defaults.headers.common["Authorization"];
    if (navigate) {
        navigate("/emailLogin", { replace: true });
    } else {
        setTimeout(() => {
            window.location.href = "/emailLogin";
        }, 100);
    }
};

const tryRenewTokenForSiteInit = async (navigate, retryDepth, reason) => {
    if (retryDepth >= MAX_INIT_SITE_AUTH_RETRIES) {
        console.warn(`initializeCustomerSite: ${reason} (stopped after ${MAX_INIT_SITE_AUTH_RETRIES} renewal attempt)`);
        return false;
    }
    if (!gw.token || !gw.auth) {
        return false;
    }
    console.warn(`initializeCustomerSite: ${reason}, attempting token renewal...`);
    try {
        const renewResult = await createApiEndpoint(Endpoints.GrainWave).fetchRenew();
        if (renewResult && gw.token) {
            return await initializeCustomerSite(navigate, retryDepth + 1);
        }
        redirectToLoginAfterAuthFailure(navigate);
        return false;
    } catch {
        redirectToLoginAfterAuthFailure(navigate);
        return false;
    }
};

const initializeCustomerSite = async (navigate, retryDepth = 0) => {
    try {
        if (!gw.selectedSite?.siteGuid) {
            console.warn('initializeCustomerSite: no siteGuid on selectedSite; open a site from Customer Sites first');
            gw.selectedSite.isSiteInitialized = false;
            return;
        }

        // Same transport as CustomerSite (Bearer /fetchc when logged in)
        const response = await createApiEndpoint().fetchUserSite("compressed");

        if (response === false) {
            if (!gw.selectedSite?.siteGuid) {
                gw.selectedSite.isSiteInitialized = false;
                return;
            }
            const renewed = await tryRenewTokenForSiteInit(
                navigate,
                retryDepth,
                'fetchUserSite returned false'
            );
            if (renewed !== false) return renewed;
            gw.selectedSite.isSiteInitialized = false;
            return;
        }

        const { sites: siteList, graphqlErrors } = parseFetchUserSiteAttributeSites(response);
        if (!siteList || siteList.length === 0) {
            if (graphqlErrors?.length) {
                console.warn('initializeCustomerSite: GraphQL errors', graphqlErrors);
            } else {
                const attr =
                    response?.data?.fetchUserSiteAttribute ??
                    response?.data?.FetchUserSiteAttribute;
                console.warn('initializeCustomerSite: empty site list', {
                    siteGuid: gw.selectedSite.siteGuid,
                    responseKeys: response?.data ? Object.keys(response.data) : null,
                    sitesType: attr?.sites == null ? 'null' : typeof attr.sites,
                    sitesLength: Array.isArray(attr?.sites) ? attr.sites.length : null,
                    first: attr?.first ?? attr?.First,
                });
            }

            // Empty sites is usually a server/siteId match issue, not auth — do not renew token.

            console.warn('initializeCustomerSite: Invalid API response structure, using fallback data...');
            gw.selectedSite.data = { bins: [] };
            gw.selectedSite.layout = {};
            gw.selectedSite.layoutGroups = [];
            gw.selectedSite.layoutBins = [];
            gw.selectedSite.layoutSides = [];
            gw.selectedSite.layoutCables = [];
            gw.selectedSite.layoutSensors = [];
            gw.selectedSite.aframes = [];
            gw.selectedSite.bins = [];
            gw.selectedSite.devices = [];
            gw.selectedSite.modules = [];
            gw.selectedSite.heltecSensorMappings = [];
            gw.selectedSite.isSiteInitialized = false;
            return; // Exit early with fallback data
        }
        
        // Use valid response — row that matches this site (only that row has Main from BTS)
        const siteData = pickSiteDataFromFetchResponse(siteList, gw.selectedSite.siteGuid);
        if (!siteData) {
            console.warn('initializeCustomerSite: could not resolve site row for', gw.selectedSite.siteGuid);
            gw.selectedSite.isSiteInitialized = false;
            return;
        }
        gw.selectedSite.data = siteData;
        syncSelectedSiteLastScanFromData();
        gw.selectedSite.layout = siteData.layout || {};
        gw.selectedSite.layoutGroups = siteData.layoutGroups || [];
        gw.selectedSite.layoutBins = siteData.layoutBins || [];
        gw.selectedSite.hasPersistedSiteLayout =
            (siteData.layoutBins || []).length > 0;
        gw.selectedSite.layoutSavedFromSiteLayoutEditor = false;
        gw.selectedSite.hasEditorLayoutDraft = false;
        gw.selectedSite.hasCommittedSite3dLayoutProxy = false;
        const layoutProxyFromFetchUserSite =
            extractLayoutProxyFromSiteFetch(siteData);
        try {
            const editorRaw = parseSiteLayoutEditorDraftFromApi(
                await createApiEndpoint().fetchSiteLayoutEditorDraft(
                    "direct",
                    gw.selectedSite.siteGuid
                )
            );
            gw.selectedSite.siteLayoutEditorDraftRaw = editorRaw;
            gw.selectedSite.hasEditorLayoutDraft =
                editorDraftPayloadIsValid(editorRaw);
            // Server LayoutProxyJson is an Apply to Site 3D. EditorDraftJson from merely
            // opening Site Layout must not count as committed (ADM Memphis / Temputech).
            gw.selectedSite.persistedLayoutProxyFromEditor =
                layoutProxyFromFetchUserSite;
        } catch {
            gw.selectedSite.hasEditorLayoutDraft = false;
            gw.selectedSite.siteLayoutEditorDraftRaw = null;
            gw.selectedSite.persistedLayoutProxyFromEditor =
                layoutProxyFromFetchUserSite;
        }
        gw.selectedSite.hasCommittedSite3dLayoutProxy =
            !!(layoutProxyFromFetchUserSite?.layoutBins?.length);
        gw.selectedSite.layoutSides = siteData.layoutSides || [];
        gw.selectedSite.layoutCables = siteData.layoutCables || [];
        gw.selectedSite.layoutSensors = siteData.layoutSensors || [];
        gw.selectedSite.aframes = siteData.aframes || [];
        gw.selectedSite.bins = Array.from(Array((siteData.bins || []).length)).map((_, binIndex) => (getDefaultBin(0, binIndex)));
        gw.selectedSite.fans = (siteData.fans || []).length
            ? siteData.fans
            : (gw.selectedSite.bins || []).flatMap((b) => b.fanList || []);
        gw.selectedSite.devices = siteData.devices || [];
        gw.selectedSite.modules = siteData.modules || [];
        gw.selectedSite.heltecSensorMappings = siteData.heltecSensorMappings || [];
        //gw.selectedSite.sections = response.data.fetchUserSiteAttribute.sites[0].sections;
        
        // Clear cables array before populating
        gw.selectedSite.cables = [];
        for (let b = 0; b < gw.selectedSite.bins.length; b++) {
            let cables = gw.selectedSite.bins[b].cableList;
            if (cables && cables.length > 0) {
                for (var c = 0; c < cables.length; c++) {
                    gw.selectedSite.cables.push(cables[c]);
            }
        }
        }

        const mainNorm = normalizeMainFromFetch(siteData.main);
        gw.selectedSite.main = mainNorm;
        siteData.main = mainNorm;
        if (mainNorm?.siteName) {
            gw.selectedSite.siteName = mainNorm.siteName;
        }

        let editorLayoutApplied = false;
        const looksLegacy = siteLayoutLooksLegacyManual(gw.selectedSite);
        const resolvedLayoutProxy = resolveLayoutProxyForSite3d(gw.selectedSite);
        const hasResolvedLayoutProxy = !!(resolvedLayoutProxy?.layoutBins?.length);
        const committedFromServer = !!(layoutProxyFromFetchUserSite?.layoutBins?.length);
        if (hasResolvedLayoutProxy && (committedFromServer || !looksLegacy)) {
            gw.selectedSite.persistedLayoutProxyFromEditor = resolvedLayoutProxy;
            gw.selectedSite.hasCommittedSite3dLayoutProxy = true;
            saveLayoutProxyForSite3d(
                gw.selectedSite.siteGuid,
                resolvedLayoutProxy
            );
        }
        const legacyManualLayout =
            looksLegacy && !gw.selectedSite.hasCommittedSite3dLayoutProxy;
        const useEditorSite3dLayout =
            gw.selectedSite.hasCommittedSite3dLayoutProxy ||
            (hasResolvedLayoutProxy && !legacyManualLayout) ||
            (gw.selectedSite.hasPersistedSiteLayout && !legacyManualLayout);
        try {
            if (useEditorSite3dLayout) {
                editorLayoutApplied =
                    applyEditorLayoutToSiteLikePreview(gw.selectedSite) ===
                    true;
                if (!editorLayoutApplied) {
                    rehydrateLayoutGeometryForDrawSite(gw.selectedSite);
                    attachPreviewPolygonsFromLayoutSides(gw.selectedSite);
                }
            } else {
                promoteLayoutSidesForDrawSite(gw.selectedSite);
            }
        } catch (sideLayoutErr) {
            console.warn("layout side geometry prep", sideLayoutErr);
        }

        if (editorLayoutApplied || gw.selectedSite.hasCommittedSite3dLayoutProxy) {
            gw.selectedSite.layoutUsesEditorLayoutAxes = true;
            gw.selectedSite.usesLegacyManualSiteLayout = false;
        } else {
            gw.selectedSite.layoutUsesEditorLayoutAxes =
                !legacyManualLayout &&
                siteLayoutLikelyFromEditor(gw.selectedSite);
            gw.selectedSite.usesLegacyManualSiteLayout = legacyManualLayout;
        }

        if (!editorLayoutApplied) {
            const preferEditorGeometry =
                useEditorSite3dLayout ||
                (gw.selectedSite.hasPersistedSiteLayout && !legacyManualLayout);
            if (preferEditorGeometry) {
                rehydrateLayoutGeometryForDrawSite(gw.selectedSite);
                attachPreviewPolygonsFromLayoutSides(gw.selectedSite);
                gw.selectedSite.layoutUsesEditorLayoutAxes = true;
                gw.selectedSite.skipDrawSiteForEditorLayout = true;
            }
            try {
                initializeSiteLayout();
            } finally {
                delete gw.selectedSite.skipDrawSiteForEditorLayout;
            }
            populateLayoutCablesFromBins(gw.selectedSite);
            attachPreviewPolygonsFromLayoutSides(gw.selectedSite);
        }

        syncLayoutBinNamesFromCatalog(gw.selectedSite);

        // Editor layout builds bin-local cables (matches Site Layout Preview 3D). Raw BTS aframe
        // positionX/Z are in a different frame and would skew sensors across bins, so skip them.
        const useEditorBinLocalCables = siteUsesEditorBinLocalCables(gw.selectedSite);

        //  Add Aframes to layoutCables
        if (!useEditorBinLocalCables && gw.selectedSite.layoutCables && gw.selectedSite.layoutCables.length > 0) {
            gw.selectedSite.layoutCables.forEach(layoutCable => {
                let cable = gw.selectedSite.cables.find(x => x.id === layoutCable.cableId);
                if (cable) { 
                    layoutCable.aframe = gw.selectedSite.aframes.find(x => x.id === cable.aframeId);
                    if (layoutCable.aframe) {
                        cable.sensorList.forEach(sensor => {
                            if (isHumiditySensor(sensor)) return;
                            let layoutSensor = gw.selectedSite.layoutSensors.find(x => x.sensorId === sensor.id);
                            if (layoutSensor && !isHumidityLayoutSensor(layoutSensor)) {
                                layoutSensor.x = sensor.positionX;
                                layoutSensor.y = sensor.positionY + 4;
                                layoutSensor.z = sensor.positionZ;
                            }
                        });
                    }
                }
            });
        }

        // Normalize layoutCables to ensure consistent binDataId and ids across DB/generation cases
        if (gw.selectedSite.layoutCables && gw.selectedSite.layoutCables.length > 0) {
            const bins = gw.selectedSite.bins || [];
            const binByBinDataId = new Map();
            const binByLayoutBinId = new Map();
            bins.forEach(b => {
                if (typeof b.binDataId === 'number') binByBinDataId.set(b.binDataId, b);
                const layoutId = b.customerSiteLayoutBinId ?? b.binId;
                if (typeof layoutId === 'number') binByLayoutBinId.set(parseInt(layoutId, 10), b);
            });
            // layoutBins.binId is dbo.Bin id; customerSiteLayoutBinId matches layout cable rows even when bins[] lacks layout ids
            (gw.selectedSite.layoutBins || []).forEach(lb => {
                if (typeof lb.customerSiteLayoutBinId !== 'number' || typeof lb.binId !== 'number') return;
                const b = binByBinDataId.get(lb.binId);
                if (b) binByLayoutBinId.set(lb.customerSiteLayoutBinId, b);
            });

            gw.selectedSite.layoutCables.forEach(lc => {
                // Ensure cableId exists
                if (lc.cableId == null && lc.cableName) {
                    const cable = gw.selectedSite.cables?.find(c => c.name === lc.cableName);
                    if (cable) lc.cableId = cable.id;
                }
                // Ensure unique customerSiteLayoutCableId exists
                if (lc.customerSiteLayoutCableId == null) {
                    lc.customerSiteLayoutCableId = lc.cableId ?? Math.floor(Math.random() * 1000000000);
                }
                // Compute normalized binDataId for filtering/UI
                let normalizedBinDataId = null;
                if (typeof lc.binId === 'number' && binByBinDataId.has(lc.binId)) {
                    normalizedBinDataId = lc.binId;
                } else if (typeof lc.customerSiteLayoutBinId === 'number' && binByLayoutBinId.has(lc.customerSiteLayoutBinId)) {
                    normalizedBinDataId = binByLayoutBinId.get(lc.customerSiteLayoutBinId).binDataId;
                } else if (lc.binName) {
                    const bin = bins.find(b => b.binName === lc.binName);
                    if (bin) normalizedBinDataId = bin.binDataId;
                }
                if (normalizedBinDataId != null) {
                    lc.binDataId = normalizedBinDataId;
                    lc.binId = normalizedBinDataId;
                }
            });
        }

        // Add Aframes to layoutCables (second pass)
        if (!useEditorBinLocalCables && gw.selectedSite.layoutCables && gw.selectedSite.layoutCables.length > 0) {
            gw.selectedSite.layoutCables.forEach(layoutCable => {
                let cable = gw.selectedSite.cables.find(x => x.id === layoutCable.cableId);
                if (cable) { 
                    layoutCable.aframe = gw.selectedSite.aframes.find(x => x.id === cable.aframeId);
                    if (layoutCable.aframe) {
                        cable.sensorList.forEach(sensor => {
                            if (isHumiditySensor(sensor)) return;
                            let layoutSensor = gw.selectedSite.layoutSensors.find(x => x.sensorId === sensor.id);
                            if (layoutSensor && !isHumidityLayoutSensor(layoutSensor)) {
                                layoutSensor.x = sensor.positionX;
                                layoutSensor.y = sensor.positionY + 4;
                                layoutSensor.z = sensor.positionZ;
                            }
                        });
                    }
                }
            });
        }

        if (!useEditorBinLocalCables) {
            syncAframeLayoutSensorPositionsFromBts(gw.selectedSite);
        }
        // Site Setup can add layout bins without layout cables when other bins already have rows.
        fillMissingLayoutCablesFromSiteBins(gw.selectedSite);
        attachHumidityLayoutSensorsToSite(gw.selectedSite);
        reconcileLayoutCableBinLinks(gw.selectedSite);
        syncLayoutCablePositionsFromBinCableList();
        syncLayoutSensorValuesFromCableSensors();

        if (gw.selectedSite.isBinWindowView) {
            ensureLayoutGeometryForBinView();
        }

        gw.selectedSite.isSiteInitialized = true;
        if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('grainwaveSelectedSiteReady'));
        }

            } catch (error) {
                console.error('initializeCustomerSite: API call failed:', error);
                const endTime = performance.now();
                
                // Check for 401 errors (session expired)
                const is401Error = (error.response && error.response.status === 401) ||
                                  (error.message && error.message.endsWith("401"));
                
                if (is401Error && gw.token && gw.auth && retryDepth < MAX_INIT_SITE_AUTH_RETRIES) {
                    try {
                        const renewResult = await createApiEndpoint(Endpoints.GrainWave).fetchRenew();
                        if (renewResult && gw.token) {
                            return await initializeCustomerSite(navigate, retryDepth + 1);
                        }
                        redirectToLoginAfterAuthFailure(navigate);
                    } catch {
                        redirectToLoginAfterAuthFailure(navigate);
                    }
                    return;
                } else if (is401Error) {
                    // 401 error but no token/auth to renew, clear and redirect
                    // console.log('initializeCustomerSite: 401 error, no token to renew, redirecting to login');
                    // Clear everything first
                    clearAuthState();
                    gw.token = null;
                    gw.auth = null;
                    gw.isAuthenticated = false;
                    delete axios.defaults.headers.common["Authorization"];
                    // Use window.location to force a full page reload and clear auth state
                    // Add a small delay to ensure state is cleared
                    setTimeout(() => {
                        window.location.href = "/emailLogin";
                    }, 100);
                    return;
                }
                
                // For non-401 errors, continue with fallback data
                console.warn('initializeCustomerSite: Non-auth error, continuing with fallback data');
                gw.selectedSite.initializationError = true;
    }
};
const shiftVectorByRadiansOnCircleXZ = (point, angleRadians, radius) => {
    if (radius <= 0) {
        return {
            x: point.x,
            z: point.y
        };
    }

    // Calculate new x and z coordinates using rotation matrix
    const newX = point.x * Math.cos(angleRadians) - point.y * Math.sin(angleRadians);
    const newZ = point.x * Math.sin(angleRadians) + point.y * Math.cos(angleRadians);
    
    // Scale to the desired radius
    const length = Math.sqrt(newX * newX + newZ * newZ);
    const scale = radius / length;
    
    return {
        x: newX * scale,
        z: newZ * scale
    };
}

// Restore cable visibility for currently selected bins
const restoreCableVisibility = (binId) =>{
    if (!gw.selectedSite || !gw.selectedSite.cablesRef) {
        return;
    }

    let processedCount = 0;
    
    // Debug: Show which bins are currently selected
    gw.selectedSite.binsRef.forEach(bin => {
        // Check selected bins
    });
    
    // Get the correct binDataId to filter cables
    const selectedBin = gw.selectedSite.binsRef.find(bin => bin.isSelected);
    if (!selectedBin) {
        // No bin selected, restoring all cables
        // If no bin is selected, restore all cables (site view)
        gw.selectedSite.cablesRef.forEach(cable => {
            processedCount++;
            if (cable.visible !== undefined) {
                cable.visible = true;
            }
            // Process text meshes
            // Get sensor IDs for this cable to check for error values
            const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === cable.cableId) || [];
            const sensorErrorValues = new Set();
            cableSensors.forEach(sensor => {
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                if (isFaintSensorLabel(tempStr)) {
                    sensorErrorValues.add(sensor.sensorId);
                }
            });
            
            // Also check mesh names as fallback for error pattern detection
            let hasErrorPattern = false;
            let dashCount = 0;
            let eCount = 0;
            let oCount = 0;
            
            cable.meshes.forEach(m => {
                if (m.name === "AnDash") dashCount++;
                if (m.name === "AnE") eCount++;
                if (m.name === "AnO") oCount++;
            });
            
            // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
            if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                hasErrorPattern = true;
            }
            
            // Check if this cable has all invalid temperatures
            const hasAllInvalidTemperatures = cableSensors.length > 0 && cableSensors.every(sensor => {
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                return isFaintSensorLabel(tempStr);
            });
            
            // Check if ANY sensor on this cable is in the heltecSensorMappings
            // If a cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            let hasSensorsInHeltecMapping = false;
            if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
                // Check if any sensor on this cable has a mapping in heltecSensorMappings
                for (const sensor of cableSensors) {
                    const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                        mapping.sensorId === sensor.sensorId
                    );
                    if (hasMapping) {
                        hasSensorsInHeltecMapping = true;
                        break;
                    }
                }
            }
            
            // If invalid cables toggle is off and this cable has all invalid temperatures, hide it
            // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (hasAllInvalidTemperatures || hasErrorPattern) && !hasSensorsInHeltecMapping;
            
            // Update cable visibility based on invalid cable toggle
            if (cable.visible !== undefined) {
                cable.visible = !shouldHideInvalidCable;
            }
            
            cable.meshes.forEach(mesh => {
                if (mesh.name === "AnSpace") mesh.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                if (mesh.material) {
                    // Skip level indicator boxes - they should always be hidden
                    if (mesh.isLevel) {
                        mesh.material.visible = false;
                        return;
                    }
                    
                    // Hide invalid cables if toggle is off
                    if (shouldHideInvalidCable) {
                        mesh.material.visible = false;
                        return;
                    }
                    
                    // Check if this mesh belongs to a sensor with an error value
                    // sensorId can be on mesh directly or in meshProps
                    const meshSensorId = mesh.sensorId || mesh.meshProps?.sensorId;
                    const isErrorSensor = meshSensorId && sensorErrorValues.has(meshSensorId);
                    
                    // Check if it's a sensor mesh (has sensorId or isSensor flag)
                    const isSensorMesh = meshSensorId || mesh.meshProps?.isSensor;
                    
                    // AnSpace in cable name/grade - keep hidden (should be edge-on/invisible)
                    if (mesh.name === "AnSpace" && !isSensorMesh) {
                        mesh.material.visible = false;
                        mesh.visible = false;
                        return;
                    }
                    
                    mesh.material.visible = true;
                    
                    // Apply opacity based on error pattern detection
                    // Only affect temperature text (isSensor), not cable names
                    if (isSensorMesh && mesh.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (mesh.name === "AnDash" || mesh.name === "AnE" || mesh.name === "AnO")))) {
                        // In light mode, error values should be visible with same color as bin name labels
                        if (gw.theme === 'light') {
                            mesh.material.opacity = 1.0;
                            mesh.material.transparent = false;
                            mesh.material.color.set("#b4b4b4");
                            if (mesh.material.emissive) {
                                mesh.material.emissive.set("#b4b4b4");
                            }
                        } else {
                            // Invalid values should have very low opacity when not in light mode
                            mesh.material.opacity = 0.0025;
                            mesh.material.transparent = true;
                        }
                    } else if (mesh.name === "AnSpace" && isSensorMesh) {
                        // Space characters always have low opacity (only for temperature text)
                        mesh.material.opacity = 0.0025;
                        mesh.material.transparent = true;
                    } else {
                        // All other meshes get full opacity
                        mesh.material.opacity = 1.0;
                        mesh.material.transparent = false;
                    }
                    
                    mesh.material.needsUpdate = true;
                }
            });
            // Process cable geometry meshes
            if (cable.children && cable.children.length > 0) {
                cable.children.forEach(child => {
                    if (child.material) {
                        // Skip level indicator boxes - they should always be hidden
                        if (child.isLevel) {
                            child.material.visible = false;
                            return;
                        }
                        // Hide invalid cables if toggle is off
                        if (shouldHideInvalidCable) {
                            child.material.visible = false;
                            return;
                        }
                        child.material.visible = true;
                        child.material.opacity = 1.0;
                        child.material.transparent = false;
                        child.material.needsUpdate = true;
                    }
                });
            }
        });
        return;
    }
    
    gw.selectedSite.cablesRef.forEach(cable => {
        const belongsToSelectedBin = cableRefBelongsToBinRef(cable, selectedBin);

        if (!belongsToSelectedBin) {
            if (cable.visible !== undefined) {
                cable.visible = false;
            }
            cable.meshes.forEach(mesh => {
                if (mesh.material && !mesh.isLevel) {
                    mesh.material.visible = false;
                }
            });
            if (cable.children && cable.children.length > 0) {
                cable.children.forEach(child => {
                    if (child.material && !child.isLevel) {
                        child.material.visible = false;
                    }
                });
            }
            return;
        }

        const cableLayoutCable = gw.selectedSite.layoutCables?.find(lc => 
            lc.cableId === cable.cableId
        );
        
        if (cableLayoutCable || belongsToSelectedBin) {
            // Processing cable for binDataId
            processedCount++;
            
            // Make the cable group visible
            if (cable.visible !== undefined) {
                cable.visible = true;
                // Made cable visible
            }
            
            // Process text meshes
            // Get sensor IDs for this cable to check for error values
            const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === cable.cableId) || [];
            const sensorErrorValues = new Set();
            cableSensors.forEach(sensor => {
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                if (isFaintSensorLabel(tempStr)) {
                    sensorErrorValues.add(sensor.sensorId);
                }
            });
            
            // Also check mesh names as fallback for error pattern detection
            let hasErrorPattern = false;
            let dashCount = 0;
            let eCount = 0;
            let oCount = 0;
            
            cable.meshes.forEach(m => {
                if (m.name === "AnDash") dashCount++;
                if (m.name === "AnE") eCount++;
                if (m.name === "AnO") oCount++;
            });
            
            // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
            if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                hasErrorPattern = true;
            }
            
            // Check if this cable has all invalid temperatures
            const hasAllInvalidTemperatures = cableSensors.length > 0 && cableSensors.every(sensor => {
                const tempStr = sensor.sensorValueString?.toString().trim() || "";
                return isFaintSensorLabel(tempStr);
            });
            
            // Check if ANY sensor on this cable is in the heltecSensorMappings
            // If a cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            let hasSensorsInHeltecMapping = false;
            if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
                // Check if any sensor on this cable has a mapping in heltecSensorMappings
                for (const sensor of cableSensors) {
                    const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                        mapping.sensorId === sensor.sensorId
                    );
                    if (hasMapping) {
                        hasSensorsInHeltecMapping = true;
                        break;
                    }
                }
            }
            
            // If invalid cables toggle is off and this cable has all invalid temperatures, hide it
            // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
            const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (hasAllInvalidTemperatures || hasErrorPattern) && !hasSensorsInHeltecMapping;
            
            // Update cable visibility based on invalid cable toggle
            if (cable.visible !== undefined) {
                cable.visible = !shouldHideInvalidCable;
            }
            
            cable.meshes.forEach(mesh => {
                if (mesh.name === "AnSpace") mesh.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                if (mesh.material) {
                    // Skip level indicator boxes - they should always be hidden
                    if (mesh.isLevel) {
                        mesh.material.visible = false;
                        mesh.material.opacity = 0;
                        mesh.material.transparent = true;
                        mesh.material.needsUpdate = true;
                        return;
                    }
                    
                    // Hide invalid cables if toggle is off
                    if (shouldHideInvalidCable) {
                        mesh.material.visible = false;
                        return;
                    }
                    
                    // Check if this mesh belongs to a sensor with an error value
                    // sensorId can be on mesh directly or in meshProps
                    const meshSensorId = mesh.sensorId || mesh.meshProps?.sensorId;
                    const isErrorSensor = meshSensorId && sensorErrorValues.has(meshSensorId);
                    
                    // Check if it's a sensor mesh (has sensorId or isSensor flag)
                    const isSensorMesh = meshSensorId || mesh.meshProps?.isSensor;
                    
                    // AnSpace in cable name/grade - keep hidden (should be edge-on/invisible)
                    if (mesh.name === "AnSpace" && !isSensorMesh) {
                        mesh.material.visible = false;
                        mesh.visible = false;
                        return;
                    }
                    
                    mesh.material.visible = true;
                    
                    // Apply opacity based on error pattern detection
                    // Only affect temperature text (isSensor), not cable names
                    if (isSensorMesh && mesh.name !== "AnSpace" && (isErrorSensor || (hasErrorPattern && (mesh.name === "AnDash" || mesh.name === "AnE" || mesh.name === "AnO")))) {
                        // In light mode, error values should be visible with same color as bin name labels
                        if (gw.theme === 'light') {
                            mesh.material.opacity = 1.0;
                            mesh.material.transparent = false;
                            mesh.material.color.set("#b4b4b4");
                            if (mesh.material.emissive) {
                                mesh.material.emissive.set("#b4b4b4");
                            }
                        } else {
                            // Invalid values should have very low opacity when not in light mode
                            mesh.material.opacity = 0.0025;
                            mesh.material.transparent = true;
                        }
                    } else if (mesh.name === "AnSpace" && isSensorMesh) {
                        // Space characters always have low opacity (only for temperature text)
                        mesh.material.opacity = 0.0025;
                        mesh.material.transparent = true;
                    } else {
                        // All other meshes get full opacity
                        mesh.material.opacity = 1.0;
                        mesh.material.transparent = false;
                    }
                    
                    mesh.material.needsUpdate = true;
                }
            });
            
            // Process cable geometry meshes (children)
            if (cable.children && cable.children.length > 0) {
                // Processing geometry meshes for cable
                cable.children.forEach(child => {
                    if (child.material) {
                        // Skip level indicator boxes - they should always be hidden
                        if (child.isLevel) {
                            child.material.visible = false;
                            return;
                        }
                        // Hide invalid cables if toggle is off
                        if (shouldHideInvalidCable) {
                            child.material.visible = false;
                            return;
                        }
                        child.material.visible = true;
                        child.material.opacity = 1.0;
                        child.material.transparent = false;
                        child.material.needsUpdate = true;
                        // Restored geometry mesh for cable
                    }
                });
            }
        }
    });
    
    // Processed cables for bin
    
    syncAllSensorGroupVisibility();
    
    // Force a re-render to make the cable visibility changes visible immediately
    if (gw.selectedSite.orbitControls && gw.selectedSite.orbitControls.object) {
        // Trigger a re-render by updating the camera
        gw.selectedSite.orbitControls.update();
    }
    
    // Update button state - when restoring cable visibility, unselected cables are visible (bright state)
    let tvcs = window.document.getElementsByClassName("cbi");
    if (tvcs && tvcs.length > 0) {
        const isLight = gw.theme === 'light';
        const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
        tvcs[0].style.color = brightColor;
        tvcs[0].style.setProperty('color', brightColor, 'important');
    }
    
    // Trigger React component re-render
    if (gw.selectedSite.triggerCableVisibilityRerender) {
        gw.selectedSite.triggerCableVisibilityRerender();
    }
}

function updateTemperatureAfterFlash(binId, cableId, sensorId, temperature, lastTemperature) {
    try {
        const humidityRow = gw.selectedSite?.layoutSensors?.find(
            (x) => Number(x.sensorId) === Number(sensorId)
        );
        if (isHumidityLayoutSensor(humidityRow)) return;

        let temperatureString = temperature.toString();

        if (temperatureString === "" || temperatureString === "E") {
            temperatureString = "-E-";
        }

        if (temperatureString.length === 2) {
            temperatureString = ` ${temperatureString}`;
        }
        else if (temperatureString.length === 1) {
            temperatureString = `  ${temperatureString}`;
        }
        else if (temperatureString.length === 0) {
            temperatureString = `   `;
        }

        // Helper function to check if sensor is stale
        const checkIfStale = () => {
            let layoutSensorCheck = gw.selectedSite.layoutSensors.filter(x => x.sensorId === sensorId);
            if (layoutSensorCheck && layoutSensorCheck.length > 0) {
                const sensor = layoutSensorCheck[0];
                if (sensor.lastUpdateTimestamp) {
                    const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
                    const timeSinceUpdate = Date.now() - sensor.lastUpdateTimestamp;
                    return timeSinceUpdate > oneHourInMs;
                }
            }
            return false;
        };

        // Check if sensor is stale - we'll check this right before applying colors
        const isStale = checkIfStale();

        let color = new THREE.Color(
            scaleFormula(89, 0, 255, 0, 1),
            scaleFormula(89, 0, 255, 0, 1),
            scaleFormula(89, 0, 255, 0, 1)
        );

        // If sensor is stale (not updated in over an hour), ALWAYS use gray color
        // This takes precedence over everything else
        if (isStale) {
            // In light mode, use same color as cable names (#b4b4b4)
            // In dark mode, use gray RGB(128, 128, 128)
            if (gw.theme === 'light') {
                color = new THREE.Color("#b4b4b4");
            } else {
                // Gray color: RGB(128, 128, 128)
                color = new THREE.Color(
                    scaleFormula(128, 0, 255, 0, 1),
                    scaleFormula(128, 0, 255, 0, 1),
                    scaleFormula(128, 0, 255, 0, 1)
                );
            }
        }
        else if (lastTemperature && gw.selectedSite.isComparisonOn) {
            if (lastTemperature - temperature >= gw.selectedSite.main.minus) {
                color = new THREE.Color(
                    scaleFormula(0, 0, 255, 0, 1),
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(0, 0, 255, 0, 1)
                );
            }
            else if (temperature - lastTemperature >= gw.selectedSite.main.plus) {
                color = new THREE.Color(
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(255, 0, 255, 0, 1),
                    scaleFormula(0, 0, 255, 0, 1)
                );
            }
        }
        else {
            color = getTemperatureColor(temperature);
        }
        
        if (!color) {
            color = new THREE.Color(
                scaleFormula(89, 0, 255, 0, 1),
                scaleFormula(89, 0, 255, 0, 1),
                scaleFormula(89, 0, 255, 0, 1)
            );
        }
        
        // If sensor is stale, override color to gray regardless of what was calculated above
        if (isStale) {
            color = new THREE.Color(
                scaleFormula(128, 0, 255, 0, 1),
                scaleFormula(128, 0, 255, 0, 1),
                scaleFormula(128, 0, 255, 0, 1)
            );
        }

        let sensorsRef = [];
        for (let s = 0; s < gw.selectedSite.sensorsRef.length; s++) {
            let sr = gw.selectedSite.sensorsRef[s];
            if (sr.sensorId === sensorId) {
                sensorsRef.push(sr);
            }
        }
        if (sensorsRef.length === 0) {
            return;
        }

        for (let i = 0; i < 3; i++) {
            let m = sensorsRef[i];
            let ts = temperatureString[i];
            if (!m || ts == null) continue;

            if (m.name === "AnSpace") {
                m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
            }

            if (ts === "-") {
                m.geometry = gw.geometries.AnDash;
                m.name = "AnDash";
                // Check if this is part of a "-E-" error value
                let isErrorValue = false;
                let checkString = temperatureString.trim();
                if (checkString === "-E-" || checkString === "-O-") {
                    isErrorValue = true;
                }
                
                // Skip level indicator boxes - they should always be hidden
                if (m.isLevel) {
                    m.material.visible = false;
                    continue;
                }
                
                // If sensor is stale, always use gray (check again right before applying)
                // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even if stale
                const currentIsStaleDash = checkIfStale();
                const isHeltecTcError = gw.selectedSite.isSiteHeltecTc && checkString === "-E-";
                if (currentIsStaleDash && !isHeltecTcError) {
                    m.material.opacity = 1;
                    m.material.transparent = false;
                    // Always use gray for stale sensors, regardless of other conditions
                    // In light mode, use same color as cable names (#b4b4b4) - don't set emissive to match cable names
                    // In dark mode, use gray RGB(128, 128, 128)
                    if (gw.theme === 'light') {
                        // Use hex number format to match cable names exactly (0xb4b4b4)
                        m.material.color.setHex(0xb4b4b4);
                        // Cable names don't have emissive, so set it to black
                        if (m.material.emissive) {
                            m.material.emissive.setHex(0x000000);
                        }
                    } else {
                        const grayColor = new THREE.Color(
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1)
                        );
                        m.material.color.set(grayColor);
                        if (m.material.emissive) {
                            m.material.emissive.set(grayColor);
                        }
                    }
                }
                // In light mode, error values should be visible with same color as bin name labels
                // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even in light mode
                else if (isErrorValue && gw.theme === 'light' && !isHeltecTcError) {
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                    m.material.color.set("#b4b4b4");
                    if (m.material.emissive) {
                        m.material.emissive.set("#b4b4b4");
                    }
                } else {
                    m.material.opacity = isErrorValue ? 0.0025 : 1;
                    m.material.transparent = isErrorValue ? true : false;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                }
                applySensorMeshVisibility(m, binId, cableId);
            }
            else if (ts === "O") {
                m.geometry = gw.geometries.AnO;
                m.name = "AnO";
                // Check if this is part of a "-O-" error value
                let isErrorValue = false;
                if (temperatureString === "-O-") {
                    isErrorValue = true;
                }
                
                // Skip level indicator boxes - they should always be hidden
                if (m.isLevel) {
                    m.material.visible = false;
                    continue;
                }
                
                // If sensor is stale, always use gray (check again right before applying)
                const currentIsStaleE = checkIfStale();
                if (currentIsStaleE) {
                    m.material.opacity = 1;
                    m.material.transparent = false;
                    // Always use gray for stale sensors, regardless of other conditions
                    // In light mode, use same color as cable names (#b4b4b4) - don't set emissive to match cable names
                    // In dark mode, use gray RGB(128, 128, 128)
                    if (gw.theme === 'light') {
                        // Use hex number format to match cable names exactly (0xb4b4b4)
                        m.material.color.setHex(0xb4b4b4);
                        // Cable names don't have emissive, so set it to black
                        if (m.material.emissive) {
                            m.material.emissive.setHex(0x000000);
                        }
                    } else {
                        const grayColor = new THREE.Color(
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1)
                        );
                        m.material.color.set(grayColor);
                        if (m.material.emissive) {
                            m.material.emissive.set(grayColor);
                        }
                    }
                }
                // In light mode, error values should be visible with same color as bin name labels
                else if (isErrorValue && gw.theme === 'light') {
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                    m.material.color.set("#b4b4b4");
                    if (m.material.emissive) {
                        m.material.emissive.set("#b4b4b4");
                    }
                } else {
                    m.material.opacity = isErrorValue ? 0.0025 : 1;
                    m.material.transparent = isErrorValue ? true : false;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                }
                applySensorMeshVisibility(m, binId, cableId);
            }
            else if (ts === "E") {
                m.geometry = gw.geometries.AnE;
                m.name = "AnE";
                // Check if this is part of a "-E-" error value
                let isErrorValue = false;
                if (temperatureString === "-E-") {
                    isErrorValue = true;
                }
                
                // Skip level indicator boxes - they should always be hidden
                if (m.isLevel) {
                    m.material.visible = false;
                    continue;
                }
                
                // If sensor is stale, always use gray (check again right before applying)
                // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even if stale
                const currentIsStaleDash = checkIfStale();
                const isHeltecTcError = gw.selectedSite.isSiteHeltecTc && temperatureString === "-E-";
                if (currentIsStaleDash && !isHeltecTcError) {
                    m.material.opacity = 1;
                    m.material.transparent = false;
                    m.material.color.set(color); // color is already gray if isStale
                    if (m.material.emissive) {
                        m.material.emissive.set(color);
                    }
                }
                // In light mode, error values should be visible with same color as bin name labels
                // Exception: For Heltec TC sites, "-E-" error values should remain dim (never gray) even in light mode
                else if (isErrorValue && gw.theme === 'light' && !isHeltecTcError) {
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                    m.material.color.set("#b4b4b4");
                    if (m.material.emissive) {
                        m.material.emissive.set("#b4b4b4");
                    }
                } else {
                    m.material.opacity = isErrorValue ? 0.0025 : 1;
                    m.material.transparent = isErrorValue ? true : false;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                }
                applySensorMeshVisibility(m, binId, cableId);
            }
            else if (ts === " ") {
                m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                m.geometry = gw.geometries.AnSpace;
                m.name = "AnSpace";
                m.material = new THREE.MeshStandardMaterial({
                    color: "#cccccc",
                    emissive: "#cccccc",
                    side: THREE.FrontSide,
                    toneMapped: false,
                    envMapIntensity: 1,
                    metalness: 0.0,
                    transparent: true,
                    opacity: 0,
                    visible: false,
                });
                m.binId = binId;
                m.cableId = cableId;
                    m.sensorId = sensorId;
                    m.visible = false;
                }
            else if (ts === ".") {
                m.geometry = gw.geometries.AnDot;
                m.name = "AnDot";
                // If sensor is stale, always use gray (check again right before applying)
                const currentIsStaleDot = checkIfStale();
                if (currentIsStaleDot) {
                    // Always use gray for stale sensors, regardless of other conditions
                    // In light mode, use same color as cable names (0xb4b4b4) - don't set emissive to match cable names
                    // In dark mode, use gray RGB(128, 128, 128)
                    if (gw.theme === 'light') {
                        // Use hex number format to match cable names exactly (0xb4b4b4)
                        m.material.color.setHex(0xb4b4b4);
                        // Cable names don't have emissive, so set it to black
                        if (m.material.emissive) {
                            m.material.emissive.setHex(0x000000);
                        }
                    } else {
                        const grayColor = new THREE.Color(
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1)
                        );
                        m.material.color.set(grayColor);
                    }
                    m.material.opacity = 1;
                    m.material.transparent = false;
                } else {
                    m.material.opacity = 1;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                }
                applySensorMeshVisibility(m, binId, cableId);
            }
            else {
                if (!setMeshGlyphGeometry(m, glyphGeometryForChar(ts), glyphNameForChar(ts))) {
                    continue;
                }
                const label = String(temperatureString || "").trim();
                const faint = isFaintSensorLabel(label);
                const heltecDim = !!(gw.selectedSite && gw.selectedSite.isSiteHeltecTc && faint);
                const currentIsStaleNum = checkIfStale();
                if (currentIsStaleNum && !heltecDim) {
                    // Always use gray for stale sensors, regardless of other conditions
                    // In light mode, use same color as cable names (0xb4b4b4) - don't set emissive to match cable names
                    // In dark mode, use gray RGB(128, 128, 128)
                    if (gw.theme === 'light') {
                        // Use hex number format to match cable names exactly (0xb4b4b4)
                        m.material.color.setHex(0xb4b4b4);
                        // Cable names don't have emissive, so set it to black
                        if (m.material.emissive) {
                            m.material.emissive.setHex(0x000000);
                        }
                    } else {
                        const grayColor = new THREE.Color(
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1),
                            scaleFormula(128, 0, 255, 0, 1)
                        );
                        m.material.color.set(grayColor);
                        if (m.material.emissive) {
                            m.material.emissive.set(grayColor);
                        }
                    }
                    m.material.opacity = 1;
                    m.material.transparent = false;
                } else if (faint && gw.theme === "light" && !heltecDim) {
                    m.material.opacity = 1.0;
                    m.material.transparent = false;
                    m.material.color.set("#b4b4b4");
                    if (m.material.emissive) {
                        m.material.emissive.set("#b4b4b4");
                    }
                } else if (faint) {
                    m.material.opacity = 0.0025;
                    m.material.transparent = true;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                } else {
                    m.material.opacity = 1;
                    m.material.transparent = false;
                    applyTemperatureColorToSensorMaterial(m.material, color);
                }
                m.material.needsUpdate = true;
                applySensorMeshVisibility(m, binId, cableId);
            }

            if (i === 2) {
                centerText3d(m.parent.parent);
            }
        }
        
        // Update the sensor values after the flash completes
        let layoutSensor = gw.selectedSite.layoutSensors.filter(x => x.sensorId === sensorId);
        if (layoutSensor && layoutSensor.length > 0) {
            layoutSensor = layoutSensor[0];
            layoutSensor.sensorValueString = temperature.toString();
            layoutSensor.sensorValueNumber = temperature;
            // Don't update the timestamp here - it should only be updated in getHeltecData when temperature actually changes
            // This preserves the original timestamp for stale sensors
        }
        
        // Check if this cable has all invalid temperatures after update
        // IMPORTANT: Be conservative - only hide if we're CERTAIN all sensors are invalid.
        // Check if there are any valid sensors - if even one is valid, don't hide.
        const cableSensors = gw.selectedSite.layoutSensors?.filter(s => s.cableId === cableId) || [];
        let hasValidSensor = false;
        
        // Check all sensors - if any have valid temperatures, cable should not be hidden
        for (const sensor of cableSensors) {
            const tempStr = sensor.sensorValueString?.toString().trim() || "";
            if (!isFaintSensorLabel(tempStr)) {
                hasValidSensor = true;
                break;
            }
        }
        
        // Only consider cable invalid if ALL sensors are invalid AND we have sensors
        const hasAllInvalidTemperatures = cableSensors.length > 0 && !hasValidSensor && cableSensors.every(sensor => {
            const tempStr = sensor.sensorValueString?.toString().trim() || "";
            return isFaintSensorLabel(tempStr);
        });
        
        // Also check mesh names as fallback for error pattern detection
        const cableRef = gw.selectedSite.cablesRef.find(c => c.cableId === cableId);
        let hasErrorPattern = false;
        if (cableRef) {
            let dashCount = 0;
            let eCount = 0;
            let oCount = 0;
            
            cableRef.meshes.forEach(mesh => {
                if (mesh.name === "AnDash") dashCount++;
                if (mesh.name === "AnE") eCount++;
                if (mesh.name === "AnO") oCount++;
            });
            
            // Check for "-E-" pattern (2 dashes + 1 E) or "-O-" pattern (2 dashes + 1 O)
            if ((dashCount >= 2 && eCount >= 1) || (dashCount >= 2 && oCount >= 1)) {
                hasErrorPattern = true;
            }
        }
        
        // If invalid cables toggle is OFF and this cable has all invalid temperatures, hide it
        // BUT: If the cable has sensors in heltecSensorMappings (actively receiving data), don't hide it
        // Check if ANY sensor on this cable is in the heltecSensorMappings
        let hasSensorsInHeltecMapping = false;
        if (cableSensors.length > 0 && gw.selectedSite.heltecSensorMappings && gw.selectedSite.heltecSensorMappings.length > 0) {
            // Check if any sensor on this cable has a mapping in heltecSensorMappings
            for (const sensor of cableSensors) {
                const hasMapping = gw.selectedSite.heltecSensorMappings.some(mapping => 
                    mapping.sensorId === sensor.sensorId
                );
                if (hasMapping) {
                    hasSensorsInHeltecMapping = true;
                    break;
                }
            }
        }
        
        // Check if a cable is currently focused - if so, only allow visibility for the focused cable
        const isCableFocused = gw.selectedSite.isCableFocused;
        const isThisCableFocused = sameCableId(cableId, gw.selectedSite.focusedCableId);
        
        // If a cable is focused and this is not the focused cable, hide it
        const shouldHideDueToFocus = isCableFocused && !isThisCableFocused;
        
        const shouldHideInvalidCable = !gw.selectedSite.isInvalidCablesOn && (hasAllInvalidTemperatures || hasErrorPattern) && !hasSensorsInHeltecMapping;
        
        if ((shouldHideInvalidCable || shouldHideDueToFocus) && cableRef) {
            // Hide the cable group
            if (cableRef.visible !== undefined) {
                cableRef.visible = false;
            }
            
            // Hide all meshes
            cableRef.meshes.forEach(mesh => {
                if (mesh.material && !mesh.isLevel) {
                    mesh.material.visible = false;
                }
            });
            
            // Hide all children (geometry meshes)
            if (cableRef.children && cableRef.children.length > 0) {
                cableRef.children.forEach(child => {
                    if (child.material && !child.isLevel) {
                        child.material.visible = false;
                    }
                });
            }
        } else if (cableRef) {
            const shouldShowCable = shouldShowSensorMesh(binId, cableId);
            if (cableRef.visible !== undefined) {
                cableRef.visible = shouldShowCable;
            }

            if (!shouldShowCable) {
                cableRef.meshes.forEach(mesh => {
                    if (mesh.material && !mesh.isLevel) {
                        mesh.material.visible = false;
                    }
                });
                if (cableRef.children && cableRef.children.length > 0) {
                    cableRef.children.forEach(child => {
                        if (child.material && !child.isLevel) {
                            child.material.visible = false;
                        }
                    });
                }
            }
        }
    } catch (e) {
        let teste = 0;
    }
}

const scheduleHeltecRefresh = () => {
    if (!gw.selectedSite || gw.selectedSite.siteIndex < 0) return;
    if (gw.selectedSite.refreshId) {
        clearTimeout(gw.selectedSite.refreshId);
    }
    gw.selectedSite.refreshId = setTimeout(() => {
        getHeltecData();
    }, gw.selectedSite.refreshRate || 5000);
};

const getHeltecData = async () => {
    if (!gw.selectedSite || gw.selectedSite.siteIndex < 0) return;
    // Mappings can arrive after the view mounts. Keep the timer alive or bin3d/site3d never poll again.
    if (!gw.selectedSite.heltecSensorMappings || gw.selectedSite.heltecSensorMappings.length === 0) {
        scheduleHeltecRefresh();
        return;
    }

    // Skip data fetching if history slider is open, but still schedule next check
    if (gw.selectedSite.isHistorySliderOn) {
        scheduleHeltecRefresh();
        return;
    }

    try {
        let response = await createApiEndpoint().fetchSiteHeltecReadings("direct");
        
        // Check if response is false (which might indicate a 401)
        if (response === false && gw.token && gw.auth) {
            // Server likely logged us out - throw 401 to trigger interceptor
            const authError = new Error('401 Unauthorized - Server logged out user');
            authError.response = { status: 401 };
            throw authError;
        }
        
        if (response && response.data && response.data.getSiteHeltecReadings) {
        let arrayBuffer = base64StringToArrayBuffer(response.data.getSiteHeltecReadings);
        let decompressed = await decompress(arrayBuffer, "gzip");
        let wsr = JSON.parse(decompressed);

        wsr.map((k, v) => {
            let layoutSensor = gw.selectedSite.layoutSensors.filter(x => x.sensorId == k.id);
            if (layoutSensor && layoutSensor.length > 0) {
                layoutSensor = layoutSensor[0];
                const heltecReadIso = k.lastgoodtempcheck || k.lastGoodTempCheck || k.LastGoodTempCheck;
                if (heltecReadIso) {
                    layoutSensor.lastGoodTempCheck = heltecReadIso;
                    layoutSensor.heltecLastRead = heltecReadIso;
                }
                // Only update the timestamp if the temperature has actually changed
                // This ensures we preserve the original timestamp for stale sensors
                const temperatureChanged = k.temperature !== layoutSensor.sensorValueNumber;
                
                // Store the last update timestamp for this sensor only if temperature changed
                if (temperatureChanged && k.lastgoodtempcheck) {
                    try {
                        // Parse the timestamp and store it
                        const parsedTimestamp = new Date(k.lastgoodtempcheck.replace("Z", "-05:00")).getTime();
                        const now = Date.now();
                        // Only update if the timestamp is valid and not in the future (allow 1 hour buffer for timezone issues)
                        if (parsedTimestamp && parsedTimestamp <= (now + 60 * 60 * 1000) && parsedTimestamp > 0) {
                            layoutSensor.lastUpdateTimestamp = parsedTimestamp;
                        } else {
                            // If timestamp is invalid or in the future, mark as stale (don't update existing timestamp)
                            if (!layoutSensor.lastUpdateTimestamp) {
                                layoutSensor.lastUpdateTimestamp = now - (2 * 60 * 60 * 1000); // 2 hours ago
                            }
                            // If timestamp is in the future, keep the old timestamp (don't overwrite with future date)
                        }
                    } catch (e) {
                        // If parsing fails and we don't have a timestamp, mark as stale
                        if (!layoutSensor.lastUpdateTimestamp) {
                            layoutSensor.lastUpdateTimestamp = Date.now() - (2 * 60 * 60 * 1000); // 2 hours ago
                        }
                    }
                } else if (!layoutSensor.lastUpdateTimestamp) {
                    // If no timestamp provided and we don't have one, mark as stale
                    layoutSensor.lastUpdateTimestamp = Date.now() - (2 * 60 * 60 * 1000); // 2 hours ago
                }
                // If temperature hasn't changed, preserve the existing timestamp (don't overwrite it)
                // Only flash if temperature has actually changed
                let shouldFlash = temperatureChanged;
                updateSensorTemperature(layoutSensor.binId, layoutSensor.cableId, layoutSensor.sensorId, k.temperature, layoutSensor.sensorValueNumber, shouldFlash);
            }
        });

        // Temperature updates turn cable groups back on. A selected cable must stay the only one shown.
        hideUnfocusedCables();
        refreshCableNameHover();

        let lastScan = gw.selectedSite.lastScan;
        let newLastScan = formatLastScanDisplay(wsr[0].lastgoodtempcheck);
        if (newLastScan && lastScan != newLastScan) {
            setSelectedSiteLastScan(newLastScan);

            // Create a new mark with latest temperatures (strip seconds to match other marks)
            let newMarkLabel = (newLastScan || "").replace(/:\d{2}(?=\s*(?:AM|PM)?$)/i, "");
            let newMark = {
                label: newMarkLabel,
                value: gw.selectedSite.marks.length + 1,
                sensorHistories: [],
                dateCableRead: wsr[0].lastgoodtempcheck,
            };
            
            // Get the latest mark to copy values for sensors not in wsr
            let latestMark = gw.selectedSite.marks.length > 0 ? gw.selectedSite.marks[gw.selectedSite.marks.length - 1] : null;
            
            // Create a map of sensor IDs from wsr for quick lookup
            let wsrSensorIds = new Set(wsr.map(k => k.id));
            
            // Process all layout sensors
            gw.selectedSite.layoutSensors.forEach(s => {
                let sensorHistory = {
                    sensorId: s.sensorId,
                    sensorName: s.sensorName,
                    temperature: s.sensorValueString // Default to current value
                };
                
                // If this sensor is in wsr, use the new temperature
                if (wsrSensorIds.has(s.sensorId)) {
                    let wsrSensor = wsr.find(k => k.id === s.sensorId);
                    if (wsrSensor) {
                        sensorHistory.temperature = wsrSensor.temperature.toString();
                    }
                }
                // If not in wsr but we have a latest mark, use the value from there
                else if (latestMark) {
                    let existingHistory = latestMark.sensorHistories.find(sh => sh.sensorId === s.sensorId);
                    if (existingHistory) {
                        sensorHistory.temperature = existingHistory.temperature;
                    }
                }
                
                newMark.sensorHistories.push(sensorHistory);
            });
            
            // Add the new mark to the marks array
            gw.selectedSite.marks.push(newMark);
            
            // Trigger re-render for components that depend on marks
            if (gw.selectedSite.onMarksUpdate) {
                gw.selectedSite.onMarksUpdate();
            }
        }

        scheduleHeltecRefresh();
    }
    else {
        // Empty payload used to clear the timer and stop live updates until the view remounted.
        scheduleHeltecRefresh();
    }
    } catch (error) {
        const status = error.response && error.response.status;
        const isAuth = status === 401 || (error.message && (error.message.includes("401") || error.message.includes("Unauthorized")));
        if (!isAuth) {
            console.error('getHeltecData: Error fetching heltec data:', error);
        }
        scheduleHeltecRefresh();
    }
};

const LIVE_FAN_POLL_MS = 4000;
let liveFanPollCount = 0;
let liveFanPollTimer = null;
let liveFanPollInFlight = false;

const applyLiveFanStatus = (rows) => {
    const site = gw.selectedSite;
    if (!site || !Array.isArray(rows) || rows.length === 0) return false;
    const byId = new Map();
    for (const row of rows) {
        if (row?.fanId == null) continue;
        byId.set(String(row.fanId), row);
    }
    if (byId.size === 0) return false;

    const patch = (fan) => {
        if (!fan) return false;
        const live = byId.get(String(fan.fanId));
        if (!live) return false;
        const nextOn = !!live.isOn;
        const nextAuto = !!live.isOnAutomatic;
        const nextActive = live.isActive !== false;
        const nextAlarm = !!live.alarmOn;
        const nextIssue = live.fanIssue ?? fan.fanIssue;
        if (
            fan.isOn === nextOn &&
            fan.isOnAutomatic === nextAuto &&
            fan.isActive === nextActive &&
            fan.alarmOn === nextAlarm &&
            fan.fanIssue === nextIssue
        ) {
            return false;
        }
        fan.isOn = nextOn;
        fan.isOnAutomatic = nextAuto;
        fan.isActive = nextActive;
        fan.alarmOn = nextAlarm;
        fan.fanIssue = nextIssue;
        if (live.lastStartTime != null) fan.lastStartTime = live.lastStartTime;
        return true;
    };

    let changed = false;
    (site.fans || []).forEach((fan) => {
        if (patch(fan)) changed = true;
    });
    (site.bins || []).forEach((bin) => {
        (bin.fanList || []).forEach((fan) => {
            if (patch(fan)) changed = true;
        });
    });
    return changed;
};

const refreshLiveFanStatus = async () => {
    if (liveFanPollInFlight) return;
    if (!gw?.email || !gw.selectedSite?.siteGuid) return;
    if (!gw.bolts && !gw.token) return;
    liveFanPollInFlight = true;
    try {
        const result = await createApiEndpoint().fetchSiteFans("direct");
        const fans = result?.fans;
        if (Array.isArray(fans)) applyLiveFanStatus(fans);
    } catch (error) {
        if (error?.response?.status === 401) throw error;
    } finally {
        liveFanPollInFlight = false;
    }
};

const startLiveFanStatusPoll = () => {
    liveFanPollCount += 1;
    if (liveFanPollCount !== 1) return;
    void refreshLiveFanStatus();
    liveFanPollTimer = setInterval(() => {
        void refreshLiveFanStatus();
    }, LIVE_FAN_POLL_MS);
};

const stopLiveFanStatusPoll = () => {
    liveFanPollCount = Math.max(0, liveFanPollCount - 1);
    if (liveFanPollCount > 0 || !liveFanPollTimer) return;
    clearInterval(liveFanPollTimer);
    liveFanPollTimer = null;
};

const initializeHistory = (setIsHistoryLoading, navigate) => {
    void initializeHistoryAsync(setIsHistoryLoading, navigate);
};

/**
 * One slider mark per local clock hour. Heltec gateways upload cables at staggered times, so raw
 * DateCableRead rows would produce a mark every few minutes. Each sensor keeps its latest read in
 * the hour; the mark label/date is the latest read in that hour.
 */
const buildHourlyHistoryMarks = (dateCableReadHistories) => {
    const byHour = new Map();

    for (const dcr of dateCableReadHistories ?? []) {
        const d = new Date(dcr?.dateCableRead);
        const ms = d.getTime();
        if (Number.isNaN(ms)) continue;
        const hourKey = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours()).getTime();

        let bucket = byHour.get(hourKey);
        if (!bucket) {
            bucket = { latest: dcr, latestMs: ms, sensors: new Map() };
            byHour.set(hourKey, bucket);
        } else if (ms > bucket.latestMs) {
            bucket.latest = dcr;
            bucket.latestMs = ms;
        }

        for (const sh of dcr.sensorHistories ?? []) {
            const key = sh.sensorId ?? sh.sensorName;
            if (key == null || key === "") continue;
            const prev = bucket.sensors.get(String(key));
            if (!prev || ms >= prev.ms) {
                bucket.sensors.set(String(key), { sh, ms });
            }
        }
    }

    return Array.from(byHour.keys()).sort((a, b) => a - b).map((hourKey, i) => {
        const bucket = byHour.get(hourKey);
        return {
            label: jsonDateToFormatDate(bucket.latest.dateCableRead, false),
            value: i + 1,
            sensorHistories: Array.from(bucket.sensors.values()).map((x) => x.sh),
            dateCableRead: bucket.latest.dateCableRead,
        };
    });
};

const initializeHistoryAsync = async (setIsHistoryLoading, navigate) => {
    // Site3d reload restores stale lastScan from localStorage; ensure API data wins before building marks.
    syncSelectedSiteLastScanFromData();

    // Use at least 2 days for initial load - minimizes TemperatureHistory scan for sites with ~1M records
    let dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - 2);
    let dateTo = new Date();
    dateTo.setDate(dateTo.getDate());
    let start = dateFrom.toJSON().split("T")[0] + "T00:00:00.000Z";
    let end = dateTo.toJSON().split("T")[0] + "T23:59:59.999Z";

    const { resolveDefaultHistoryTimeOfDay } = await import('./historyTimeOfDay.js');
    const timeOfDay = await resolveDefaultHistoryTimeOfDay(gw.selectedSite?.siteGuid);

    // Fire the BinLevelHistory fetch in parallel with the temperature history fetch so the slider
    // has BinLevel data ready as soon as marks render (no extra round-trip on first scrub).
    loadSiteBinLevelHistory(start, end);

    const dataLastScan = gw.selectedSite?.data?.lastScan;

    try {
        const response = await createApiEndpoint().fetchUserSiteHistoryWithDate("direct", start, end, timeOfDay);
        if (response) {
            // fetchDirect returns the GraphQL body: { data: { getUserHistoryWithDateAndTime: ... } }
            var data = response.data?.getUserHistoryWithDateAndTime ?? response.data?.data?.getUserHistoryWithDateAndTime ?? {};

            if (data) {
                let dateCableReadHistories = data.dateCableReadHistories ?? [];

                if (dateCableReadHistories.length > 0 && gw.selectedSite.heltecSensorMappings?.length > 0) {
                    gw.selectedSite.marks = buildHourlyHistoryMarks(dateCableReadHistories);
                }
                else if (dateCableReadHistories.length > 0) {
                    gw.selectedSite.marks = dateCableReadHistories.map((dcr, i) => {
                        let markDate = {
                            label: jsonDateToFormatDate(dcr.dateCableRead, false),
                            value: i + 1,
                            sensorHistories: dcr.sensorHistories,
                            dateCableRead: dcr.dateCableRead,
                        };
                        return markDate;
                    });
                }

                const lastScanLabel = (gw.selectedSite.lastScan || "").replace(/:\d{2}(?=\s*(?:AM|PM)?$)/i, "");
                if (!gw.selectedSite.marks.any(x => x.label === lastScanLabel) && gw.selectedSite.layoutSensors) {
                    let originalMark = {
                        label: lastScanLabel,
                        value: gw.selectedSite.marks.length + 1,
                        sensorHistories: [],
                        dateCableRead: dataLastScan || null,
                    };

                    gw.selectedSite.layoutSensors.forEach(s => {
                        let sensorHistory = {
                            sensorId: s.sensorId,
                            sensorName: s.sensorName,
                            temperature: s.sensorValueString
                        };
                        originalMark.sensorHistories.push(sensorHistory);
                    });

                    gw.selectedSite.marks.push(originalMark);
                }
            }
            if (gw.selectedSite.onMarksUpdate) {
                gw.selectedSite.onMarksUpdate();
            }
            setIsHistoryLoading(false);
        }
        else {
            if (shouldKeepSite3dOpen()) {
                setIsHistoryLoading(false);
            } else {
                navigate("/sites");
            }
        }
    } catch (error) {
        // Check for 401 errors (session expired)
        const is401Error = (error.response && error.response.status === 401) ||
                          (error.message && error.message.endsWith("401"));
        
        if (is401Error && gw.token && gw.auth) {
            // console.log('initializeHistory: 401 error detected, attempting token renewal...');
            createApiEndpoint(Endpoints.GrainWave).fetchRenew()
                .then(r => {
                    if (r && gw.token) {
                        // Token renewed successfully, retry initialization
                        // console.log('initializeHistory: Token renewed, retrying...');
                        initializeHistory(setIsHistoryLoading, navigate);
                    } else {
                        // Token renewal failed, clear auth and redirect to login
                        // console.log('initializeHistory: Token renewal failed, redirecting to login');
                        // Clear everything first
                        clearAuthState();
                        gw.token = null;
                        gw.auth = null;
                        gw.isAuthenticated = false;
                        delete axios.defaults.headers.common["Authorization"];
                        setIsHistoryLoading(false);
                        // Use window.location to force a full page reload and clear auth state
                        // Add a small delay to ensure state is cleared
                        setTimeout(() => {
                            window.location.href = "/emailLogin";
                        }, 100);
                    }
                })
                .catch(() => {
                    // Token renewal failed, clear auth and redirect to login
                    // console.log('initializeHistory: Token renewal error, redirecting to login');
                    // Clear everything first
                    clearAuthState();
                    gw.token = null;
                    gw.auth = null;
                    gw.isAuthenticated = false;
                    delete axios.defaults.headers.common["Authorization"];
                    setIsHistoryLoading(false);
                    // Use window.location to force a full page reload and clear auth state
                    // Add a small delay to ensure state is cleared
                    setTimeout(() => {
                        window.location.href = "/emailLogin";
                    }, 100);
                });
        }
        else if (is401Error) {
            // 401 error but no token/auth to renew, clear and redirect
            // console.log('initializeHistory: 401 error, no token to renew, redirecting to login');
            // Clear everything first
            clearAuthState();
            gw.token = null;
            gw.auth = null;
            gw.isAuthenticated = false;
            delete axios.defaults.headers.common["Authorization"];
            setIsHistoryLoading(false);
            // Use window.location to force a full page reload and clear auth state
            // Add a small delay to ensure state is cleared
            setTimeout(() => {
                window.location.href = "/emailLogin";
            }, 100);
        }
        else {
            // For non-401 errors, continue without history data
            console.warn('initializeHistory: Non-auth error, continuing without history data...');
            gw.selectedSite.initializationError = true;
            setIsHistoryLoading(false);
        }
    }
};

const getBrxData = async () => {
    if (gw.selectedSite.siteIndex < 0) return;
    if (gw.selectedSite.siteGuid !== "d25e8abf-e01a-4550-ab72-d24f2cf7f34e") return;

    //axios.interceptors.response.use(function (response) {
    //    return response;
    //}, function (error) {
    //    if (error.response &&
    //        error.response.status === 401 &&
    //        gw.token &&
    //        gw.auth) {
    //        createApiEndpoint(Endpoints.GrainWave)
    //            .fetchRefresh(gw.token, gw.auth)
    //            .then(r => {
    //                if (r) {
    //                    let splits = r.split("|");
    //                    if (splits.length !== 2) {
    //                        clearToken();
    //                        navigate("/");
    //                        if (gw.selectedSite.refreshId) {
    //                            clearTimeout(gw.selectedSite.refreshId);
    //                        }
    //                    }
    //                    else {
    //                        gw.token = splits[0];
    //                        gw.auth = splits[1];
    //                    }
    //                }
    //            });
    //        if (gw.selectedSite.refreshId) {
    //            clearTimeout(gw.selectedSite.refreshId);
    //        }
    //        gw.selectedSite.refreshId = setTimeout(() => {
    //            getBrxData();
    //        }, gw.selectedSite.refreshRate);
    //    }
    //    else {
    //        return Promise.reject(error);
    //    }
    //});
    //try {
    //    const { outerResponse } = await axios.post(
    //        `${ApiUrl}${Endpoints.GrainWave}`, {
    //        headers: {
    //            'Content-Type': 'application/json',
    //            "Authorization": "Bearer " + gw.token,
    //        },
    //        data: {
    //            query: `
    //                query UserAttribute($email: String!, $siteId: String!, $location: String!) {
    //                    getBrxData(email: $email, siteId: $siteId, location: $location) {
    //                        sites {
    //                            brxData {
    //                                json
    //                            }
    //                        }
    //                    }
    //                }
    //            `,
    //            variables: {
    //                email: gw.email,
    //                siteId: gw.selectedSite.siteGuid,
    //                location: "000"
    //            }
    //        }
    //    });

    //    let temperature = 0;
    //    //temperature = getRandomInt(90, 109);
    //    temperature = getRandomInt(-15, 20);
    //    let asdfasdf = gw.selectedSite.layoutSensors;
    //    gw.selectedSite.layoutSensors.forEach(s => {
    //        updateSensorTemperature(s.binId, s.cableId, s.sensorId, temperature);
    //        s.sensorValueString = temperature.toString();
    //        s.sensorValueNumber = temperature;
    //        temperature += 1;
    //    });

    //    if (gw.selectedSite.isCablesOff) {
    //        setAllBinsTextColorOn();
    //    }
    //    else {
    //        setAllBinsTextColorOff();
    //    }

    //    gw.selectedSite.refreshId = setTimeout(() => {
    //        getBrxData();
    //    }, gw.selectedSite.refreshRate);
    //} catch (error) {
    //    if (error.response &&
    //        error.response.status === 401 &&
    //        gw.token &&
    //        gw.auth) {
    //        createApiEndpoint(Endpoints.GrainWave)
    //            .fetchRefresh(gw.token, gw.auth)
    //            .then(r => {
    //                if (r) {
    //                    let splits = r.split("|");
    //                    if (splits.length !== 2) {
    //                        clearToken();
    //                        navigate("/");
    //                        if (gw.selectedSite.refreshId) {
    //                            clearTimeout(gw.selectedSite.refreshId);
    //                        }
    //                    }
    //                    else {
    //                        gw.token = splits[0];
    //                        gw.auth = splits[1];
    //                    }
    //                }
    //            });
    //        if (gw.selectedSite.refreshId) {
    //            clearTimeout(gw.selectedSite.refreshId);
    //        }
    //        gw.selectedSite.refreshId = setTimeout(() => {
    //            getBrxData();
    //        }, gw.selectedSite.refreshRate);
    //    }
    //    else {
    //        return Promise.reject(error);
    //    }
    //}
};

// Capture the initial material state of all cables when they are first loaded
// This ensures we can restore to the true original state, not the state when focusOnCable was called
const captureInitialCableStates = () => {
    if (!gw.selectedSite || !gw.selectedSite.cablesRef) {
        return;
    }

    // Only capture once to avoid overwriting
    if (gw.selectedSite.initialCableStates) {
        return;
    }

    gw.selectedSite.initialCableStates = new Map();
    
    gw.selectedSite.cablesRef.forEach(cable => {
        cable.meshes.forEach(mesh => {
            if (mesh.material && !mesh.isText) {
                const meshKey = `${cable.cableId}_${mesh.uuid}`;
                gw.selectedSite.initialCableStates.set(meshKey, {
                    opacity: mesh.material.opacity,
                    transparent: mesh.material.transparent,
                    visible: mesh.material.visible,
                    color: mesh.material.color ? mesh.material.color.clone() : null,
                    emissive: mesh.material.emissive ? mesh.material.emissive.clone() : null,
                    name: mesh.name // Store the mesh name for debugging
                });
            }
        });
    });
};

// Update the cable toggle button visual state to reflect the current cable visibility
const updateCableToggleButtonState = () => {
    if (gw.selectedSite && gw.selectedSite.isCablesOff !== undefined) {
        let tvcs = window.document.getElementsByClassName("cbi");
        if (tvcs && tvcs.length > 0) {
            const isLight = gw.theme === 'light';
            const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
            if (gw.selectedSite.isCablesOff) {
                // Cables are OFF - button should be dimmed
                tvcs[0].style.color = dimmedColor;
            } else {
                // Cables are ON - button should be bright
                tvcs[0].style.color = brightColor;
            }
        }
    }
};

const SITE_LAYOUT_RETURN_STORAGE_KEY = "siteLayout_return";
const SITE3D_PINNED_STORAGE_KEY = "grainwave_site3d_pinned";

/** Mark production Site 3D as intentionally open (survives hourly reload; cleared on close). */
const markSite3dPinned = () => {
    try {
        sessionStorage.setItem(SITE3D_PINNED_STORAGE_KEY, "1");
    } catch { /* ignore quota */ }
};

const clearSite3dPinned = () => {
    try {
        sessionStorage.removeItem(SITE3D_PINNED_STORAGE_KEY);
    } catch { /* ignore */ }
};

const isSite3dPinned = () => {
    try {
        return sessionStorage.getItem(SITE3D_PINNED_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
};

/** True when Site 3D should not be auto-closed by background fetches. */
const shouldKeepSite3dOpen = () => {
    if (typeof window === "undefined") return isSite3dPinned();
    return isSite3dPinned() || window.location.pathname === "/site3d";
};

/** Remember Site Layout editor context when opening production Site 3D from the editor. */
const setSiteLayoutReturn = (siteGuid) => {
    if (!siteGuid) return;
    const guid = String(siteGuid);
    gw.siteLayoutReturnPath = "/siteLayout";
    gw.siteLayoutReturnSiteGuid = guid;
    try {
        sessionStorage.setItem(
            SITE_LAYOUT_RETURN_STORAGE_KEY,
            JSON.stringify({ path: "/siteLayout", siteGuid: guid, fromEditor: true })
        );
    } catch { /* ignore quota */ }
};

const peekSiteLayoutReturn = () => {
    if (gw.siteLayoutReturnPath) {
        return {
            path: gw.siteLayoutReturnPath,
            siteGuid: gw.siteLayoutReturnSiteGuid ?? null,
            fromEditor: true,
        };
    }
    try {
        const raw = sessionStorage.getItem(SITE_LAYOUT_RETURN_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const clearSiteLayoutReturn = () => {
    delete gw.siteLayoutReturnPath;
    delete gw.siteLayoutReturnSiteGuid;
    try {
        sessionStorage.removeItem(SITE_LAYOUT_RETURN_STORAGE_KEY);
    } catch { /* ignore */ }
};

/** True only when Site 3D was opened from Site Layout for the currently selected site. */
export function isSiteLayoutEditorReturn() {
    const ret = peekSiteLayoutReturn();
    if (!ret?.fromEditor || ret.path !== "/siteLayout" || !ret.siteGuid) return false;
    const current = gw.selectedSite?.siteGuid;
    if (!current) return false;
    return normalizeGuidForCompare(ret.siteGuid) === normalizeGuidForCompare(current);
}

/** Close Site 3D and return to Site Layout when preview was opened from the editor. */
const navigateBackToSiteLayout = (navigate) => {
    if (!isSiteLayoutEditorReturn()) return false;
    const ret = peekSiteLayoutReturn();
    clearSite3dPinned();
    clearSiteLayoutReturn();
    navigate(ret.path || "/siteLayout", {
        state: {
            restoreSiteLayoutEditor: true,
            siteGuid: ret?.siteGuid ?? gw.selectedSite?.siteGuid ?? null,
        },
    });
    return true;
};

// Export helper functions for auth state management
export {
    saveAuthState,
    clearAuthState,
    restoreAuthState,
    readImpersonator,
    beginImpersonation,
    endImpersonation,
    saveSelectedSite,
    restoreSelectedSite,
    GRAINWAVE_BROWSER_SESSION_KEY,
    setSiteLayoutReturn,
    peekSiteLayoutReturn,
    clearSiteLayoutReturn,
    navigateBackToSiteLayout,
    markSite3dPinned,
    clearSite3dPinned,
    isSite3dPinned,
};

export {
    clearAllFlashAnimations,
    toggleCableLevelRef,
    hexToBase64,
    dateToJsonCurrentTimeZone,
    decompressIn,
    arrayBufferToBase64String,
    base64StringToArrayBuffer,
    base64ToString,
    compress,
    decompress,
    decompressAsync,
    drawSite,
    bytesToHex,
    stringToUTF8Bytes,
    stringToUTF16Bytes,
    stringToUTF32Bytes,
    hexToBytes,
    bytesToStringUTF32,
    ApiUrl,
    isAdminUser,
    applyMargin,
    arcAngleRadians,
    calculateFOV,
    centerSite,
    centerText3d,
    checkLineIntersection,
    data,
    dateFormat,
    decryptDataIn,
    decryptMessageIn,
    encryptMessage,
    encryptMessageOutAsync,
    extendLine,
    Endpoints,
    fitCameraToObject,
    fitCameraToObjectNoAngle,
    fitCameraToObjectWithAngle,
    fitCameraToObjectWithAngleAndZoomCount,
    generateRsaAsync,
    getAngleQuadrant,
    getAngleRadiansBetweenPoints,
    getArcPoint,
    getArcPoints,
    getArcStartAndEndRadians,
    getBinTemperatureColor,
    getCarbondioxideColor,
    getCenterOfPoints,
    getCircleCenterPoint,
    getCircleLineIntersections,
    getClientDeviceInfo,
    getClosestPointToPoints,
    getDatesByHour,
    getDateInfo,
    getJsonDateInfo,
    getDefaultBin,
    getFitCameraToObjectVector3,
    getFreshDataModel,
    getHighestNumber,
    getHighestTemperatureColor,
    getHighestCarbonDioxideColor,
    getIntersectionOfTwoCircles,
    getLineCylinderIntersection,
    getLineIntersect,
    getMiddleAngle,
    getMiddleArcPoint,
    getObjectUnderCursor,
    getOtherLineIntersection,
    getPlaneRotation,
    getPointToPointLength,
    getRandomInt,
    getScaledAngle,
    getScaledPoints,
    getSiteLayoutDimensions,
    getSiteSceneBox,
    getSiteSceneDimensions,
    getSlope,
    getCarbonDioxideColor,
    getCarbonDioxideColorString,
    getTemperatureColor,
    getTemperatureColorString,
    getGradeColorString,
    normalizeGuidForCompare,
    getThetaAngles,
    getThetaRadians,
    getThetaStart,
    getTotalAngle,
    getWorldPointUnderCursor,
    getYIntercept,
    grainLevelMaterial,
    groupByMultiple,
    gw,
    highlightStice,
    applySelectedBinWallHighlight,
    findBinRefFromDropdownId,
    findBinRefForHighlight,
    focusOnCable,
    initializeCustomerSite,
    ensureLayoutGeometryForBinView,
    focusBinViewOnBin,
    pruneDetachedBinViewRefs,
    prepareBinViewSceneMount,
    isLiveBinViewCamera,
    prefetchBinSavedView,
    applyBinWindowCamera,
    initializeSiteLayout,
    syncLayoutBinNamesFromCatalog,
    populateLayoutCablesFromBins,
    attachHumidityLayoutSensorsToSite,
    isPointYInsideMeshY,
    isPointInsidePoints,
    isPointInsidePolygon,
    isPointOutsideCircle,
    isValidUuid,
    jsonDateToFormatDate,
    jsonDateToFormatDateOnly,
    List,
    lowlight,
    phaseShiftPassY,
    pointToPointLength,
    positionSite,
    raycastBin,
    rayCylinderIntersection,
    renderBinGrainLevel,
    renderGrainLevels,
    renderSiteBinLevels,
    clearSiteBinLevels,
    clearBinLevelLabelHover,
    updateBinLevelLabelHover,
    clearCableNameHover,
    updateCableNameHover,
    showCableNameLastRead,
    BIN_LEVEL_SITE_CARD_FILL,
    fetchBinLevelSnapshotRowsForSiteGuid,
    findBinLevelRowForCardBin,
    getCardBinLevelFillRatio,
    isDashboardBinFlatHouse,
    formatBinLevelContentsDisplay,
    formatBinLevelTooltipTextFromRows,
    summarizeSiteInventoryTotalsFromBinLevelRows,
    fetchSiteInventoryBinDimensionsForSiteGuid,
    fetchSiteInventoryEnvelopeForSiteGuid,
    renderSensorParticles,
    resetCamera,
    resetBinViewToDefault,
    restoreCableVisibility,
    hideUnfocusedCables,
    revertCamera,
    Site,
    saveBinCamera,
    saveSiteCamera,
    sensorReadMatchesSiteLastScan,
    isLiveTemperatureReading,
    syncLayoutSensorValuesFromCableSensors,
    buildSiteViewPayload,
    buildBinViewPayload,
    applySavedView,
    scaleBinText,
    scaleFormula,
    serializeDataIn,
    setCameraLookAtBinOrigin,
    setUnselectedCablesOff,
    setInvalidCables,
    setGradesVisibility,
    setAllCablesOn,
    setAllBinsTextColor,
    setAllBinsTextColorOff,
    setAllBinsTextColorOn,
    setAllSitesWeatherStationReadings,
    setSiteBinLevels,
    loadSiteBinLevelHistory,
    setIsObjectSelected,
    setPosition,
    setPosition2,
    setSiteName,
    shiftVectorByRadiansOnCircleXZ,
    signMessageAsync,
    set3dCameraModeOn,
    refreshAllSensorTemperatureColors,
    refreshSite3dFromLayoutEditor,
    resetSite3dToSiteOverview,
    prepareSite3dSceneMount,
    siteView,
    startLiveFanStatusPoll,
    stopLiveFanStatusPoll,
    toggleComparison,
    toggleHistorySlider,
    topView,
    updateCompass,
    updateSensorTemperature,
    setSensorLabelAsync,
    applyDatesAsync,
    updateDateAsync,
    getHeltecData,
    initializeHistory,
    buildHourlyHistoryMarks,
    sensorIsHeltec,
    getBrxData,
    captureInitialCableStates
}

